/**
 * Advertised in Journal — Status tab, step 5 (s5)
 *
 * Standard Advertisement → TM-* selector → sub-steps:
 *   - Status Details (Journal Date + Last date for Opposition (auto +60d) +
 *     Acknowledgement No. (read-only) + Order from Registry PDF upload)
 *   - Notice to Client (lists uploaded Order from Registry PDFs across the TMs
 *     in this status; "Send & Continue" merges them and forwards via WhatsApp)
 *   - Inform Client about Advertised in Journal (placeholder for now)
 *
 * "Opposed" variant is rendered as a placeholder so navigation matches Figma.
 *
 * Dashboard footer integration mirrors FormalitiesCheckView / ExaminationReportView:
 *   - When the active sub-step has an actionable form, this view sets the matching
 *     footer-mode flag via callback so the dashboard's footer button submits the
 *     correct form id.
 */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { HiChevronDown, HiOutlineInformationCircle, HiOutlineX } from "react-icons/hi";
import { Document, Page } from "react-pdf";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import {
  getAdvertisedInJournalStatus,
  setAdvertisedInJournalStatusDetails,
  sendAdvertisedInJournalNoticeToClient,
  getAdvertisedInJournalNoticePreview,
  type AdvertisedInJournalNoticePreview,
  informClientAdvertisedInJournal,
  previewInformClientAdvertisedInJournal,
  getOppositionCounterStatus,
  setOppositionCounterStatusDetails,
  sendOppositionCounterNoticeToClient,
  getOppositionCounterNoticePreview,
  type CounterNoticePreview,
  getInformClientAboutCounterPreview,
  informClientAboutOppositionCounter,
  recordCallClientForCounter,
  getCounterConfirmationFromClient,
  saveCounterConfirmationFromClient,
  getCounterInvoice,
  getCounterInvoicePreview,
  getCounterInvoiceWhatsAppPreview,
  saveCounterInvoice,
  generateCounterInvoicePdf,
  sendCounterInvoiceToClient,
  saveCounterCollectedAmount,
  getPrepareCounterStatus,
  savePrepareCounter,
  generatePrepareCounterPdf,
  getFileCounterStatus,
  generateFileCounterPresignedUrls,
  saveFileCounter,
  getFileCounterInformClientPreview,
  sendFileCounterInformClientWhatsApp,
  getNoticeOfCounter,
  saveNoticeOfCounter,
  getInformClientNoticeOfCounterPreview,
  sendInformClientNoticeOfCounterWhatsApp,
  getEvidenceByOpponentStatus,
  saveEvidenceByOpponentStatus,
  sendEvidenceByOpponentNoticeToClient,
  getEvidenceByOpponentNoticeToClientPreview,
  type EvidenceByOpponentNoticePreview,
  getInformClientEvidenceByOpponentPreview,
  sendInformClientEvidenceByOpponentWhatsApp,
  recordCallClientForEvidence,
  getEvidenceConfirmationFromClient,
  saveEvidenceConfirmationFromClient,
  getEvidenceInvoice,
  getEvidenceInvoicePreview,
  getEvidenceInvoiceWhatsAppPreview,
  saveEvidenceInvoice,
  generateEvidenceInvoicePdf,
  sendEvidenceInvoiceToClient,
  saveEvidenceCollectedAmount,
  sendEvidenceInvoicePaymentMessage,
  getStampPaperForEvidenceStatus,
  saveStampPaperForEvidenceDetails,
  getStampPaperForEvidenceMessagePreview,
  sendStampPaperForEvidenceToVendor,
  saveStampPaperPaymentToVendorForEvidence,
  getDocumentsFromClientForEvidenceStatus,
  saveDocumentsFromClientForEvidence,
  getEvidenceAffidavitStatus,
  saveEvidenceAffidavit,
  generateEvidenceAffidavitPdf,
  recordSignatureFromClientForAffidavit,
  getNotarisationForEvidenceStatus,
  saveNotaryPaymentForEvidence,
  recordScanningOfDocumentsForEvidence,
  getFileEvidenceAffidavitStatus,
  saveFileEvidenceAffidavit,
  generateFiledEvidenceAffidavitPresignedUrls,
  getInformClientFilingOfEvidencePreview,
  sendInformClientFilingOfEvidenceWhatsApp,
  getDownloadRejoinderAffidavitStatus,
  saveDownloadRejoinderAffidavit,
  generateRejoinderAffidavitPresignedUrls,
  getOppositionHearingStatus,
  setOppositionHearingStatusDetails,
  sendOppositionHearingNoticeToClient,
  getOppositionHearingNoticeToClientPreview,
  getInformClientOppositionHearingPreview,
  sendInformClientOppositionHearing,
  getOppositionHearingInvoiceStatus,
  getOppositionHearingInvoicePreview,
  getOppositionHearingInvoiceWhatsAppPreview,
  saveOppositionHearingInvoice,
  generateOppositionHearingInvoicePdf,
  sendOppositionHearingInvoiceToClient,
  saveOppositionHearingCollectedAmount,
  recordCallClientOppositionHearing,
  getOppositionHearingConfirmation,
  saveOppositionHearingConfirmation,
  getTmMBeforeOppositionHearingInvoiceStatus,
  getTmMBeforeOppositionHearingInvoicePreview,
  getTmMBeforeOppositionHearingInvoiceWhatsAppPreview,
  saveTmMBeforeOppositionHearingInvoice,
  generateTmMBeforeOppositionHearingInvoicePdf,
  sendTmMBeforeOppositionHearingInvoiceToClient,
  saveTmMBeforeOppositionHearingCollectedAmount,
  getTmMBeforeOppositionHearingFiledStatus,
  saveTmMBeforeOppositionHearingFiled,
  getInformClientTmMBeforeOppositionHearingPreview,
  sendInformClientTmMBeforeOppositionHearingWhatsApp,
  getOppositionHearingPaymentStatus,
  saveOppositionHearingPayment,
  progressDocumentsFromClientOppositionHearing,
  progressFilingOppositionHearingDocument,
  getNoteOnOppositionHearingStatus,
  saveNoteOnOppositionHearing,
  getAttendOppositionHearingStatus,
  saveAttendOppositionHearing,
  getInformAfterOppositionHearingStatus,
  sendAllInformAfterOppositionHearing,
  recordInformAfterOppositionHearing,
  getTmMAfterOppositionHearingInvoiceStatus,
  getTmMAfterOppositionHearingInvoicePreview,
  getTmMAfterOppositionHearingInvoiceWhatsAppPreview,
  saveTmMAfterOppositionHearingInvoice,
  generateTmMAfterOppositionHearingInvoicePdf,
  sendTmMAfterOppositionHearingInvoiceToClient,
  saveTmMAfterOppositionHearingCollectedAmount,
  getTmMAfterOppositionHearingFiledStatus,
  saveTmMAfterOppositionHearingFiled,
  getInformClientTmMAfterOppositionHearingPreview,
  sendInformClientTmMAfterOppositionHearingWhatsApp,
  getOrderOfOppositionHearingStatus,
  saveOrderOfOppositionHearingDetails,
  sendAllOrderOfOppositionHearingMessages,
  getOrderOfOppositionHearingMessagePreview,
  generateAcknowledgementUploadUrls,
  uploadAcknowledgementPdf,
  type AdvertisedInJournalApplication,
  type AdvertisedInJournalStatusResponse,
  type AdvertisedInJournalAdvertisedFrom,
  type OppositionCounterApplication,
  type OppositionCounterStatusResponse,
  type SetOppositionCounterApplicationInput,
  type CounterConfirmationStatus,
  type CounterInvoiceStatus,
  type CounterInvoicePreview,
  type CounterInvoiceWhatsAppPreview,
  type PrepareCounterApplicationItem,
  type PrepareCounterStatus,
  type FileCounterStatus,
  type FileCounterApplicationRow,
  type NoticeOfCounterRow,
  type InformClientNoticeOfCounterPreview,
  type EvidenceByOpponentStatus,
  type InformClientEvidenceByOpponentPreview,
  type EvidenceConfirmationStatus,
  type EvidenceInvoiceStatus,
  type EvidenceInvoicePreview,
  type EvidenceInvoicePreviewApplication,
  type StampPaperForEvidenceStatus,
  type DocumentsFromClientForEvidenceStatus,
  type EvidenceAffidavitStatus,
  type EvidenceAffidavitItem,
  type NotarisationForEvidenceStatus,
  type FileEvidenceAffidavitStatus,
  type FileEvidenceAffidavitApplicationRow,
  type InformClientFilingOfEvidencePreview,
  type DownloadRejoinderAffidavitStatus,
  type DownloadRejoinderAffidavitApplicationRow,
  type OppositionHearingStatus,
  type OppositionHearingNoticePreview,
  type InformClientOppositionHearingPreview,
  type OppositionHearingInvoiceStatus,
  type OppositionHearingInvoicePreview,
  type OppositionHearingInvoiceWhatsAppPreview,
  type OppositionHearingConfirmationStatus,
  type OppositionHearingClientConfirmation,
  type TmMBeforeOppositionHearingInvoiceStatus,
  type TmMBeforeOppositionHearingInvoicePreview,
  type TmMBeforeOppositionHearingInvoiceWhatsAppPreview,
  type TmMBeforeOppositionHearingFiledStatus,
  type TmMBeforeOppositionHearingFiledApplicationRow,
  type InformClientTmMBeforeOppositionHearingPreview,
  type OppositionHearingPaymentStatus,
  type NoteOnOppositionHearingStatus,
  type NoteOnOppositionHearingSimilarMarkNote,
  type AttendOppositionHearingStatusResponse,
  type AttendOppositionHearingStatusValue,
  type AttendOppositionHearingOfficerRemarkValue,
  type InformAfterOppositionHearingStatus,
  type InformAfterOppositionHearingScenarioValue,
  type TmMAfterOppositionHearingInvoiceStatus,
  type TmMAfterOppositionHearingInvoicePreview,
  type TmMAfterOppositionHearingInvoiceWhatsAppPreview,
  type TmMAfterOppositionHearingFiledStatus,
  type TmMAfterOppositionHearingFiledApplicationRow,
  type InformClientTmMAfterOppositionHearingPreview,
  type OrderOfOppositionHearingStatus,
  type OrderOfOppositionHearingOutcomeValue,
  getSubsequentOppositionHearingStatus,
  saveSubsequentOppositionHearingDetails,
  saveSubsequentOppositionHearingOutcome,
  saveSubsequentOppositionHearingPostponement,
  getSubsequentOppositionHearingNoticePreview,
  sendSubsequentOppositionHearingNoticeToClient,
  getSubsequentOppositionHearingInvoiceStatus,
  saveSubsequentOppositionHearingInvoice,
  generateSubsequentOppositionHearingInvoicePdf,
  sendSubsequentOppositionHearingInvoiceToClient,
  saveSubsequentOppositionHearingCollectedAmount,
  type SubsequentOppositionHearingStatus,
  type SubsequentOppositionHearingNoticePreview,
  type SubsequentOppositionHearingInvoiceStatus,
  type SaveSubsequentOppositionHearingDetailsItem,
  type SaveSubsequentOppositionHearingOutcomeItem,
  type SaveSubsequentOppositionHearingPostponementItem,
  type SaveSubsequentOppositionHearingInvoiceItem,
  getLeadDetails,
  getStampVendors,
  type StampVendorOption,
} from "api/discussion";
import { getWorkflowStatus, skipStep, NewApplicationStep } from "api/applications";
import DateTimePicker from "components/DateTimePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Checkbox } from "components/ui/checkbox";
import IncludeInvoiceNoCheckbox from "components/IncludeInvoiceNoCheckbox";
import PaymentStatusLine from "components/PaymentStatusLine";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import ConfirmDialog from "components/ConfirmDialog";
import {
  EmailPreviewBlock,
  WhatsAppMessageBlock,
} from "components/InvoiceMessagePreview";
import radioCheck from "assets/svg/radio-check.svg";
import pdfIcon from "assets/svg/pdf-icon.svg";
import chevronDownIcon from "assets/svg/chevron-down.svg";
import uploadDropzoneGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";
import legalAdviceHeadset from "assets/svg/legal-advice-headset.svg";
import processToNextStep from "assets/svg/Process-to-next-step.svg";
import invoiceAmountCheck from "assets/svg/invoice-amount-check.svg";
import invoiceGenerate from "assets/svg/invoice-generate.svg";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import { formatOrdinalLongDate } from "utils/ordinalLongDate";
import { toLocalYmd } from "utils/filingApiDates";
import { downloadFileFromUrlWithFallback } from "utils/downloadFileFromUrl";

// ---------------------------------------------------------------------------
// Public form ids — referenced by the dashboard footer to submit the right form
// ---------------------------------------------------------------------------

export const ADVERTISED_IN_JOURNAL_STATUS_FORM_ID =
  "advertised-in-journal-status-details-form";
export const ADVERTISED_IN_JOURNAL_NOTICE_FORM_ID =
  "advertised-in-journal-notice-to-client-form";
export const ADVERTISED_IN_JOURNAL_INFORM_FORM_ID =
  "advertised-in-journal-inform-client-form";
export const OPPOSITION_COUNTER_STATUS_FORM_ID =
  "opposition-counter-status-details-form";
export const OPPOSITION_COUNTER_NOTICE_FORM_ID =
  "opposition-counter-notice-to-client-form";
export const OPPOSITION_COUNTER_INFORM_FORM_ID =
  "opposition-counter-inform-client-form";
export const OPPOSITION_COUNTER_CALL_FORM_ID =
  "opposition-counter-call-client-form";
export const OPPOSITION_COUNTER_CONFIRMATION_FORM_ID =
  "opposition-counter-confirmation-form";
export const OPPOSITION_COUNTER_INVOICE_FORM_ID =
  "opposition-counter-invoice-form";
export const OPPOSITION_COUNTER_PAYMENT_FORM_ID =
  "opposition-counter-payment-form";
export const OPPOSITION_COUNTER_PREPARE_FORM_ID =
  "opposition-counter-prepare-form";
export const OPPOSITION_COUNTER_FILE_COUNTER_FORM_ID =
  "opposition-counter-file-counter-form";
export const OPPOSITION_COUNTER_INFORM_FILING_FORM_ID =
  "opposition-counter-inform-filing-form";
export const OPPOSITION_COUNTER_NOTICE_OF_COUNTER_FORM_ID =
  "opposition-counter-notice-of-counter-form";
export const OPPOSITION_COUNTER_INFORM_NOTICE_FORM_ID =
  "opposition-counter-inform-notice-form";
export const OPPOSITION_COUNTER_EVIDENCE_BY_OPPONENT_FORM_ID =
  "opposition-counter-evidence-by-opponent-form";
export const OPPOSITION_COUNTER_NOTICE_EVIDENCE_BY_OPP_FORM_ID =
  "opposition-counter-notice-evidence-by-opp-form";
export const OPPOSITION_COUNTER_INFORM_EVIDENCE_BY_OPP_FORM_ID =
  "opposition-counter-inform-evidence-by-opp-form";
export const OPPOSITION_COUNTER_CALL_CLIENT_EVIDENCE_FORM_ID =
  "opposition-counter-call-client-evidence-form";
export const OPPOSITION_COUNTER_EVIDENCE_CONFIRMATION_FORM_ID =
  "opposition-counter-evidence-confirmation-form";
export const OPPOSITION_COUNTER_EVIDENCE_INVOICE_FORM_ID =
  "opposition-counter-evidence-invoice-form";
export const OPPOSITION_COUNTER_EVIDENCE_PAYMENT_FORM_ID =
  "opposition-counter-evidence-payment-form";
export const OPPOSITION_COUNTER_STAMP_VENDOR_EVIDENCE_FORM_ID =
  "opposition-counter-stamp-vendor-evidence-form";
export const OPPOSITION_COUNTER_STAMP_FROM_VENDOR_EVIDENCE_FORM_ID =
  "opposition-counter-stamp-from-vendor-evidence-form";
export const OPPOSITION_COUNTER_DOCUMENTS_FROM_CLIENT_EVIDENCE_FORM_ID =
  "opposition-counter-documents-from-client-evidence-form";
export const OPPOSITION_COUNTER_PREPARATION_EVIDENCE_AFFIDAVIT_FORM_ID =
  "opposition-counter-preparation-evidence-affidavit-form";
export const OPPOSITION_COUNTER_SIGNATURE_AFFIDAVIT_FORM_ID =
  "opposition-counter-signature-affidavit-form";
export const OPPOSITION_COUNTER_NOTARISATION_EVIDENCE_FORM_ID =
  "opposition-counter-notarisation-evidence-form";
export const OPPOSITION_COUNTER_SCANNING_EVIDENCE_FORM_ID =
  "opposition-counter-scanning-evidence-form";
export const OPPOSITION_COUNTER_FILE_EVIDENCE_AFFIDAVIT_FORM_ID =
  "opposition-counter-file-evidence-affidavit-form";
export const OPPOSITION_COUNTER_INFORM_FILING_EVIDENCE_FORM_ID =
  "opposition-counter-inform-filing-evidence-form";
export const OPPOSITION_COUNTER_DOWNLOAD_REJOINDER_FORM_ID =
  "opposition-counter-download-rejoinder-form";
export const OPPOSITION_COUNTER_OPPOSITION_HEARING_STATUS_FORM_ID =
  "opposition-counter-opposition-hearing-status-form";
export const OPPOSITION_COUNTER_OPPOSITION_HEARING_NOTICE_FORM_ID =
  "opposition-counter-opposition-hearing-notice-form";
export const OPPOSITION_COUNTER_OPPOSITION_HEARING_INFORM_FORM_ID =
  "opposition-counter-opposition-hearing-inform-form";
export const OPPOSITION_COUNTER_OPPOSITION_HEARING_INVOICE_FORM_ID =
  "opposition-counter-opposition-hearing-invoice-form";
export const OPPOSITION_COUNTER_OPPOSITION_HEARING_CALL_FORM_ID =
  "opposition-counter-opposition-hearing-call-form";
export const OPPOSITION_COUNTER_OPPOSITION_HEARING_CONFIRMATION_FORM_ID =
  "opposition-counter-opposition-hearing-confirmation-form";
export const OPPOSITION_COUNTER_TM_M_BEFORE_HEARING_INVOICE_FORM_ID =
  "opposition-invoice-for-tmm-before-hearing-form";
export const OPPOSITION_COUNTER_TM_M_BEFORE_HEARING_PAYMENT_FORM_ID =
  "opposition-counter-tm-m-before-hearing-payment-form";
export const OPPOSITION_COUNTER_TM_M_BEFORE_HEARING_FILED_FORM_ID =
  "opposition-counter-tm-m-before-hearing-filed-form";
export const OPPOSITION_COUNTER_TM_M_BEFORE_HEARING_INFORM_FORM_ID =
  "opposition-counter-tm-m-before-hearing-inform-form";
export const OPPOSITION_COUNTER_OPPOSITION_HEARING_PAYMENT_FORM_ID =
  "opposition-counter-opposition-hearing-payment-form";
export const OPPOSITION_COUNTER_DOCUMENTS_OPP_HEARING_FORM_ID =
  "opposition-counter-documents-opp-hearing-form";
export const OPPOSITION_COUNTER_FILING_OPP_HEARING_DOC_FORM_ID =
  "opposition-counter-filing-opp-hearing-document-form";
export const OPPOSITION_COUNTER_NOTE_ON_OPP_HEARING_FORM_ID =
  "opposition-counter-note-on-opp-hearing-form";
export const OPPOSITION_COUNTER_ATTEND_OPP_HEARING_FORM_ID =
  "opposition-counter-attend-opp-hearing-form";
export const OPPOSITION_COUNTER_INFORM_AFTER_OPP_HEARING_FORM_ID =
  "opposition-counter-inform-after-opp-hearing-form";
export const OPPOSITION_COUNTER_INVOICE_TMM_AFTER_OPP_HEARING_FORM_ID =
  "opposition-counter-invoice-tmm-after-opp-hearing-form";
export const OPPOSITION_COUNTER_PAYMENT_TMM_AFTER_OPP_HEARING_FORM_ID =
  "opposition-counter-payment-tmm-after-opp-hearing-form";
export const OPPOSITION_COUNTER_FILE_TMM_AFTER_OPP_HEARING_FORM_ID =
  "opposition-counter-file-tmm-after-opp-hearing-form";
export const OPPOSITION_COUNTER_INFORM_TMM_AFTER_OPP_HEARING_FORM_ID =
  "opposition-counter-inform-tmm-after-opp-hearing-form";
export const OPPOSITION_COUNTER_SUBSEQUENT_OPP_HEARINGS_FORM_ID =
  "opposition-counter-subsequent-opp-hearings-form";
export const OPPOSITION_COUNTER_SUBSEQUENT_OPP_HEARING_INVOICE_FORM_ID =
  "opposition-counter-subsequent-opp-hearing-invoice-form";
export const OPPOSITION_COUNTER_SUBSEQUENT_OPP_HEARING_PAYMENT_FORM_ID =
  "opposition-counter-subsequent-opp-hearing-payment-form";
export const OPPOSITION_COUNTER_ORDER_OF_OPP_HEARING_FORM_ID =
  "opposition-counter-order-of-opp-hearing-form";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Variant = "standard" | "opposed";

const SUB_STEPS = [
  { id: "status-details", label: "Status Details" },
  { id: "notice-to-client", label: "Notice to Client" },
  {
    id: "inform-client",
    label: "Inform Client about Advertised in Journal",
  },
] as const;

type SubStep = (typeof SUB_STEPS)[number]["id"];

/**
 * Inform Client about Advertised in Journal: the three scenarios the API
 * accepts. UI shows them as checkbox-styled options but only one can be active
 * (the API enum is single-valued).
 */
const INFORM_SCENARIOS: ReadonlyArray<{
  id: AdvertisedInJournalAdvertisedFrom;
  label: string;
  description: string;
}> = [
  {
    id: "ACCEPTANCE",
    label: "Acceptance without Objection",
    description:
      "Trademark Update will inform the client that the application/s have been advertised in the Trademark Journal without any objection from the Registry; the public has 4 months to oppose.",
  },
  {
    id: "ACCEPTANCE_AFTER_OBJECTION",
    label: "Acceptance after Objection",
    description:
      "Trademark Update will inform the client that the trademark was advertised after the objection raised in the Examination Report was overcome; the public has 4 months to oppose.",
  },
  {
    id: "HEARING",
    label: "Acceptance after Hearing",
    description:
      "Trademark Update will inform the client that the trademark was advertised after a Hearing where the objection was overcome; the public has 4 months to oppose.",
  },
];

/**
 * Opposed variant left rail. Only "status-details-counter" is implemented in
 * this iteration; everything else renders a "Coming soon" placeholder so the
 * rail visually matches Figma.
 */
const OPPOSED_SUB_STEPS = [
  { id: "status-details-counter", label: "Status details for Counter" },
  { id: "notice-to-client-counter", label: "Notice to Client for Counter" },
  { id: "inform-client-about-counter", label: "Inform Client about Counter" },
  { id: "call-client-counter", label: "Call Client for Counter" },
  { id: "confirmation-from-client-counter", label: "Confirmation from Client for Counter" },
  { id: "invoice-to-client-counter", label: "Invoice to Client for Counter" },
  { id: "payment-from-client-counter", label: "Payment from Client for Counter" },
  { id: "prepare-counter", label: "Prepare Counter" },
  { id: "file-counter", label: "File Counter" },
  { id: "inform-client-filing-counter", label: "Inform Client about Filing of Counter" },
  { id: "notice-of-counter-registry", label: "Notice of Counter to Registry" },
  { id: "inform-client-notice-counter", label: "Inform Client about Notice of Counter" },
  { id: "status-evidence-by-opp", label: "Status details for Evidence by Opp..." },
  { id: "notice-evidence-by-opp", label: "Notice to Client for Evidence by Opp..." },
  { id: "inform-evidence-by-opp", label: "Inform Client about Evidence by Opp..." },
  { id: "call-client-evidence", label: "Call Client for Evidence" },
  { id: "confirmation-evidence", label: "Confirmation from Client for Evidence" },
  { id: "invoice-evidence", label: "Invoice to Client for Evidence" },
  { id: "payment-evidence", label: "Payment from Client for Evidence" },
  { id: "stamp-vendor-evidence", label: "Stamp Paper details to Vendor for Ev..." },
  { id: "stamp-from-vendor-evidence", label: "Stamp Paper from Vendor for Eviden..." },
  { id: "documents-from-client-evidence", label: "Documents from Client for Evidence" },
  { id: "preparation-evidence-affidavit", label: "Preparation of Evidence Affidavit" },
  { id: "signature-evidence-affidavit", label: "Signature from Client for Affidavit" },
  { id: "notarisation-evidence", label: "Notarisation of Documents for Evid..." },
  { id: "scanning-evidence", label: "Scanning of Documents for Evidence" },
  { id: "file-evidence-affidavit", label: "File Evidence Affidavit" },
  { id: "inform-filing-evidence", label: "Inform Client about Filing of Evidence" },
  { id: "download-rejoinder-affidavit", label: "Download Rejoinder Affidavit" },
  { id: "status-opposition-hearing", label: "Status details for Opposition Hearing" },
  { id: "notice-opposition-hearing", label: "Notice to Client for Opposition Hearing" },
  { id: "inform-opposition-hearing", label: "Inform Client about Opposition Hearing" },
  { id: "invoice-opposition-hearing", label: "Invoice to Client for Opposition Hearing" },
  { id: "call-opposition-hearing", label: "Call Client for Opposition Hearing" },
  { id: "confirmation-opposition-hearing", label: "Confirmation from Client for Opposition..." },
  { id: "invoice-tmm-before-opp-hearing", label: "Invoice for TM-M before Opposition..." },
  { id: "payment-tmm-before-opp-hearing", label: "Payment for TM-M before Opposition..." },
  { id: "file-tmm-before-opp-hearing", label: "File TM-M before Opposition Hearing" },
  { id: "inform-tmm-before-opp-hearing", label: "Inform Client about Filing of TM-M b..." },
  { id: "payment-opposition-hearing", label: "Payment from Client for Opposition H..." },
  { id: "documents-opp-hearing", label: "Documents from Client for Opposition Hearing" },
  { id: "filing-opp-hearing-document", label: "Filing of Opposition Hearing Document" },
  { id: "note-on-opp-hearing", label: "Note on Opposition Hearing" },
  { id: "attend-opp-hearing", label: "Attend Opposition Hearing" },
  { id: "inform-after-opp-hearing", label: "Inform Client after Opposition Hearin..." },
  { id: "invoice-tmm-after-opp-hearing", label: "Invoice for TM-M after Opposition Hearing..." },
  { id: "payment-tmm-after-opp-hearing", label: "Payment from Client for TM-M after Op..." },
  { id: "file-tmm-after-opp-hearing", label: "File TM-M after Opposition Hearing" },
  { id: "inform-tmm-after-opp-hearing", label: "Inform Client after filing TM-M after o..." },
  { id: "subsequent-opp-hearings", label: "Subsequent Hearings for Opposition..." },
  {
    id: "invoice-subsequent-opp-hearing",
    label: "Invoice for Subsequent Opposition Hearing",
  },
  {
    id: "payment-subsequent-opp-hearing",
    label: "Payment from Client for Subsequent Opposition Hearing",
  },
  { id: "order-of-opp-hearing", label: "Order of Opposition Hearing to Cli..." },
] as const;

type OpposedSubStep = (typeof OPPOSED_SUB_STEPS)[number]["id"];

// TM-M after Opposition Hearing sub-steps (4A(xlvi)-4A(xlix)).
// Backend gates these on at least one app having FILE_TM_M remark in 4A(xliv);
// hide them from the side nav when no app qualifies so users don't land on a dead screen.
const TMM_AFTER_OPP_HEARING_SUB_STEP_IDS: readonly OpposedSubStep[] = [
  "invoice-tmm-after-opp-hearing",
  "payment-tmm-after-opp-hearing",
  "file-tmm-after-opp-hearing",
  "inform-tmm-after-opp-hearing",
];

type PerTmFormState = {
  /** YYYY-MM-DD or empty string */
  journalDate: string;
  /** File chosen but not yet uploaded to S3 */
  pendingFile: File | null;
};

/** Per-TM draft for Prepare Counter (only the user-editable fields per the DTO). */
type PrepareCounterDraft = {
  noOfParagraphsInOpponentIntro: string;
  paragraphWiseReplies: Array<{ heading: string; text: string }>;
  opponentAttorneyName: string;
  opponentAttorneyAddress: string;
  opponentAttorneyEmail: string;
  /** YYYY-MM-DD or empty */
  dateOfEvidenceAffidavit: string;
  /** 4u) Date of Filing Counter Statement. YYYY-MM-DD or empty.
   *  Spec pg 125 puts this Date on the Prepare Counter window, above Generate PDF —
   *  the Counter Statement PDF's "Dated this …" line reads from it. 4A(ix) File Counter
   *  writes the same column, so entering it here or there keeps them in sync. */
  dateOfFilingCounterStatement: string;
};

/** Per-TM editable state for the Opposed → Status details for Counter form. */
type OpposedPerTmFormState = {
  oppositionNo: string;
  /** YYYY-MM-DD or empty */
  dateOfOpposition: string;
  /** YYYY-MM-DD or empty */
  dateOfReceiptOfOpposition: string;
  nameOfOpponent: string;
  addressOfOpponent: string;
  placeOfOpponent: string;
  opponentsMark: string;
  classOfOpponentsMark: string;
  goodsServicesOfOpponentsMark: string;
  /** YYYY-MM-DD or empty */
  userDateOfOpponentsMark: string;
  /** YYYY-MM-DD or empty (override of derived value) */
  lastDateOfFilingCounter: string;
  /** Pending Opposition Notice PDF */
  pendingNoticeFile: File | null;
  /** Pending Image of Opponent's Mark */
  pendingImageFile: File | null;
};

const NOTICE_STEP_SLUG = "S3_04_II_NOTICE_TO_CLIENT_ADVERTISED_IN_JOURNAL";
const INFORM_STEP_SLUG = "S3_04_III_INFORM_CLIENT_ADVERTISED_IN_JOURNAL";
const COUNTER_NOTICE_STEP_SLUG = "S3_04A_II_NOTICE_TO_CLIENT_FOR_COUNTER";
const COUNTER_INFORM_STEP_SLUG = "S3_04A_III_INFORM_CLIENT_ABOUT_COUNTER";
const COUNTER_CALL_STEP_SLUG = "S3_04A_IV_CALL_CLIENT_FOR_COUNTER";
const COUNTER_INVOICE_SEND_STEP_SLUG = "S3_04A_VI_B_SEND_INVOICE_TO_CLIENT_COUNTER";
const COUNTER_INFORM_FILING_STEP_SLUG = "S3_04A_X_INFORM_CLIENT_ABOUT_FILING_OF_COUNTER";
const COUNTER_INFORM_NOTICE_STEP_SLUG = "S3_04A_XII_INFORM_CLIENT_NOTICE_OF_COUNTER";
const COUNTER_NOTICE_EVIDENCE_BY_OPP_STEP_SLUG =
  "S3_04A_XIV_NOTICE_TO_CLIENT_EVIDENCE_BY_OPPONENT";
const COUNTER_INFORM_EVIDENCE_BY_OPP_STEP_SLUG =
  "S3_04A_XV_INFORM_CLIENT_EVIDENCE_BY_OPPONENT";
const COUNTER_CALL_CLIENT_EVIDENCE_STEP_SLUG =
  "S3_04A_XVI_CALL_CLIENT_FOR_EVIDENCE";
const COUNTER_EVIDENCE_INVOICE_SEND_STEP_SLUG =
  "S3_04A_XVIII_B_SEND_INVOICE_TO_CLIENT_EVIDENCE";
const COUNTER_STAMP_VENDOR_EVIDENCE_SEND_STEP_SLUG =
  "S3_04A_XX_B_SEND_STAMP_PAPER_MESSAGE_TO_VENDOR_EVIDENCE";
const COUNTER_SIGNATURE_AFFIDAVIT_STEP_SLUG =
  "S3_04A_XXIV_SIGNATURE_FROM_CLIENT_FOR_AFFIDAVIT";
const COUNTER_SCANNING_EVIDENCE_STEP_SLUG =
  "S3_04A_XXVI_SCANNING_OF_DOCUMENTS_FOR_EVIDENCE";
const COUNTER_FILE_EVIDENCE_AFFIDAVIT_STEP_SLUG =
  "S3_04A_XXVII_FILE_EVIDENCE_AFFIDAVIT";
const COUNTER_INFORM_FILING_EVIDENCE_STEP_SLUG =
  "S3_04A_XXVIII_INFORM_CLIENT_ABOUT_FILING_OF_EVIDENCE";
const COUNTER_OPPOSITION_HEARING_NOTICE_STEP_SLUG =
  "S3_04A_XXXI_NOTICE_TO_CLIENT_OPPOSITION_HEARING";
const COUNTER_OPPOSITION_HEARING_INFORM_STEP_SLUG =
  "S3_04A_XXXII_INFORM_CLIENT_ABOUT_OPPOSITION_HEARING";
const COUNTER_OPPOSITION_HEARING_INVOICE_SEND_STEP_SLUG =
  "S3_04A_XXXIII_B_SEND_INVOICE_TO_CLIENT_OPPOSITION_HEARING";
const COUNTER_OPPOSITION_HEARING_CALL_STEP_SLUG =
  "S3_04A_XXXIV_CALL_CLIENT_OPPOSITION_HEARING";
const COUNTER_TM_M_BEFORE_HEARING_INFORM_STEP_SLUG =
  "S3_04A_XXXIX_INFORM_CLIENT_TM_M_BEFORE_OPPOSITION_HEARING_FILED";

const ADVERTISED_FOLDER = "status/advertised-in-journal";
const OPPOSITION_COUNTER_FOLDER = "status/opposition-counter";
const EVIDENCE_BY_OPPONENT_FOLDER = "status/evidence-by-opponent";
const FILED_EVIDENCE_AFFIDAVIT_FOLDER = "status/filed-evidence-affidavit";
const OPPOSITION_HEARING_NOTICE_FOLDER = "status/opposition-hearing-notice";
const SUBSEQUENT_OPPOSITION_HEARING_NOTICE_FOLDER =
  "status/subsequent-opposition-hearing-notice";
const SUBSEQUENT_OPPOSITION_HEARING_POSTPONEMENT_FOLDER =
  "status/subsequent-opposition-hearing-postponement";
const TM_M_BEFORE_OPPOSITION_HEARING_FILED_FOLDER =
  "status/tm-m-before-opposition-hearing-filed";
const TM_M_AFTER_OPPOSITION_HEARING_FILED_FOLDER =
  "status/tm-m-after-opposition-hearing-filed";

const OPPOSITION_DAYS = 60;
const COUNTER_DAYS = 60;

// Backend (`@common/constants/file-types`) accepts only these extensions / MIME types.
const ALLOWED_FILE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "pdf"] as const;
const ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
] as const;
const ALLOWED_FILE_ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf";
const SUPPORTED_FORMATS_LABEL = "JPG, JPEG, PNG, WEBP, and PDF formats, up to 50MB";
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024; // 50 MB

// ---------------------------------------------------------------------------
// Small icon primitives (kept inline so the file is self-contained)
// ---------------------------------------------------------------------------

function RadioCircle({ active }: { active: boolean }) {
  if (active) {
    return (
      <img
        src={radioCheck}
        alt=""
        width={14}
        height={14}
        className="flex-shrink-0"
      />
    );
  }
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <circle cx="7" cy="7" r="6.5" stroke="#6D6D6D" />
    </svg>
  );
}

function ChevronRight({ active }: { active: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <path
        d="M6 4L10 8L6 12"
        stroke={active ? "white" : "#222222"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Renders the leading-* indicator used on required field labels. */
function RequiredMark() {
  return (
    <span className="text-[#E5252A] ml-0.5" aria-hidden="true">
      *
    </span>
  );
}

/**
 * Returns null when the file is acceptable, otherwise a user-facing error
 * message describing exactly what's wrong (type / size).
 */
function validateUploadFile(file: File): string | null {
  if (!file) return "No file selected.";
  if (file.size === 0) return "File is empty.";
  if (file.size > MAX_UPLOAD_BYTES) {
    return `File is larger than 50MB (got ${(file.size / (1024 * 1024)).toFixed(1)}MB).`;
  }
  const ext = (file.name.split(".").pop() ?? "").toLowerCase();
  const mime = (file.type || "").toLowerCase();
  const okByExt = (ALLOWED_FILE_EXTENSIONS as readonly string[]).includes(ext);
  const okByMime =
    !mime || (ALLOWED_MIME_TYPES as readonly string[]).includes(mime);
  if (!okByExt || !okByMime) {
    return "Unsupported file type. Allowed: JPG, JPEG, PNG, WEBP, PDF.";
  }
  return null;
}

/**
 * Parse a DD-MM-YYYY or YYYY-MM-DD string into plain {year, month, day} parts
 * without ever calling Date prototype methods for validation. Some users have
 * Chrome extensions (e.g., TronLink) that patch `Date.prototype.getDate`, which
 * causes `utils/filingApiDates.parseFilingApiDateString` to incorrectly return
 * null on valid input. Mirrors `parseDocDateParts` in `FilingDetailsToClientView`.
 */
type DateParts = { year: number; month: number; day: number };

function isAllDigitsStr(s: string): boolean {
  if (!s || s.length === 0) return false;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 48 || c > 57) return false;
  }
  return true;
}

function parseDateParts(s: string | null | undefined): DateParts | null {
  if (s == null) return null;
  const t = String(s).trim();
  if (!t) return null;
  const parts = t.split("-");
  if (parts.length !== 3) return null;
  if (
    !isAllDigitsStr(parts[0]) ||
    !isAllDigitsStr(parts[1]) ||
    !isAllDigitsStr(parts[2])
  ) {
    return null;
  }
  let year: number;
  let month: number;
  let day: number;
  if (parts[0].length === 4) {
    year = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    day = parseInt(parts[2], 10);
  } else if (parts[2].length === 4) {
    day = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    year = parseInt(parts[2], 10);
  } else {
    return null;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
    return null;
  }
  return { year, month, day };
}

function partsToIsoYmd(p: DateParts): string {
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/** Construct a Date from parts directly (no validation round-trip). */
function partsToDate(p: DateParts): Date {
  return new Date(p.year, p.month - 1, p.day);
}

function addDaysToYmd(ymd: string, days: number): string | null {
  const p = parseDateParts(ymd);
  if (!p) return null;
  // Use Date math without relying on .getDate() for validation.
  const d = new Date(p.year, p.month - 1, p.day + days);
  return toLocalYmd(d);
}

function addMonthsToYmd(ymd: string, months: number): string | null {
  const p = parseDateParts(ymd);
  if (!p) return null;
  const d = new Date(p.year, p.month - 1 + months, p.day);
  return toLocalYmd(d);
}

function applicationsToFormMap(
  apps: AdvertisedInJournalApplication[]
): Record<string, PerTmFormState> {
  const map: Record<string, PerTmFormState> = {};
  for (const a of apps) {
    const parts = parseDateParts(a.journalDate);
    map[a.tmNo] = {
      journalDate: parts ? partsToIsoYmd(parts) : "",
      pendingFile: null,
    };
  }
  return map;
}

function opposedApplicationsToFormMap(
  apps: OppositionCounterApplication[]
): Record<string, OpposedPerTmFormState> {
  const map: Record<string, OpposedPerTmFormState> = {};
  const toYmd = (s: string | null | undefined): string => {
    const p = parseDateParts(s);
    return p ? partsToIsoYmd(p) : "";
  };
  for (const a of apps) {
    map[a.tmNo] = {
      oppositionNo: a.oppositionNo ?? "",
      dateOfOpposition: toYmd(a.dateOfOpposition),
      dateOfReceiptOfOpposition: toYmd(a.dateOfReceiptOfOpposition),
      nameOfOpponent: a.nameOfOpponent ?? "",
      addressOfOpponent: a.addressOfOpponent ?? "",
      placeOfOpponent: a.placeOfOpponent ?? "",
      opponentsMark: a.opponentsMark ?? "",
      classOfOpponentsMark: a.classOfOpponentsMark ?? "",
      goodsServicesOfOpponentsMark: a.goodsServicesOfOpponentsMark ?? "",
      userDateOfOpponentsMark: toYmd(a.userDateOfOpponentsMark),
      lastDateOfFilingCounter: toYmd(a.lastDateOfFilingCounter),
      pendingNoticeFile: null,
      pendingImageFile: null,
    };
  }
  return map;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type AdvertisedInJournalViewProps = {
  leadId?: string | null;
  onSavingChange?: (saving: boolean) => void;
  onSuccess?: (leadId: string) => void;

  /** Status Details: footer "Save & Continue" submits the status-details form */
  onStatusDetailsFooterFormModeChange?: (needsSubmit: boolean) => void;

  /** Notice to Client: footer "Send & Continue" submits the notice form (first time) */
  onNoticeFooterSendModeChange?: (needsSend: boolean) => void;

  /** Notice to Client (already sent): footer "Save & Continue" advances */
  onNoticeFooterSaveModeChange?: (needsSave: boolean) => void;

  /** Inform Client about Advertised in Journal: "Send & Continue" until S3_04(iii) completed */
  onInformFooterSendModeChange?: (needsSend: boolean) => void;
  /** Inform Client (already sent): "Save & Continue" advances */
  onInformFooterSaveModeChange?: (needsSave: boolean) => void;

  /** Opposed → Status details for Counter: "Save & Continue" submits the counter status form */
  onCounterStatusFooterFormModeChange?: (needsSubmit: boolean) => void;

  /** Opposed → Notice to Client for Counter: "Send & Continue" until S3_04a(ii) completed */
  onCounterNoticeFooterSendModeChange?: (needsSend: boolean) => void;
  /** Opposed → Notice to Client for Counter (already sent): "Save & Continue" advances */
  onCounterNoticeFooterSaveModeChange?: (needsSave: boolean) => void;

  /** Opposed → Inform Client about Counter: "Send & Continue" until S3_04a(iii) completed */
  onCounterInformFooterSendModeChange?: (needsSend: boolean) => void;
  /** Opposed → Inform Client about Counter (already sent): "Save & Continue" advances */
  onCounterInformFooterSaveModeChange?: (needsSave: boolean) => void;

  /** Opposed → Call Client for Counter: "Save & Continue" submits the call form (records step) */
  onCounterCallFooterFormModeChange?: (needsSubmit: boolean) => void;

  /** Opposed → Confirmation from Client for Counter: "Save & Continue" submits */
  onCounterConfirmationFooterFormModeChange?: (needsSubmit: boolean) => void;

  /**
   * Review item 73 — 4A(v) NO must go straight to 5B(i) Status details for Abandonment
   * (spec pg 119). 5B(i) lives in the Final Order section, so the page owns the navigation.
   */
  onAdvanceToRefusalAbandonment?: () => void;

  /** Opposed → Invoice to Client for Counter: Send & Continue (first time) / Save & Continue (after sent) */
  onCounterInvoiceFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterInvoiceFooterSaveModeChange?: (needsSave: boolean) => void;

  /** Opposed → Payment from Client for Counter: "Save & Continue" submits */
  onCounterPaymentFooterFormModeChange?: (needsSubmit: boolean) => void;

  /** Opposed → Payment from Client for Counter: registers the "Progress" (skip) handler */
  onCounterPaymentProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;

  /** Opposed → Prepare Counter: "Save & Continue" submits */
  onCounterPrepareFooterFormModeChange?: (needsSubmit: boolean) => void;

  /** Opposed → File Counter: "Save & Continue" submits */
  onCounterFileFooterFormModeChange?: (needsSubmit: boolean) => void;

  /** Opposed → Inform Client about Filing of Counter: Send & Continue / Save & Continue */
  onCounterInformFilingFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterInformFilingFooterSaveModeChange?: (needsSave: boolean) => void;

  /** Opposed → Notice of Counter by Registry: Save & Continue submits */
  onCounterNoticeOfCounterFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Notice of Counter by Registry: registers the "Progress" (skip) handler */
  onCounterNoticeOfCounterProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** Opposed → Inform Client about Notice of Counter: Send & Continue / Save & Continue */
  onCounterInformNoticeFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterInformNoticeFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Status details for Evidence by Opponent: Save & Continue submits */
  onCounterEvidenceByOpponentFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Notice to Client for Evidence by Opp...: Send & Continue / Save & Continue */
  onCounterNoticeEvidenceByOppFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterNoticeEvidenceByOppFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Inform Client about Evidence by Opp...: Send & Continue / Save & Continue */
  onCounterInformEvidenceByOppFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterInformEvidenceByOppFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Call Client for Evidence: Save & Continue submits */
  onCounterCallClientEvidenceFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Confirmation from Client for Evidence: Save & Continue submits */
  onCounterEvidenceConfirmationFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Invoice to Client for Evidence: Send & Continue / Save & Continue */
  onCounterEvidenceInvoiceFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterEvidenceInvoiceFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Payment from Client for Evidence: Save & Continue submits */
  onCounterEvidencePaymentFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Stamp Paper details to Vendor for Evidence: Send & Continue / Save & Continue */
  onCounterStampVendorEvidenceFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterStampVendorEvidenceFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Stamp Paper from Vendor for Evidence: Save & Continue submits */
  onCounterStampFromVendorEvidenceFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Documents from Client for Evidence: Save & Continue submits */
  onCounterDocumentsFromClientEvidenceFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Documents from Client for Evidence: registers the "Progress" (skip) handler */
  onCounterDocumentsFromClientEvidenceProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** Opposed → Preparation of Evidence Affidavit: Save & Continue submits */
  onCounterPreparationEvidenceAffidavitFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Signature from Client for Affidavit: Save & Continue records */
  onCounterSignatureAffidavitFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Notarisation of Documents for Evidence: Save & Continue submits */
  onCounterNotarisationEvidenceFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Scanning of Documents for Evidence: Save & Continue records */
  onCounterScanningEvidenceFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → File Evidence Affidavit: Save & Continue submits */
  onCounterFileEvidenceAffidavitFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Inform Client about Filing of Evidence: Send & Continue / Save & Continue */
  onCounterInformFilingEvidenceFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterInformFilingEvidenceFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Download Rejoinder Affidavit: Save & Continue submits */
  onCounterDownloadRejoinderFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Status details of Opposition Hearing: Save & Continue submits */
  onCounterOppositionHearingStatusFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Notice to Client for Opposition Hearing: Send & Continue / Save & Continue */
  onCounterOppositionHearingNoticeFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterOppositionHearingNoticeFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Inform Client about Opposition Hearing: Send & Continue / Save & Continue */
  onCounterOppositionHearingInformFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterOppositionHearingInformFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Invoice to Client for Opposition Hearing: Send & Continue / Save & Continue */
  onCounterOppositionHearingInvoiceFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterOppositionHearingInvoiceFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Call Client for Opposition Hearing: Save & Continue records */
  onCounterOppositionHearingCallFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Confirmation from Client for Opposition Hearing: Save & Continue submits */
  onCounterOppositionHearingConfirmationFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Invoice for TM-M before Opposition Hearing: Send & Continue / Save & Continue */
  onCounterTmMBeforeHearingInvoiceFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterTmMBeforeHearingInvoiceFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Invoice for TM-M before Opposition Hearing: registers the "Progress" (skip) handler */
  onCounterTmMBeforeHearingInvoiceProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** Opposed → Payment from Client for TM-M before Opposition Hearing: Save & Continue submits */
  onCounterTmMBeforeHearingPaymentFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → File TM-M before Opposition Hearing: Save & Continue submits */
  onCounterTmMBeforeHearingFiledFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Inform Client about Filing of TM-M before Opposition Hearing: Send & Continue / Save & Continue */
  onCounterTmMBeforeHearingInformFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterTmMBeforeHearingInformFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Payment from Client for Opposition Hearing: Save & Continue submits */
  onCounterOppositionHearingPaymentFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Payment from Client for Opposition Hearing: registers the "Progress" (skip) handler */
  onCounterOppositionHearingPaymentProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;

  /** Opposed → Documents from Client for Opposition Hearing: Save & Continue records */
  onCounterDocumentsOppHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Documents from Client for Opposition Hearing: registers the "Progress" (skip) handler */
  onCounterDocumentsOppHearingProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** Opposed → Filing of Opposition Hearing Document: Save & Continue records */
  onCounterFilingOppHearingDocFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Note on Opposition Hearing: Save & Continue submits */
  onCounterNoteOnOppHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Attend Opposition Hearing: Save & Continue submits */
  onCounterAttendOppHearingFooterFormModeChange?: (needsSubmit: boolean) => void;

  /** Opposed → Inform Client after Opposition Hearing: Send & Continue / Save & Continue */
  onCounterInformAfterOppHearingFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterInformAfterOppHearingFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Invoice for TM-M after Opposition Hearing: Send & Continue / Save & Continue */
  onCounterInvoiceTmMAfterOppHearingFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterInvoiceTmMAfterOppHearingFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Opposed → Invoice for TM-M after Opposition Hearing: registers the "Progress" (skip) handler */
  onCounterInvoiceTmMAfterOppHearingProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** Opposed → Payment from Client for TM-M after Opposition Hearing: Save & Continue submits */
  onCounterPaymentTmMAfterOppHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Payment from Client for TM-M after Opposition Hearing: registers the "Progress" (skip) handler */
  onCounterPaymentTmMAfterOppHearingProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** Opposed → File TM-M after Opposition Hearing: Save & Continue submits */
  onCounterFileTmMAfterOppHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Opposed → Inform Client after filing TM-M after Opp Hearing: Send & Continue / Save & Continue */
  onCounterInformTmMAfterOppHearingFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterInformTmMAfterOppHearingFooterSaveModeChange?: (needsSave: boolean) => void;

  /** Opposed → Subsequent Hearings for Opposition Hearing (empty placeholder): Save & Continue advances to order */
  onCounterSubsequentOppHearingsFooterFormModeChange?: (needsSubmit: boolean) => void;

  /** Opposed → Invoice for Subsequent Opposition Hearing: Save & Continue */
  onCounterSubsequentOppHearingInvoiceFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;

  /** Opposed → Payment from Client for Subsequent Opposition Hearing: Save & Continue */
  onCounterSubsequentOppHearingPaymentFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;

  /** Opposed → Order of Opposition Hearing: Send & Continue / Save & Continue */
  onCounterOrderOfOppHearingFooterSendModeChange?: (needsSend: boolean) => void;
  onCounterOrderOfOppHearingFooterSaveModeChange?: (needsSave: boolean) => void;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdvertisedInJournalView({
  leadId,
  onSavingChange,
  onSuccess,
  onStatusDetailsFooterFormModeChange,
  onNoticeFooterSendModeChange,
  onNoticeFooterSaveModeChange,
  onInformFooterSendModeChange,
  onInformFooterSaveModeChange,
  onCounterStatusFooterFormModeChange,
  onCounterNoticeFooterSendModeChange,
  onCounterNoticeFooterSaveModeChange,
  onCounterInformFooterSendModeChange,
  onCounterInformFooterSaveModeChange,
  onCounterCallFooterFormModeChange,
  onCounterConfirmationFooterFormModeChange,
  onAdvanceToRefusalAbandonment,
  onCounterInvoiceFooterSendModeChange,
  onCounterInvoiceFooterSaveModeChange,
  onCounterPaymentFooterFormModeChange,
  onCounterPaymentProgressActionChange,
  onCounterPrepareFooterFormModeChange,
  onCounterFileFooterFormModeChange,
  onCounterInformFilingFooterSendModeChange,
  onCounterInformFilingFooterSaveModeChange,
  onCounterNoticeOfCounterFooterFormModeChange,
  onCounterNoticeOfCounterProgressActionChange,
  onCounterInformNoticeFooterSendModeChange,
  onCounterInformNoticeFooterSaveModeChange,
  onCounterEvidenceByOpponentFooterFormModeChange,
  onCounterNoticeEvidenceByOppFooterSendModeChange,
  onCounterNoticeEvidenceByOppFooterSaveModeChange,
  onCounterInformEvidenceByOppFooterSendModeChange,
  onCounterInformEvidenceByOppFooterSaveModeChange,
  onCounterCallClientEvidenceFooterFormModeChange,
  onCounterEvidenceConfirmationFooterFormModeChange,
  onCounterEvidenceInvoiceFooterSendModeChange,
  onCounterEvidenceInvoiceFooterSaveModeChange,
  onCounterEvidencePaymentFooterFormModeChange,
  onCounterStampVendorEvidenceFooterSendModeChange,
  onCounterStampVendorEvidenceFooterSaveModeChange,
  onCounterStampFromVendorEvidenceFooterFormModeChange,
  onCounterDocumentsFromClientEvidenceFooterFormModeChange,
  onCounterDocumentsFromClientEvidenceProgressActionChange,
  onCounterPreparationEvidenceAffidavitFooterFormModeChange,
  onCounterSignatureAffidavitFooterFormModeChange,
  onCounterNotarisationEvidenceFooterFormModeChange,
  onCounterScanningEvidenceFooterFormModeChange,
  onCounterFileEvidenceAffidavitFooterFormModeChange,
  onCounterInformFilingEvidenceFooterSendModeChange,
  onCounterInformFilingEvidenceFooterSaveModeChange,
  onCounterDownloadRejoinderFooterFormModeChange,
  onCounterOppositionHearingStatusFooterFormModeChange,
  onCounterOppositionHearingNoticeFooterSendModeChange,
  onCounterOppositionHearingNoticeFooterSaveModeChange,
  onCounterOppositionHearingInformFooterSendModeChange,
  onCounterOppositionHearingInformFooterSaveModeChange,
  onCounterOppositionHearingInvoiceFooterSendModeChange,
  onCounterOppositionHearingInvoiceFooterSaveModeChange,
  onCounterOppositionHearingCallFooterFormModeChange,
  onCounterOppositionHearingConfirmationFooterFormModeChange,
  onCounterTmMBeforeHearingInvoiceFooterSendModeChange,
  onCounterTmMBeforeHearingInvoiceFooterSaveModeChange,
  onCounterTmMBeforeHearingInvoiceProgressActionChange,
  onCounterTmMBeforeHearingPaymentFooterFormModeChange,
  onCounterTmMBeforeHearingFiledFooterFormModeChange,
  onCounterTmMBeforeHearingInformFooterSendModeChange,
  onCounterTmMBeforeHearingInformFooterSaveModeChange,
  onCounterOppositionHearingPaymentFooterFormModeChange,
  onCounterOppositionHearingPaymentProgressActionChange,
  onCounterDocumentsOppHearingFooterFormModeChange,
  onCounterDocumentsOppHearingProgressActionChange,
  onCounterFilingOppHearingDocFooterFormModeChange,
  onCounterNoteOnOppHearingFooterFormModeChange,
  onCounterAttendOppHearingFooterFormModeChange,
  onCounterInformAfterOppHearingFooterSendModeChange,
  onCounterInformAfterOppHearingFooterSaveModeChange,
  onCounterInvoiceTmMAfterOppHearingFooterSendModeChange,
  onCounterInvoiceTmMAfterOppHearingFooterSaveModeChange,
  onCounterInvoiceTmMAfterOppHearingProgressActionChange,
  onCounterPaymentTmMAfterOppHearingFooterFormModeChange,
  onCounterPaymentTmMAfterOppHearingProgressActionChange,
  onCounterFileTmMAfterOppHearingFooterFormModeChange,
  onCounterInformTmMAfterOppHearingFooterSendModeChange,
  onCounterInformTmMAfterOppHearingFooterSaveModeChange,
  onCounterSubsequentOppHearingsFooterFormModeChange,
  onCounterSubsequentOppHearingInvoiceFooterFormModeChange,
  onCounterSubsequentOppHearingPaymentFooterFormModeChange,
  onCounterOrderOfOppHearingFooterSendModeChange,
  onCounterOrderOfOppHearingFooterSaveModeChange,
}: AdvertisedInJournalViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Persist variant + sub-step in URL query params so a refresh stays on
  // the same screen. Values are validated against the known id sets so an
  // unknown / stale param falls back to the defaults.
  const STD_SUB_IDS = SUB_STEPS.map((s) => s.id);
  const OPP_SUB_IDS = OPPOSED_SUB_STEPS.map((s) => s.id);

  const initialVariant: Variant =
    searchParams.get("variant") === "opposed" ? "opposed" : "standard";
  const urlSub = searchParams.get("sub") ?? "";
  const initialStdSub: SubStep =
    (STD_SUB_IDS as ReadonlyArray<string>).includes(urlSub)
      ? (urlSub as SubStep)
      : "status-details";
  const initialOppSub: OpposedSubStep =
    (OPP_SUB_IDS as ReadonlyArray<string>).includes(urlSub)
      ? (urlSub as OpposedSubStep)
      : "status-details-counter";

  const [variant, setVariantState] = useState<Variant>(initialVariant);
  const [subStep, setSubStepState] = useState<SubStep>(initialStdSub);
  const [loading, setLoading] = useState<boolean>(!!leadId);

  /** Mirror state changes to the URL (replace history to avoid back-button noise). */
  const writeUrl = (next: {
    variant?: Variant;
    sub?: string;
  }) => {
    const params = new URLSearchParams(searchParams);
    if (next.variant != null) params.set("variant", next.variant);
    if (next.sub != null) params.set("sub", next.sub);
    setSearchParams(params, { replace: true });
  };

  const setVariant = (v: Variant) => {
    setVariantState(v);
    // When switching variants, reset to the variant's first sub-step
    const nextSub = v === "opposed" ? "status-details-counter" : "status-details";
    writeUrl({ variant: v, sub: nextSub });
  };

  const setSubStep = (s: SubStep) => {
    setSubStepState(s);
    writeUrl({ sub: s });
  };

  const [status, setStatus] = useState<AdvertisedInJournalStatusResponse | null>(
    null
  );
  const [perTm, setPerTm] = useState<Record<string, PerTmFormState>>({});
  const [selectedTmIdx, setSelectedTmIdx] = useState(0);

  // Shared confirmation dialog for all "Resend Message" buttons. Each resend
  // button opens this dialog with a context-specific title/description; the
  // actual resend handler is only invoked when the user confirms. Prevents
  // accidental duplicate messages to the client.
  const [resendConfirm, setResendConfirm] = useState<{
    title: string;
    description: string;
    previewContent?: string;
    onConfirm: () => void;
  } | null>(null);
  const openResendConfirm = (
    title: string,
    description: string,
    action: () => void,
    previewContent?: string,
  ) => {
    setResendConfirm({ title, description, onConfirm: action, previewContent });
  };

  const [hasSentNotice, setHasSentNotice] = useState(false);
  const [resendingNotice, setResendingNotice] = useState(false);
  const [noticeSendConfirmOpen, setNoticeSendConfirmOpen] = useState(false);
  const [noticeResendConfirmOpen, setNoticeResendConfirmOpen] = useState(false);
  const [noticeSending, setNoticeSending] = useState(false);
  const [noticePreview, setNoticePreview] =
    useState<AdvertisedInJournalNoticePreview | null>(null);
  const [noticePreviewLoading, setNoticePreviewLoading] = useState(false);
  const [noticePreviewError, setNoticePreviewError] = useState<string | null>(
    null
  );
  const [autoSaving, setAutoSaving] = useState(false);

  // Inform Client about Advertised in Journal (sub-step 3 in Standard variant)
  const [informScenario, setInformScenario] =
    useState<AdvertisedInJournalAdvertisedFrom | null>(null);
  const [hasSentInform, setHasSentInform] = useState(false);
  const [resendingInform, setResendingInform] = useState(false);

  // Opposed variant
  const [opposedSubStep, setOpposedSubStepState] =
    useState<OpposedSubStep>(initialOppSub);

  const setOpposedSubStep = (s: OpposedSubStep) => {
    setOpposedSubStepState(s);
    writeUrl({ sub: s });
  };

  // Backfill URL on first render so the current sub-step is bookmarkable
  // even when the user arrived without query params.
  useEffect(() => {
    const have = searchParams.get("variant") || searchParams.get("sub");
    if (have) return;
    writeUrl({
      variant,
      sub: variant === "opposed" ? opposedSubStep : subStep,
    });
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to URL changes (back/forward, deep links) by syncing state.
  useEffect(() => {
    const v: Variant =
      searchParams.get("variant") === "opposed" ? "opposed" : "standard";
    const s = searchParams.get("sub") ?? "";
    if (v !== variant) setVariantState(v);
    if (v === "opposed") {
      if (
        (OPP_SUB_IDS as ReadonlyArray<string>).includes(s) &&
        s !== opposedSubStep
      ) {
        setOpposedSubStepState(s as OpposedSubStep);
      }
    } else {
      if (
        (STD_SUB_IDS as ReadonlyArray<string>).includes(s) &&
        s !== subStep
      ) {
        setSubStepState(s as SubStep);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [opposedStatus, setOpposedStatus] =
    useState<OppositionCounterStatusResponse | null>(null);
  const [opposedSelectedTmIdx, setOpposedSelectedTmIdx] = useState(0);
  const [opposedLoading, setOpposedLoading] = useState(false);
  const [opposedPerTm, setOpposedPerTm] = useState<
    Record<string, OpposedPerTmFormState>
  >({});
  const [opposedDragOverTmNo, setOpposedDragOverTmNo] = useState<string | null>(
    null
  );

  // Opposed → Notice to Client for Counter
  const [hasSentCounterNotice, setHasSentCounterNotice] = useState(false);
  const [resendingCounterNotice, setResendingCounterNotice] = useState(false);
  const [counterNoticeSendConfirmOpen, setCounterNoticeSendConfirmOpen] = useState(false);
  const [counterNoticeSending, setCounterNoticeSending] = useState(false);
  const [counterNoticeResendConfirmOpen, setCounterNoticeResendConfirmOpen] =
    useState(false);
  const [counterNoticePreview, setCounterNoticePreview] =
    useState<CounterNoticePreview | null>(null);
  const [counterNoticePreviewLoading, setCounterNoticePreviewLoading] =
    useState(false);
  const [counterNoticePreviewError, setCounterNoticePreviewError] = useState<
    string | null
  >(null);
  // Opposed → Inform Client about Counter
  const [counterInformPreview, setCounterInformPreview] = useState<
    { message: string } | null
  >(null);
  const [hasSentCounterInform, setHasSentCounterInform] = useState(false);
  const [resendingCounterInform, setResendingCounterInform] = useState(false);
  // Opposed → Call Client for Counter
  const [hasRecordedCounterCall, setHasRecordedCounterCall] = useState(false);
  const [recordingCounterCall, setRecordingCounterCall] = useState(false);

  // Opposed → Confirmation from Client for Counter (4A(v))
  const [counterConfirmation, setCounterConfirmation] =
    useState<CounterConfirmationStatus | null>(null);
  const [counterConfirmationDraft, setCounterConfirmationDraft] = useState<
    Record<string, boolean | null>
  >({});
  const [counterNoOpposedDraft, setCounterNoOpposedDraft] = useState<string>("");
  const [counterNoForCounterDraft, setCounterNoForCounterDraft] = useState<string>("");

  // Opposed → Invoice to Client for Counter (4A(vi))
  const [counterInvoice, setCounterInvoice] = useState<CounterInvoiceStatus | null>(
    null
  );
  const [counterInvoicePreview, setCounterInvoicePreview] =
    useState<CounterInvoicePreview | null>(null);
  const [counterInvoiceRows, setCounterInvoiceRows] = useState<
    Record<
      string,
      {
        invoiceAmount: string;
        discount: string;
        invoiceDate: string;
        includeInvoiceNo: boolean;
      }
    >
  >({});
  const [hasSentCounterInvoice, setHasSentCounterInvoice] = useState(false);
  const [counterInvoiceGenerating, setCounterInvoiceGenerating] = useState(false);
  const [resendingCounterInvoice, setResendingCounterInvoice] = useState(false);
  const [counterInvoiceResendConfirmOpen, setCounterInvoiceResendConfirmOpen] =
    useState(false);
  const [counterInvoiceSendConfirmOpen, setCounterInvoiceSendConfirmOpen] =
    useState(false);
  const [counterInvoiceSending, setCounterInvoiceSending] = useState(false);
  const [clientPhone, setClientPhone] = useState("");

  // Opposed → Payment from Client for Counter (4A(vii))
  const [counterPaymentRows, setCounterPaymentRows] = useState<
    Record<string, string>
  >({});

  // Opposed → Prepare Counter (4A(viii))
  const [prepareCounter, setPrepareCounter] =
    useState<PrepareCounterStatus | null>(null);
  const [prepareCounterPerTm, setPrepareCounterPerTm] = useState<
    Record<string, PrepareCounterDraft>
  >({});
  const [prepareCounterGenerating, setPrepareCounterGenerating] = useState(false);

  // Opposed → File Counter (4A(ix))
  const [fileCounter, setFileCounter] = useState<FileCounterStatus | null>(null);
  const [fileCounterPerTm, setFileCounterPerTm] = useState<
    Record<string, { filingDate: string; pendingFile: File | null }>
  >({});

  // Opposed → Inform Client about Filing of Counter (4A(x))
  const [informFilingPreview, setInformFilingPreview] = useState<{ message: string } | null>(null);
  const [hasSentInformFiling, setHasSentInformFiling] = useState(false);
  const [resendingInformFiling, setResendingInformFiling] = useState(false);

  // Opposed → Notice of Counter by Registry (4A(xi))
  const [noticeOfCounterRows, setNoticeOfCounterRows] = useState<
    NoticeOfCounterRow[]
  >([]);
  const [noticeOfCounterDraft, setNoticeOfCounterDraft] = useState<
    Record<string, string>
  >({});
  const [noticeOfCounterLastDateDraft, setNoticeOfCounterLastDateDraft] =
    useState<Record<string, string>>({});

  // Opposed → Inform Client about Notice of Counter by Registry (4A(xii))
  const [hasSentInformNotice, setHasSentInformNotice] = useState(false);
  const [resendingInformNotice, setResendingInformNotice] = useState(false);
  const [
    informNoticeOfCounterPreview,
    setInformNoticeOfCounterPreview,
  ] = useState<InformClientNoticeOfCounterPreview | null>(null);

  // Opposed → Status details for Evidence by Opponent (4A(xiii))
  const [evidenceByOpponent, setEvidenceByOpponent] =
    useState<EvidenceByOpponentStatus | null>(null);
  const [evidenceByOpponentDraft, setEvidenceByOpponentDraft] = useState<
    Record<
      string,
      {
        opponentEvidenceFiled: boolean | null;
        dateOfEvidenceByOpponent: string;
        dateOfReceiptOfEvidenceByOpponent: string;
        lastDateOfFilingEvidence: string;
        pendingFile: File | null;
      }
    >
  >({});

  // Opposed → Notice to Client for Evidence by Opponent (4A(xiv))
  const [hasSentNoticeEvidenceByOpp, setHasSentNoticeEvidenceByOpp] =
    useState(false);
  const [resendingNoticeEvidenceByOpp, setResendingNoticeEvidenceByOpp] =
    useState(false);
  const [
    noticeEvidenceByOppSendConfirmOpen,
    setNoticeEvidenceByOppSendConfirmOpen,
  ] = useState(false);
  const [noticeEvidenceByOppSending, setNoticeEvidenceByOppSending] =
    useState(false);
  const [
    noticeEvidenceByOppResendConfirmOpen,
    setNoticeEvidenceByOppResendConfirmOpen,
  ] = useState(false);
  const [noticeEvidenceByOppPreview, setNoticeEvidenceByOppPreview] =
    useState<EvidenceByOpponentNoticePreview | null>(null);
  const [
    noticeEvidenceByOppPreviewLoading,
    setNoticeEvidenceByOppPreviewLoading,
  ] = useState(false);
  const [
    noticeEvidenceByOppPreviewError,
    setNoticeEvidenceByOppPreviewError,
  ] = useState<string | null>(null);

  // Opposed → Inform Client about Evidence by Opponent (4A(xv))
  const [hasSentInformEvidenceByOpp, setHasSentInformEvidenceByOpp] =
    useState(false);
  const [resendingInformEvidenceByOpp, setResendingInformEvidenceByOpp] =
    useState(false);
  const [
    informEvidenceByOppPreview,
    setInformEvidenceByOppPreview,
  ] = useState<InformClientEvidenceByOpponentPreview | null>(null);

  // Opposed → Call Client for Evidence (4A(xvi))
  const [hasRecordedCallClientEvidence, setHasRecordedCallClientEvidence] =
    useState(false);
  const [recordingCallClientEvidence, setRecordingCallClientEvidence] =
    useState(false);

  // Opposed → Confirmation from Client for Evidence (4A(xvii))
  const [evidenceConfirmation, setEvidenceConfirmation] =
    useState<EvidenceConfirmationStatus | null>(null);
  const [evidenceConfirmationDraft, setEvidenceConfirmationDraft] = useState<
    Record<string, boolean | null>
  >({});
  const [evidenceNoOfAppsOverride, setEvidenceNoOfAppsOverride] =
    useState<string>("");

  // Opposed → Invoice to Client for Evidence (4A(xviii))
  const [evidenceInvoice, setEvidenceInvoice] =
    useState<EvidenceInvoiceStatus | null>(null);
  const [evidenceInvoicePreviewData, setEvidenceInvoicePreviewData] =
    useState<EvidenceInvoicePreview | null>(null);
  const [evidenceInvoiceRows, setEvidenceInvoiceRows] = useState<
    Record<
      string,
      {
        invoiceAmount: string;
        discount: string;
        invoiceDate: string;
        includeInvoiceNo: boolean;
      }
    >
  >({});
  const [hasSentEvidenceInvoice, setHasSentEvidenceInvoice] = useState(false);
  const [resendingEvidenceInvoice, setResendingEvidenceInvoice] =
    useState(false);
  const [
    evidenceInvoiceResendConfirmOpen,
    setEvidenceInvoiceResendConfirmOpen,
  ] = useState(false);
  const [evidenceInvoiceGenerating, setEvidenceInvoiceGenerating] =
    useState(false);
  const [evidenceInvoiceDownloading, setEvidenceInvoiceDownloading] =
    useState(false);

  // Opposed → Payment from Client for Evidence (4A(xix))
  const [evidencePaymentRows, setEvidencePaymentRows] = useState<
    Record<string, string>
  >({});
  const [recordingEvidencePayment, setRecordingEvidencePayment] =
    useState(false);

  // Opposed → Stamp Paper details to Vendor for Evidence (4A(xx))
  const [stampPaperEvidence, setStampPaperEvidence] =
    useState<StampPaperForEvidenceStatus | null>(null);
  const [stampPaperEvidenceDraft, setStampPaperEvidenceDraft] = useState<
    Record<
      string,
      {
        stampPapersForEvidence: string;
        stampVendorNameForEvidence: string;
        stampVendorPhoneForEvidence: string;
      }
    >
  >({});
  const [hasSentStampPaperEvidence, setHasSentStampPaperEvidence] =
    useState(false);
  const [resendingStampPaperEvidence, setResendingStampPaperEvidence] =
    useState(false);
  const [showStampEvidenceConfirmModal, setShowStampEvidenceConfirmModal] =
    useState(false);
  const [stampEvidencePreviewMessage, setStampEvidencePreviewMessage] =
    useState("");
  const [stampEvidenceConfirmMode, setStampEvidenceConfirmMode] = useState<
    "send" | "resend"
  >("send");
  const [stampVendorsForEvidence, setStampVendorsForEvidence] = useState<
    StampVendorOption[]
  >([]);

  // Opposed → Stamp Paper from Vendor for Evidence (4A(xxi))
  const [stampPaperFromVendorDraft, setStampPaperFromVendorDraft] = useState<
    Record<
      string,
      {
        amountPayableToStampVendorForEvidence: string;
        paidToVendorForEvidence: boolean | null;
      }
    >
  >({});

  // Opposed → Documents from Client for Evidence (4A(xxii))
  const [documentsFromClientEvidence, setDocumentsFromClientEvidence] =
    useState<DocumentsFromClientForEvidenceStatus | null>(null);
  const [
    documentsFromClientEvidenceDraft,
    setDocumentsFromClientEvidenceDraft,
  ] = useState<Record<string, string>>({});

  // Opposed → Preparation of Evidence Affidavit (4A(xxiii))
  type EvidenceAffidavitDraft = {
    noOfParagraphsInOpponentIntro: string;
    paragraphWiseReplies: Array<{ heading: string; text: string }>;
    nameOfOpponentsAttorney: string;
    addressOfOpponentsAttorney: string;
    emailOfOpponentsAttorney: string;
    dateOfEvidenceAffidavit: string;
  };
  const [evidenceAffidavit, setEvidenceAffidavit] =
    useState<EvidenceAffidavitStatus | null>(null);
  const [evidenceAffidavitPerTm, setEvidenceAffidavitPerTm] = useState<
    Record<string, EvidenceAffidavitDraft>
  >({});
  const [evidenceAffidavitGenerating, setEvidenceAffidavitGenerating] =
    useState(false);
  const [evidenceAffidavitDownloading, setEvidenceAffidavitDownloading] =
    useState(false);

  // Opposed → Signature from Client for Affidavit (4A(xxiv))
  const [
    hasRecordedSignatureAffidavit,
    setHasRecordedSignatureAffidavit,
  ] = useState(false);
  const [recordingSignatureAffidavit, setRecordingSignatureAffidavit] =
    useState(false);

  // Opposed → Notarisation of Documents for Evidence (4A(xxv))
  const [notarisationEvidence, setNotarisationEvidence] =
    useState<NotarisationForEvidenceStatus | null>(null);
  const [notarisationEvidenceDraft, setNotarisationEvidenceDraft] = useState<
    Record<
      string,
      {
        amountPayableToNotaryForEvidence: string;
        paidToNotaryForEvidence: boolean | null;
      }
    >
  >({});
  // Per-TM debounce timers + a draft mirror so the debounced callback reads
  // the latest user input instead of the stale snapshot captured at scheduling.
  const notarisationAutoSaveTimers = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});
  const notarisationDraftRef = useRef(notarisationEvidenceDraft);
  useEffect(() => {
    notarisationDraftRef.current = notarisationEvidenceDraft;
  }, [notarisationEvidenceDraft]);

  // Opposed → Scanning of Documents for Evidence (4A(xxvi))
  const [hasRecordedScanningEvidence, setHasRecordedScanningEvidence] =
    useState(false);
  const [recordingScanningEvidence, setRecordingScanningEvidence] =
    useState(false);

  // Opposed → File Evidence Affidavit (4A(xxvii))
  const [fileEvidenceAffidavit, setFileEvidenceAffidavit] =
    useState<FileEvidenceAffidavitStatus | null>(null);
  const [fileEvidenceAffidavitPerTm, setFileEvidenceAffidavitPerTm] = useState<
    Record<string, { filingDate: string; pendingFile: File | null }>
  >({});
  const [
    fileEvidenceAffidavitLastDateDraft,
    setFileEvidenceAffidavitLastDateDraft,
  ] = useState<Record<string, string>>({});

  // Opposed → Inform Client about Filing of Evidence (4A(xxviii))
  const [
    informFilingEvidencePreview,
    setInformFilingEvidencePreview,
  ] = useState<InformClientFilingOfEvidencePreview | null>(null);
  const [hasSentInformFilingEvidence, setHasSentInformFilingEvidence] =
    useState(false);
  const [resendingInformFilingEvidence, setResendingInformFilingEvidence] =
    useState(false);

  // Opposed → Download Rejoinder Affidavit (4A(xxix))
  const [downloadRejoinder, setDownloadRejoinder] =
    useState<DownloadRejoinderAffidavitStatus | null>(null);
  const [downloadRejoinderPerTm, setDownloadRejoinderPerTm] = useState<
    Record<string, { rejoinderDate: string; pendingFile: File | null }>
  >({});

  // Opposed → Status details of Opposition Hearing (4A(xxx))
  const [oppositionHearing, setOppositionHearing] =
    useState<OppositionHearingStatus | null>(null);
  const [oppositionHearingPerTm, setOppositionHearingPerTm] = useState<
    Record<string, { hearingDate: string; pendingFile: File | null }>
  >({});

  // Opposed → Notice to Client for Opposition Hearing (4A(xxxi))
  const [
    hasSentOppositionHearingNotice,
    setHasSentOppositionHearingNotice,
  ] = useState(false);
  const [
    resendingOppositionHearingNotice,
    setResendingOppositionHearingNotice,
  ] = useState(false);
  const [
    oppositionHearingNoticePreview,
    setOppositionHearingNoticePreview,
  ] = useState<OppositionHearingNoticePreview | null>(null);
  const [
    oppositionHearingNoticeSendConfirmOpen,
    setOppositionHearingNoticeSendConfirmOpen,
  ] = useState(false);
  const [
    oppositionHearingNoticeResendConfirmOpen,
    setOppositionHearingNoticeResendConfirmOpen,
  ] = useState(false);
  const [
    oppositionHearingNoticeSending,
    setOppositionHearingNoticeSending,
  ] = useState(false);

  // Opposed → Inform Client about Opposition Hearing (4A(xxxii))
  const [
    informOppositionHearingPreview,
    setInformOppositionHearingPreview,
  ] = useState<InformClientOppositionHearingPreview | null>(null);
  const [
    hasSentOppositionHearingInform,
    setHasSentOppositionHearingInform,
  ] = useState(false);
  const [
    resendingOppositionHearingInform,
    setResendingOppositionHearingInform,
  ] = useState(false);

  // Opposed → Invoice to Client for Opposition Hearing (4A(xxxiii))
  const [oppositionHearingInvoice, setOppositionHearingInvoice] =
    useState<OppositionHearingInvoiceStatus | null>(null);
  const [
    oppositionHearingInvoicePreviewData,
    setOppositionHearingInvoicePreviewData,
  ] = useState<OppositionHearingInvoicePreview | null>(null);
  const [
    oppositionHearingInvoiceRows,
    setOppositionHearingInvoiceRows,
  ] = useState<
    Record<string, { invoiceAmount: string; invoiceDate: string }>
  >({});
  const [
    hasSentOppositionHearingInvoice,
    setHasSentOppositionHearingInvoice,
  ] = useState(false);
  const [
    oppositionHearingInvoiceGenerating,
    setOppositionHearingInvoiceGenerating,
  ] = useState(false);
  const [
    resendingOppositionHearingInvoice,
    setResendingOppositionHearingInvoice,
  ] = useState(false);
  const [
    oppositionHearingInvoiceResendConfirmOpen,
    setOppositionHearingInvoiceResendConfirmOpen,
  ] = useState(false);

  // Opposed → Call Client for Opposition Hearing (4A(xxxiv))
  const [
    hasRecordedOppositionHearingCall,
    setHasRecordedOppositionHearingCall,
  ] = useState(false);
  const [
    recordingOppositionHearingCall,
    setRecordingOppositionHearingCall,
  ] = useState(false);

  // Opposed → Confirmation from Client for Opposition Hearing (4A(xxxv))
  const [
    oppositionHearingConfirmation,
    setOppositionHearingConfirmation,
  ] = useState<OppositionHearingConfirmationStatus | null>(null);
  const [
    oppositionHearingConfirmationDraft,
    setOppositionHearingConfirmationDraft,
  ] = useState<Record<string, OppositionHearingClientConfirmation | null>>({});

  // Opposed → Invoice for TM-M before Opposition Hearing (4A(xxxvi))
  const [
    tmMBeforeHearingInvoice,
    setTmMBeforeHearingInvoice,
  ] = useState<TmMBeforeOppositionHearingInvoiceStatus | null>(null);
  const [
    tmMBeforeHearingInvoicePreview,
    setTmMBeforeHearingInvoicePreview,
  ] = useState<TmMBeforeOppositionHearingInvoicePreview | null>(null);
  const [
    tmMBeforeHearingInvoiceDateDraft,
    setTmMBeforeHearingInvoiceDateDraft,
  ] = useState<string>("");
  const [
    tmMBeforeHearingInvoiceAmountDraft,
    setTmMBeforeHearingInvoiceAmountDraft,
  ] = useState<string>("");
  const [
    tmMBeforeHearingInvoiceGenerating,
    setTmMBeforeHearingInvoiceGenerating,
  ] = useState(false);
  const [
    tmMBeforeHearingInvoiceDownloading,
    setTmMBeforeHearingInvoiceDownloading,
  ] = useState(false);
  const [
    hasSentTmMBeforeHearingInvoice,
    setHasSentTmMBeforeHearingInvoice,
  ] = useState(false);

  // Opposed → Payment from Client for TM-M before Opposition Hearing (4A(xxxvii))
  const [
    tmMBeforeHearingCollectedAmountDraft,
    setTmMBeforeHearingCollectedAmountDraft,
  ] = useState<string>("");

  // Opposed → File TM-M before Opposition Hearing (4A(xxxviii))
  const [
    tmMBeforeHearingFiled,
    setTmMBeforeHearingFiled,
  ] = useState<TmMBeforeOppositionHearingFiledStatus | null>(null);
  const [
    tmMBeforeHearingFiledPerTm,
    setTmMBeforeHearingFiledPerTm,
  ] = useState<
    Record<string, { filingDate: string; pendingFile: File | null }>
  >({});

  // Opposed → Inform Client about Filing of TM-M before Opposition Hearing (4A(xxxix))
  const [
    informTmMBeforeHearingPreview,
    setInformTmMBeforeHearingPreview,
  ] = useState<InformClientTmMBeforeOppositionHearingPreview | null>(null);
  const [
    hasSentInformTmMBeforeHearing,
    setHasSentInformTmMBeforeHearing,
  ] = useState(false);
  const [
    resendingInformTmMBeforeHearing,
    setResendingInformTmMBeforeHearing,
  ] = useState(false);

  // Opposed → Payment from Client for Opposition Hearing (4A(xl))
  const [
    oppositionHearingPayment,
    setOppositionHearingPayment,
  ] = useState<OppositionHearingPaymentStatus | null>(null);
  const [
    oppositionHearingPaymentDraft,
    setOppositionHearingPaymentDraft,
  ] = useState<string>("");

  // Opposed → Documents from Client for Opposition Hearing (4A(xli))
  const [
    hasRecordedDocumentsOppHearing,
    setHasRecordedDocumentsOppHearing,
  ] = useState(false);
  const [recordingDocumentsOppHearing, setRecordingDocumentsOppHearing] =
    useState(false);

  // Opposed → Filing of Opposition Hearing Document (4A(xlii))
  const [
    hasRecordedFilingOppHearingDoc,
    setHasRecordedFilingOppHearingDoc,
  ] = useState(false);
  const [recordingFilingOppHearingDoc, setRecordingFilingOppHearingDoc] =
    useState(false);

  // Opposed → Note on Opposition Hearing (4A(xliii))
  const [noteOnOppHearing, setNoteOnOppHearing] =
    useState<NoteOnOppositionHearingStatus | null>(null);
  const [noteOnOppHearingSelectedIdx, setNoteOnOppHearingSelectedIdx] =
    useState(0);
  const [noteOnOppHearingDraft, setNoteOnOppHearingDraft] = useState<
    Record<
      string,
      {
        noteOtherOpposition1: string;
        noteOtherOpposition2: string;
        similarMarkNotes: Array<{
          mark: string;
          tmNo: string;
          imageKey?: string | null;
          userDate?: string | null;
          goodsServices?: string | null;
          address?: string | null;
          note1?: string | null;
          note2?: string | null;
        }>;
      }
    >
  >({});

  // Opposed → Attend Opposition Hearing (4A(xliv))
  const [attendOppHearing, setAttendOppHearing] =
    useState<AttendOppositionHearingStatusResponse | null>(null);
  const [attendOppHearingSelectedIdx, setAttendOppHearingSelectedIdx] =
    useState(0);
  const [attendOppHearingDraft, setAttendOppHearingDraft] = useState<
    Record<
      string,
      {
        hearingStatus: AttendOppositionHearingStatusValue | null;
        hearingOfficerRemark: AttendOppositionHearingOfficerRemarkValue | null;
        directionOfOfficer: string;
      }
    >
  >({});

  // True when at least one app in 4A(xliv) has FILE_TM_M remark — controls
  // whether the TM-M-after-opp-hearing sub-steps (4A(xlvi)-4A(xlix)) are shown.
  const hasFileTmMAfterHearing = useMemo(
    () =>
      (attendOppHearing?.applications ?? []).some(
        (app) => app.hearingOfficerRemark === "FILE_TM_M"
      ),
    [attendOppHearing]
  );

  // Opposed → Subsequent Opposition Hearings (4A(l))
  const [subsequentOppHearing, setSubsequentOppHearing] =
    useState<SubsequentOppositionHearingStatus | null>(null);
  type SubsequentOppHearingDraft = {
    dateOfHearing: string;
    pendingNoticeFile: File | null;
    hearingStatus: AttendOppositionHearingStatusValue | null;
    hearingOfficerRemark: AttendOppositionHearingOfficerRemarkValue | null;
    directionOfOfficer: string;
    pendingPostponementFile: File | null;
  };
  const [subsequentOppHearingDraft, setSubsequentOppHearingDraft] = useState<
    Record<string, SubsequentOppHearingDraft>
  >({});
  const [sendingSubsequentOppHearingNotice, setSendingSubsequentOppHearingNotice] =
    useState(false);
  const [subsequentOppHearingNoticePreview, setSubsequentOppHearingNoticePreview] =
    useState<SubsequentOppositionHearingNoticePreview | null>(null);
  const [
    subsequentOppHearingNoticePreviewError,
    setSubsequentOppHearingNoticePreviewError,
  ] = useState<string | null>(null);

  // Opposed → Subsequent Opposition Hearing Invoice (4A(xxxiii) for subsequent)
  const [subsequentOppHearingInvoice, setSubsequentOppHearingInvoice] =
    useState<SubsequentOppositionHearingInvoiceStatus | null>(null);
  const [
    subsequentOppHearingInvoiceAmountDraft,
    setSubsequentOppHearingInvoiceAmountDraft,
  ] = useState("");
  const [
    subsequentOppHearingInvoiceDateDraft,
    setSubsequentOppHearingInvoiceDateDraft,
  ] = useState("");
  const [
    subsequentOppHearingInvoiceIncludeNoDraft,
    setSubsequentOppHearingInvoiceIncludeNoDraft,
  ] = useState(false);
  const [
    subsequentOppHearingInvoiceGenerating,
    setSubsequentOppHearingInvoiceGenerating,
  ] = useState(false);
  const [
    subsequentOppHearingInvoiceDownloading,
    setSubsequentOppHearingInvoiceDownloading,
  ] = useState(false);
  const [
    subsequentOppHearingInvoiceSending,
    setSubsequentOppHearingInvoiceSending,
  ] = useState(false);

  // Opposed → Subsequent Opposition Hearing Payment (4A(xl) for subsequent)
  const [
    subsequentOppHearingCollectedDraft,
    setSubsequentOppHearingCollectedDraft,
  ] = useState("");
  // Remembers where to route AFTER Payment is saved. Set when the user saves
  // the Subsequent Hearing outcome (so we capture the just-entered hearing's
  // outcome before the backend's auto-advance hides it from us). Defaults to
  // "subsequent-opp-hearings" so non-final / unset outcomes loop back for the
  // next hearing.
  const [
    afterSubsequentOppHearingPaymentRoute,
    setAfterSubsequentOppHearingPaymentRoute,
  ] = useState<"subsequent-opp-hearings" | "order-of-opp-hearing">(
    "subsequent-opp-hearings"
  );

  const visibleOpposedSubSteps = useMemo(
    () =>
      OPPOSED_SUB_STEPS.filter(
        (s) =>
          hasFileTmMAfterHearing ||
          !TMM_AFTER_OPP_HEARING_SUB_STEP_IDS.includes(s.id)
      ),
    [hasFileTmMAfterHearing]
  );

  // Opposed → Inform Client after Opposition Hearing (4A(xlv))
  const [
    informAfterOppHearing,
    setInformAfterOppHearing,
  ] = useState<InformAfterOppositionHearingStatus | null>(null);
  const [hasSentInformAfterOppHearing, setHasSentInformAfterOppHearing] =
    useState(false);
  const [resendingInformAfterOppHearing, setResendingInformAfterOppHearing] =
    useState(false);
  const [
    informAfterOppHearingSendConfirmOpen,
    setInformAfterOppHearingSendConfirmOpen,
  ] = useState(false);
  const [informAfterOppHearingSending, setInformAfterOppHearingSending] =
    useState(false);

  // Opposed → Invoice for TM-M after Opposition Hearing (4A(xlvi))
  const [
    tmMAfterHearingInvoice,
    setTmMAfterHearingInvoice,
  ] = useState<TmMAfterOppositionHearingInvoiceStatus | null>(null);
  const [
    tmMAfterHearingInvoicePreview,
    setTmMAfterHearingInvoicePreview,
  ] = useState<TmMAfterOppositionHearingInvoicePreview | null>(null);
  const [
    tmMAfterHearingInvoiceDraft,
    setTmMAfterHearingInvoiceDraft,
  ] = useState<{ invoiceAmount: string; invoiceDate: string }>({
    invoiceAmount: "",
    invoiceDate: "",
  });
  const [
    hasSentTmMAfterHearingInvoice,
    setHasSentTmMAfterHearingInvoice,
  ] = useState(false);
  const [
    tmMAfterHearingInvoiceGenerating,
    setTmMAfterHearingInvoiceGenerating,
  ] = useState(false);
  const [
    tmMAfterHearingInvoiceDownloading,
    setTmMAfterHearingInvoiceDownloading,
  ] = useState(false);

  // Opposed → File TM-M after Opposition Hearing (treated as 4A(xlvii) Collected Amount per Figma)
  const [
    tmMAfterHearingCollectedDraft,
    setTmMAfterHearingCollectedDraft,
  ] = useState<string>("");
  // Opposed → File TM-M after Opposition Hearing — separate filed step (4A(xlviii)) data load
  const [
    tmMAfterHearingFiled,
    setTmMAfterHearingFiled,
  ] = useState<TmMAfterOppositionHearingFiledStatus | null>(null);
  const [
    tmMAfterHearingFiledPerTm,
    setTmMAfterHearingFiledPerTm,
  ] = useState<
    Record<string, { filingDate: string; pendingFile: File | null }>
  >({});

  // Opposed → Inform Client after filing TM-M after Opposition Hearing (4A(xlix))
  const [
    informTmMAfterHearingPreview,
    setInformTmMAfterHearingPreview,
  ] = useState<InformClientTmMAfterOppositionHearingPreview | null>(null);
  const [
    hasSentInformTmMAfterHearing,
    setHasSentInformTmMAfterHearing,
  ] = useState(false);
  const [
    resendingInformTmMAfterHearing,
    setResendingInformTmMAfterHearing,
  ] = useState(false);

  // Opposed → Order of Opposition Hearing to Client (4A(li))
  const [
    orderOfOppHearing,
    setOrderOfOppHearing,
  ] = useState<OrderOfOppositionHearingStatus | null>(null);
  const [orderOfOppHearingDraft, setOrderOfOppHearingDraft] = useState<
    Record<string, OrderOfOppositionHearingOutcomeValue | null>
  >({});
  const [
    hasSentOrderOfOppHearing,
    setHasSentOrderOfOppHearing,
  ] = useState(false);
  const [resendingOrderOfOppHearing, setResendingOrderOfOppHearing] =
    useState(false);
  const [
    orderOfOppHearingSendConfirmOpen,
    setOrderOfOppHearingSendConfirmOpen,
  ] = useState(false);
  const [
    orderOfOppHearingResendConfirmOpen,
    setOrderOfOppHearingResendConfirmOpen,
  ] = useState(false);
  const [orderOfOppHearingSending, setOrderOfOppHearingSending] =
    useState(false);
  const [
    orderOfOppHearingMessagesPreview,
    setOrderOfOppHearingMessagesPreview,
  ] = useState<string[]>([]);
  const [
    orderOfOppHearingMessagePreviewLoading,
    setOrderOfOppHearingMessagePreviewLoading,
  ] = useState(false);
  const [
    orderOfOppHearingMessagePreviewError,
    setOrderOfOppHearingMessagePreviewError,
  ] = useState<string | null>(null);

  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
    revokeOnClose?: boolean;
  } | null>(null);

  const [dragOverTmNo, setDragOverTmNo] = useState<string | null>(null);

  // Debounce timer for auto-save when journal date changes
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs that mirror the latest state so the debounced auto-save
  // (whose setTimeout captures the closure at scheduling time) reads
  // fresh values instead of the stale snapshot from the render where
  // the user picked the date.
  const perTmRef = useRef<Record<string, PerTmFormState>>({});
  const statusRef = useRef<AdvertisedInJournalStatusResponse | null>(null);
  useEffect(() => {
    perTmRef.current = perTm;
  }, [perTm]);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // ───────────────────── Initial load ─────────────────────

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setStatus(null);
      setPerTm({});
      return;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await getAdvertisedInJournalStatus(leadId);
        if (cancelled) return;
        setStatus(data);
        setPerTm(applicationsToFormMap(data.applications));
        setSelectedTmIdx(0);
        // Review item 79 — restore the 4(iii) scenario chosen previously. It is stored per
        // application; take the first non-null, which is the shared answer in the normal case.
        const savedScenario =
          data.applications.find((a) => a.advertisedFrom)?.advertisedFrom ?? null;
        if (savedScenario) setInformScenario(savedScenario);
        try {
          const workflow = await getWorkflowStatus(leadId);
          if (cancelled) return;
          const noticeStep = workflow.steps.find(
            (s) => s.slug === NOTICE_STEP_SLUG
          );
          setHasSentNotice(Boolean(noticeStep?.isCompleted));
          const informStep = workflow.steps.find(
            (s) => s.slug === INFORM_STEP_SLUG
          );
          setHasSentInform(Boolean(informStep?.isCompleted));
        } catch {
          if (!cancelled) {
            setHasSentNotice(false);
            setHasSentInform(false);
          }
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Advertised in Journal status"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // ───────────────────── Footer mode signalling ─────────────────────

  useLayoutEffect(() => {
    onStatusDetailsFooterFormModeChange?.(
      variant === "standard" && subStep === "status-details"
    );
  }, [variant, subStep, onStatusDetailsFooterFormModeChange]);

  useLayoutEffect(() => {
    const isNotice = variant === "standard" && subStep === "notice-to-client";
    onNoticeFooterSendModeChange?.(isNotice && !hasSentNotice);
    onNoticeFooterSaveModeChange?.(isNotice && hasSentNotice);
  }, [
    variant,
    subStep,
    hasSentNotice,
    onNoticeFooterSendModeChange,
    onNoticeFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const isInform = variant === "standard" && subStep === "inform-client";
    onInformFooterSendModeChange?.(isInform && !hasSentInform);
    onInformFooterSaveModeChange?.(isInform && hasSentInform);
  }, [
    variant,
    subStep,
    hasSentInform,
    onInformFooterSendModeChange,
    onInformFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterStatusFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "status-details-counter"
    );
  }, [variant, opposedSubStep, onCounterStatusFooterFormModeChange]);

  useLayoutEffect(() => {
    const isNotice =
      variant === "opposed" && opposedSubStep === "notice-to-client-counter";
    onCounterNoticeFooterSendModeChange?.(isNotice && !hasSentCounterNotice);
    onCounterNoticeFooterSaveModeChange?.(isNotice && hasSentCounterNotice);
  }, [
    variant,
    opposedSubStep,
    hasSentCounterNotice,
    onCounterNoticeFooterSendModeChange,
    onCounterNoticeFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const isInform =
      variant === "opposed" && opposedSubStep === "inform-client-about-counter";
    onCounterInformFooterSendModeChange?.(isInform && !hasSentCounterInform);
    onCounterInformFooterSaveModeChange?.(isInform && hasSentCounterInform);
  }, [
    variant,
    opposedSubStep,
    hasSentCounterInform,
    onCounterInformFooterSendModeChange,
    onCounterInformFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterCallFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "call-client-counter"
    );
  }, [variant, opposedSubStep, onCounterCallFooterFormModeChange]);

  useLayoutEffect(() => {
    onCounterConfirmationFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "confirmation-from-client-counter"
    );
  }, [variant, opposedSubStep, onCounterConfirmationFooterFormModeChange]);

  useLayoutEffect(() => {
    const isInvoice =
      variant === "opposed" && opposedSubStep === "invoice-to-client-counter";
    onCounterInvoiceFooterSendModeChange?.(isInvoice && !hasSentCounterInvoice);
    onCounterInvoiceFooterSaveModeChange?.(isInvoice && hasSentCounterInvoice);
  }, [
    variant,
    opposedSubStep,
    hasSentCounterInvoice,
    onCounterInvoiceFooterSendModeChange,
    onCounterInvoiceFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterPaymentFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "payment-from-client-counter"
    );
  }, [variant, opposedSubStep, onCounterPaymentFooterFormModeChange]);

  // Register the Progress (skip) handler with the dashboard footer so the
  // Progress button shows up next to "Save & Continue" on this sub-step
  // — mirrors the HAO Payment-from-Client pattern.
  useEffect(() => {
    if (!onCounterPaymentProgressActionChange) return;
    const active =
      variant === "opposed" && opposedSubStep === "payment-from-client-counter";
    if (!active || !leadId) {
      onCounterPaymentProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_04a_vii_PaymentFromClientCounter
        );
        toast.success("Step skipped successfully.");
        setOpposedSubStep("prepare-counter");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onCounterPaymentProgressActionChange(run);
    return () => onCounterPaymentProgressActionChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, opposedSubStep, leadId, onCounterPaymentProgressActionChange]);

  useLayoutEffect(() => {
    onCounterPrepareFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "prepare-counter"
    );
  }, [variant, opposedSubStep, onCounterPrepareFooterFormModeChange]);

  useLayoutEffect(() => {
    onCounterFileFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "file-counter"
    );
  }, [variant, opposedSubStep, onCounterFileFooterFormModeChange]);

  useLayoutEffect(() => {
    const isInformFiling =
      variant === "opposed" && opposedSubStep === "inform-client-filing-counter";
    onCounterInformFilingFooterSendModeChange?.(isInformFiling && !hasSentInformFiling);
    onCounterInformFilingFooterSaveModeChange?.(isInformFiling && hasSentInformFiling);
  }, [
    variant,
    opposedSubStep,
    hasSentInformFiling,
    onCounterInformFilingFooterSendModeChange,
    onCounterInformFilingFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterNoticeOfCounterFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "notice-of-counter-registry"
    );
  }, [variant, opposedSubStep, onCounterNoticeOfCounterFooterFormModeChange]);

  useEffect(() => {
    if (!onCounterNoticeOfCounterProgressActionChange) return;
    const active =
      variant === "opposed" && opposedSubStep === "notice-of-counter-registry";
    if (!active || !leadId) {
      onCounterNoticeOfCounterProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_04a_xi_DateOfNoticeOfCounterStatement
        );
        toast.success("Step skipped successfully.");
        setOpposedSubStep("inform-client-notice-counter");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onCounterNoticeOfCounterProgressActionChange(run);
    return () => onCounterNoticeOfCounterProgressActionChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, opposedSubStep, leadId, onCounterNoticeOfCounterProgressActionChange]);

  useLayoutEffect(() => {
    const isInformNotice =
      variant === "opposed" && opposedSubStep === "inform-client-notice-counter";
    onCounterInformNoticeFooterSendModeChange?.(isInformNotice && !hasSentInformNotice);
    onCounterInformNoticeFooterSaveModeChange?.(isInformNotice && hasSentInformNotice);
  }, [
    variant,
    opposedSubStep,
    hasSentInformNotice,
    onCounterInformNoticeFooterSendModeChange,
    onCounterInformNoticeFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterEvidenceByOpponentFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "status-evidence-by-opp"
    );
  }, [variant, opposedSubStep, onCounterEvidenceByOpponentFooterFormModeChange]);

  useLayoutEffect(() => {
    const isNoticeEvidence =
      variant === "opposed" && opposedSubStep === "notice-evidence-by-opp";
    onCounterNoticeEvidenceByOppFooterSendModeChange?.(
      isNoticeEvidence && !hasSentNoticeEvidenceByOpp
    );
    onCounterNoticeEvidenceByOppFooterSaveModeChange?.(
      isNoticeEvidence && hasSentNoticeEvidenceByOpp
    );
  }, [
    variant,
    opposedSubStep,
    hasSentNoticeEvidenceByOpp,
    onCounterNoticeEvidenceByOppFooterSendModeChange,
    onCounterNoticeEvidenceByOppFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const isInformEvidence =
      variant === "opposed" && opposedSubStep === "inform-evidence-by-opp";
    onCounterInformEvidenceByOppFooterSendModeChange?.(
      isInformEvidence && !hasSentInformEvidenceByOpp
    );
    onCounterInformEvidenceByOppFooterSaveModeChange?.(
      isInformEvidence && hasSentInformEvidenceByOpp
    );
  }, [
    variant,
    opposedSubStep,
    hasSentInformEvidenceByOpp,
    onCounterInformEvidenceByOppFooterSendModeChange,
    onCounterInformEvidenceByOppFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterCallClientEvidenceFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "call-client-evidence"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterCallClientEvidenceFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterEvidenceConfirmationFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "confirmation-evidence"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterEvidenceConfirmationFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    const isInvoice =
      variant === "opposed" && opposedSubStep === "invoice-evidence";
    onCounterEvidenceInvoiceFooterSendModeChange?.(
      isInvoice && !hasSentEvidenceInvoice
    );
    onCounterEvidenceInvoiceFooterSaveModeChange?.(
      isInvoice && hasSentEvidenceInvoice
    );
  }, [
    variant,
    opposedSubStep,
    hasSentEvidenceInvoice,
    onCounterEvidenceInvoiceFooterSendModeChange,
    onCounterEvidenceInvoiceFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterEvidencePaymentFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "payment-evidence"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterEvidencePaymentFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    const isStampVendor =
      variant === "opposed" && opposedSubStep === "stamp-vendor-evidence";
    onCounterStampVendorEvidenceFooterSendModeChange?.(
      isStampVendor && !hasSentStampPaperEvidence
    );
    onCounterStampVendorEvidenceFooterSaveModeChange?.(
      isStampVendor && hasSentStampPaperEvidence
    );
  }, [
    variant,
    opposedSubStep,
    hasSentStampPaperEvidence,
    onCounterStampVendorEvidenceFooterSendModeChange,
    onCounterStampVendorEvidenceFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterStampFromVendorEvidenceFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "stamp-from-vendor-evidence"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterStampFromVendorEvidenceFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterDocumentsFromClientEvidenceFooterFormModeChange?.(
      variant === "opposed" &&
        opposedSubStep === "documents-from-client-evidence"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterDocumentsFromClientEvidenceFooterFormModeChange,
  ]);

  useEffect(() => {
    if (!onCounterDocumentsFromClientEvidenceProgressActionChange) return;
    const active =
      variant === "opposed" && opposedSubStep === "documents-from-client-evidence";
    if (!active || !leadId) {
      onCounterDocumentsFromClientEvidenceProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_04a_xxii_DocumentsFromClientForEvidence
        );
        toast.success("Step skipped successfully.");
        setOpposedSubStep("preparation-evidence-affidavit");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onCounterDocumentsFromClientEvidenceProgressActionChange(run);
    return () => onCounterDocumentsFromClientEvidenceProgressActionChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, opposedSubStep, leadId, onCounterDocumentsFromClientEvidenceProgressActionChange]);

  useLayoutEffect(() => {
    onCounterPreparationEvidenceAffidavitFooterFormModeChange?.(
      variant === "opposed" &&
        opposedSubStep === "preparation-evidence-affidavit"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterPreparationEvidenceAffidavitFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterSignatureAffidavitFooterFormModeChange?.(
      variant === "opposed" &&
        opposedSubStep === "signature-evidence-affidavit"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterSignatureAffidavitFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterNotarisationEvidenceFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "notarisation-evidence"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterNotarisationEvidenceFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterScanningEvidenceFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "scanning-evidence"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterScanningEvidenceFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterFileEvidenceAffidavitFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "file-evidence-affidavit"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterFileEvidenceAffidavitFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    const isInform =
      variant === "opposed" && opposedSubStep === "inform-filing-evidence";
    onCounterInformFilingEvidenceFooterSendModeChange?.(
      isInform && !hasSentInformFilingEvidence
    );
    onCounterInformFilingEvidenceFooterSaveModeChange?.(
      isInform && hasSentInformFilingEvidence
    );
  }, [
    variant,
    opposedSubStep,
    hasSentInformFilingEvidence,
    onCounterInformFilingEvidenceFooterSendModeChange,
    onCounterInformFilingEvidenceFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterDownloadRejoinderFooterFormModeChange?.(
      variant === "opposed" &&
        opposedSubStep === "download-rejoinder-affidavit"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterDownloadRejoinderFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterOppositionHearingStatusFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "status-opposition-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterOppositionHearingStatusFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    const isNotice =
      variant === "opposed" && opposedSubStep === "notice-opposition-hearing";
    onCounterOppositionHearingNoticeFooterSendModeChange?.(
      isNotice && !hasSentOppositionHearingNotice
    );
    onCounterOppositionHearingNoticeFooterSaveModeChange?.(
      isNotice && hasSentOppositionHearingNotice
    );
  }, [
    variant,
    opposedSubStep,
    hasSentOppositionHearingNotice,
    onCounterOppositionHearingNoticeFooterSendModeChange,
    onCounterOppositionHearingNoticeFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const isInform =
      variant === "opposed" && opposedSubStep === "inform-opposition-hearing";
    onCounterOppositionHearingInformFooterSendModeChange?.(
      isInform && !hasSentOppositionHearingInform
    );
    onCounterOppositionHearingInformFooterSaveModeChange?.(
      isInform && hasSentOppositionHearingInform
    );
  }, [
    variant,
    opposedSubStep,
    hasSentOppositionHearingInform,
    onCounterOppositionHearingInformFooterSendModeChange,
    onCounterOppositionHearingInformFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const isInvoice =
      variant === "opposed" && opposedSubStep === "invoice-opposition-hearing";
    onCounterOppositionHearingInvoiceFooterSendModeChange?.(
      isInvoice && !hasSentOppositionHearingInvoice
    );
    onCounterOppositionHearingInvoiceFooterSaveModeChange?.(
      isInvoice && hasSentOppositionHearingInvoice
    );
  }, [
    variant,
    opposedSubStep,
    hasSentOppositionHearingInvoice,
    onCounterOppositionHearingInvoiceFooterSendModeChange,
    onCounterOppositionHearingInvoiceFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterOppositionHearingCallFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "call-opposition-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterOppositionHearingCallFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterOppositionHearingConfirmationFooterFormModeChange?.(
      variant === "opposed" &&
        opposedSubStep === "confirmation-opposition-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterOppositionHearingConfirmationFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    const isInvoice =
      variant === "opposed" &&
      opposedSubStep === "invoice-tmm-before-opp-hearing";
    onCounterTmMBeforeHearingInvoiceFooterSendModeChange?.(
      isInvoice && !hasSentTmMBeforeHearingInvoice
    );
    onCounterTmMBeforeHearingInvoiceFooterSaveModeChange?.(
      isInvoice && hasSentTmMBeforeHearingInvoice
    );
  }, [
    variant,
    opposedSubStep,
    hasSentTmMBeforeHearingInvoice,
    onCounterTmMBeforeHearingInvoiceFooterSendModeChange,
    onCounterTmMBeforeHearingInvoiceFooterSaveModeChange,
  ]);

  // Register the Progress (skip) handler with the dashboard footer so the
  // Progress button shows up next to "Save & Continue" on this sub-step
  // — mirrors the Counter Opposition-Hearing Payment pattern.
  useEffect(() => {
    if (!onCounterTmMBeforeHearingInvoiceProgressActionChange) return;
    const active =
      variant === "opposed" &&
      opposedSubStep === "invoice-tmm-before-opp-hearing";
    if (!active || !leadId) {
      onCounterTmMBeforeHearingInvoiceProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_04a_xxxvi_a_SaveInvoiceTmMBeforeOppositionHearing
        );
        toast.success("Step skipped successfully.");
        setOpposedSubStep("payment-tmm-before-opp-hearing");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onCounterTmMBeforeHearingInvoiceProgressActionChange(run);
    return () => onCounterTmMBeforeHearingInvoiceProgressActionChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    variant,
    opposedSubStep,
    leadId,
    onCounterTmMBeforeHearingInvoiceProgressActionChange,
  ]);

  useLayoutEffect(() => {
    onCounterTmMBeforeHearingPaymentFooterFormModeChange?.(
      variant === "opposed" &&
        opposedSubStep === "payment-tmm-before-opp-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterTmMBeforeHearingPaymentFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterTmMBeforeHearingFiledFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "file-tmm-before-opp-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterTmMBeforeHearingFiledFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    const isInform =
      variant === "opposed" &&
      opposedSubStep === "inform-tmm-before-opp-hearing";
    onCounterTmMBeforeHearingInformFooterSendModeChange?.(
      isInform && !hasSentInformTmMBeforeHearing
    );
    onCounterTmMBeforeHearingInformFooterSaveModeChange?.(
      isInform && hasSentInformTmMBeforeHearing
    );
  }, [
    variant,
    opposedSubStep,
    hasSentInformTmMBeforeHearing,
    onCounterTmMBeforeHearingInformFooterSendModeChange,
    onCounterTmMBeforeHearingInformFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterOppositionHearingPaymentFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "payment-opposition-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterOppositionHearingPaymentFooterFormModeChange,
  ]);

  // Register the Progress (skip) handler with the dashboard footer so the
  // Progress button shows up next to "Save & Continue" on this sub-step
  // — mirrors the Counter Payment-from-Client pattern.
  useEffect(() => {
    if (!onCounterOppositionHearingPaymentProgressActionChange) return;
    const active =
      variant === "opposed" && opposedSubStep === "payment-opposition-hearing";
    if (!active || !leadId) {
      onCounterOppositionHearingPaymentProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_04a_xl_PaymentFromClientOppositionHearing
        );
        toast.success("Step skipped successfully.");
        setOpposedSubStep("documents-opp-hearing");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onCounterOppositionHearingPaymentProgressActionChange(run);
    return () => onCounterOppositionHearingPaymentProgressActionChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    variant,
    opposedSubStep,
    leadId,
    onCounterOppositionHearingPaymentProgressActionChange,
  ]);

  useLayoutEffect(() => {
    onCounterDocumentsOppHearingFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "documents-opp-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterDocumentsOppHearingFooterFormModeChange,
  ]);

  // Register the Progress (skip) handler with the dashboard footer so the
  // Progress button shows up next to "Save & Continue" on this sub-step
  // — mirrors the Counter Opposition-Hearing Payment pattern.
  useEffect(() => {
    if (!onCounterDocumentsOppHearingProgressActionChange) return;
    const active =
      variant === "opposed" && opposedSubStep === "documents-opp-hearing";
    if (!active || !leadId) {
      onCounterDocumentsOppHearingProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_04a_xli_DocumentsFromClientOppositionHearing
        );
        toast.success("Step skipped successfully.");
        setOpposedSubStep("filing-opp-hearing-document");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onCounterDocumentsOppHearingProgressActionChange(run);
    return () => onCounterDocumentsOppHearingProgressActionChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    variant,
    opposedSubStep,
    leadId,
    onCounterDocumentsOppHearingProgressActionChange,
  ]);

  useLayoutEffect(() => {
    onCounterFilingOppHearingDocFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "filing-opp-hearing-document"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterFilingOppHearingDocFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterNoteOnOppHearingFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "note-on-opp-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterNoteOnOppHearingFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterAttendOppHearingFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "attend-opp-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterAttendOppHearingFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    const isInform =
      variant === "opposed" && opposedSubStep === "inform-after-opp-hearing";
    onCounterInformAfterOppHearingFooterSendModeChange?.(
      isInform && !hasSentInformAfterOppHearing
    );
    onCounterInformAfterOppHearingFooterSaveModeChange?.(
      isInform && hasSentInformAfterOppHearing
    );
  }, [
    variant,
    opposedSubStep,
    hasSentInformAfterOppHearing,
    onCounterInformAfterOppHearingFooterSendModeChange,
    onCounterInformAfterOppHearingFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const isInvoice =
      variant === "opposed" &&
      opposedSubStep === "invoice-tmm-after-opp-hearing";
    onCounterInvoiceTmMAfterOppHearingFooterSendModeChange?.(
      isInvoice && !hasSentTmMAfterHearingInvoice
    );
    onCounterInvoiceTmMAfterOppHearingFooterSaveModeChange?.(
      isInvoice && hasSentTmMAfterHearingInvoice
    );
  }, [
    variant,
    opposedSubStep,
    hasSentTmMAfterHearingInvoice,
    onCounterInvoiceTmMAfterOppHearingFooterSendModeChange,
    onCounterInvoiceTmMAfterOppHearingFooterSaveModeChange,
  ]);

  // Register the Progress (skip) handler with the dashboard footer so the
  // Progress button shows up next to "Save & Continue" on this sub-step
  // — mirrors the Counter Opposition-Hearing Payment pattern.
  useEffect(() => {
    if (!onCounterInvoiceTmMAfterOppHearingProgressActionChange) return;
    const active =
      variant === "opposed" &&
      opposedSubStep === "invoice-tmm-after-opp-hearing";
    if (!active || !leadId) {
      onCounterInvoiceTmMAfterOppHearingProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_04a_xlvi_a_SaveInvoiceTmMAfterOppositionHearing
        );
        toast.success("Step skipped successfully.");
        setOpposedSubStep("payment-tmm-after-opp-hearing");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onCounterInvoiceTmMAfterOppHearingProgressActionChange(run);
    return () => onCounterInvoiceTmMAfterOppHearingProgressActionChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    variant,
    opposedSubStep,
    leadId,
    onCounterInvoiceTmMAfterOppHearingProgressActionChange,
  ]);

  useLayoutEffect(() => {
    onCounterPaymentTmMAfterOppHearingFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "payment-tmm-after-opp-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterPaymentTmMAfterOppHearingFooterFormModeChange,
  ]);

  // Register the Progress (skip) handler with the dashboard footer so the
  // Progress button shows up next to "Save & Continue" on this sub-step
  // — mirrors the Counter Opposition-Hearing Payment pattern.
  useEffect(() => {
    if (!onCounterPaymentTmMAfterOppHearingProgressActionChange) return;
    const active =
      variant === "opposed" &&
      opposedSubStep === "payment-tmm-after-opp-hearing";
    if (!active || !leadId) {
      onCounterPaymentTmMAfterOppHearingProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_04a_xlvii_PaymentFromClientTmMAfterOppositionHearing
        );
        toast.success("Step skipped successfully.");
        setOpposedSubStep("file-tmm-after-opp-hearing");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onCounterPaymentTmMAfterOppHearingProgressActionChange(run);
    return () => onCounterPaymentTmMAfterOppHearingProgressActionChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    variant,
    opposedSubStep,
    leadId,
    onCounterPaymentTmMAfterOppHearingProgressActionChange,
  ]);

  useLayoutEffect(() => {
    onCounterFileTmMAfterOppHearingFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "file-tmm-after-opp-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterFileTmMAfterOppHearingFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    const isInform =
      variant === "opposed" &&
      opposedSubStep === "inform-tmm-after-opp-hearing";
    onCounterInformTmMAfterOppHearingFooterSendModeChange?.(
      isInform && !hasSentInformTmMAfterHearing
    );
    onCounterInformTmMAfterOppHearingFooterSaveModeChange?.(
      isInform && hasSentInformTmMAfterHearing
    );
  }, [
    variant,
    opposedSubStep,
    hasSentInformTmMAfterHearing,
    onCounterInformTmMAfterOppHearingFooterSendModeChange,
    onCounterInformTmMAfterOppHearingFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const isOnOrder =
      variant === "opposed" && opposedSubStep === "order-of-opp-hearing";
    onCounterOrderOfOppHearingFooterSendModeChange?.(
      isOnOrder && !hasSentOrderOfOppHearing
    );
    onCounterOrderOfOppHearingFooterSaveModeChange?.(
      isOnOrder && hasSentOrderOfOppHearing
    );
  }, [
    variant,
    opposedSubStep,
    hasSentOrderOfOppHearing,
    onCounterOrderOfOppHearingFooterSendModeChange,
    onCounterOrderOfOppHearingFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterSubsequentOppHearingsFooterFormModeChange?.(
      variant === "opposed" && opposedSubStep === "subsequent-opp-hearings"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterSubsequentOppHearingsFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterSubsequentOppHearingInvoiceFooterFormModeChange?.(
      variant === "opposed" &&
        opposedSubStep === "invoice-subsequent-opp-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterSubsequentOppHearingInvoiceFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onCounterSubsequentOppHearingPaymentFooterFormModeChange?.(
      variant === "opposed" &&
        opposedSubStep === "payment-subsequent-opp-hearing"
    );
  }, [
    variant,
    opposedSubStep,
    onCounterSubsequentOppHearingPaymentFooterFormModeChange,
  ]);

  // Eagerly load Notice of Counter rows for the whole opposed flow so the
  // Evidence-by-Opponent deadline indicator can be shown on each TM pill
  // regardless of which sub-step is currently active.
  useEffect(() => {
    if (!leadId || variant !== "opposed") return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await getNoticeOfCounter(leadId);
        if (cancelled) return;
        setNoticeOfCounterRows(rows);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Notice of Counter status"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant]);

  // Seed Notice of Counter form drafts from the loaded rows when the user
  // enters the sub-step. Kept separate from the load above so navigating
  // between sub-steps does not refetch and so user edits are not clobbered
  // by data refreshes triggered elsewhere.
  useEffect(() => {
    if (variant !== "opposed" || opposedSubStep !== "notice-of-counter-registry")
      return;
    const draft: Record<string, string> = {};
    const lastDateDraft: Record<string, string> = {};
    for (const r of noticeOfCounterRows) {
      const parts = parseDateParts(r.dateOfNoticeOfCounterStatement ?? null);
      draft[r.tmNo] = parts ? partsToIsoYmd(parts) : "";
      const lastParts = parseDateParts(r.lastDateForEvidenceByOpponent ?? null);
      lastDateDraft[r.tmNo] = lastParts ? partsToIsoYmd(lastParts) : "";
    }
    setNoticeOfCounterDraft(draft);
    setNoticeOfCounterLastDateDraft(lastDateDraft);
  }, [variant, opposedSubStep, noticeOfCounterRows]);

  // Lazy load: Inform Client about Notice of Counter — preview + workflow
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "inform-client-notice-counter"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const p = await getInformClientNoticeOfCounterPreview(leadId);
        if (cancelled) return;
        setInformNoticeOfCounterPreview(p);
      } catch {
        if (!cancelled) setInformNoticeOfCounterPreview(null);
      }
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        setHasSentInformNotice(
          Boolean(
            wf.steps.find((s) => s.slug === COUNTER_INFORM_NOTICE_STEP_SLUG)
              ?.isCompleted
          )
        );
      } catch {
        if (!cancelled) setHasSentInformNotice(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Evidence by Opponent
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "status-evidence-by-opp"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getEvidenceByOpponentStatus(leadId);
        if (cancelled) return;
        setEvidenceByOpponent(data);
        const map: Record<
          string,
          {
            opponentEvidenceFiled: boolean | null;
            dateOfEvidenceByOpponent: string;
            dateOfReceiptOfEvidenceByOpponent: string;
            lastDateOfFilingEvidence: string;
            pendingFile: File | null;
          }
        > = {};
        const toYmd = (s: string | null) => {
          const p = parseDateParts(s);
          return p ? partsToIsoYmd(p) : "";
        };
        for (const a of data.applications ?? []) {
          map[a.tmNo] = {
            opponentEvidenceFiled: a.opponentEvidenceFiled ?? null,
            dateOfEvidenceByOpponent: toYmd(a.dateOfEvidenceByOpponent),
            dateOfReceiptOfEvidenceByOpponent: toYmd(
              a.dateOfReceiptOfEvidenceByOpponent
            ),
            lastDateOfFilingEvidence: toYmd(a.lastDateOfFilingEvidence),
            pendingFile: null,
          };
        }
        setEvidenceByOpponentDraft(map);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Evidence by Opponent status"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Notice to Client for Evidence by Opponent — needs Evidence PDFs + workflow
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "notice-evidence-by-opp"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getEvidenceByOpponentStatus(leadId);
        if (cancelled) return;
        setEvidenceByOpponent(data);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Evidence by Opponent files"
          );
        }
      }
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        setHasSentNoticeEvidenceByOpp(
          Boolean(
            wf.steps.find(
              (s) => s.slug === COUNTER_NOTICE_EVIDENCE_BY_OPP_STEP_SLUG
            )?.isCompleted
          )
        );
      } catch {
        if (!cancelled) setHasSentNoticeEvidenceByOpp(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Inform Client about Evidence by Opponent — needs preview + workflow
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "inform-evidence-by-opp"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const p = await getInformClientEvidenceByOpponentPreview(leadId);
        if (cancelled) return;
        setInformEvidenceByOppPreview(p);
      } catch {
        if (!cancelled) setInformEvidenceByOppPreview(null);
      }
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        setHasSentInformEvidenceByOpp(
          Boolean(
            wf.steps.find(
              (s) => s.slug === COUNTER_INFORM_EVIDENCE_BY_OPP_STEP_SLUG
            )?.isCompleted
          )
        );
      } catch {
        if (!cancelled) setHasSentInformEvidenceByOpp(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Call Client for Evidence — workflow only
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "call-client-evidence"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        setHasRecordedCallClientEvidence(
          Boolean(
            wf.steps.find(
              (s) => s.slug === COUNTER_CALL_CLIENT_EVIDENCE_STEP_SLUG
            )?.isCompleted
          )
        );
      } catch {
        if (!cancelled) setHasRecordedCallClientEvidence(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Confirmation from Client for Evidence
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "confirmation-evidence"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getEvidenceConfirmationFromClient(leadId);
        if (cancelled) return;
        setEvidenceConfirmation(data);
        setEvidenceNoOfAppsOverride(
          data.noOfApplicationsForEvidence != null
            ? String(data.noOfApplicationsForEvidence)
            : ""
        );
        const draft: Record<string, boolean | null> = {};
        for (const a of data.applications ?? []) {
          draft[a.tmNo] = a.clientGoingAhead;
        }
        setEvidenceConfirmationDraft(draft);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Confirmation from Client for Evidence"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Invoice for Evidence — status + preview + workflow
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "invoice-evidence"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const [data, p] = await Promise.all([
          getEvidenceInvoice(leadId),
          getEvidenceInvoicePreview(leadId),
        ]);
        if (cancelled) return;
        setEvidenceInvoice(data);
        setEvidenceInvoicePreviewData(p);
        const rows: Record<
          string,
          {
            invoiceAmount: string;
            discount: string;
            invoiceDate: string;
            includeInvoiceNo: boolean;
          }
        > = {};
        for (const r of data.tmInvoiceRows ?? []) {
          const parts = r.invoiceDate ? parseDateParts(r.invoiceDate) : null;
          rows[r.tmNo] = {
            invoiceAmount: String(r.invoiceAmount ?? 0),
            discount: String(r.discount ?? 0),
            invoiceDate: parts ? partsToIsoYmd(parts) : "",
            includeInvoiceNo: Boolean(
              r.includeInvoiceNo ?? data.includeInvoiceNo
            ),
          };
        }
        setEvidenceInvoiceRows(rows);
        try {
          const wf = await getWorkflowStatus(leadId);
          if (cancelled) return;
          setHasSentEvidenceInvoice(
            Boolean(
              wf.steps.find(
                (s) => s.slug === COUNTER_EVIDENCE_INVOICE_SEND_STEP_SLUG
              )?.isCompleted
            )
          );
        } catch {
          if (!cancelled) setHasSentEvidenceInvoice(false);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Evidence Invoice"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Payment from Client for Evidence — invoice rows for collected/outstanding
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "payment-evidence"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getEvidenceInvoice(leadId);
        if (cancelled) return;
        setEvidenceInvoice(data);
        const rows: Record<string, string> = {};
        for (const r of data.tmInvoiceRows ?? [])
          rows[r.tmNo] = String(r.collectedAmount ?? 0);
        setEvidencePaymentRows(rows);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Evidence Payment"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Stamp Paper details to Vendor for Evidence (4A(xx))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "stamp-vendor-evidence"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getStampPaperForEvidenceStatus(leadId);
        if (cancelled) return;
        setStampPaperEvidence(data);
        const draft: Record<
          string,
          {
            stampPapersForEvidence: string;
            stampVendorNameForEvidence: string;
            stampVendorPhoneForEvidence: string;
          }
        > = {};
        for (const r of data.applications ?? []) {
          draft[r.tmNo] = {
            stampPapersForEvidence:
              r.stampPapersForEvidence != null
                ? String(r.stampPapersForEvidence)
                : "",
            stampVendorNameForEvidence: r.stampVendorNameForEvidence ?? "",
            stampVendorPhoneForEvidence: r.stampVendorPhoneForEvidence ?? "",
          };
        }
        setStampPaperEvidenceDraft(draft);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Stamp Paper details to Vendor"
          );
        }
      }
      try {
        const vendors = await getStampVendors();
        if (!cancelled && Array.isArray(vendors) && vendors.length > 0) {
          setStampVendorsForEvidence(vendors);
        }
      } catch {
        // non-critical — vendor list stays empty
      }
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        setHasSentStampPaperEvidence(
          Boolean(
            wf.steps.find(
              (s) => s.slug === COUNTER_STAMP_VENDOR_EVIDENCE_SEND_STEP_SLUG
            )?.isCompleted
          )
        );
      } catch {
        if (!cancelled) setHasSentStampPaperEvidence(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Stamp Paper from Vendor for Evidence (4A(xxi))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "stamp-from-vendor-evidence"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getStampPaperForEvidenceStatus(leadId);
        if (cancelled) return;
        setStampPaperEvidence(data);
        const draft: Record<
          string,
          {
            amountPayableToStampVendorForEvidence: string;
            paidToVendorForEvidence: boolean | null;
          }
        > = {};
        for (const r of data.applications ?? []) {
          const eff =
            r.amountPayableToStampVendorForEvidence ??
            r.defaultAmountPayableToStampVendorForEvidence;
          draft[r.tmNo] = {
            amountPayableToStampVendorForEvidence:
              eff != null ? String(eff) : "",
            paidToVendorForEvidence: r.paidToVendorForEvidence,
          };
        }
        setStampPaperFromVendorDraft(draft);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Stamp Paper from Vendor"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Preparation of Evidence Affidavit (4A(xxiii))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "preparation-evidence-affidavit"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getEvidenceAffidavitStatus(leadId);
        if (cancelled) return;
        setEvidenceAffidavit(data);
        const drafts: Record<string, EvidenceAffidavitDraft> = {};
        for (const a of data.evidenceAffidavits ?? []) {
          // Try to back-derive a YMD from the formatted "Nth day of Month YYYY"
          const dateYmd = (() => {
            const f = a.dateOfEvidenceAffidavitFormatted;
            if (!f) return "";
            const m = /(\d{1,2})(?:st|nd|rd|th)\s+day\s+of\s+(\w+)\s+(\d{4})/i.exec(
              f
            );
            if (!m) return "";
            const months = [
              "january",
              "february",
              "march",
              "april",
              "may",
              "june",
              "july",
              "august",
              "september",
              "october",
              "november",
              "december",
            ];
            const mi = months.indexOf(m[2].toLowerCase());
            if (mi < 0) return "";
            const dd = String(parseInt(m[1], 10)).padStart(2, "0");
            const mm = String(mi + 1).padStart(2, "0");
            return `${m[3]}-${mm}-${dd}`;
          })();
          drafts[a.tmNo] = {
            noOfParagraphsInOpponentIntro:
              a.noOfParagraphsInOpponentIntro != null
                ? String(a.noOfParagraphsInOpponentIntro)
                : "",
            paragraphWiseReplies: a.paragraphWiseReplies?.length
              ? a.paragraphWiseReplies.map((p) => ({
                  heading: p.heading ?? "",
                  text: p.text ?? "",
                }))
              : [{ heading: "", text: "" }],
            nameOfOpponentsAttorney: a.nameOfOpponentsAttorney ?? "",
            addressOfOpponentsAttorney: a.addressOfOpponentsAttorney ?? "",
            emailOfOpponentsAttorney: a.emailOfOpponentsAttorney ?? "",
            dateOfEvidenceAffidavit: dateYmd,
          };
        }
        setEvidenceAffidavitPerTm(drafts);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Evidence Affidavit"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Signature from Client for Affidavit (4A(xxiv))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "signature-evidence-affidavit"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        setHasRecordedSignatureAffidavit(
          Boolean(
            wf.steps.find(
              (s) => s.slug === COUNTER_SIGNATURE_AFFIDAVIT_STEP_SLUG
            )?.isCompleted
          )
        );
      } catch {
        if (!cancelled) setHasRecordedSignatureAffidavit(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Notarisation of Documents for Evidence (4A(xxv))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "notarisation-evidence"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getNotarisationForEvidenceStatus(leadId);
        if (cancelled) return;
        setNotarisationEvidence(data);
        const draft: Record<
          string,
          {
            amountPayableToNotaryForEvidence: string;
            paidToNotaryForEvidence: boolean | null;
          }
        > = {};
        for (const r of data.applications ?? []) {
          const eff =
            r.amountPayableToNotaryForEvidence ??
            r.defaultAmountPayableToNotaryForEvidence;
          draft[r.tmNo] = {
            amountPayableToNotaryForEvidence:
              eff != null ? String(eff) : "",
            paidToNotaryForEvidence: r.paidToNotaryForEvidence,
          };
        }
        setNotarisationEvidenceDraft(draft);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Notarisation for Evidence"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Scanning of Documents for Evidence (4A(xxvi))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "scanning-evidence"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        setHasRecordedScanningEvidence(
          Boolean(
            wf.steps.find(
              (s) => s.slug === COUNTER_SCANNING_EVIDENCE_STEP_SLUG
            )?.isCompleted
          )
        );
      } catch {
        if (!cancelled) setHasRecordedScanningEvidence(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: File Evidence Affidavit (4A(xxvii))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "file-evidence-affidavit"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getFileEvidenceAffidavitStatus(leadId);
        if (cancelled) return;
        setFileEvidenceAffidavit(data);
        const map: Record<
          string,
          { filingDate: string; pendingFile: File | null }
        > = {};
        const lastDateMap: Record<string, string> = {};
        for (const a of data.applications ?? []) {
          const parts = parseDateParts(a.dateOfFilingEvidenceAffidavit);
          map[a.tmNo] = {
            filingDate: parts ? partsToIsoYmd(parts) : "",
            pendingFile: null,
          };
          const lastParts = parseDateParts(a.lastDateForRejoinderByOpponent);
          lastDateMap[a.tmNo] = lastParts ? partsToIsoYmd(lastParts) : "";
        }
        setFileEvidenceAffidavitPerTm(map);
        setFileEvidenceAffidavitLastDateDraft(lastDateMap);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load File Evidence Affidavit"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Inform Client about Filing of Evidence (4A(xxviii))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "inform-filing-evidence"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const p = await getInformClientFilingOfEvidencePreview(leadId);
        if (cancelled) return;
        setInformFilingEvidencePreview(p);
      } catch {
        if (!cancelled) setInformFilingEvidencePreview(null);
      }
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        setHasSentInformFilingEvidence(
          Boolean(
            wf.steps.find(
              (s) => s.slug === COUNTER_INFORM_FILING_EVIDENCE_STEP_SLUG
            )?.isCompleted
          )
        );
      } catch {
        if (!cancelled) setHasSentInformFilingEvidence(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Download Rejoinder Affidavit (4A(xxix))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "download-rejoinder-affidavit"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getDownloadRejoinderAffidavitStatus(leadId);
        if (cancelled) return;
        setDownloadRejoinder(data);
        const map: Record<
          string,
          { rejoinderDate: string; pendingFile: File | null }
        > = {};
        for (const a of data.applications ?? []) {
          const parts = parseDateParts(a.dateOfRejoinderByOpponent);
          map[a.tmNo] = {
            rejoinderDate: parts ? partsToIsoYmd(parts) : "",
            pendingFile: null,
          };
        }
        setDownloadRejoinderPerTm(map);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Download Rejoinder Affidavit"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Status details of Opposition Hearing (4A(xxx))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "status-opposition-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getOppositionHearingStatus(leadId);
        if (cancelled) return;
        setOppositionHearing(data);
        // Merge saved hearing data into perTm without overwriting blank
        // entries seeded for counter TMs that haven't been saved yet.
        setOppositionHearingPerTm((prev) => {
          const next = { ...prev };
          for (const a of data.applications ?? []) {
            const parts = parseDateParts(a.dateOfOppositionHearing);
            next[a.tmNo] = {
              hearingDate: parts ? partsToIsoYmd(parts) : "",
              pendingFile: null,
            };
          }
          return next;
        });
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Opposition Hearing status"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Ensure all counter TMs have a blank perTm draft entry for the hearing
  // status substep. Hearing data loads lazily and applications may be empty
  // on first visit, so we seed from opposedStatus which is always populated.
  useEffect(() => {
    if (
      variant !== "opposed" ||
      opposedSubStep !== "status-opposition-hearing" ||
      !opposedStatus
    )
      return;
    setOppositionHearingPerTm((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const ct of opposedStatus.applications ?? []) {
        if (!next[ct.tmNo]) {
          next[ct.tmNo] = { hearingDate: "", pendingFile: null };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [variant, opposedSubStep, opposedStatus]);

  // Lazy load: Notice to Client for Opposition Hearing (4A(xxxi)) — needs status + workflow
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "notice-opposition-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getOppositionHearingStatus(leadId);
        if (cancelled) return;
        setOppositionHearing(data);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Opposition Hearing files"
          );
        }
      }
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        setHasSentOppositionHearingNotice(
          Boolean(
            wf.steps.find(
              (s) => s.slug === COUNTER_OPPOSITION_HEARING_NOTICE_STEP_SLUG
            )?.isCompleted
          )
        );
      } catch {
        if (!cancelled) setHasSentOppositionHearingNotice(false);
      }
      try {
        const p = await getOppositionHearingNoticeToClientPreview(leadId);
        if (cancelled) return;
        setOppositionHearingNoticePreview(p);
      } catch {
        if (!cancelled) setOppositionHearingNoticePreview(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Inform Client about Opposition Hearing (4A(xxxii))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "inform-opposition-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const p = await getInformClientOppositionHearingPreview(leadId);
        if (cancelled) return;
        setInformOppositionHearingPreview(p);
      } catch {
        if (!cancelled) setInformOppositionHearingPreview(null);
      }
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        setHasSentOppositionHearingInform(
          Boolean(
            wf.steps.find(
              (s) => s.slug === COUNTER_OPPOSITION_HEARING_INFORM_STEP_SLUG
            )?.isCompleted
          )
        );
      } catch {
        if (!cancelled) setHasSentOppositionHearingInform(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Invoice to Client for Opposition Hearing (4A(xxxiii))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "invoice-opposition-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const [data, p] = await Promise.all([
          getOppositionHearingInvoiceStatus(leadId),
          getOppositionHearingInvoicePreview(leadId),
        ]);
        if (cancelled) return;
        setOppositionHearingInvoice(data);
        setOppositionHearingInvoicePreviewData(p);
        const rows: Record<
          string,
          { invoiceAmount: string; invoiceDate: string }
        > = {};
        for (const r of data.tmInvoiceRows ?? []) {
          const parts = r.invoiceDate ? parseDateParts(r.invoiceDate) : null;
          rows[r.tmNo] = {
            invoiceAmount: String(r.invoiceAmount ?? 0),
            invoiceDate: parts ? partsToIsoYmd(parts) : "",
          };
        }
        setOppositionHearingInvoiceRows(rows);
        try {
          const wf = await getWorkflowStatus(leadId);
          if (cancelled) return;
          setHasSentOppositionHearingInvoice(
            Boolean(
              wf.steps.find(
                (s) => s.slug === COUNTER_OPPOSITION_HEARING_INVOICE_SEND_STEP_SLUG
              )?.isCompleted
            )
          );
        } catch {
          if (!cancelled) setHasSentOppositionHearingInvoice(false);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Opposition Hearing Invoice"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Call Client for Opposition Hearing (4A(xxxiv))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "call-opposition-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        setHasRecordedOppositionHearingCall(
          Boolean(
            wf.steps.find(
              (s) => s.slug === COUNTER_OPPOSITION_HEARING_CALL_STEP_SLUG
            )?.isCompleted
          )
        );
      } catch {
        if (!cancelled) setHasRecordedOppositionHearingCall(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Confirmation from Client for Opposition Hearing (4A(xxxv))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "confirmation-opposition-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getOppositionHearingConfirmation(leadId);
        if (cancelled) return;
        setOppositionHearingConfirmation(data);
        const draft: Record<
          string,
          OppositionHearingClientConfirmation | null
        > = {};
        for (const a of data.applications ?? []) {
          draft[a.tmNo] = a.clientConfirmation;
        }
        setOppositionHearingConfirmationDraft(draft);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Confirmation from Client for Opposition Hearing"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Invoice for TM-M before Opposition Hearing (4A(xxxvi))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "invoice-tmm-before-opp-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const [status, preview] = await Promise.all([
          getTmMBeforeOppositionHearingInvoiceStatus(leadId),
          getTmMBeforeOppositionHearingInvoicePreview(leadId),
        ]);
        if (cancelled) return;
        setTmMBeforeHearingInvoice(status);
        setTmMBeforeHearingInvoicePreview(preview);
        setTmMBeforeHearingInvoiceDateDraft(
          status.invoiceDate ?? preview?.invoiceDate ?? ""
        );
        const initialAmount =
          status.invoiceAmount ?? preview?.invoiceAmount ?? null;
        setTmMBeforeHearingInvoiceAmountDraft(
          initialAmount != null ? String(Math.round(Number(initialAmount))) : ""
        );
        setHasSentTmMBeforeHearingInvoice(
          Boolean(status.tmMBeforeOppositionHearingInvoicePdfKey)
        );
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load TM-M before Opposition Hearing Invoice"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Payment from Client for TM-M before Opposition Hearing (4A(xxxvii))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "payment-tmm-before-opp-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getTmMBeforeOppositionHearingInvoiceStatus(leadId);
        if (cancelled) return;
        setTmMBeforeHearingInvoice(data);
        setTmMBeforeHearingCollectedAmountDraft("");
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load TM-M before Opposition Hearing Payment"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: File TM-M before Opposition Hearing (4A(xxxviii))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "file-tmm-before-opp-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getTmMBeforeOppositionHearingFiledStatus(leadId);
        if (cancelled) return;
        setTmMBeforeHearingFiled(data);
        const map: Record<
          string,
          { filingDate: string; pendingFile: File | null }
        > = {};
        for (const a of data.applications ?? []) {
          const parts = parseDateParts(
            a.dateOfTmMBeforeOppositionHearingFiled
          );
          map[a.tmNo] = {
            filingDate: parts ? partsToIsoYmd(parts) : "",
            pendingFile: null,
          };
        }
        setTmMBeforeHearingFiledPerTm(map);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load File TM-M before Opposition Hearing"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Inform Client about Filing of TM-M before OH (4A(xxxix))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "inform-tmm-before-opp-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const p = await getInformClientTmMBeforeOppositionHearingPreview(leadId);
        if (cancelled) return;
        setInformTmMBeforeHearingPreview(p);
      } catch {
        if (!cancelled) setInformTmMBeforeHearingPreview(null);
      }
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        setHasSentInformTmMBeforeHearing(
          Boolean(
            wf.steps.find(
              (s) => s.slug === COUNTER_TM_M_BEFORE_HEARING_INFORM_STEP_SLUG
            )?.isCompleted
          )
        );
      } catch {
        if (!cancelled) setHasSentInformTmMBeforeHearing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Payment from Client for Opposition Hearing (4A(xl))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "payment-opposition-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getOppositionHearingPaymentStatus(leadId);
        if (cancelled) return;
        setOppositionHearingPayment(data);
        setOppositionHearingPaymentDraft("");
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Opposition Hearing Payment"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Documents/Filing for Opposition Hearing — workflow status only
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      (opposedSubStep !== "documents-opp-hearing" &&
        opposedSubStep !== "filing-opp-hearing-document")
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        const isStepCompleted = (slug: string): boolean => {
          const found = wf.steps.find((s) => s.slug === slug);
          return Boolean(found?.isCompleted);
        };
        setHasRecordedDocumentsOppHearing(
          isStepCompleted("S3_04A_XLI_DOCUMENTS_FROM_CLIENT_OPPOSITION_HEARING")
        );
        setHasRecordedFilingOppHearingDoc(
          isStepCompleted("S3_04A_XLII_FILING_OPPOSITION_HEARING_DOCUMENT")
        );
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load workflow status"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Note on Opposition Hearing (4A(xliii))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "note-on-opp-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getNoteOnOppositionHearingStatus(leadId);
        if (cancelled) return;
        setNoteOnOppHearing(data);
        setNoteOnOppHearingSelectedIdx(0);
        const draft: Record<
          string,
          {
            noteOtherOpposition1: string;
            noteOtherOpposition2: string;
            similarMarkNotes: Array<{
              mark: string;
              tmNo: string;
              imageKey?: string | null;
              userDate?: string | null;
              goodsServices?: string | null;
              address?: string | null;
              note1?: string | null;
              note2?: string | null;
            }>;
          }
        > = {};
        for (const app of data.applications ?? []) {
          // Build similar mark note rows using existing notes if present,
          // else seed from similarMarks (3r) so the row count matches.
          const seededFromSimilarMarks = (app.similarMarks ?? []).map(
            (m) => ({
              mark: m.mark ?? "",
              tmNo: m.tmNo ?? "",
              imageKey: null as string | null,
              userDate: m.userDate ?? null,
              goodsServices: m.goodsServices ?? null,
              address: m.place ?? null,
              note1: null as string | null,
              note2: null as string | null,
            })
          );
          const sourceMarks =
            app.similarMarkNotes && app.similarMarkNotes.length > 0
              ? app.similarMarkNotes.map((n: NoteOnOppositionHearingSimilarMarkNote) => ({
                  mark: n.mark ?? "",
                  tmNo: n.tmNo ?? "",
                  imageKey: n.imageKey ?? null,
                  userDate: n.userDate ?? null,
                  goodsServices: n.goodsServices ?? null,
                  address: n.address ?? null,
                  note1: n.note1 ?? null,
                  note2: n.note2 ?? null,
                }))
              : seededFromSimilarMarks.length > 0
                ? seededFromSimilarMarks
                : [
                    {
                      mark: "",
                      tmNo: "",
                      imageKey: null as string | null,
                      userDate: null as string | null,
                      goodsServices: null as string | null,
                      address: null as string | null,
                      note1: null as string | null,
                      note2: null as string | null,
                    },
                  ];
          draft[app.ackApplicationId] = {
            noteOtherOpposition1: app.noteOtherOpposition1 ?? "",
            noteOtherOpposition2: app.noteOtherOpposition2 ?? "",
            similarMarkNotes: sourceMarks,
          };
        }
        setNoteOnOppHearingDraft(draft);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Note on Opposition Hearing"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Prefetch Attend Opposition Hearing once when variant becomes opposed.
  // Needed by the side nav to decide whether to show the TM-M-after sub-steps
  // (gated on at least one app having FILE_TM_M remark in 4A(xliv)).
  useEffect(() => {
    if (!leadId || variant !== "opposed" || attendOppHearing) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getAttendOppositionHearingStatus(leadId);
        if (!cancelled) setAttendOppHearing(data);
      } catch {
        // Silent — the lazy-load below will retry and surface errors when the
        // user actually opens the Attend Opposition Hearing sub-step.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, attendOppHearing]);

  // If the user deep-links to a hidden TM-M-after sub-step (or arrives there
  // before any app has FILE_TM_M), bounce them back to the predecessor step.
  useEffect(() => {
    if (variant !== "opposed" || !attendOppHearing) return;
    if (hasFileTmMAfterHearing) return;
    if (TMM_AFTER_OPP_HEARING_SUB_STEP_IDS.includes(opposedSubStep)) {
      setOpposedSubStep("inform-after-opp-hearing");
    }
    // setOpposedSubStep is stable (defined in component scope) — safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, attendOppHearing, hasFileTmMAfterHearing, opposedSubStep]);

  // Lazy load: Attend Opposition Hearing (4A(xliv))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "attend-opp-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getAttendOppositionHearingStatus(leadId);
        if (cancelled) return;
        setAttendOppHearing(data);
        setAttendOppHearingSelectedIdx(0);
        const draft: Record<
          string,
          {
            hearingStatus: AttendOppositionHearingStatusValue | null;
            hearingOfficerRemark: AttendOppositionHearingOfficerRemarkValue | null;
            directionOfOfficer: string;
          }
        > = {};
        for (const app of data.applications ?? []) {
          draft[app.ackApplicationId] = {
            hearingStatus: app.hearingStatus,
            hearingOfficerRemark: app.hearingOfficerRemark,
            directionOfOfficer:
              app.officerDirections?.[0]?.direction ?? "",
          };
        }
        setAttendOppHearingDraft(draft);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Attend Opposition Hearing"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Subsequent Opposition Hearings (4A(l))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "subsequent-opp-hearings"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getSubsequentOppositionHearingStatus(leadId);
        if (cancelled) return;
        setSubsequentOppHearing(data);
        const draft: Record<string, SubsequentOppHearingDraft> = {};
        for (const app of data.applications ?? []) {
          // Convert backend DD-MM-YYYY to YYYY-MM-DD for native date input.
          const parts = app.dateOfHearing
            ? parseDateParts(app.dateOfHearing)
            : null;
          draft[app.tmNo] = {
            dateOfHearing: parts ? partsToIsoYmd(parts) : "",
            pendingNoticeFile: null,
            hearingStatus: app.hearingStatus,
            hearingOfficerRemark: app.hearingOfficerRemark,
            directionOfOfficer:
              app.officerDirections?.[0]?.direction ?? "",
            pendingPostponementFile: null,
          };
        }
        setSubsequentOppHearingDraft(draft);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Subsequent Opposition Hearings"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Subsequent Opposition Hearings — WhatsApp notice preview.
  // Refreshes whenever the saved hearing data changes so the user sees the
  // exact message the backend will send.
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "subsequent-opp-hearings" ||
      !subsequentOppHearing
    ) {
      return;
    }
    const eligibleWithSavedDate = (subsequentOppHearing.applications ?? []).some(
      (a) => a.isActive && a.dateOfHearing
    );
    if (!eligibleWithSavedDate) {
      setSubsequentOppHearingNoticePreview(null);
      setSubsequentOppHearingNoticePreviewError(null);
      return;
    }
    let cancelled = false;
    setSubsequentOppHearingNoticePreviewError(null);
    (async () => {
      try {
        const data = await getSubsequentOppositionHearingNoticePreview(leadId);
        if (!cancelled) {
          setSubsequentOppHearingNoticePreview(data);
        }
      } catch (err) {
        if (!cancelled) {
          setSubsequentOppHearingNoticePreview(null);
          setSubsequentOppHearingNoticePreviewError(
            err instanceof Error
              ? err.message
              : "Failed to load message preview"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep, subsequentOppHearing]);

  // Lazy load: Subsequent Opposition Hearing Invoice (4A(xxxiii) for subsequent)
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      (opposedSubStep !== "invoice-subsequent-opp-hearing" &&
        opposedSubStep !== "payment-subsequent-opp-hearing")
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Need current hearing number — fetch hearing status first if missing.
        let hearingNumber =
          subsequentOppHearing?.currentHearingNumber ?? null;
        if (!hearingNumber) {
          const subStatus = await getSubsequentOppositionHearingStatus(leadId);
          if (cancelled) return;
          setSubsequentOppHearing(subStatus);
          hearingNumber = subStatus.currentHearingNumber;
        }
        const invoice = await getSubsequentOppositionHearingInvoiceStatus(
          leadId,
          hearingNumber
        );
        if (cancelled) return;
        setSubsequentOppHearingInvoice(invoice);
        // Prefill draft from server values, falling back to defaults.
        setSubsequentOppHearingInvoiceAmountDraft(
          invoice.invoiceAmount != null
            ? String(Math.round(Number(invoice.invoiceAmount)))
            : ""
        );
        if (invoice.invoiceDate) {
          const parts = parseDateParts(invoice.invoiceDate);
          setSubsequentOppHearingInvoiceDateDraft(
            parts ? partsToIsoYmd(parts) : ""
          );
        } else {
          // Default to today.
          const today = new Date();
          const yyyy = today.getFullYear();
          const mm = String(today.getMonth() + 1).padStart(2, "0");
          const dd = String(today.getDate()).padStart(2, "0");
          setSubsequentOppHearingInvoiceDateDraft(`${yyyy}-${mm}-${dd}`);
        }
        setSubsequentOppHearingInvoiceIncludeNoDraft(
          Boolean(invoice.includeInvoiceNo)
        );
        setSubsequentOppHearingCollectedDraft("");
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Subsequent Opposition Hearing Invoice"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // subsequentOppHearing is read once at the top — depend on the substep only
    // to avoid refetching whenever the subsequent hearing state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Inform Client after Opposition Hearing (4A(xlv))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "inform-after-opp-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const [data, wf] = await Promise.all([
          getInformAfterOppositionHearingStatus(leadId),
          getWorkflowStatus(leadId).catch(() => null),
        ]);
        if (cancelled) return;
        setInformAfterOppHearing(data);
        if (wf) {
          const step = wf.steps.find(
            (s: { slug: string; isCompleted: boolean }) =>
              s.slug === "S3_04A_XLV_INFORM_CLIENT_AFTER_OPPOSITION_HEARING"
          );
          setHasSentInformAfterOppHearing(Boolean(step?.isCompleted));
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Inform Client after Opposition Hearing"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Invoice for TM-M after Opposition Hearing (4A(xlvi))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "invoice-tmm-after-opp-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const [status, preview, wf] = await Promise.all([
          getTmMAfterOppositionHearingInvoiceStatus(leadId),
          getTmMAfterOppositionHearingInvoicePreview(leadId).catch(() => null),
          getWorkflowStatus(leadId).catch(() => null),
        ]);
        if (cancelled) return;
        setTmMAfterHearingInvoice(status);
        setTmMAfterHearingInvoicePreview(preview);
        const dateParts = parseDateParts(status.invoiceDate);
        setTmMAfterHearingInvoiceDraft({
          invoiceAmount:
            status.invoiceAmount != null ? String(status.invoiceAmount) : "",
          invoiceDate: dateParts ? partsToIsoYmd(dateParts) : "",
        });
        if (wf) {
          const step = wf.steps.find(
            (s: { slug: string; isCompleted: boolean }) =>
              s.slug ===
              "S3_04A_XLVI_B_SEND_INVOICE_TM_M_AFTER_OPPOSITION_HEARING"
          );
          setHasSentTmMAfterHearingInvoice(Boolean(step?.isCompleted));
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load TM-M after Opposition Hearing Invoice"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Payment from Client for TM-M after Opposition Hearing (4A(xlvii) Collected Amount + 4A(xlviii) filed)
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "payment-tmm-after-opp-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const [invoice, filed] = await Promise.all([
          getTmMAfterOppositionHearingInvoiceStatus(leadId),
          getTmMAfterOppositionHearingFiledStatus(leadId).catch(() => null),
        ]);
        if (cancelled) return;
        setTmMAfterHearingInvoice(invoice);
        setTmMAfterHearingFiled(filed);
        setTmMAfterHearingCollectedDraft("");
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load File TM-M after Opposition Hearing"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: File TM-M after Opposition Hearing (4A(xlviii))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "file-tmm-after-opp-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getTmMAfterOppositionHearingFiledStatus(leadId);
        if (cancelled) return;
        setTmMAfterHearingFiled(data);
        const map: Record<
          string,
          { filingDate: string; pendingFile: File | null }
        > = {};
        for (const a of data.applications ?? []) {
          const parts = parseDateParts(
            a.dateOfTmMAfterOppositionHearingFiled
          );
          map[a.tmNo] = {
            filingDate: parts ? partsToIsoYmd(parts) : "",
            pendingFile: null,
          };
        }
        setTmMAfterHearingFiledPerTm(map);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load File TM-M after Opposition Hearing"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Inform Client after filing TM-M after Opposition Hearing (4A(xlix))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "inform-tmm-after-opp-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const [preview, filed, wf] = await Promise.all([
          getInformClientTmMAfterOppositionHearingPreview(leadId).catch(
            () => null
          ),
          getTmMAfterOppositionHearingFiledStatus(leadId).catch(() => null),
          getWorkflowStatus(leadId).catch(() => null),
        ]);
        if (cancelled) return;
        setInformTmMAfterHearingPreview(preview);
        setTmMAfterHearingFiled(filed);
        if (wf) {
          const step = wf.steps.find(
            (s: { slug: string; isCompleted: boolean }) =>
              s.slug ===
              "S3_04A_XLIX_INFORM_CLIENT_TM_M_AFTER_OPPOSITION_HEARING_FILED"
          );
          setHasSentInformTmMAfterHearing(Boolean(step?.isCompleted));
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Inform Client after filing TM-M after Opposition Hearing"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Order of Opposition Hearing to Client (4A(li))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "order-of-opp-hearing"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const [order, wf] = await Promise.all([
          getOrderOfOppositionHearingStatus(leadId),
          getWorkflowStatus(leadId).catch(() => null),
        ]);
        if (cancelled) return;
        setOrderOfOppHearing(order);
        const draft: Record<string, OrderOfOppositionHearingOutcomeValue | null> =
          {};
        for (const a of order.applications ?? []) {
          draft[a.tmNo] = a.outcomeOfHearing;
        }
        setOrderOfOppHearingDraft(draft);
        if (wf) {
          // 4A(li) has four backend steps (_A_ save, _B_ forward PDFs, _C_ send one
          // scenario, _D_ send all). `hasSentOrderOfOppHearing` gates the Send/Resend
          // footer, and the only send path in this view is
          // `sendAllOrderOfOppositionHearingMessages` — which the backend records as
          // _D_ (order-of-opposition-hearing.service.ts sendAllMessages). Item 80 left
          // this literal as a non-existent slug, so the flag never rehydrated and the
          // step forgot it had been sent after every refresh.
          const step = wf.steps.find(
            (s: { slug: string; isCompleted: boolean }) =>
              s.slug ===
              "S3_04A_LI_D_SEND_ALL_ORDER_OF_OPPOSITION_HEARING_OUTCOME_MESSAGES"
          );
          setHasSentOrderOfOppHearing(Boolean(step?.isCompleted));
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Order of Opposition Hearing"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Lazy load: Documents from Client for Evidence (4A(xxii))
  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "documents-from-client-evidence"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getDocumentsFromClientForEvidenceStatus(leadId);
        if (cancelled) return;
        setDocumentsFromClientEvidence(data);
        const draft: Record<string, string> = {};
        for (const r of data.applications ?? []) {
          draft[r.tmNo] =
            r.noOfExhibitsForEvidence != null
              ? String(r.noOfExhibitsForEvidence)
              : "";
        }
        setDocumentsFromClientEvidenceDraft(draft);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Documents from Client for Evidence"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // ───── Lazy load each sub-step's data on entry ─────

  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "confirmation-from-client-counter"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getCounterConfirmationFromClient(leadId);
        if (cancelled) return;
        setCounterConfirmation(data);
        const draft: Record<string, boolean | null> = {};
        for (const a of data.applications) draft[a.tmNo] = a.clientGoingAhead;
        setCounterConfirmationDraft(draft);
        setCounterNoOpposedDraft(
          data.noOfApplicationsOpposed != null ? String(data.noOfApplicationsOpposed) : ""
        );
        setCounterNoForCounterDraft(
          data.noOfApplicationsForCounter != null
            ? String(data.noOfApplicationsForCounter)
            : data.noOfApplicationsOpposed != null
              ? String(data.noOfApplicationsOpposed)
              : ""
        );
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Confirmation from Client"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    (async () => {
      try {
        const { lead } = await getLeadDetails(leadId);
        if (!cancelled && lead.phone) setClientPhone(lead.phone);
      } catch {
        // ignore — phone display falls back to placeholder
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "invoice-to-client-counter"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const [data, preview] = await Promise.all([
          getCounterInvoice(leadId),
          getCounterInvoicePreview(leadId).catch(() => null),
        ]);
        if (cancelled) return;
        setCounterInvoice(data);
        setCounterInvoicePreview(preview);
        const rows: Record<
          string,
          {
            invoiceAmount: string;
            discount: string;
            invoiceDate: string;
            includeInvoiceNo: boolean;
          }
        > = {};
        for (const r of data.tmInvoiceRows ?? []) {
          const parts = r.invoiceDate ? parseDateParts(r.invoiceDate) : null;
          rows[r.tmNo] = {
            invoiceAmount: String(r.invoiceAmount ?? 0),
            discount: String(r.discount ?? 0),
            invoiceDate: parts ? partsToIsoYmd(parts) : "",
            includeInvoiceNo: r.includeInvoiceNo ?? true,
          };
        }
        setCounterInvoiceRows(rows);
        try {
          const wf = await getWorkflowStatus(leadId);
          if (cancelled) return;
          setHasSentCounterInvoice(
            Boolean(
              wf.steps.find((s) => s.slug === COUNTER_INVOICE_SEND_STEP_SLUG)
                ?.isCompleted
            )
          );
        } catch {
          if (!cancelled) setHasSentCounterInvoice(false);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load Counter Invoice"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "payment-from-client-counter"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getCounterInvoice(leadId);
        if (cancelled) return;
        setCounterInvoice(data);
        const rows: Record<string, string> = {};
        for (const r of data.tmInvoiceRows ?? []) rows[r.tmNo] = "";
        setCounterPaymentRows(rows);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load Counter Payment"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "prepare-counter"
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getPrepareCounterStatus(leadId);
        if (cancelled) return;
        setPrepareCounter(data);
        const drafts: Record<string, PrepareCounterDraft> = {};
        for (const a of data.counterStatements ?? []) {
          drafts[a.tmNo] = {
            noOfParagraphsInOpponentIntro:
              a.noOfParagraphsInOpponentIntro != null
                ? String(a.noOfParagraphsInOpponentIntro)
                : "",
            paragraphWiseReplies: a.paragraphWiseReplies?.length
              ? a.paragraphWiseReplies.map((p) => ({
                  heading: p.heading ?? "",
                  text: p.text ?? "",
                }))
              : [{ heading: "", text: "" }],
            opponentAttorneyName: a.opponentAttorneyName ?? "",
            opponentAttorneyAddress: a.opponentAttorneyAddress ?? "",
            opponentAttorneyEmail: a.opponentAttorneyEmail ?? "",
            dateOfEvidenceAffidavit: a.dateOfEvidenceAffidavit ?? "",
            dateOfFilingCounterStatement: a.dateOfFilingCounterStatement ?? "",
          };
        }
        setPrepareCounterPerTm(drafts);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Prepare Counter status"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      (opposedSubStep !== "file-counter" &&
        opposedSubStep !== "notice-of-counter-registry")
    )
      return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getFileCounterStatus(leadId);
        if (cancelled) return;
        setFileCounter(data);
        if (opposedSubStep === "file-counter") {
          const map: Record<
            string,
            { filingDate: string; pendingFile: File | null }
          > = {};
          for (const a of data.applications ?? []) {
            const parts = parseDateParts(a.filingDate ?? null);
            map[a.tmNo] = {
              filingDate: parts ? partsToIsoYmd(parts) : "",
              pendingFile: null,
            };
          }
          setFileCounterPerTm(map);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load File Counter status"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  useEffect(() => {
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "inform-client-filing-counter"
    )
      return;
    let cancelled = false;
    (async () => {
      const [previewResult, wfResult] = await Promise.allSettled([
        getFileCounterInformClientPreview(leadId),
        getWorkflowStatus(leadId),
      ]);
      if (cancelled) return;
      setInformFilingPreview(
        previewResult.status === "fulfilled" ? previewResult.value : null
      );
      setHasSentInformFiling(
        wfResult.status === "fulfilled"
          ? Boolean(
              wfResult.value.steps.find(
                (s) => s.slug === COUNTER_INFORM_FILING_STEP_SLUG
              )?.isCompleted
            )
          : false
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep]);

  // Load Opposed counter status when user switches to Opposed variant.
  // Also eagerly load Opposition Hearing apps so the merged TM rail stays
  // populated even when all apps have already advanced past Opposed.
  useEffect(() => {
    if (!leadId || variant !== "opposed") return;
    let cancelled = false;
    (async () => {
      try {
        const hearingData = await getOppositionHearingStatus(leadId);
        if (!cancelled) setOppositionHearing(hearingData);
      } catch {
        // Hearing data may legitimately be empty for this lead — ignore.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant]);

  useEffect(() => {
    if (!leadId || variant !== "opposed") return;
    let cancelled = false;
    setOpposedLoading(true);
    (async () => {
      try {
        const data = await getOppositionCounterStatus(leadId);
        if (cancelled) return;
        setOpposedStatus(data);
        setOpposedPerTm(opposedApplicationsToFormMap(data.applications));
        setOpposedSelectedTmIdx(0);
        try {
          const workflow = await getWorkflowStatus(leadId);
          if (cancelled) return;
          setHasSentCounterNotice(
            Boolean(
              workflow.steps.find((s) => s.slug === COUNTER_NOTICE_STEP_SLUG)
                ?.isCompleted
            )
          );
          setHasSentCounterInform(
            Boolean(
              workflow.steps.find((s) => s.slug === COUNTER_INFORM_STEP_SLUG)
                ?.isCompleted
            )
          );
          setHasRecordedCounterCall(
            Boolean(
              workflow.steps.find((s) => s.slug === COUNTER_CALL_STEP_SLUG)
                ?.isCompleted
            )
          );
        } catch {
          if (!cancelled) {
            setHasSentCounterNotice(false);
            setHasSentCounterInform(false);
            setHasRecordedCounterCall(false);
          }
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Opposition Counter status"
          );
        }
      } finally {
        if (!cancelled) setOpposedLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant]);

  // Cleanup any pending auto-save when unmounting / lead changes
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [leadId]);

  // ───────────────────── Helpers ─────────────────────

  const tms = status?.applications ?? [];
  const selectedTm = tms[selectedTmIdx] ?? null;
  const selectedTmForm = selectedTm
    ? perTm[selectedTm.tmNo] ?? { journalDate: "", pendingFile: null }
    : { journalDate: "", pendingFile: null };

  const lastDateForOppositionYmd = selectedTmForm.journalDate
    ? addDaysToYmd(selectedTmForm.journalDate, OPPOSITION_DAYS)
    : null;

  const updatePerTm = (tmNo: string, patch: Partial<PerTmFormState>) => {
    setPerTm((prev) => ({
      ...prev,
      [tmNo]: { ...(prev[tmNo] ?? { journalDate: "", pendingFile: null }), ...patch },
    }));
  };

  /**
   * Build the PATCH payload from the LATEST state (read via refs so a
   * debounced caller doesn't see a stale snapshot from the render where it
   * was scheduled). Existing fileKey/filename are preserved unless the user
   * picked a new pending file (resolved into `uploadedKeysByTmNo` first).
   */
  const buildSavePayload = (
    uploadedKeysByTmNo: Record<string, { key: string; filename: string }>
  ) => {
    const latestTms = statusRef.current?.applications ?? [];
    const latestPerTm = perTmRef.current;
    return {
      applications: latestTms.map((app) => {
        const form = latestPerTm[app.tmNo];
        const uploaded = uploadedKeysByTmNo[app.tmNo];
        const out: {
          tmNo: string;
          journalDate?: string;
          orderFromRegistryFileKey?: string;
          orderFromRegistryFilename?: string;
        } = { tmNo: app.tmNo };

        if (form?.journalDate) {
          out.journalDate = form.journalDate;
        }

        if (uploaded) {
          out.orderFromRegistryFileKey = uploaded.key;
          out.orderFromRegistryFilename = uploaded.filename;
        } else if (
          app.orderFromRegistryFileKey &&
          app.orderFromRegistryFilename
        ) {
          out.orderFromRegistryFileKey = app.orderFromRegistryFileKey;
          out.orderFromRegistryFilename = app.orderFromRegistryFilename;
        }

        return out;
      }),
    };
  };

  const uploadPendingFiles = async (): Promise<
    Record<string, { key: string; filename: string }>
  > => {
    const latestTms = statusRef.current?.applications ?? [];
    const latestPerTm = perTmRef.current;
    const pending = latestTms
      .map((app) => ({ app, file: latestPerTm[app.tmNo]?.pendingFile ?? null }))
      .filter((x): x is { app: AdvertisedInJournalApplication; file: File } => !!x.file);

    if (pending.length === 0) return {};

    const presigned = await generateAcknowledgementUploadUrls(
      pending.map((p) => ({
        filename: p.file.name,
        mimeType: p.file.type || "application/pdf",
      })),
      ADVERTISED_FOLDER
    );

    const out: Record<string, { key: string; filename: string }> = {};
    for (let i = 0; i < pending.length; i += 1) {
      const { app, file } = pending[i];
      const url = presigned[i]?.url;
      const key = presigned[i]?.key;
      if (!url || !key) {
        throw new Error(`Upload URL not returned for ${app.tmNo}`);
      }
      const putRes = await fetch(url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) {
        throw new Error(`Failed to upload file for ${app.tmNo}`);
      }
      out[app.tmNo] = { key, filename: file.name };
    }
    return out;
  };

  const persistStatusDetails = async (): Promise<
    AdvertisedInJournalStatusResponse | null
  > => {
    if (!leadId) return null;
    const uploaded = await uploadPendingFiles();
    const payload = buildSavePayload(uploaded);
    if (payload.applications.length === 0) return null;
    const updated = await setAdvertisedInJournalStatusDetails(leadId, payload);
    setStatus(updated);
    setPerTm(applicationsToFormMap(updated.applications));
    return updated;
  };

  // ───────────────────── Auto-save on journal date change ─────────────────────

  const handleJournalDateChange = (tmNo: string, date: Date | null) => {
    const ymd = date ? toLocalYmd(date) : "";
    updatePerTm(tmNo, { journalDate: ymd });
    if (!leadId || !ymd) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      void (async () => {
        setAutoSaving(true);
        try {
          await persistStatusDetails();
        } catch (err) {
          toast.error(
            err instanceof Error ? err.message : "Failed to auto-save"
          );
        } finally {
          setAutoSaving(false);
        }
      })();
    }, 800);
  };

  // ───────────────────── File handlers ─────────────────────

  const handleFileChosen = (tmNo: string, file: File) => {
    const reason = validateUploadFile(file);
    if (reason) {
      toast.error(reason);
      return;
    }
    updatePerTm(tmNo, { pendingFile: file });
  };

  const handleFileInput = (
    tmNo: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) handleFileChosen(tmNo, file);
    // Reset value so re-selecting the same file fires onChange
    e.target.value = "";
  };

  const removeFileForSelectedTm = (tmNo: string) => {
    updatePerTm(tmNo, { pendingFile: null });
    // Also clear server-side reference locally; the next save will omit the file
    setStatus((prev) =>
      prev
        ? {
            ...prev,
            applications: prev.applications.map((a) =>
              a.tmNo === tmNo
                ? {
                    ...a,
                    orderFromRegistryFileKey: null,
                    orderFromRegistryFilename: null,
                    orderFromRegistryFileUrl: null,
                  }
                : a
            ),
          }
        : prev
    );
  };

  const openSelectedTmFilePreview = (app: AdvertisedInJournalApplication) => {
    const form = perTm[app.tmNo];
    if (form?.pendingFile) {
      setPreview({
        url: URL.createObjectURL(form.pendingFile),
        name: form.pendingFile.name,
        kind: detectPreviewKind(
          form.pendingFile.name,
          form.pendingFile.type
        ),
        revokeOnClose: true,
      });
      return;
    }
    if (app.orderFromRegistryFileUrl && app.orderFromRegistryFilename) {
      setPreview({
        url: app.orderFromRegistryFileUrl,
        name: app.orderFromRegistryFilename,
        kind: detectPreviewKind(app.orderFromRegistryFilename),
      });
    }
  };

  const closePreview = () => {
    if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  // ───────────────────── Form submit handlers ─────────────────────

  const handleStatusDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;

    // Flush any pending debounced auto-save so we don't race against the explicit save.
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }

    // Validate the currently-visible TM: both Journal Date and Order from
    // Registry PDF are required before advancing. Other TMs may stay partial —
    // auto-save persists what's there as the user fills them in.
    if (!selectedTm) {
      toast.error("Select a TM application first");
      return;
    }
    const form = perTm[selectedTm.tmNo];
    if (!form?.journalDate) {
      toast.error(
        `Enter Journal Date for ${formatTmNoForDisplay(selectedTm.tmNo)}`
      );
      return;
    }
    const hasFile = Boolean(
      form?.pendingFile || selectedTm.orderFromRegistryFileKey
    );
    if (!hasFile) {
      toast.error(
        `Upload Order from Registry PDF for ${formatTmNoForDisplay(selectedTm.tmNo)}`
      );
      return;
    }

    onSavingChange?.(true);
    try {
      await persistStatusDetails();
      toast.success("Status Details saved");
      setSubStep("notice-to-client");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Advertised in Journal status details"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const advertisedTmsWithPdf = tms.filter(
    (a) => a.orderFromRegistryFileKey && a.orderFromRegistryFilename
  );
  const canSendNotice = advertisedTmsWithPdf.length > 0;

  const loadNoticePreview = async () => {
    if (!leadId) return;
    setNoticePreviewLoading(true);
    setNoticePreviewError(null);
    try {
      const data = await getAdvertisedInJournalNoticePreview(leadId);
      setNoticePreview(data);
    } catch (err) {
      setNoticePreview(null);
      setNoticePreviewError(
        err instanceof Error ? err.message : "Failed to load preview"
      );
    } finally {
      setNoticePreviewLoading(false);
    }
  };

  const handleNoticeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentNotice) {
      // Already sent — just advance
      setSubStep("inform-client");
      return;
    }
    if (!canSendNotice) {
      toast.error(
        "Upload Order from Registry PDF in Status Details before sending."
      );
      return;
    }
    setNoticeSendConfirmOpen(true);
    void loadNoticePreview();
  };

  const performNoticeSend = async () => {
    if (!leadId) return;
    setNoticeSending(true);
    onSavingChange?.(true);
    try {
      await sendAdvertisedInJournalNoticeToClient(leadId, {
        sendWhatsApp: true,
        sendEmail: true,
      });
      setHasSentNotice(true);
      setNoticeSendConfirmOpen(false);
      toast.success("Order from Registry forwarded to client");
      setSubStep("inform-client");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Order from Registry to client"
      );
    } finally {
      setNoticeSending(false);
      onSavingChange?.(false);
    }
  };

  const handleResendNotice = async () => {
    if (!leadId || resendingNotice || !canSendNotice) return;
    setResendingNotice(true);
    onSavingChange?.(true);
    try {
      await sendAdvertisedInJournalNoticeToClient(leadId, {
        sendWhatsApp: true,
        sendEmail: true,
      });
      setNoticeResendConfirmOpen(false);
      toast.success("Order from Registry resent to client");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Order from Registry"
      );
    } finally {
      setResendingNotice(false);
      onSavingChange?.(false);
    }
  };

  // ───────────────────── Inform Client (sub-step 3) ─────────────────────

  const handleInformSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentInform) {
      // Already sent — Save & Continue navigates the user back to the main rail.
      onSuccess?.(leadId);
      return;
    }
    if (!informScenario) {
      toast.error("Select an acceptance scenario before sending");
      return;
    }
    onSavingChange?.(true);
    try {
      await informClientAdvertisedInJournal(leadId, {
        advertisedFrom: informScenario,
      });
      setHasSentInform(true);
      toast.success("Trademark Update sent to client");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send Inform Client"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendInform = async () => {
    if (!leadId || resendingInform) return;
    if (!informScenario) {
      toast.error("Select an acceptance scenario before resending");
      return;
    }
    setResendingInform(true);
    onSavingChange?.(true);
    try {
      await informClientAdvertisedInJournal(leadId, {
        advertisedFrom: informScenario,
      });
      toast.success("Trademark Update resent to client");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Trademark Update"
      );
    } finally {
      setResendingInform(false);
      onSavingChange?.(false);
    }
  };

  // ───────────────────── Opposed: Status details for Counter ─────────────────────

  const opposedTmsRaw = opposedStatus?.applications ?? [];
  const hearingApps = oppositionHearing?.applications ?? [];

  // The Opposed flow spans multiple application statuses
  // ("Advertised in Journal - Opposed" → "Opposition Hearing" → …).
  // Each backend endpoint returns only the apps in *its* status. To keep the
  // top-level Opposed rail populated as a TM progresses, merge the lists.
  // Hearing-only TMs are surfaced as a thin wrapper that conforms to the
  // OppositionCounterApplication shape (downstream handlers only read .tmNo
  // on these — the opposed-status handlers gate themselves on per-TM drafts
  // that simply don't exist for hearing-status TMs).
  const opposedTms: OppositionCounterApplication[] = (() => {
    const byTmNo = new Map(opposedTmsRaw.map((t) => [t.tmNo, t]));
    for (const h of hearingApps) {
      if (byTmNo.has(h.tmNo)) continue;
      byTmNo.set(h.tmNo, {
        tmNo: h.tmNo,
        mark: h.mark ?? "",
      } as OppositionCounterApplication);
    }
    return Array.from(byTmNo.values());
  })();

  // Some sub-steps (Opposition Hearing 4A(xxx) and onward) only apply to a
  // subset of opposed applications — those whose status has advanced to
  // "Opposition Hearing". Per-substep filtering of the rail narrows to that
  // subset; the merged list above keeps the rail visible even when the
  // currently-shown sub-step is Opposed-only.
  const HEARING_SCOPED_SUBSTEPS: ReadonlyArray<string> = [
    // "status-opposition-hearing" intentionally excluded — it's the first
    // entry point where hearing data is entered; applications haven't yet
    // transitioned to hearing status, so all counter TMs must be visible.
    "notice-opposition-hearing",
    "inform-opposition-hearing",
    "invoice-opposition-hearing",
    "call-opposition-hearing",
    "confirmation-opposition-hearing",
    "invoice-tmm-before-opp-hearing",
    "payment-tmm-before-opp-hearing",
    "file-tmm-before-opp-hearing",
    "inform-tmm-before-opp-hearing",
    "payment-opposition-hearing",
    "order-of-opp-hearing",
  ];
  const isHearingScopedSubstep = HEARING_SCOPED_SUBSTEPS.includes(
    opposedSubStep
  );
  const hearingTmNos = new Set(hearingApps.map((a) => a.tmNo));
  const opposedOnlyTmNos = new Set(
    opposedTmsRaw.filter((t) => !hearingTmNos.has(t.tmNo)).map((t) => t.tmNo)
  );
  // For hearing-scoped substeps, show only hearing TMs when data is loaded;
  // fall back to counter TMs when hearingTmNos is empty (data not yet saved or
  // still loading) so the rail is never blank.
  const nonHearingFilter = (t: (typeof opposedTms)[number]) =>
    opposedOnlyTmNos.has(t.tmNo) || !hearingTmNos.has(t.tmNo);
  const visibleOpposedTms = isHearingScopedSubstep
    ? hearingTmNos.size > 0
      ? opposedTms.filter((t) => hearingTmNos.has(t.tmNo))
      : opposedTms.filter(nonHearingFilter)
    : opposedTms;

  const opposedSelectedTm = opposedTms[opposedSelectedTmIdx] ?? null;

  // Per-TM "Last Date for Evidence by Opponent" alert state, derived once and
  // reused by both the TM pill indicator and the inline banner inside the
  // Notice of Counter sub-step. Rows are loaded eagerly for the opposed flow,
  // so the indicator is visible regardless of which sub-step is active.
  const evidenceDeadlineByTm: Record<
    string,
    ReturnType<typeof deriveEvidenceDeadlineState>
  > = {};
  for (const r of noticeOfCounterRows) {
    evidenceDeadlineByTm[r.tmNo] = deriveEvidenceDeadlineState(
      r.lastDateForEvidenceByOpponent,
      r.lastDateForEvidenceByOpponentExpired,
    );
  }

  // Alert state for the TM on screen. Prefers the live draft value (or the auto-calc from the
  // Notice Date being picked) over the saved row, so the banner tracks 4A(xi) as it is edited.
  const selectedEvidenceDeadline = (() => {
    const tmNo = opposedSelectedTm?.tmNo;
    if (!tmNo) return { state: "none" as const, date: null, daysUntil: null };
    const noticeYmd = noticeOfCounterDraft[tmNo] ?? "";
    const draftLast = noticeOfCounterLastDateDraft[tmNo] ?? "";
    const autoLast = noticeYmd ? computeNoticeCounterLastDate(noticeYmd) : null;
    const pending = draftLast || (autoLast ? toLocalYmd(autoLast) : "");
    // A pending edit is judged on the picked date alone; the server `expired` flag belongs to the
    // saved date and would otherwise mark a newly-picked future date as overdue.
    if (pending) return deriveEvidenceDeadlineState(pending, undefined);
    return (
      evidenceDeadlineByTm[tmNo] ?? {
        state: "none" as const,
        date: null,
        daysUntil: null,
      }
    );
  })();

  // Keep the selected TM valid for the current sub-step. When entering a
  // hearing-scoped sub-step from a TM that isn't in Hearing status (or vice
  // versa), auto-switch to the first TM that is.
  useEffect(() => {
    const validSet =
      isHearingScopedSubstep && hearingTmNos.size > 0
        ? hearingTmNos
        : null;
    if (validSet !== null) {
      if (validSet.size === 0) return;
      if (opposedSelectedTm && validSet.has(opposedSelectedTm.tmNo)) return;
    } else {
      if (opposedSelectedTm) return;
    }
    const firstValidIdx = validSet
      ? opposedTms.findIndex((t) => validSet.has(t.tmNo))
      : 0;
    if (firstValidIdx >= 0 && firstValidIdx !== opposedSelectedTmIdx) {
      setOpposedSelectedTmIdx(firstValidIdx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opposedSubStep, opposedStatus, oppositionHearing]);

  // Inform Client about Counter (4A(iii)) — fetch WhatsApp preview from API,
  // scoped to the currently selected TM No. Mirrors the Inform Client about
  // Filing of Counter (4A(x)) preview pattern.
  useEffect(() => {
    const selectedTmNo = opposedSelectedTm?.tmNo ?? null;
    if (
      !leadId ||
      variant !== "opposed" ||
      opposedSubStep !== "inform-client-about-counter" ||
      !selectedTmNo
    ) {
      return;
    }
    let cancelled = false;
    setCounterInformPreview(null);
    (async () => {
      try {
        const preview = await getInformClientAboutCounterPreview(leadId, [
          selectedTmNo,
        ]);
        if (!cancelled) setCounterInformPreview({ message: preview.message });
      } catch {
        if (!cancelled) setCounterInformPreview(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, variant, opposedSubStep, opposedSelectedTm?.tmNo]);

  const blankOpposedForm: OpposedPerTmFormState = {
    oppositionNo: "",
    dateOfOpposition: "",
    dateOfReceiptOfOpposition: "",
    nameOfOpponent: "",
    addressOfOpponent: "",
    placeOfOpponent: "",
    opponentsMark: "",
    classOfOpponentsMark: "",
    goodsServicesOfOpponentsMark: "",
    userDateOfOpponentsMark: "",
    lastDateOfFilingCounter: "",
    pendingNoticeFile: null,
    pendingImageFile: null,
  };
  const opposedSelectedForm = opposedSelectedTm
    ? opposedPerTm[opposedSelectedTm.tmNo] ?? blankOpposedForm
    : blankOpposedForm;

  const updateOpposedPerTm = (
    tmNo: string,
    patch: Partial<OpposedPerTmFormState>
  ) => {
    setOpposedPerTm((prev) => ({
      ...prev,
      [tmNo]: { ...(prev[tmNo] ?? blankOpposedForm), ...patch },
    }));
  };

  // Auto-derive Last date of filing Counter (4n) when 4e changes and the user
  // hasn't manually set lastDateOfFilingCounter (i.e., it's empty).
  const opposedDerivedLastDate = (() => {
    if (opposedSelectedForm.lastDateOfFilingCounter) {
      return opposedSelectedForm.lastDateOfFilingCounter;
    }
    if (!opposedSelectedForm.dateOfReceiptOfOpposition) return "";
    const ymd = addDaysToYmd(
      opposedSelectedForm.dateOfReceiptOfOpposition,
      COUNTER_DAYS
    );
    return ymd ?? "";
  })();

  const uploadOpposedPendingFiles = async (
    tmNo: string
  ): Promise<{
    noticeKey?: string;
    noticeFilename?: string;
    imageKey?: string;
  }> => {
    const form = opposedPerTm[tmNo];
    if (!form) return {};
    const out: {
      noticeKey?: string;
      noticeFilename?: string;
      imageKey?: string;
    } = {};
    const filesToUpload: Array<{ file: File; kind: "notice" | "image" }> = [];
    if (form.pendingNoticeFile) {
      filesToUpload.push({ file: form.pendingNoticeFile, kind: "notice" });
    }
    if (form.pendingImageFile) {
      filesToUpload.push({ file: form.pendingImageFile, kind: "image" });
    }
    if (filesToUpload.length === 0) return out;
    const presigned = await generateAcknowledgementUploadUrls(
      filesToUpload.map((f) => ({
        filename: f.file.name,
        mimeType: f.file.type || "application/octet-stream",
      })),
      OPPOSITION_COUNTER_FOLDER
    );
    for (let i = 0; i < filesToUpload.length; i += 1) {
      const { file, kind } = filesToUpload[i];
      const url = presigned[i]?.url;
      const key = presigned[i]?.key;
      if (!url || !key) {
        throw new Error(`Upload URL not returned for ${tmNo}`);
      }
      const putRes = await fetch(url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
      });
      if (!putRes.ok) {
        throw new Error(`Failed to upload file for ${tmNo}`);
      }
      if (kind === "notice") {
        out.noticeKey = key;
        out.noticeFilename = file.name;
      } else {
        out.imageKey = key;
      }
    }
    return out;
  };

  /** Build a PATCH application item for a single TM — preserves existing keys. */
  const buildOpposedPatchItem = (
    app: OppositionCounterApplication,
    form: OpposedPerTmFormState,
    uploaded: { noticeKey?: string; noticeFilename?: string; imageKey?: string }
  ): SetOppositionCounterApplicationInput => {
    const out: SetOppositionCounterApplicationInput = { tmNo: app.tmNo };
    if (form.oppositionNo) out.oppositionNo = form.oppositionNo;
    if (form.dateOfOpposition) out.dateOfOpposition = form.dateOfOpposition;
    if (form.dateOfReceiptOfOpposition) {
      out.dateOfReceiptOfOpposition = form.dateOfReceiptOfOpposition;
    }
    if (form.nameOfOpponent) out.nameOfOpponent = form.nameOfOpponent;
    if (form.addressOfOpponent) out.addressOfOpponent = form.addressOfOpponent;
    if (form.placeOfOpponent) out.placeOfOpponent = form.placeOfOpponent;
    if (form.opponentsMark) out.opponentsMark = form.opponentsMark;
    if (form.classOfOpponentsMark) {
      out.classOfOpponentsMark = form.classOfOpponentsMark;
    }
    if (form.goodsServicesOfOpponentsMark) {
      out.goodsServicesOfOpponentsMark = form.goodsServicesOfOpponentsMark;
    }
    if (form.userDateOfOpponentsMark) {
      out.userDateOfOpponentsMark = form.userDateOfOpponentsMark;
    }
    if (form.lastDateOfFilingCounter) {
      out.lastDateOfFilingCounter = form.lastDateOfFilingCounter;
    }
    if (uploaded.noticeKey) {
      out.oppositionNoticeFileKey = uploaded.noticeKey;
      out.oppositionNoticeFilename = uploaded.noticeFilename ?? "";
    } else if (
      app.oppositionNoticeFileKey &&
      app.oppositionNoticeFilename
    ) {
      out.oppositionNoticeFileKey = app.oppositionNoticeFileKey;
      out.oppositionNoticeFilename = app.oppositionNoticeFilename;
    }
    if (uploaded.imageKey) {
      out.imageOfOpponentsMarkFileKey = uploaded.imageKey;
    } else if (app.imageOfOpponentsMarkFileKey) {
      out.imageOfOpponentsMarkFileKey = app.imageOfOpponentsMarkFileKey;
    }
    return out;
  };

  const handleCounterStatusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !opposedSelectedTm) return;

    // Required fields per the form: Opposition No., Date of Opposition,
    // Date of Receipt of Opposition, and the Opposition Notice PDF (functionally
    // required for the next sub-step to send to client).
    if (!opposedSelectedForm.oppositionNo.trim()) {
      toast.error("Enter Opposition No.");
      return;
    }
    if (!opposedSelectedForm.dateOfOpposition) {
      toast.error("Select Date of Opposition");
      return;
    }
    if (!opposedSelectedForm.dateOfReceiptOfOpposition) {
      toast.error("Select Date of Receipt of Opposition");
      return;
    }
    if (!opposedSelectedForm.nameOfOpponent.trim()) {
      toast.error("Enter Name of Opponent");
      return;
    }
    if (!opposedSelectedForm.addressOfOpponent.trim()) {
      toast.error("Enter Address of Opponent");
      return;
    }
    if (!opposedSelectedForm.placeOfOpponent.trim()) {
      toast.error("Enter Place of Opponent");
      return;
    }
    if (!opposedSelectedForm.opponentsMark.trim()) {
      toast.error("Enter Opponent's Mark");
      return;
    }
    if (!opposedSelectedForm.classOfOpponentsMark.trim()) {
      toast.error("Enter Class of Opponent's Mark");
      return;
    }
    if (!opposedSelectedForm.goodsServicesOfOpponentsMark.trim()) {
      toast.error("Enter Goods/Services of Opponent's Mark");
      return;
    }
    if (!opposedSelectedForm.userDateOfOpponentsMark) {
      toast.error("Select User Date of Opponent's Mark");
      return;
    }
    const hasNoticeFile = Boolean(
      opposedSelectedForm.pendingNoticeFile ||
        opposedSelectedTm.oppositionNoticeFileKey
    );
    if (!hasNoticeFile) {
      toast.error("Upload Opposition Notice PDF");
      return;
    }

    onSavingChange?.(true);
    try {
      const uploaded = await uploadOpposedPendingFiles(opposedSelectedTm.tmNo);
      const item = buildOpposedPatchItem(
        opposedSelectedTm,
        opposedSelectedForm,
        uploaded
      );
      const updated = await setOppositionCounterStatusDetails(leadId, {
        applications: [item],
      });
      setOpposedStatus(updated);
      setOpposedPerTm(opposedApplicationsToFormMap(updated.applications));
      toast.success("Status details for Counter saved");
      // Advance to next sub-step (Notice to Client for Counter — placeholder for now)
      setOpposedSubStep("notice-to-client-counter");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Status details for Counter"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleOpposedFileChosen = (
    tmNo: string,
    kind: "notice" | "image",
    file: File
  ) => {
    const reason = validateUploadFile(file);
    if (reason) {
      toast.error(reason);
      return;
    }
    if (kind === "image") {
      // Image of Mark must be an image format (the inline preview is an <img>)
      const ext = (file.name.split(".").pop() ?? "").toLowerCase();
      if (!["jpg", "jpeg", "png", "webp"].includes(ext)) {
        toast.error("Image of Mark must be JPG, JPEG, PNG or WEBP");
        return;
      }
    }
    if (kind === "notice") {
      updateOpposedPerTm(tmNo, { pendingNoticeFile: file });
    } else {
      updateOpposedPerTm(tmNo, { pendingImageFile: file });
    }
  };

  // ───────────────────── Opposed: Notice to Client for Counter (4A(ii)) ─────────────────────

  const opposedTmsWithNoticePdf = opposedTms.filter(
    (a) => a.oppositionNoticeFileKey && a.oppositionNoticeFilename
  );
  const canSendCounterNotice = opposedTmsWithNoticePdf.length > 0;

  const loadCounterNoticePreview = async () => {
    if (!leadId) return;
    setCounterNoticePreviewLoading(true);
    setCounterNoticePreviewError(null);
    try {
      const data = await getOppositionCounterNoticePreview(leadId);
      setCounterNoticePreview(data);
    } catch (err) {
      setCounterNoticePreview(null);
      setCounterNoticePreviewError(
        err instanceof Error ? err.message : "Failed to load preview"
      );
    } finally {
      setCounterNoticePreviewLoading(false);
    }
  };

  const handleCounterNoticeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentCounterNotice) {
      setOpposedSubStep("inform-client-about-counter");
      return;
    }
    if (!canSendCounterNotice) {
      toast.error(
        "Upload Opposition Notice PDF in Status details for Counter first"
      );
      return;
    }
    setCounterNoticeSendConfirmOpen(true);
    void loadCounterNoticePreview();
  };

  const performCounterNoticeSend = async () => {
    if (!leadId) return;
    setCounterNoticeSending(true);
    onSavingChange?.(true);
    try {
      await sendOppositionCounterNoticeToClient(leadId, {
        sendWhatsApp: true,
        sendEmail: true,
      });
      setHasSentCounterNotice(true);
      setCounterNoticeSendConfirmOpen(false);
      toast.success("Opposition Notice forwarded to client");
      setOpposedSubStep("inform-client-about-counter");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to forward Opposition Notice"
      );
    } finally {
      setCounterNoticeSending(false);
      onSavingChange?.(false);
    }
  };

  const handleResendCounterNotice = async () => {
    if (!leadId || resendingCounterNotice || !canSendCounterNotice) return;
    setResendingCounterNotice(true);
    onSavingChange?.(true);
    try {
      await sendOppositionCounterNoticeToClient(leadId, {
        sendWhatsApp: true,
        sendEmail: true,
      });
      setCounterNoticeResendConfirmOpen(false);
      toast.success("Opposition Notice resent to client");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Opposition Notice"
      );
    } finally {
      setResendingCounterNotice(false);
      onSavingChange?.(false);
    }
  };

  // ───────────────────── Opposed: Inform Client about Counter (4A(iii)) ─────────────────────

  const handleCounterInformSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentCounterInform) {
      setOpposedSubStep("call-client-counter");
      return;
    }
    const selectedTmNo = opposedSelectedTm?.tmNo;
    if (!selectedTmNo) {
      toast.error("Select a TM application first");
      return;
    }
    onSavingChange?.(true);
    try {
      await informClientAboutOppositionCounter(leadId, [selectedTmNo]);
      setHasSentCounterInform(true);
      toast.success("Inform Client about Counter sent");
      setOpposedSubStep("call-client-counter");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Inform Client about Counter"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendCounterInform = async () => {
    if (!leadId || resendingCounterInform) return;
    const selectedTmNo = opposedSelectedTm?.tmNo;
    if (!selectedTmNo) {
      toast.error("Select a TM application first");
      return;
    }
    setResendingCounterInform(true);
    onSavingChange?.(true);
    try {
      await informClientAboutOppositionCounter(leadId, [selectedTmNo]);
      toast.success("Inform Client about Counter resent");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Inform Client about Counter"
      );
    } finally {
      setResendingCounterInform(false);
      onSavingChange?.(false);
    }
  };

  // ───────────────────── Opposed: Call Client for Counter (4A(iv)) ─────────────────────

  const handleCounterCallSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasRecordedCounterCall) {
      // Already recorded — Save & Continue advances to next sub-step
      setOpposedSubStep("confirmation-from-client-counter");
      return;
    }
    setRecordingCounterCall(true);
    onSavingChange?.(true);
    try {
      await recordCallClientForCounter(leadId);
      setHasRecordedCounterCall(true);
      toast.success("Call Client for Counter recorded");
      setOpposedSubStep("confirmation-from-client-counter");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to record Call Client for Counter"
      );
    } finally {
      setRecordingCounterCall(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(v) Confirmation from Client for Counter ─────────

  const handleCounterConfirmationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !counterConfirmation) return;
    const apps = counterConfirmation.applications
      .filter((a) => counterConfirmationDraft[a.tmNo] != null)
      .map((a) => ({
        tmNo: a.tmNo,
        clientGoingAhead: counterConfirmationDraft[a.tmNo] as boolean,
      }));
    if (apps.length === 0) {
      toast.error("Pick YES or NO for at least one TM");
      return;
    }
    // The panel is per-application, so "at least one TM answered" is not enough: with TM A already
    // answered the user could Save & Continue while standing on an unanswered TM B and still get the
    // success toast (review item 19). Require a pick for the application actually on screen.
    if (
      opposedSelectedTm &&
      counterConfirmationDraft[opposedSelectedTm.tmNo] == null
    ) {
      toast.error(
        `Select YES or NO for TM No. ${opposedSelectedTm.tmNo} before continuing`
      );
      return;
    }
    onSavingChange?.(true);
    try {
      const noOpposed = counterNoOpposedDraft.trim() !== "" ? Number(counterNoOpposedDraft) : undefined;
      const noForCounter =
        counterNoForCounterDraft.trim() !== "" ? Number(counterNoForCounterDraft) : undefined;
      const updated = await saveCounterConfirmationFromClient(
        leadId,
        apps,
        noOpposed,
        noForCounter
      );
      setCounterConfirmation(updated);
      setCounterNoOpposedDraft(
        updated.noOfApplicationsOpposed != null ? String(updated.noOfApplicationsOpposed) : ""
      );
      setCounterNoForCounterDraft(
        updated.noOfApplicationsForCounter != null
          ? String(updated.noOfApplicationsForCounter)
          : updated.noOfApplicationsOpposed != null
            ? String(updated.noOfApplicationsOpposed)
            : ""
      );
      toast.success("Confirmation from Client for Counter saved");
      // YES → continue with the Counter flow (invoice next).
      // NO → 5B(i) Status details for Abandonment (spec pg 119). That screen is in the
      //      Final Order section, so the page navigates for us (review item 73). Before
      //      this the substep just saved and sat there with no feedback.
      const selectedPick = opposedSelectedTm
        ? counterConfirmationDraft[opposedSelectedTm.tmNo]
        : null;
      const hasAnyYes = apps.some((a) => a.clientGoingAhead === true);
      const shouldAdvance = selectedPick === true || (selectedPick == null && hasAnyYes);
      if (shouldAdvance) {
        setOpposedSubStep("invoice-to-client-counter");
      }
      // NO no longer jumps straight to 5B(i). Item 73 fixed a genuinely dead NO
      // branch by auto-advancing, but the spec allows two destinations here, so
      // items 74/75 replace the jump with an explicit choice rendered below the
      // fields (`NonFilingNextStepChoice`). NO is still never silently dead.
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Confirmation from Client"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(vi) Invoice to Client for Counter ─────────

  const persistCounterInvoice = async (): Promise<CounterInvoiceStatus | null> => {
    if (!leadId || !counterInvoice) return null;
    const apps = (counterInvoice.tmInvoiceRows ?? []).map((row) => {
      const draft = counterInvoiceRows[row.tmNo] ?? {
        invoiceAmount: String(row.invoiceAmount ?? 0),
        discount: String(row.discount ?? 0),
        invoiceDate: "",
        includeInvoiceNo: row.includeInvoiceNo ?? true,
      };
      return {
        tmNo: row.tmNo,
        invoiceAmount: Number(draft.invoiceAmount) || 0,
        discount: Number(draft.discount) || 0,
        includeInvoiceNo: draft.includeInvoiceNo,
        ...(draft.invoiceDate ? { invoiceDate: draft.invoiceDate } : {}),
      };
    });
    const updated = await saveCounterInvoice(leadId, apps);
    setCounterInvoice(updated);
    return updated;
  };

  const handleCounterInvoiceSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentCounterInvoice) {
      setOpposedSubStep("payment-from-client-counter");
      return;
    }
    if (!counterInvoice) {
      toast.error("Invoice not loaded yet");
      return;
    }
    setCounterInvoiceSendConfirmOpen(true);
  };

  const performCounterInvoiceSend = async () => {
    if (!leadId || !counterInvoice) return;
    setCounterInvoiceSending(true);
    onSavingChange?.(true);
    try {
      const saved = await persistCounterInvoice();
      if (!saved || (saved.invoiceAmount ?? 0) <= 0) {
        toast.error("Enter at least one TM with a non-zero Invoice Amount");
        return;
      }
      // Generate PDF if not already generated
      if (!saved.counterInvoicePdfKey) {
        await generateCounterInvoicePdf(leadId);
      }
      // Send the invoice to the client
      await sendCounterInvoiceToClient(leadId, { sendWhatsApp: true });
      setHasSentCounterInvoice(true);
      setCounterInvoiceSendConfirmOpen(false);
      toast.success("Counter Invoice sent to client");
      setOpposedSubStep("payment-from-client-counter");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send Counter Invoice"
      );
    } finally {
      setCounterInvoiceSending(false);
      onSavingChange?.(false);
    }
  };

  const handleGenerateCounterInvoicePdf = async () => {
    if (!leadId || counterInvoiceGenerating) return;
    setCounterInvoiceGenerating(true);
    onSavingChange?.(true);
    try {
      await persistCounterInvoice();
      const pdf = await generateCounterInvoicePdf(leadId);
      setCounterInvoice((prev) =>
        prev
          ? {
              ...prev,
              counterInvoicePdfKey: pdf.key,
              counterInvoicePdfUrl: pdf.url,
            }
          : prev
      );
      toast.success("Counter Invoice PDF generated");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to generate Counter Invoice PDF"
      );
    } finally {
      setCounterInvoiceGenerating(false);
      onSavingChange?.(false);
    }
  };

  const handleResendCounterInvoice = async () => {
    if (!leadId || resendingCounterInvoice) return;
    setResendingCounterInvoice(true);
    onSavingChange?.(true);
    try {
      await sendCounterInvoiceToClient(leadId, { sendWhatsApp: true });
      setCounterInvoiceResendConfirmOpen(false);
      toast.success("Counter Invoice resent to client");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to resend Counter Invoice"
      );
    } finally {
      setResendingCounterInvoice(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(vii) Payment from Client for Counter ─────────

  const handleCounterPaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !counterInvoice) return;
    const tmInvoiceRows = counterInvoice.tmInvoiceRows ?? [];
    const apps: { tmNo: string; collectedAmount: number }[] = [];
    for (const r of tmInvoiceRows) {
      const invoiceAmount = r.invoiceAmount - (r.discount ?? 0);
      const savedCollected = Number(r.collectedAmount ?? 0) || 0;
      const thisPayment = parseAmount(counterPaymentRows[r.tmNo] ?? "");
      const totalCollected = savedCollected + thisPayment;
      if (
        thisPayment > 0 &&
        invoiceAmount > 0 &&
        totalCollected > invoiceAmount
      ) {
        toast.error(
          `TM ${r.tmNo}: Collected amount cannot exceed the outstanding invoice balance.`
        );
        return;
      }
      apps.push({ tmNo: r.tmNo, collectedAmount: totalCollected });
    }
    onSavingChange?.(true);
    try {
      const updated = await saveCounterCollectedAmount(leadId, apps);
      setCounterInvoice(updated);
      const cleared: Record<string, string> = {};
      for (const r of updated.tmInvoiceRows ?? []) cleared[r.tmNo] = "";
      setCounterPaymentRows(cleared);
      toast.success("Payment recorded");
      setOpposedSubStep("prepare-counter");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record payment"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(viii) Prepare Counter ─────────

  const handlePrepareCounterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !prepareCounter) return;
    const counterStatements = prepareCounter.counterStatements ?? [];
    for (const a of counterStatements) {
      const d = prepareCounterPerTm[a.tmNo];
      const tmLabel = a.tmNo || "selected TM";
      if (!d) {
        toast.error(`Please complete Prepare Counter for ${tmLabel}.`);
        return;
      }
      if (!d.noOfParagraphsInOpponentIntro.trim()) {
        toast.error(
          `No. of Paragraphs in Opponent's Intro is required for ${tmLabel}.`
        );
        return;
      }
      const validReplies = (d.paragraphWiseReplies ?? []).filter(
        (p) => (p.heading ?? "").trim() && (p.text ?? "").trim()
      );
      if (validReplies.length === 0) {
        toast.error(
          `Add at least one Paragraph-wise Reply (heading and text) for ${tmLabel}.`
        );
        return;
      }
      const hasIncompleteReply = (d.paragraphWiseReplies ?? []).some(
        (p) =>
          (p.heading ?? "").trim().length === 0 ||
          (p.text ?? "").trim().length === 0
      );
      if (hasIncompleteReply) {
        toast.error(
          `Each Paragraph-wise Reply needs both a heading and text for ${tmLabel}.`
        );
        return;
      }
      if (!d.opponentAttorneyName.trim()) {
        toast.error(`Name of Opponent's Attorney is required for ${tmLabel}.`);
        return;
      }
      if (!d.opponentAttorneyAddress.trim()) {
        toast.error(
          `Address of Opponent's Attorney is required for ${tmLabel}.`
        );
        return;
      }
      const email = d.opponentAttorneyEmail.trim();
      if (!email) {
        toast.error(`Email of Opponent's Attorney is required for ${tmLabel}.`);
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error(
          `Enter a valid Email of Opponent's Attorney for ${tmLabel}.`
        );
        return;
      }
      if (!d.dateOfEvidenceAffidavit) {
        toast.error(`Date of Evidence Affidavit is required for ${tmLabel}.`);
        return;
      }
    }
    const apps = counterStatements.map((a) => {
      const d = prepareCounterPerTm[a.tmNo];
      const replies = (d?.paragraphWiseReplies ?? []).filter(
        (p) => (p.heading ?? "").trim() && (p.text ?? "").trim()
      );
      return {
        tmNo: a.tmNo,
        noOfParagraphsInOpponentIntro:
          d?.noOfParagraphsInOpponentIntro
            ? Number(d.noOfParagraphsInOpponentIntro) || null
            : null,
        paragraphWiseReplies: replies,
        opponentAttorneyName: d?.opponentAttorneyName ?? "",
        opponentAttorneyAddress: d?.opponentAttorneyAddress ?? "",
        opponentAttorneyEmail: d?.opponentAttorneyEmail ?? "",
        dateOfEvidenceAffidavit: d?.dateOfEvidenceAffidavit ?? "",
        dateOfFilingCounterStatement: d?.dateOfFilingCounterStatement ?? "",
      };
    });
    onSavingChange?.(true);
    try {
      const updated = await savePrepareCounter(leadId, apps);
      setPrepareCounter(updated);
      toast.success("Prepare Counter saved");
      setOpposedSubStep("file-counter");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save Prepare Counter"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleGeneratePrepareCounterPdf = async (
    app: PrepareCounterApplicationItem
  ) => {
    if (!leadId || prepareCounterGenerating) return;
    setPrepareCounterGenerating(true);
    onSavingChange?.(true);
    try {
      const pdf = await generatePrepareCounterPdf(leadId, app.ackApplicationId);
      setPrepareCounter((prev) =>
        prev
          ? {
              ...prev,
              counterStatements: (prev.counterStatements ?? []).map((x) =>
                x.tmNo === app.tmNo
                  ? {
                      ...x,
                      counterStatementPdfKey: pdf.key,
                      counterStatementPdfUrl: pdf.url,
                    }
                  : x
              ),
            }
          : prev
      );
      toast.success("Counter Statement PDF generated");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to generate Counter Statement PDF"
      );
    } finally {
      setPrepareCounterGenerating(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(ix) File Counter ─────────

  const handleFileCounterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !fileCounter || !fileCounter.acknowledgementId) return;

    // Validate: the currently-visible TM must have both Date of Filing and a
    // Counter Statement PDF before advancing. The backend's "Inform Client
    // about Filing of Counter" step refuses to send unless at least one TM has
    // a `dateOfFilingCounterStatement` persisted, so silently advancing with
    // an empty form would land us on a screen that can't proceed either.
    const selTm = opposedSelectedTm?.tmNo ?? null;
    if (!selTm) {
      toast.error("Select a TM application first");
      return;
    }
    const selDraft = fileCounterPerTm[selTm];
    const selRow = (fileCounter.applications ?? []).find((a) => a.tmNo === selTm);
    if (!selDraft?.filingDate) {
      toast.error(
        `Enter Date of Filing Counter Statement for ${formatTmNoForDisplay(selTm)}`
      );
      return;
    }
    const hasPdf = Boolean(selDraft.pendingFile || selRow?.pdfUploaded);
    if (!hasPdf) {
      toast.error(
        `Upload Counter Statement PDF for ${formatTmNoForDisplay(selTm)}`
      );
      return;
    }

    onSavingChange?.(true);
    try {
      const apps = fileCounter.applications ?? [];
      const pending = apps
        .map((a) => ({
          app: a,
          file: fileCounterPerTm[a.tmNo]?.pendingFile ?? null,
        }))
        .filter(
          (x): x is { app: FileCounterApplicationRow; file: File } => !!x.file
        );

      let presigned: Awaited<ReturnType<typeof generateFileCounterPresignedUrls>> = [];
      if (pending.length > 0) {
        presigned = await generateFileCounterPresignedUrls(
          leadId,
          fileCounter.acknowledgementId,
          pending.map((p) => ({
            tmNo: p.app.tmNo,
            fileName: p.file.name,
            mimeType: p.file.type || "application/pdf",
          }))
        );
        for (const item of presigned) {
          const row = pending.find((p) => p.app.tmNo === item.tmNo);
          if (!row) continue;
          const putRes = await fetch(item.presignedUrl, {
            method: "PUT",
            body: row.file,
            headers: {
              "Content-Type": row.file.type || "application/pdf",
            },
          });
          if (!putRes.ok)
            throw new Error(`Failed to upload Counter Statement for ${item.tmNo}`);
        }
      }

      const payload = apps.map((a) => {
        const draft = fileCounterPerTm[a.tmNo];
        const uploaded = presigned.find((p) => p.tmNo === a.tmNo);
        const uploadedFilename =
          pending.find((p) => p.app.tmNo === a.tmNo)?.file.name ?? null;
        return {
          tmNo: a.tmNo,
          ...(draft?.filingDate ? { filingDate: draft.filingDate } : {}),
          ...(uploaded?.fileKey
            ? {
                counterStatementPdfKey: uploaded.fileKey,
                counterStatementPdfFilename: uploadedFilename,
              }
            : {}),
        };
      });

      const updated = await saveFileCounter(
        leadId,
        fileCounter.acknowledgementId,
        payload
      );
      setFileCounter(updated);
      const map: Record<
        string,
        { filingDate: string; pendingFile: File | null }
      > = {};
      for (const a of updated?.applications ?? []) {
        const parts = parseDateParts(a.filingDate ?? null);
        map[a.tmNo] = {
          filingDate: parts ? partsToIsoYmd(parts) : "",
          pendingFile: null,
        };
      }
      setFileCounterPerTm(map);
      toast.success("Counter Statement filed");
      setOpposedSubStep("inform-client-filing-counter");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to file Counter Statement"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(x) Inform Client about Filing of Counter ─────────

  const handleInformFilingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentInformFiling) {
      setOpposedSubStep("notice-of-counter-registry");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendFileCounterInformClientWhatsApp(leadId);
      setHasSentInformFiling(true);
      toast.success("Inform Client about Filing of Counter sent");
      setOpposedSubStep("notice-of-counter-registry");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Inform Client about Filing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendInformFiling = async () => {
    if (!leadId || resendingInformFiling) return;
    setResendingInformFiling(true);
    onSavingChange?.(true);
    try {
      await sendFileCounterInformClientWhatsApp(leadId);
      toast.success("Inform Client about Filing of Counter resent");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Inform Client about Filing"
      );
    } finally {
      setResendingInformFiling(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xi) Notice of Counter by Registry ─────────

  const handleNoticeOfCounterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !opposedSelectedTm) return;
    if (!fileCounter?.acknowledgementId) {
      toast.error("Acknowledgement context unavailable; reload the page");
      return;
    }
    const noticeDate = noticeOfCounterDraft[opposedSelectedTm.tmNo];
    if (!noticeDate) {
      toast.error(
        `Enter Date of Notice of Counter Statement for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    const lastDate =
      noticeOfCounterLastDateDraft[opposedSelectedTm.tmNo] || null;
    onSavingChange?.(true);
    try {
      await saveNoticeOfCounter(leadId, fileCounter.acknowledgementId, [
        { tmNo: opposedSelectedTm.tmNo, noticeDate, lastDate },
      ]);
      toast.success("Date of Notice of Counter Statement saved");
      const rows = await getNoticeOfCounter(leadId);
      setNoticeOfCounterRows(rows);
      setOpposedSubStep("inform-client-notice-counter");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Date of Notice of Counter"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xii) Inform Client about Notice of Counter ─────────

  const handleInformNoticeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentInformNotice) {
      setOpposedSubStep("status-evidence-by-opp");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendInformClientNoticeOfCounterWhatsApp(leadId);
      setHasSentInformNotice(true);
      toast.success("Inform Client about Notice of Counter sent");
      setOpposedSubStep("status-evidence-by-opp");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Inform Client about Notice of Counter"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendInformNotice = async () => {
    if (!leadId || resendingInformNotice) return;
    setResendingInformNotice(true);
    onSavingChange?.(true);
    try {
      await sendInformClientNoticeOfCounterWhatsApp(leadId);
      toast.success("Inform Client about Notice of Counter resent");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Inform Client about Notice of Counter"
      );
    } finally {
      setResendingInformNotice(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xiii) Status details for Evidence by Opponent ─────────

  const handleEvidenceByOpponentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !evidenceByOpponent) return;
    if (!opposedSelectedTm) {
      toast.error("Select a TM application first");
      return;
    }
    const draft = evidenceByOpponentDraft[opposedSelectedTm.tmNo];
    if (draft?.opponentEvidenceFiled == null) {
      toast.error(
        `Pick YES or NO for "Has the Opponent filed Evidence?" for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    if (draft.opponentEvidenceFiled && !draft.dateOfEvidenceByOpponent) {
      toast.error(
        `Enter Date of Evidence by Opponent for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    if (
      draft.opponentEvidenceFiled &&
      !draft.dateOfReceiptOfEvidenceByOpponent
    ) {
      toast.error(
        `Enter Date of Receipt of Evidence by Opponent for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    onSavingChange?.(true);
    try {
      // Upload pending Evidence by Opponent PDF first if present
      const existingApp = (evidenceByOpponent.applications ?? []).find(
        (a) => a.tmNo === opposedSelectedTm.tmNo
      );
      let evidenceByOpponentFileKey: string | null | undefined =
        existingApp?.evidenceByOpponentFileKey ?? null;
      let evidenceByOpponentFilename: string | null | undefined =
        existingApp?.evidenceByOpponentFilename ?? null;
      if (draft.pendingFile) {
        const reason = validateUploadFile(draft.pendingFile);
        if (reason) {
          toast.error(reason);
          onSavingChange?.(false);
          return;
        }
        evidenceByOpponentFileKey = await uploadAcknowledgementPdf(
          draft.pendingFile,
          EVIDENCE_BY_OPPONENT_FOLDER
        );
        evidenceByOpponentFilename = draft.pendingFile.name;
      }

      await saveEvidenceByOpponentStatus(leadId, [
        {
          tmNo: opposedSelectedTm.tmNo,
          opponentEvidenceFiled: draft.opponentEvidenceFiled,
          ...(draft.dateOfEvidenceByOpponent
            ? { dateOfEvidenceByOpponent: draft.dateOfEvidenceByOpponent }
            : {}),
          ...(draft.dateOfReceiptOfEvidenceByOpponent
            ? {
                dateOfReceiptOfEvidenceByOpponent:
                  draft.dateOfReceiptOfEvidenceByOpponent,
              }
            : {}),
          ...(draft.lastDateOfFilingEvidence
            ? { lastDateOfFilingEvidence: draft.lastDateOfFilingEvidence }
            : {}),
          evidenceByOpponentFileKey: evidenceByOpponentFileKey ?? null,
          evidenceByOpponentFilename: evidenceByOpponentFilename ?? null,
        },
      ]);
      // Refetch so the auto-calculated 4z and 4w show after save
      const updated = await getEvidenceByOpponentStatus(leadId);
      setEvidenceByOpponent(updated);
      const map: Record<
        string,
        {
          opponentEvidenceFiled: boolean | null;
          dateOfEvidenceByOpponent: string;
          dateOfReceiptOfEvidenceByOpponent: string;
          lastDateOfFilingEvidence: string;
          pendingFile: File | null;
        }
      > = {};
      const toYmd = (s: string | null) => {
        const p = parseDateParts(s);
        return p ? partsToIsoYmd(p) : "";
      };
      for (const a of updated.applications ?? []) {
        map[a.tmNo] = {
          opponentEvidenceFiled: a.opponentEvidenceFiled ?? null,
          dateOfEvidenceByOpponent: toYmd(a.dateOfEvidenceByOpponent),
          dateOfReceiptOfEvidenceByOpponent: toYmd(
            a.dateOfReceiptOfEvidenceByOpponent
          ),
          lastDateOfFilingEvidence: toYmd(a.lastDateOfFilingEvidence),
          pendingFile: null,
        };
      }
      setEvidenceByOpponentDraft(map);
      toast.success("Status details for Evidence by Opponent saved");
      setOpposedSubStep("notice-evidence-by-opp");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Evidence by Opponent status"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xiv) Notice to Client for Evidence by Opponent ─────────

  // 4A(xiv) forwards the Evidence-by-Opponent PDF, which only exists when 4A(xiii) answered YES
  // (spec pg 126: "If the above option is YES, our staff will download the pdf file"). The filter
  // used to check the file key alone, so a NO application still offered the forward option — and an
  // application whose answer was later flipped to NO kept offering its now-irrelevant PDF.
  const evidenceByOppTmsWithPdf = (evidenceByOpponent?.applications ?? []).filter(
    (a) =>
      a.opponentEvidenceFiled === true &&
      a.evidenceByOpponentFileKey &&
      a.evidenceByOpponentFilename
  );
  const canSendNoticeEvidenceByOpp = evidenceByOppTmsWithPdf.length > 0;
  const evidenceByOppAllAnsweredNo =
    (evidenceByOpponent?.applications ?? []).length > 0 &&
    (evidenceByOpponent?.applications ?? []).every(
      (a) => a.opponentEvidenceFiled === false
    );

  const loadNoticeEvidenceByOppPreview = async () => {
    if (!leadId) return;
    setNoticeEvidenceByOppPreviewLoading(true);
    setNoticeEvidenceByOppPreviewError(null);
    try {
      const data = await getEvidenceByOpponentNoticeToClientPreview(leadId);
      setNoticeEvidenceByOppPreview(data);
    } catch (err) {
      setNoticeEvidenceByOppPreview(null);
      setNoticeEvidenceByOppPreviewError(
        err instanceof Error ? err.message : "Failed to load preview"
      );
    } finally {
      setNoticeEvidenceByOppPreviewLoading(false);
    }
  };

  const handleNoticeEvidenceByOppSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentNoticeEvidenceByOpp) {
      setOpposedSubStep("inform-evidence-by-opp");
      return;
    }
    if (!canSendNoticeEvidenceByOpp) {
      toast.error(
        "Upload Evidence by Opponent PDF in Status details for Evidence by Opponent first"
      );
      return;
    }
    setNoticeEvidenceByOppSendConfirmOpen(true);
    void loadNoticeEvidenceByOppPreview();
  };

  const performNoticeEvidenceByOppSend = async () => {
    if (!leadId) return;
    const hasEmail = !!noticeEvidenceByOppPreview?.recipientEmail;
    setNoticeEvidenceByOppSending(true);
    onSavingChange?.(true);
    try {
      const result = await sendEvidenceByOpponentNoticeToClient(
        leadId,
        undefined,
        { sendWhatsApp: true, sendEmail: hasEmail }
      );
      setHasSentNoticeEvidenceByOpp(true);
      setNoticeEvidenceByOppSendConfirmOpen(false);
      if (result.errors?.email) toast.error(result.errors.email);
      if (result.errors?.whatsapp) toast.error(result.errors.whatsapp);
      if (!result.errors?.email && !result.errors?.whatsapp) {
        toast.success(
          hasEmail
            ? "Evidence by Opponent forwarded to client (WhatsApp & Email)"
            : "Evidence by Opponent forwarded to client"
        );
      }
      setOpposedSubStep("inform-evidence-by-opp");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to forward Evidence by Opponent"
      );
    } finally {
      setNoticeEvidenceByOppSending(false);
      onSavingChange?.(false);
    }
  };

  const handleResendNoticeEvidenceByOpp = async () => {
    if (
      !leadId ||
      resendingNoticeEvidenceByOpp ||
      !canSendNoticeEvidenceByOpp
    )
      return;
    const hasEmail = !!noticeEvidenceByOppPreview?.recipientEmail;
    setResendingNoticeEvidenceByOpp(true);
    onSavingChange?.(true);
    try {
      const result = await sendEvidenceByOpponentNoticeToClient(
        leadId,
        undefined,
        { sendWhatsApp: true, sendEmail: hasEmail }
      );
      setNoticeEvidenceByOppResendConfirmOpen(false);
      if (result.errors?.email) toast.error(result.errors.email);
      if (result.errors?.whatsapp) toast.error(result.errors.whatsapp);
      if (!result.errors?.email && !result.errors?.whatsapp) {
        toast.success(
          hasEmail
            ? "Evidence by Opponent resent to client (WhatsApp & Email)"
            : "Evidence by Opponent resent to client"
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Evidence by Opponent"
      );
    } finally {
      setResendingNoticeEvidenceByOpp(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xv) Inform Client about Evidence by Opponent ─────────

  const handleInformEvidenceByOppSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentInformEvidenceByOpp) {
      setOpposedSubStep("call-client-evidence");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendInformClientEvidenceByOpponentWhatsApp(leadId);
      setHasSentInformEvidenceByOpp(true);
      toast.success("Inform Client about Evidence by Opponent sent");
      setOpposedSubStep("call-client-evidence");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Inform Client about Evidence by Opponent"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendInformEvidenceByOpp = async () => {
    if (!leadId || resendingInformEvidenceByOpp) return;
    setResendingInformEvidenceByOpp(true);
    onSavingChange?.(true);
    try {
      await sendInformClientEvidenceByOpponentWhatsApp(leadId);
      toast.success("Inform Client about Evidence by Opponent resent");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Inform Client about Evidence by Opponent"
      );
    } finally {
      setResendingInformEvidenceByOpp(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xvi) Call Client for Evidence ─────────

  const handleCallClientEvidenceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasRecordedCallClientEvidence) {
      // Already recorded — Save & Continue advances to next sub-step
      setOpposedSubStep("confirmation-evidence");
      return;
    }
    setRecordingCallClientEvidence(true);
    onSavingChange?.(true);
    try {
      await recordCallClientForEvidence(leadId);
      setHasRecordedCallClientEvidence(true);
      toast.success("Call Client for Evidence recorded");
      setOpposedSubStep("confirmation-evidence");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to record Call Client for Evidence"
      );
    } finally {
      setRecordingCallClientEvidence(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xvii) Confirmation from Client for Evidence ─────────

  const handleEvidenceConfirmationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !evidenceConfirmation) return;
    const apps = evidenceConfirmation.applications
      .filter((a) => evidenceConfirmationDraft[a.tmNo] != null)
      .map((a) => ({
        tmNo: a.tmNo,
        clientGoingAhead: evidenceConfirmationDraft[a.tmNo] as boolean,
      }));
    if (apps.length === 0) {
      toast.error("Pick YES or NO for at least one TM");
      return;
    }
    let noOfAppsOverride: number | undefined;
    const trimmed = evidenceNoOfAppsOverride.trim();
    if (trimmed !== "") {
      const parsed = Number(trimmed);
      if (!Number.isInteger(parsed) || parsed < 0) {
        toast.error(
          "No. of applications for Evidence must be a non-negative integer."
        );
        return;
      }
      noOfAppsOverride = parsed;
    }
    onSavingChange?.(true);
    try {
      const updated = await saveEvidenceConfirmationFromClient(
        leadId,
        apps,
        noOfAppsOverride
      );
      setEvidenceConfirmation(updated);
      setEvidenceNoOfAppsOverride(
        updated.noOfApplicationsForEvidence != null
          ? String(updated.noOfApplicationsForEvidence)
          : ""
      );
      toast.success("Confirmation from Client for Evidence saved");
      // YES → continue with the Evidence flow (invoice next).
      // NO → workflow branches to 5B(i) Abandonment / Non-Filing-of-Evidence
      //      Hearing outside this substep list; stay on this substep and let
      //      the hint banner explain the next stage to the user.
      const selectedPick = opposedSelectedTm
        ? evidenceConfirmationDraft[opposedSelectedTm.tmNo]
        : null;
      const hasAnyYes = apps.some((a) => a.clientGoingAhead === true);
      const shouldAdvance =
        selectedPick === true || (selectedPick == null && hasAnyYes);
      if (shouldAdvance) {
        setOpposedSubStep("invoice-evidence");
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Confirmation from Client for Evidence"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xviii) Invoice to Client for Evidence ─────────

  const persistEvidenceInvoice =
    async (): Promise<EvidenceInvoiceStatus | null> => {
      if (!leadId || !evidenceInvoice) return null;
      const apps = (evidenceInvoice.tmInvoiceRows ?? []).map((row) => {
        const draft = evidenceInvoiceRows[row.tmNo] ?? {
          invoiceAmount: String(row.invoiceAmount ?? 0),
          discount: String(row.discount ?? 0),
          invoiceDate: "",
          includeInvoiceNo: Boolean(row.includeInvoiceNo),
        };
        return {
          tmNo: row.tmNo,
          invoiceAmount: Number(draft.invoiceAmount) || 0,
          discount: Number(draft.discount) || 0,
          includeInvoiceNo: draft.includeInvoiceNo,
          ...(draft.invoiceDate ? { invoiceDate: draft.invoiceDate } : {}),
        };
      });
      const updated = await saveEvidenceInvoice(leadId, apps);
      setEvidenceInvoice(updated);
      return updated;
    };

  const handleEvidenceInvoiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentEvidenceInvoice) {
      setOpposedSubStep("payment-evidence");
      return;
    }
    if (!evidenceInvoice) {
      toast.error("Invoice not loaded yet");
      return;
    }
    onSavingChange?.(true);
    try {
      const saved = await persistEvidenceInvoice();
      if (!saved || (saved.invoiceAmount ?? 0) <= 0) {
        toast.error("Enter at least one TM with a non-zero Invoice Amount");
        return;
      }
      if (!saved.evidenceInvoicePdfKey) {
        await generateEvidenceInvoicePdf(leadId);
      }
      await sendEvidenceInvoiceToClient(leadId, { sendWhatsApp: true });
      setHasSentEvidenceInvoice(true);
      toast.success("Evidence Invoice sent to client");
      setOpposedSubStep("payment-evidence");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Evidence Invoice"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendEvidenceInvoice = async () => {
    if (!leadId || resendingEvidenceInvoice) return;
    setResendingEvidenceInvoice(true);
    onSavingChange?.(true);
    try {
      await sendEvidenceInvoiceToClient(leadId, { sendWhatsApp: true });
      toast.success("Evidence Invoice resent to client");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Evidence Invoice"
      );
    } finally {
      setResendingEvidenceInvoice(false);
      onSavingChange?.(false);
    }
  };

  const handleGenerateEvidenceInvoicePdf = async () => {
    if (!leadId || evidenceInvoiceGenerating) return;
    setEvidenceInvoiceGenerating(true);
    onSavingChange?.(true);
    try {
      await persistEvidenceInvoice();
      const pdf = await generateEvidenceInvoicePdf(leadId);
      setEvidenceInvoice((prev) =>
        prev
          ? {
              ...prev,
              evidenceInvoicePdfKey: pdf.key,
              evidenceInvoicePdfUrl: pdf.url,
            }
          : prev
      );
      toast.success("Evidence Invoice PDF generated");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to generate Evidence Invoice PDF"
      );
    } finally {
      setEvidenceInvoiceGenerating(false);
      onSavingChange?.(false);
    }
  };

  /**
   * Persist current draft and ensure a PDF exists. Mirrors the follow-up
   * invoice `ensurePdf` pattern: always save first, only generate when no
   * PDF is on record. Returns the PDF URL or null on error.
   */
  const ensureEvidenceInvoicePdf = async (): Promise<{
    url: string;
    filename: string;
  } | null> => {
    if (!leadId) return null;
    try {
      const saved = await persistEvidenceInvoice();
      if (!saved) return null;
      let pdfUrl = saved.evidenceInvoicePdfUrl;
      if (!saved.evidenceInvoicePdfKey || !pdfUrl) {
        const pdf = await generateEvidenceInvoicePdf(leadId);
        pdfUrl = pdf.url;
        setEvidenceInvoice((prev) =>
          prev
            ? {
                ...prev,
                evidenceInvoicePdfKey: pdf.key,
                evidenceInvoicePdfUrl: pdf.url,
              }
            : prev
        );
      }
      return pdfUrl
        ? { url: pdfUrl, filename: "Evidence Invoice.pdf" }
        : null;
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to prepare Evidence Invoice PDF"
      );
      return null;
    }
  };

  const handleDownloadEvidenceInvoicePdf = async () => {
    if (!leadId || evidenceInvoiceDownloading || evidenceInvoiceGenerating) {
      return;
    }
    setEvidenceInvoiceDownloading(true);
    onSavingChange?.(true);
    try {
      const pdf = await ensureEvidenceInvoicePdf();
      if (!pdf) return;
      const result = await downloadFileFromUrlWithFallback(
        pdf.url,
        pdf.filename
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Download failed"
      );
    } finally {
      setEvidenceInvoiceDownloading(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xix) Payment from Client for Evidence ─────────

  const handleEvidencePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !evidenceInvoice) return;
    const apps = (evidenceInvoice.tmInvoiceRows ?? []).map((r) => ({
      tmNo: r.tmNo,
      collectedAmount:
        Number(evidencePaymentRows[r.tmNo] ?? r.collectedAmount ?? 0) || 0,
    }));
    setRecordingEvidencePayment(true);
    onSavingChange?.(true);
    try {
      const updated = await saveEvidenceCollectedAmount(leadId, apps);
      setEvidenceInvoice(updated);
      // Send payment message via WhatsApp (mirrors Counter Payment flow not needed —
      // we keep it minimal: save collected then advance). User can also resend separately.
      toast.success("Payment recorded");
      setOpposedSubStep("stamp-vendor-evidence");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record payment"
      );
    } finally {
      setRecordingEvidencePayment(false);
      onSavingChange?.(false);
    }
  };

  const handleSendEvidencePaymentMessage = async () => {
    if (!leadId) return;
    onSavingChange?.(true);
    try {
      await sendEvidenceInvoicePaymentMessage(leadId);
      toast.success("Payment message sent to client");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send payment message"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xx) Stamp Paper details to Vendor for Evidence ─────────

  const persistStampPaperEvidence =
    async (): Promise<StampPaperForEvidenceStatus | null> => {
      if (!leadId || !stampPaperEvidence?.applications?.length) return null;
      const apps = stampPaperEvidence.applications.map((r) => {
        const draft = stampPaperEvidenceDraft[r.tmNo] ?? {
          stampPapersForEvidence: "",
          stampVendorNameForEvidence: "",
          stampVendorPhoneForEvidence: "",
        };
        return {
          tmNo: r.tmNo,
          stampPapersForEvidence: Number(draft.stampPapersForEvidence) || 0,
          stampVendorNameForEvidence: draft.stampVendorNameForEvidence.trim(),
          stampVendorPhoneForEvidence: draft.stampVendorPhoneForEvidence.trim(),
        };
      });
      const updated = await saveStampPaperForEvidenceDetails(leadId, apps);
      setStampPaperEvidence(updated);
      return updated;
    };

  const handleStampPaperEvidenceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !opposedSelectedTm) return;
    if (!stampPaperEvidenceDraft[opposedSelectedTm.tmNo]) {
      toast.error("Stamp paper details not loaded yet");
      return;
    }
    // Save covers every opposed application at once, so validate them all — not just the one on
    // screen — and name the incomplete ones. See validateStampPaperEvidenceDrafts.
    const validationError = validateStampPaperEvidenceDrafts(
      stampPaperEvidence?.applications ?? [],
      stampPaperEvidenceDraft,
      opposedSelectedTm.tmNo
    );
    if (validationError) {
      toast.error(validationError);
      return;
    }
    onSavingChange?.(true);
    try {
      await persistStampPaperEvidence();
      if (hasSentStampPaperEvidence) {
        toast.success("Stamp Paper details saved");
        setOpposedSubStep("stamp-from-vendor-evidence");
        return;
      }
      const preview = await getStampPaperForEvidenceMessagePreview(leadId).catch(() => ({ message: "" }));
      setStampEvidencePreviewMessage(preview.message ?? "");
      setStampEvidenceConfirmMode("send");
      setShowStampEvidenceConfirmModal(true);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to prepare Stamp Paper message preview"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleConfirmStampPaperEvidenceSend = async () => {
    if (!leadId) return;
    onSavingChange?.(true);
    try {
      await sendStampPaperForEvidenceToVendor(leadId);
      setHasSentStampPaperEvidence(true);
      if (stampEvidenceConfirmMode === "send") {
        toast.success("Stamp Paper details sent to vendor");
        setOpposedSubStep("stamp-from-vendor-evidence");
      } else {
        toast.success("Stamp Paper details resent to vendor");
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Stamp Paper details to vendor"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendStampPaperEvidence = async () => {
    if (!leadId || resendingStampPaperEvidence) return;
    // Resend re-persists the whole applications[] too, so it needs the same all-rows guard.
    const validationError = validateStampPaperEvidenceDrafts(
      stampPaperEvidence?.applications ?? [],
      stampPaperEvidenceDraft,
      opposedSelectedTm?.tmNo ?? null
    );
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setResendingStampPaperEvidence(true);
    onSavingChange?.(true);
    try {
      await persistStampPaperEvidence();
      const preview = await getStampPaperForEvidenceMessagePreview(leadId).catch(() => ({ message: "" }));
      setStampEvidencePreviewMessage(preview.message ?? "");
      setStampEvidenceConfirmMode("resend");
      setShowStampEvidenceConfirmModal(true);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Stamp Paper details"
      );
    } finally {
      setResendingStampPaperEvidence(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxi) Stamp Paper from Vendor for Evidence ─────────

  const handleStampPaperFromVendorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !opposedSelectedTm) return;
    const draft = stampPaperFromVendorDraft[opposedSelectedTm.tmNo];
    if (!draft || draft.paidToVendorForEvidence == null) {
      toast.error(
        `Pick YES or NO for Paid to Vendor for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    onSavingChange?.(true);
    try {
      const items = [
        {
          tmNo: opposedSelectedTm.tmNo,
          paidToVendorForEvidence: draft.paidToVendorForEvidence,
          ...(draft.amountPayableToStampVendorForEvidence
            ? {
                amountPayableToStampVendorForEvidence:
                  Number(draft.amountPayableToStampVendorForEvidence) || 0,
              }
            : {}),
        },
      ];
      const updated = await saveStampPaperPaymentToVendorForEvidence(
        leadId,
        items
      );
      setStampPaperEvidence(updated);
      toast.success("Stamp Paper payment to Vendor saved");
      setOpposedSubStep("documents-from-client-evidence");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Stamp Paper payment"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxii) Documents from Client for Evidence ─────────

  const handleDocumentsFromClientEvidenceSubmit = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();
    if (!leadId || !opposedSelectedTm) return;
    const value = documentsFromClientEvidenceDraft[opposedSelectedTm.tmNo];
    if (!value || Number(value) < 0) {
      toast.error(
        `Enter No. of Exhibits for Evidence for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    onSavingChange?.(true);
    try {
      const updated = await saveDocumentsFromClientForEvidence(leadId, [
        {
          tmNo: opposedSelectedTm.tmNo,
          noOfExhibitsForEvidence: Number(value) || 0,
        },
      ]);
      setDocumentsFromClientEvidence(updated);
      toast.success("Documents from Client for Evidence recorded");
      setOpposedSubStep("preparation-evidence-affidavit");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Documents from Client for Evidence"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxiii) Preparation of Evidence Affidavit ─────────

  const persistEvidenceAffidavit = async (): Promise<void> => {
    if (!leadId || !evidenceAffidavit) return;
    const apps = (evidenceAffidavit.evidenceAffidavits ?? []).map((a) => {
      const d = evidenceAffidavitPerTm[a.tmNo];
      const replies = (d?.paragraphWiseReplies ?? []).filter(
        (p) => (p.heading ?? "").trim() || (p.text ?? "").trim()
      );
      return {
        tmNo: a.tmNo,
        noOfParagraphsInOpponentIntro: d?.noOfParagraphsInOpponentIntro
          ? Number(d.noOfParagraphsInOpponentIntro) || null
          : null,
        ...(replies.length ? { paragraphWiseReplies: replies } : {}),
        ...(d?.nameOfOpponentsAttorney
          ? { nameOfOpponentsAttorney: d.nameOfOpponentsAttorney }
          : {}),
        ...(d?.addressOfOpponentsAttorney
          ? { addressOfOpponentsAttorney: d.addressOfOpponentsAttorney }
          : {}),
        ...(d?.emailOfOpponentsAttorney
          ? { emailOfOpponentsAttorney: d.emailOfOpponentsAttorney }
          : {}),
        ...(d?.dateOfEvidenceAffidavit
          ? { dateOfEvidenceAffidavit: d.dateOfEvidenceAffidavit }
          : {}),
      };
    });
    await saveEvidenceAffidavit(leadId, apps);
  };

  const handleEvidenceAffidavitSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !evidenceAffidavit) return;
    onSavingChange?.(true);
    try {
      await persistEvidenceAffidavit();
      const updated = await getEvidenceAffidavitStatus(leadId);
      setEvidenceAffidavit(updated);
      toast.success("Evidence Affidavit saved");
      setOpposedSubStep("signature-evidence-affidavit");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Evidence Affidavit"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleGenerateEvidenceAffidavitPdf = async (
    app: EvidenceAffidavitItem
  ) => {
    if (!leadId || evidenceAffidavitGenerating) return;
    setEvidenceAffidavitGenerating(true);
    onSavingChange?.(true);
    try {
      await persistEvidenceAffidavit();
      const pdf = await generateEvidenceAffidavitPdf(
        leadId,
        app.ackApplicationId
      );
      setEvidenceAffidavit((prev) =>
        prev
          ? {
              ...prev,
              evidenceAffidavits: (prev.evidenceAffidavits ?? []).map((x) =>
                x.tmNo === app.tmNo
                  ? {
                      ...x,
                      evidenceAffidavitPdfKey: pdf.key,
                      evidenceAffidavitPdfUrl: pdf.url,
                    }
                  : x
              ),
            }
          : prev
      );
      toast.success("Evidence Affidavit PDF generated");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to generate Evidence Affidavit PDF"
      );
    } finally {
      setEvidenceAffidavitGenerating(false);
      onSavingChange?.(false);
    }
  };

  const handleDownloadEvidenceAffidavitPdf = async (
    app: EvidenceAffidavitItem
  ) => {
    if (!app.evidenceAffidavitPdfUrl || evidenceAffidavitDownloading) return;
    setEvidenceAffidavitDownloading(true);
    try {
      const filename = `Evidence_Affidavit_${app.tmNo || "affidavit"}.pdf`;
      const result = await downloadFileFromUrlWithFallback(
        app.evidenceAffidavitPdfUrl,
        filename
      );
      if (result === "local") {
        toast.success("Download started");
      } else {
        toast.success("Opened in new tab");
      }
    } catch {
      toast.error("Failed to download Evidence Affidavit PDF");
    } finally {
      setEvidenceAffidavitDownloading(false);
    }
  };

  // ───────── 4A(xxiv) Signature from Client for Affidavit ─────────

  const handleSignatureAffidavitSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasRecordedSignatureAffidavit) {
      setOpposedSubStep("notarisation-evidence");
      return;
    }
    setRecordingSignatureAffidavit(true);
    onSavingChange?.(true);
    try {
      await recordSignatureFromClientForAffidavit(leadId);
      setHasRecordedSignatureAffidavit(true);
      toast.success("Signature from Client for Affidavit recorded");
      setOpposedSubStep("notarisation-evidence");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to record Signature from Client for Affidavit"
      );
    } finally {
      setRecordingSignatureAffidavit(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxv) Notarisation of Documents for Evidence ─────────

  // Debounced autosave so edits to amount / paid persist on browser refresh
  // even when the user hasn't clicked "Save & Continue" (which advances steps).
  const scheduleNotarisationAutoSave = (tmNo: string) => {
    if (!leadId) return;
    const existing = notarisationAutoSaveTimers.current[tmNo];
    if (existing) clearTimeout(existing);
    notarisationAutoSaveTimers.current[tmNo] = setTimeout(() => {
      void (async () => {
        const d = notarisationDraftRef.current[tmNo];
        if (!d) return;
        const amt = (d.amountPayableToNotaryForEvidence ?? "").trim();
        const hasAmount = amt !== "" && Number.isFinite(Number(amt));
        const hasPaid = d.paidToNotaryForEvidence != null;
        if (!hasAmount && !hasPaid) return;
        try {
          const updated = await saveNotaryPaymentForEvidence(leadId, [
            {
              tmNo,
              ...(hasAmount
                ? { amountPayableToNotaryForEvidence: Number(amt) }
                : {}),
              ...(hasPaid
                ? { paidToNotaryForEvidence: d.paidToNotaryForEvidence! }
                : {}),
            },
          ]);
          setNotarisationEvidence(updated);
        } catch {
          // Silent — explicit Save & Continue surfaces errors with toast.
        }
      })();
    }, 800);
  };

  useEffect(() => {
    const timers = notarisationAutoSaveTimers.current;
    return () => {
      Object.values(timers).forEach((t) => clearTimeout(t));
      notarisationAutoSaveTimers.current = {};
    };
  }, [leadId]);

  const handleNotarisationEvidenceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !opposedSelectedTm) return;
    const draft = notarisationEvidenceDraft[opposedSelectedTm.tmNo];
    if (!draft || draft.paidToNotaryForEvidence == null) {
      toast.error(
        `Pick YES or NO for Paid to Notary for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    onSavingChange?.(true);
    try {
      const apps = [
        {
          tmNo: opposedSelectedTm.tmNo,
          paidToNotaryForEvidence: draft.paidToNotaryForEvidence,
          ...(draft.amountPayableToNotaryForEvidence
            ? {
                amountPayableToNotaryForEvidence:
                  Number(draft.amountPayableToNotaryForEvidence) || 0,
              }
            : {}),
        },
      ];
      const updated = await saveNotaryPaymentForEvidence(leadId, apps);
      setNotarisationEvidence(updated);
      toast.success("Notarisation for Evidence saved");
      setOpposedSubStep("scanning-evidence");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Notarisation for Evidence"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxvi) Scanning of Documents for Evidence ─────────

  const handleScanningEvidenceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasRecordedScanningEvidence) {
      setOpposedSubStep("file-evidence-affidavit");
      return;
    }
    setRecordingScanningEvidence(true);
    onSavingChange?.(true);
    try {
      await recordScanningOfDocumentsForEvidence(leadId);
      setHasRecordedScanningEvidence(true);
      toast.success("Scanning of Documents for Evidence recorded");
      setOpposedSubStep("file-evidence-affidavit");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to record Scanning of Documents for Evidence"
      );
    } finally {
      setRecordingScanningEvidence(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxvii) File Evidence Affidavit ─────────

  const handleFileEvidenceAffidavitSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !leadId ||
      !fileEvidenceAffidavit ||
      !fileEvidenceAffidavit.acknowledgementId ||
      !opposedSelectedTm
    )
      return;
    const draft = fileEvidenceAffidavitPerTm[opposedSelectedTm.tmNo];
    const existing = (fileEvidenceAffidavit.applications ?? []).find(
      (a) => a.tmNo === opposedSelectedTm.tmNo
    );
    if (!draft?.filingDate) {
      toast.error(
        `Enter Date of Filing Evidence Affidavit for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    if (!draft.pendingFile && !existing?.filedEvidenceAffidavitPdfKey) {
      toast.error(
        `Upload filed Evidence Affidavit PDF for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    onSavingChange?.(true);
    try {
      let fileKey = existing?.filedEvidenceAffidavitPdfKey ?? null;
      if (draft.pendingFile) {
        const reason = validateUploadFile(draft.pendingFile);
        if (reason) {
          toast.error(reason);
          onSavingChange?.(false);
          return;
        }
        const { urls } = await generateFiledEvidenceAffidavitPresignedUrls(
          leadId,
          fileEvidenceAffidavit.acknowledgementId,
          [
            {
              tmNo: opposedSelectedTm.tmNo,
              fileName: draft.pendingFile.name,
              mimeType: draft.pendingFile.type || "application/pdf",
            },
          ]
        );
        const row = urls?.[0];
        if (!row?.presignedUrl || !row?.fileKey) {
          throw new Error("Upload URL not returned by server");
        }
        const put = await fetch(row.presignedUrl, {
          method: "PUT",
          body: draft.pendingFile,
          headers: {
            "Content-Type": draft.pendingFile.type || "application/pdf",
          },
        });
        if (!put.ok) {
          throw new Error(`Upload failed: ${put.status} ${put.statusText}`);
        }
        fileKey = row.fileKey;
      }
      const overrideYmd =
        fileEvidenceAffidavitLastDateDraft[opposedSelectedTm.tmNo] ?? "";
      const lastDateForRejoinder = overrideYmd
        ? overrideYmd
        : computeRejoinderLastDate(draft.filingDate)
          ? toLocalYmd(computeRejoinderLastDate(draft.filingDate) as Date)
          : null;
      await saveFileEvidenceAffidavit(
        leadId,
        fileEvidenceAffidavit.acknowledgementId,
        [
          {
            tmNo: opposedSelectedTm.tmNo,
            dateOfFilingEvidenceAffidavit: draft.filingDate,
            ...(lastDateForRejoinder
              ? { lastDateForRejoinderByOpponent: lastDateForRejoinder }
              : {}),
            ...(fileKey ? { filedEvidenceAffidavitPdfKey: fileKey } : {}),
          },
        ]
      );
      const refreshed = await getFileEvidenceAffidavitStatus(leadId);
      setFileEvidenceAffidavit(refreshed);
      const map: Record<
        string,
        { filingDate: string; pendingFile: File | null }
      > = {};
      const lastDateMap: Record<string, string> = {};
      for (const a of refreshed.applications ?? []) {
        const parts = parseDateParts(a.dateOfFilingEvidenceAffidavit);
        map[a.tmNo] = {
          filingDate: parts ? partsToIsoYmd(parts) : "",
          pendingFile: null,
        };
        const lastParts = parseDateParts(a.lastDateForRejoinderByOpponent);
        lastDateMap[a.tmNo] = lastParts ? partsToIsoYmd(lastParts) : "";
      }
      setFileEvidenceAffidavitPerTm(map);
      setFileEvidenceAffidavitLastDateDraft(lastDateMap);
      toast.success("File Evidence Affidavit saved");
      setOpposedSubStep("inform-filing-evidence");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save File Evidence Affidavit"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxviii) Inform Client about Filing of Evidence ─────────

  const handleInformFilingEvidenceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentInformFilingEvidence) {
      setOpposedSubStep("download-rejoinder-affidavit");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendInformClientFilingOfEvidenceWhatsApp(leadId);
      setHasSentInformFilingEvidence(true);
      toast.success("Inform Client about Filing of Evidence sent");
      setOpposedSubStep("download-rejoinder-affidavit");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Inform Client about Filing of Evidence"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendInformFilingEvidence = async () => {
    if (!leadId || resendingInformFilingEvidence) return;
    setResendingInformFilingEvidence(true);
    onSavingChange?.(true);
    try {
      await sendInformClientFilingOfEvidenceWhatsApp(leadId);
      toast.success("Inform Client about Filing of Evidence resent");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Inform Client about Filing of Evidence"
      );
    } finally {
      setResendingInformFilingEvidence(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxix) Download Rejoinder Affidavit ─────────

  const handleDownloadRejoinderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !leadId ||
      !downloadRejoinder?.acknowledgementId ||
      !opposedSelectedTm
    )
      return;
    const draft = downloadRejoinderPerTm[opposedSelectedTm.tmNo];
    const existing = (downloadRejoinder.applications ?? []).find(
      (a) => a.tmNo === opposedSelectedTm.tmNo
    );
    if (!draft?.rejoinderDate) {
      toast.error(
        `Enter Date of Rejoinder by Opponent for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    if (!draft.pendingFile && !existing?.rejoinderAffidavitPdfKey) {
      toast.error(
        `Upload Rejoinder Affidavit PDF for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    onSavingChange?.(true);
    try {
      let fileKey = existing?.rejoinderAffidavitPdfKey ?? null;
      if (draft.pendingFile) {
        const reason = validateUploadFile(draft.pendingFile);
        if (reason) {
          toast.error(reason);
          onSavingChange?.(false);
          return;
        }
        const { urls } = await generateRejoinderAffidavitPresignedUrls(
          leadId,
          downloadRejoinder.acknowledgementId,
          [
            {
              tmNo: opposedSelectedTm.tmNo,
              fileName: draft.pendingFile.name,
              mimeType: draft.pendingFile.type || "application/pdf",
            },
          ]
        );
        const row = urls?.[0];
        if (!row?.presignedUrl || !row?.fileKey) {
          throw new Error("Upload URL not returned by server");
        }
        const put = await fetch(row.presignedUrl, {
          method: "PUT",
          body: draft.pendingFile,
          headers: {
            "Content-Type": draft.pendingFile.type || "application/pdf",
          },
        });
        if (!put.ok) {
          throw new Error(`Upload failed: ${put.status} ${put.statusText}`);
        }
        fileKey = row.fileKey;
      }
      await saveDownloadRejoinderAffidavit(
        leadId,
        downloadRejoinder.acknowledgementId,
        [
          {
            tmNo: opposedSelectedTm.tmNo,
            dateOfRejoinderByOpponent: draft.rejoinderDate,
            ...(fileKey ? { rejoinderAffidavitPdfKey: fileKey } : {}),
          },
        ]
      );
      const refreshed = await getDownloadRejoinderAffidavitStatus(leadId);
      setDownloadRejoinder(refreshed);
      const map: Record<
        string,
        { rejoinderDate: string; pendingFile: File | null }
      > = {};
      for (const a of refreshed.applications ?? []) {
        const parts = parseDateParts(a.dateOfRejoinderByOpponent);
        map[a.tmNo] = {
          rejoinderDate: parts ? partsToIsoYmd(parts) : "",
          pendingFile: null,
        };
      }
      setDownloadRejoinderPerTm(map);
      toast.success("Download Rejoinder Affidavit saved");
      setOpposedSubStep("status-opposition-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Download Rejoinder Affidavit"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxx) Status details of Opposition Hearing ─────────

  const handleOppositionHearingStatusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !opposedSelectedTm) return;
    const draft = oppositionHearingPerTm[opposedSelectedTm.tmNo];
    const existing = (oppositionHearing?.applications ?? []).find(
      (a) => a.tmNo === opposedSelectedTm.tmNo
    );
    if (!draft?.hearingDate) {
      toast.error(
        `Enter Date of Opposition Hearing for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    onSavingChange?.(true);
    try {
      let fileKey: string | null =
        existing?.oppositionHearingNoticeFileKey ?? null;
      let fileName: string | null =
        existing?.oppositionHearingNoticeFilename ?? null;
      if (draft.pendingFile) {
        const reason = validateUploadFile(draft.pendingFile);
        if (reason) {
          toast.error(reason);
          onSavingChange?.(false);
          return;
        }
        fileKey = await uploadAcknowledgementPdf(
          draft.pendingFile,
          OPPOSITION_HEARING_NOTICE_FOLDER
        );
        fileName = draft.pendingFile.name;
      }
      const apps = [
        {
          tmNo: opposedSelectedTm.tmNo,
          dateOfOppositionHearing: draft.hearingDate,
          ...(fileKey
            ? {
                oppositionHearingNoticeFileKey: fileKey,
                oppositionHearingNoticeFilename: fileName ?? "",
              }
            : {}),
        },
      ];
      const updated = await setOppositionHearingStatusDetails(leadId, apps);
      setOppositionHearing(updated);
      // Merge saved hearing data back into perTm while preserving blank
      // entries for counter TMs that haven't been saved yet.
      setOppositionHearingPerTm((prev) => {
        const next = { ...prev };
        for (const a of updated.applications ?? []) {
          const parts = parseDateParts(a.dateOfOppositionHearing);
          next[a.tmNo] = {
            hearingDate: parts ? partsToIsoYmd(parts) : "",
            pendingFile: null,
          };
        }
        return next;
      });
      toast.success("Opposition Hearing status saved");
      setOpposedSubStep("notice-opposition-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Opposition Hearing status"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxxi) Notice to Client for Opposition Hearing ─────────

  const oppositionHearingTmsWithPdf = (
    oppositionHearing?.applications ?? []
  ).filter(
    (a) => a.oppositionHearingNoticeFileKey && a.oppositionHearingNoticeFilename
  );
  const canSendOppositionHearingNotice = oppositionHearingTmsWithPdf.length > 0;

  const handleOppositionHearingNoticeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentOppositionHearingNotice) {
      setOpposedSubStep("inform-opposition-hearing");
      return;
    }
    if (!canSendOppositionHearingNotice) {
      toast.error(
        "Upload Opposition Hearing Notice PDF in Status details for Opposition Hearing first"
      );
      return;
    }
    setOppositionHearingNoticeSendConfirmOpen(true);
  };

  const performOppositionHearingNoticeSend = async () => {
    if (!leadId) return;
    const hasEmail = !!oppositionHearingNoticePreview?.recipientEmail;
    setOppositionHearingNoticeSending(true);
    onSavingChange?.(true);
    try {
      const result = await sendOppositionHearingNoticeToClient(
        leadId,
        undefined,
        { sendWhatsApp: true, sendEmail: hasEmail }
      );
      setHasSentOppositionHearingNotice(true);
      setOppositionHearingNoticeSendConfirmOpen(false);
      if (result.errors?.email) toast.error(result.errors.email);
      if (result.errors?.whatsapp) toast.error(result.errors.whatsapp);
      if (!result.errors?.email && !result.errors?.whatsapp) {
        toast.success(
          hasEmail
            ? "Opposition Hearing Notice forwarded to client (WhatsApp & Email)"
            : "Opposition Hearing Notice forwarded to client"
        );
      }
      setOpposedSubStep("inform-opposition-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to forward Opposition Hearing Notice"
      );
    } finally {
      setOppositionHearingNoticeSending(false);
      onSavingChange?.(false);
    }
  };

  const handleResendOppositionHearingNotice = async () => {
    if (
      !leadId ||
      resendingOppositionHearingNotice ||
      !canSendOppositionHearingNotice
    )
      return;
    const hasEmail = !!oppositionHearingNoticePreview?.recipientEmail;
    setResendingOppositionHearingNotice(true);
    onSavingChange?.(true);
    try {
      const result = await sendOppositionHearingNoticeToClient(
        leadId,
        undefined,
        { sendWhatsApp: true, sendEmail: hasEmail }
      );
      setOppositionHearingNoticeResendConfirmOpen(false);
      if (result.errors?.email) toast.error(result.errors.email);
      if (result.errors?.whatsapp) toast.error(result.errors.whatsapp);
      if (!result.errors?.email && !result.errors?.whatsapp) {
        toast.success(
          hasEmail
            ? "Opposition Hearing Notice resent to client (WhatsApp & Email)"
            : "Opposition Hearing Notice resent to client"
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Opposition Hearing Notice"
      );
    } finally {
      setResendingOppositionHearingNotice(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxxii) Inform Client about Opposition Hearing ─────────

  const handleOppositionHearingInformSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentOppositionHearingInform) {
      setOpposedSubStep("invoice-opposition-hearing");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendInformClientOppositionHearing(leadId);
      setHasSentOppositionHearingInform(true);
      toast.success("Inform Client about Opposition Hearing sent");
      setOpposedSubStep("invoice-opposition-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Inform Client about Opposition Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendOppositionHearingInform = async () => {
    if (!leadId || resendingOppositionHearingInform) return;
    setResendingOppositionHearingInform(true);
    onSavingChange?.(true);
    try {
      await sendInformClientOppositionHearing(leadId);
      toast.success("Inform Client about Opposition Hearing resent");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Inform Client about Opposition Hearing"
      );
    } finally {
      setResendingOppositionHearingInform(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxxiii) Invoice to Client for Opposition Hearing ─────────

  const persistOppositionHearingInvoice =
    async (): Promise<OppositionHearingInvoiceStatus | null> => {
      if (!leadId || !oppositionHearingInvoice) return null;
      const apps = (oppositionHearingInvoice.tmInvoiceRows ?? []).map((row) => {
        const draft = oppositionHearingInvoiceRows[row.tmNo] ?? {
          invoiceAmount: String(row.invoiceAmount ?? 0),
          invoiceDate: "",
        };
        return {
          tmNo: row.tmNo,
          invoiceAmount: Number(draft.invoiceAmount) || 0,
          invoiceDate: draft.invoiceDate || row.invoiceDate || "",
          includeInvoiceNo: row.includeInvoiceNo ?? true,
        };
      });
      if (apps.some((a) => !a.invoiceDate)) {
        // Default missing dates to today (DD-MM-YYYY)
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const todayYmd = `${yyyy}-${mm}-${dd}`;
        for (const a of apps) {
          if (!a.invoiceDate) a.invoiceDate = todayYmd;
        }
      }
      const updated = await saveOppositionHearingInvoice(leadId, apps);
      setOppositionHearingInvoice(updated);
      return updated;
    };

  const handleOppositionHearingInvoiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentOppositionHearingInvoice) {
      setOpposedSubStep("call-opposition-hearing");
      return;
    }
    if (!oppositionHearingInvoice) {
      toast.error("Invoice not loaded yet");
      return;
    }
    onSavingChange?.(true);
    try {
      const saved = await persistOppositionHearingInvoice();
      if (!saved || (saved.invoiceAmount ?? 0) <= 0) {
        toast.error("Enter at least one TM with a non-zero Invoice Amount");
        return;
      }
      if (!saved.oppositionHearingInvoicePdfKey) {
        await generateOppositionHearingInvoicePdf(leadId);
      }
      await sendOppositionHearingInvoiceToClient(leadId, { sendWhatsApp: true });
      setHasSentOppositionHearingInvoice(true);
      toast.success("Opposition Hearing Invoice sent to client");
      setOpposedSubStep("call-opposition-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Opposition Hearing Invoice"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleGenerateOppositionHearingInvoicePdf = async () => {
    if (!leadId || oppositionHearingInvoiceGenerating) return;
    setOppositionHearingInvoiceGenerating(true);
    onSavingChange?.(true);
    try {
      await persistOppositionHearingInvoice();
      const pdf = await generateOppositionHearingInvoicePdf(leadId);
      setOppositionHearingInvoice((prev) =>
        prev
          ? {
              ...prev,
              oppositionHearingInvoicePdfKey: pdf.key,
              oppositionHearingInvoicePdfUrl: pdf.url,
            }
          : prev
      );
      toast.success("Opposition Hearing Invoice PDF generated");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to generate Opposition Hearing Invoice PDF"
      );
    } finally {
      setOppositionHearingInvoiceGenerating(false);
      onSavingChange?.(false);
    }
  };

  const handleResendOppositionHearingInvoice = async () => {
    if (!leadId || resendingOppositionHearingInvoice) return;
    setResendingOppositionHearingInvoice(true);
    onSavingChange?.(true);
    try {
      const tmNo = opposedSelectedTm?.tmNo;
      await sendOppositionHearingInvoiceToClient(leadId, {
        sendWhatsApp: true,
        ...(tmNo ? { tmNos: [tmNo] } : {}),
      });
      setOppositionHearingInvoiceResendConfirmOpen(false);
      toast.success("Opposition Hearing Invoice resent to client");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Opposition Hearing Invoice"
      );
    } finally {
      setResendingOppositionHearingInvoice(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxxiv) Call Client for Opposition Hearing ─────────

  const handleOppositionHearingCallSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasRecordedOppositionHearingCall) {
      setOpposedSubStep("confirmation-opposition-hearing");
      return;
    }
    setRecordingOppositionHearingCall(true);
    onSavingChange?.(true);
    try {
      await recordCallClientOppositionHearing(leadId);
      setHasRecordedOppositionHearingCall(true);
      toast.success("Call Client for Opposition Hearing recorded");
      setOpposedSubStep("confirmation-opposition-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to record Call Client for Opposition Hearing"
      );
    } finally {
      setRecordingOppositionHearingCall(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxxv) Confirmation from Client for Opposition Hearing ─────────

  const handleOppositionHearingConfirmationSubmit = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();
    if (!leadId || !oppositionHearingConfirmation) return;
    const apps = oppositionHearingConfirmation.applications
      .filter((a) => oppositionHearingConfirmationDraft[a.tmNo] != null)
      .map((a) => ({
        tmNo: a.tmNo,
        clientConfirmation: oppositionHearingConfirmationDraft[
          a.tmNo
        ] as OppositionHearingClientConfirmation,
      }));
    if (apps.length === 0) {
      toast.error("Pick YES, NO or POSTPONE for at least one TM");
      return;
    }
    onSavingChange?.(true);
    try {
      const updated = await saveOppositionHearingConfirmation(leadId, apps);
      setOppositionHearingConfirmation(updated);
      toast.success("Confirmation from Client for Opposition Hearing saved");
      // Backend enforces a single confirmation value per save, so derive the
      // next substep from the decision the user just submitted.
      // YES → proceed with hearing flow (payment from client).
      // POSTPONE → TM-M invoice sub-flow.
      // NO → skip the hearing sub-flow and jump to the last step of this
      //      stage: 4A(li) Order of Opposition Hearing to Client.
      const decision = apps[0]?.clientConfirmation;
      if (decision === "YES") {
        setOpposedSubStep("payment-opposition-hearing");
      } else if (decision === "POSTPONE") {
        setOpposedSubStep("invoice-tmm-before-opp-hearing");
      } else if (decision === "NO") {
        setOpposedSubStep("order-of-opp-hearing");
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Confirmation from Client"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxxvi) Invoice for TM-M before Opposition Hearing ─────────

  const persistTmMBeforeHearingInvoice =
    async (): Promise<TmMBeforeOppositionHearingInvoiceStatus | null> => {
      if (!leadId) return null;
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const todayYmd = `${yyyy}-${mm}-${dd}`;
      const trimmedAmount = tmMBeforeHearingInvoiceAmountDraft.trim();
      const parsedAmount = trimmedAmount === "" ? NaN : Number(trimmedAmount);
      const updated = await saveTmMBeforeOppositionHearingInvoice(leadId, {
        invoiceDate: tmMBeforeHearingInvoiceDateDraft || todayYmd,
        ...(Number.isFinite(parsedAmount) && parsedAmount >= 0
          ? { invoiceAmount: parsedAmount }
          : {}),
      });
      setTmMBeforeHearingInvoice(updated);
      setTmMBeforeHearingInvoiceDateDraft(updated.invoiceDate ?? "");
      setTmMBeforeHearingInvoiceAmountDraft(
        updated.invoiceAmount != null
          ? String(Math.round(Number(updated.invoiceAmount)))
          : ""
      );
      return updated;
    };

  const handleTmMBeforeHearingInvoiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentTmMBeforeHearingInvoice) {
      setOpposedSubStep("payment-tmm-before-opp-hearing");
      return;
    }
    if (!tmMBeforeHearingInvoice?.isActive) {
      toast.error(
        "No applications are eligible. Mark POSTPONE in the previous step first."
      );
      return;
    }
    onSavingChange?.(true);
    try {
      const saved = await persistTmMBeforeHearingInvoice();
      if (!saved || (saved.invoiceAmount ?? 0) <= 0) {
        toast.error("Invoice Amount is not available");
        return;
      }
      if (!saved.tmMBeforeOppositionHearingInvoicePdfKey) {
        const pdf = await generateTmMBeforeOppositionHearingInvoicePdf(leadId);
        setTmMBeforeHearingInvoice((prev) =>
          prev
            ? {
                ...prev,
                tmMBeforeOppositionHearingInvoicePdfKey: pdf.key,
                tmMBeforeOppositionHearingInvoicePdfUrl: pdf.url,
              }
            : prev
        );
      }
      await sendTmMBeforeOppositionHearingInvoiceToClient(leadId, {
        sendWhatsApp: true,
      });
      setHasSentTmMBeforeHearingInvoice(true);
      toast.success("TM-M before Opposition Hearing Invoice sent to client");
      setOpposedSubStep("payment-tmm-before-opp-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send TM-M before Opposition Hearing Invoice"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleGenerateTmMBeforeHearingInvoicePdf = async () => {
    if (!leadId || tmMBeforeHearingInvoiceGenerating) return;
    setTmMBeforeHearingInvoiceGenerating(true);
    onSavingChange?.(true);
    try {
      await persistTmMBeforeHearingInvoice();
      const pdf = await generateTmMBeforeOppositionHearingInvoicePdf(leadId);
      setTmMBeforeHearingInvoice((prev) =>
        prev
          ? {
              ...prev,
              tmMBeforeOppositionHearingInvoicePdfKey: pdf.key,
              tmMBeforeOppositionHearingInvoicePdfUrl: pdf.url,
            }
          : prev
      );
      toast.success("TM-M before Opposition Hearing Invoice PDF generated");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to generate TM-M before Opposition Hearing Invoice PDF"
      );
    } finally {
      setTmMBeforeHearingInvoiceGenerating(false);
      onSavingChange?.(false);
    }
  };

  const ensureTmMBeforeHearingInvoicePdf = async (): Promise<{
    url: string;
    filename: string;
  } | null> => {
    if (!leadId) return null;
    try {
      const saved = await persistTmMBeforeHearingInvoice();
      if (!saved) return null;
      let pdfUrl = saved.tmMBeforeOppositionHearingInvoicePdfUrl;
      if (!saved.tmMBeforeOppositionHearingInvoicePdfKey || !pdfUrl) {
        const pdf = await generateTmMBeforeOppositionHearingInvoicePdf(leadId);
        pdfUrl = pdf.url;
        setTmMBeforeHearingInvoice((prev) =>
          prev
            ? {
                ...prev,
                tmMBeforeOppositionHearingInvoicePdfKey: pdf.key,
                tmMBeforeOppositionHearingInvoicePdfUrl: pdf.url,
              }
            : prev
        );
      }
      return pdfUrl
        ? {
            url: pdfUrl,
            filename: "Invoice for TM-M before Opposition Hearing.pdf",
          }
        : null;
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to prepare TM-M before Opposition Hearing Invoice PDF"
      );
      return null;
    }
  };

  const handleDownloadTmMBeforeHearingInvoicePdf = async () => {
    if (
      !leadId ||
      tmMBeforeHearingInvoiceDownloading ||
      tmMBeforeHearingInvoiceGenerating
    ) {
      return;
    }
    setTmMBeforeHearingInvoiceDownloading(true);
    onSavingChange?.(true);
    try {
      const pdf = await ensureTmMBeforeHearingInvoicePdf();
      if (!pdf) return;
      const result = await downloadFileFromUrlWithFallback(
        pdf.url,
        pdf.filename
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Download failed"
      );
    } finally {
      setTmMBeforeHearingInvoiceDownloading(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxxvii) Payment from Client for TM-M before Opposition Hearing ─────────

  const handleTmMBeforeHearingPaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    const amount = Number(tmMBeforeHearingCollectedAmountDraft);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error("Enter a valid Collected Amount");
      return;
    }
    const savedCollected =
      Number(tmMBeforeHearingInvoice?.collectedAmount ?? 0) || 0;
    const newTotal = savedCollected + amount;
    onSavingChange?.(true);
    try {
      const updated = await saveTmMBeforeOppositionHearingCollectedAmount(
        leadId,
        newTotal
      );
      setTmMBeforeHearingInvoice(updated);
      setTmMBeforeHearingCollectedAmountDraft("");
      toast.success("Payment for TM-M before Opposition Hearing saved");
      setOpposedSubStep("file-tmm-before-opp-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save TM-M before Opposition Hearing Payment"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxxviii) File TM-M before Opposition Hearing ─────────

  const handleTmMBeforeHearingFiledSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !tmMBeforeHearingFiled || !opposedSelectedTm) return;
    const draft = tmMBeforeHearingFiledPerTm[opposedSelectedTm.tmNo];
    const existing = (tmMBeforeHearingFiled.applications ?? []).find(
      (a) => a.tmNo === opposedSelectedTm.tmNo
    );
    if (!draft?.filingDate) {
      toast.error(
        `Enter Date of Filing of TM-M before Opposition Hearing for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    onSavingChange?.(true);
    try {
      let fileKey = existing?.tmMBeforeOppositionHearingFileKey ?? null;
      let fileName = existing?.tmMBeforeOppositionHearingFilename ?? null;
      if (draft.pendingFile) {
        const reason = validateUploadFile(draft.pendingFile);
        if (reason) {
          toast.error(reason);
          onSavingChange?.(false);
          return;
        }
        fileKey = await uploadAcknowledgementPdf(
          draft.pendingFile,
          TM_M_BEFORE_OPPOSITION_HEARING_FILED_FOLDER
        );
        fileName = draft.pendingFile.name;
      }
      const updated = await saveTmMBeforeOppositionHearingFiled(leadId, [
        {
          tmNo: opposedSelectedTm.tmNo,
          dateOfTmMBeforeOppositionHearingFiled: draft.filingDate,
          ...(fileKey
            ? {
                tmMBeforeOppositionHearingFileKey: fileKey,
                tmMBeforeOppositionHearingFilename: fileName ?? "",
              }
            : {}),
        },
      ]);
      setTmMBeforeHearingFiled(updated);
      const map: Record<
        string,
        { filingDate: string; pendingFile: File | null }
      > = {};
      for (const a of updated.applications ?? []) {
        const parts = parseDateParts(a.dateOfTmMBeforeOppositionHearingFiled);
        map[a.tmNo] = {
          filingDate: parts ? partsToIsoYmd(parts) : "",
          pendingFile: null,
        };
      }
      setTmMBeforeHearingFiledPerTm(map);
      toast.success("File TM-M before Opposition Hearing saved");
      setOpposedSubStep("inform-tmm-before-opp-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save File TM-M before Opposition Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xxxix) Inform Client about Filing of TM-M before OH ─────────

  const handleInformTmMBeforeHearingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentInformTmMBeforeHearing) {
      setOpposedSubStep("subsequent-opp-hearings");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendInformClientTmMBeforeOppositionHearingWhatsApp(leadId);
      setHasSentInformTmMBeforeHearing(true);
      toast.success("Inform Client about TM-M before Opposition Hearing sent");
      setOpposedSubStep("subsequent-opp-hearings");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Inform Client about TM-M before Opposition Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendInformTmMBeforeHearing = async () => {
    if (!leadId || resendingInformTmMBeforeHearing) return;
    setResendingInformTmMBeforeHearing(true);
    onSavingChange?.(true);
    try {
      await sendInformClientTmMBeforeOppositionHearingWhatsApp(leadId);
      toast.success(
        "Inform Client about TM-M before Opposition Hearing resent"
      );
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Inform Client about TM-M before Opposition Hearing"
      );
    } finally {
      setResendingInformTmMBeforeHearing(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xl) Payment from Client for Opposition Hearing ─────────

  const handleOppositionHearingPaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    const amount = Number(oppositionHearingPaymentDraft);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error("Enter a valid Collected Amount");
      return;
    }
    onSavingChange?.(true);
    try {
      const updated = await saveOppositionHearingPayment(leadId, amount);
      setOppositionHearingPayment(updated);
      setOppositionHearingPaymentDraft("");
      toast.success("Payment from Client for Opposition Hearing saved");
      setOpposedSubStep("documents-opp-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Opposition Hearing Payment"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xli) Documents from Client for Opposition Hearing ─────────

  const handleDocumentsOppHearingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasRecordedDocumentsOppHearing) {
      setOpposedSubStep("filing-opp-hearing-document");
      return;
    }
    setRecordingDocumentsOppHearing(true);
    onSavingChange?.(true);
    try {
      await progressDocumentsFromClientOppositionHearing(leadId);
      setHasRecordedDocumentsOppHearing(true);
      toast.success(
        "Documents from Client for Opposition Hearing recorded"
      );
      setOpposedSubStep("filing-opp-hearing-document");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to record Documents from Client for Opposition Hearing"
      );
    } finally {
      setRecordingDocumentsOppHearing(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xlii) Filing of Opposition Hearing Document ─────────

  const handleFilingOppHearingDocSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasRecordedFilingOppHearingDoc) {
      setOpposedSubStep("note-on-opp-hearing");
      return;
    }
    setRecordingFilingOppHearingDoc(true);
    onSavingChange?.(true);
    try {
      await progressFilingOppositionHearingDocument(leadId);
      setHasRecordedFilingOppHearingDoc(true);
      toast.success("Filing of Opposition Hearing Document recorded");
      setOpposedSubStep("note-on-opp-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to record Filing of Opposition Hearing Document"
      );
    } finally {
      setRecordingFilingOppHearingDoc(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xliii) Note on Opposition Hearing ─────────

  const handleNoteOnOppHearingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !noteOnOppHearing) return;
    const app = noteOnOppHearing.applications?.[noteOnOppHearingSelectedIdx];
    if (!app) {
      toast.error("No application selected");
      return;
    }
    const draft = noteOnOppHearingDraft[app.ackApplicationId];
    if (!draft) {
      toast.error("Draft not loaded");
      return;
    }
    onSavingChange?.(true);
    try {
      await saveNoteOnOppositionHearing(leadId, {
        ackApplicationId: app.ackApplicationId,
        noteOtherOpposition1: draft.noteOtherOpposition1 || null,
        noteOtherOpposition2: draft.noteOtherOpposition2 || null,
        similarMarkNotes: draft.similarMarkNotes
          .filter((n) => n.mark.trim() && n.tmNo.trim())
          .map((n) => ({
            mark: n.mark,
            tmNo: n.tmNo,
            imageKey: n.imageKey ?? null,
            userDate: n.userDate ?? null,
            goodsServices: n.goodsServices ?? null,
            address: n.address ?? null,
            note1: n.note1 ?? null,
            note2: n.note2 ?? null,
          })),
      });
      toast.success("Note on Opposition Hearing saved");
      // If multiple apps, advance to next index; otherwise move to next sub-step
      const nextIdx = noteOnOppHearingSelectedIdx + 1;
      if (
        noteOnOppHearing.applications &&
        nextIdx < noteOnOppHearing.applications.length
      ) {
        setNoteOnOppHearingSelectedIdx(nextIdx);
      } else {
        setOpposedSubStep("attend-opp-hearing");
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Note on Opposition Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xlv) Inform Client after Opposition Hearing ─────────

  const handleInformAfterOppHearingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentInformAfterOppHearing) {
      setOpposedSubStep(
        hasFileTmMAfterHearing
          ? "invoice-tmm-after-opp-hearing"
          : "order-of-opp-hearing"
      );
      return;
    }
    setInformAfterOppHearingSendConfirmOpen(true);
  };

  const performInformAfterOppHearingSend = async () => {
    if (!leadId || informAfterOppHearingSending) return;
    setInformAfterOppHearingSending(true);
    onSavingChange?.(true);
    try {
      if (informAfterOppHearing?.hasMessagesToSend) {
        await sendAllInformAfterOppositionHearing(leadId);
      }
      await recordInformAfterOppositionHearing(leadId);
      setHasSentInformAfterOppHearing(true);
      setInformAfterOppHearingSendConfirmOpen(false);
      toast.success("Inform Client after Opposition Hearing sent");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Inform Client after Opposition Hearing"
      );
    } finally {
      setInformAfterOppHearingSending(false);
      onSavingChange?.(false);
    }
  };

  const handleResendInformAfterOppHearing = async () => {
    if (!leadId || resendingInformAfterOppHearing) return;
    setResendingInformAfterOppHearing(true);
    onSavingChange?.(true);
    try {
      await sendAllInformAfterOppositionHearing(leadId);
      toast.success("Inform Client after Opposition Hearing resent");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Inform Client after Opposition Hearing"
      );
    } finally {
      setResendingInformAfterOppHearing(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xlvi) Invoice for TM-M after Opposition Hearing ─────────

  const persistTmMAfterHearingInvoice = async (
    overrides?: { invoiceAmount?: string; invoiceDate?: string }
  ): Promise<TmMAfterOppositionHearingInvoiceStatus | null> => {
    if (!leadId) return null;
    const rows = tmMAfterHearingInvoice?.tmInvoiceRows ?? [];
    if (rows.length === 0) {
      toast.error("No TM rows available for invoice");
      return null;
    }
    const amtStr =
      overrides?.invoiceAmount ?? tmMAfterHearingInvoiceDraft.invoiceAmount;
    const dt = overrides?.invoiceDate ?? tmMAfterHearingInvoiceDraft.invoiceDate;
    const totalOverride =
      amtStr !== "" && Number.isFinite(Number(amtStr))
        ? Number(amtStr)
        : null;
    // Build per-row entries; if user provided a total, distribute evenly.
    // Deduplicate by tmNo (backend rejects duplicates).
    const seen = new Set<string>();
    const dedupedRows = rows.filter((r) => {
      if (seen.has(r.tmNo)) return false;
      seen.add(r.tmNo);
      return true;
    });
    const perRowAmount =
      totalOverride != null
        ? Math.round((totalOverride / dedupedRows.length) * 100) / 100
        : null;
    const applications = dedupedRows.map((r) => ({
      tmNo: r.tmNo,
      invoiceAmount:
        perRowAmount != null
          ? perRowAmount
          : r.invoiceAmount ?? r.defaultInvoiceAmount,
      invoiceDate: dt || r.invoiceDate || toLocalYmd(new Date()) || "",
      includeInvoiceNo: r.includeInvoiceNo ?? false,
    }));
    try {
      const updated = await saveTmMAfterOppositionHearingInvoice(
        leadId,
        applications
      );
      setTmMAfterHearingInvoice(updated);
      return updated;
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save TM-M after Opposition Hearing Invoice"
      );
      return null;
    }
  };

  const handleGenerateTmMAfterHearingInvoicePdf = async () => {
    if (!leadId || tmMAfterHearingInvoiceGenerating) return;
    setTmMAfterHearingInvoiceGenerating(true);
    onSavingChange?.(true);
    try {
      await persistTmMAfterHearingInvoice();
      const updated = await generateTmMAfterOppositionHearingInvoicePdf(leadId);
      setTmMAfterHearingInvoice(updated);
      toast.success("TM-M after Opposition Hearing Invoice PDF generated");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to generate TM-M after Opposition Hearing Invoice PDF"
      );
    } finally {
      setTmMAfterHearingInvoiceGenerating(false);
      onSavingChange?.(false);
    }
  };

  const ensureTmMAfterHearingInvoicePdf = async (): Promise<{
    url: string;
    filename: string;
  } | null> => {
    if (!leadId) return null;
    try {
      const saved = await persistTmMAfterHearingInvoice();
      if (!saved) return null;
      let pdfUrl = saved.tmMAfterOppositionHearingInvoicePdfUrl;
      if (!saved.tmMAfterOppositionHearingInvoicePdfKey || !pdfUrl) {
        const updated = await generateTmMAfterOppositionHearingInvoicePdf(
          leadId
        );
        setTmMAfterHearingInvoice(updated);
        pdfUrl = updated.tmMAfterOppositionHearingInvoicePdfUrl;
      }
      return pdfUrl
        ? {
            url: pdfUrl,
            filename: "Invoice for TM-M after Opposition Hearing.pdf",
          }
        : null;
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to prepare TM-M after Opposition Hearing Invoice PDF"
      );
      return null;
    }
  };

  const handleDownloadTmMAfterHearingInvoicePdf = async () => {
    if (
      !leadId ||
      tmMAfterHearingInvoiceDownloading ||
      tmMAfterHearingInvoiceGenerating
    ) {
      return;
    }
    setTmMAfterHearingInvoiceDownloading(true);
    onSavingChange?.(true);
    try {
      const pdf = await ensureTmMAfterHearingInvoicePdf();
      if (!pdf) return;
      const result = await downloadFileFromUrlWithFallback(
        pdf.url,
        pdf.filename
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Download failed"
      );
    } finally {
      setTmMAfterHearingInvoiceDownloading(false);
      onSavingChange?.(false);
    }
  };

  const handleTmMAfterHearingInvoiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentTmMAfterHearingInvoice) {
      setOpposedSubStep("payment-tmm-after-opp-hearing");
      return;
    }
    if (
      !tmMAfterHearingInvoice?.tmMAfterOppositionHearingInvoicePdfKey
    ) {
      toast.error("Generate the Invoice PDF first");
      return;
    }
    onSavingChange?.(true);
    try {
      await persistTmMAfterHearingInvoice();
      await sendTmMAfterOppositionHearingInvoiceToClient(leadId, {
        sendWhatsApp: true,
        sendEmail: true,
      });
      setHasSentTmMAfterHearingInvoice(true);
      toast.success("TM-M after Opposition Hearing Invoice sent");
      setOpposedSubStep("payment-tmm-after-opp-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send TM-M after Opposition Hearing Invoice"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xlvii) Collected Amount for TM-M after Opp Hearing ─────────

  const handleFileTmMAfterHearingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    const amount = Number(tmMAfterHearingCollectedDraft);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error("Enter a valid Collected Amount");
      return;
    }
    const savedCollected =
      Number(tmMAfterHearingInvoice?.collectedAmount ?? 0) || 0;
    const newTotal = savedCollected + amount;
    onSavingChange?.(true);
    try {
      const updated = await saveTmMAfterOppositionHearingCollectedAmount(
        leadId,
        newTotal
      );
      setTmMAfterHearingInvoice(updated);
      setTmMAfterHearingCollectedDraft("");
      // If a filed status & rows exist on the lead, persist a no-op filed PATCH so the workflow step 4A(xlviii) is recorded.
      try {
        const filed = await getTmMAfterOppositionHearingFiledStatus(leadId);
        setTmMAfterHearingFiled(filed);
        if (filed?.isActive && filed.applications?.length) {
          // Patch with whatever the API last returned (no-op data preservation)
          await saveTmMAfterOppositionHearingFiled(
            leadId,
            filed.applications.map((a) => ({
              tmNo: a.tmNo,
              dateOfTmMAfterOppositionHearingFiled:
                a.dateOfTmMAfterOppositionHearingFiled ?? null,
              tmMAfterOppositionHearingFileKey:
                a.tmMAfterOppositionHearingFileKey ?? null,
              tmMAfterOppositionHearingFilename:
                a.tmMAfterOppositionHearingFilename ?? null,
            }))
          );
        }
      } catch {
        // Filed step may not be active or available; that's fine
      }
      toast.success("Collected Amount for TM-M after Opposition Hearing saved");
      setOpposedSubStep("file-tmm-after-opp-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Collected Amount for TM-M after Opposition Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xlviii) File TM-M after Opposition Hearing ─────────

  const handleTmMAfterHearingFiledSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !tmMAfterHearingFiled || !opposedSelectedTm) return;
    const draft = tmMAfterHearingFiledPerTm[opposedSelectedTm.tmNo];
    const existing = (tmMAfterHearingFiled.applications ?? []).find(
      (a) => a.tmNo === opposedSelectedTm.tmNo
    );
    if (!draft?.filingDate) {
      toast.error(
        `Enter Date of Filing of TM-M after Opposition Hearing for ${formatTmNoForDisplay(opposedSelectedTm.tmNo)}`
      );
      return;
    }
    onSavingChange?.(true);
    try {
      let fileKey = existing?.tmMAfterOppositionHearingFileKey ?? null;
      let fileName = existing?.tmMAfterOppositionHearingFilename ?? null;
      if (draft.pendingFile) {
        const reason = validateUploadFile(draft.pendingFile);
        if (reason) {
          toast.error(reason);
          onSavingChange?.(false);
          return;
        }
        fileKey = await uploadAcknowledgementPdf(
          draft.pendingFile,
          TM_M_AFTER_OPPOSITION_HEARING_FILED_FOLDER
        );
        fileName = draft.pendingFile.name;
      }
      const updated = await saveTmMAfterOppositionHearingFiled(leadId, [
        {
          tmNo: opposedSelectedTm.tmNo,
          dateOfTmMAfterOppositionHearingFiled: draft.filingDate,
          ...(fileKey
            ? {
                tmMAfterOppositionHearingFileKey: fileKey,
                tmMAfterOppositionHearingFilename: fileName ?? "",
              }
            : {}),
        },
      ]);
      setTmMAfterHearingFiled(updated);
      const map: Record<
        string,
        { filingDate: string; pendingFile: File | null }
      > = {};
      for (const a of updated.applications ?? []) {
        const parts = parseDateParts(a.dateOfTmMAfterOppositionHearingFiled);
        map[a.tmNo] = {
          filingDate: parts ? partsToIsoYmd(parts) : "",
          pendingFile: null,
        };
      }
      setTmMAfterHearingFiledPerTm(map);
      toast.success("File TM-M after Opposition Hearing saved");
      setOpposedSubStep("inform-tmm-after-opp-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save File TM-M after Opposition Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xlix) Inform Client after filing TM-M after Opp Hearing ─────────

  const handleInformTmMAfterHearingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentInformTmMAfterHearing) {
      setOpposedSubStep("order-of-opp-hearing");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendInformClientTmMAfterOppositionHearingWhatsApp(leadId);
      setHasSentInformTmMAfterHearing(true);
      toast.success("Inform Client about TM-M after Opposition Hearing sent");
      setOpposedSubStep("order-of-opp-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Inform Client about TM-M after Opposition Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendInformTmMAfterHearing = async () => {
    if (!leadId || resendingInformTmMAfterHearing) return;
    setResendingInformTmMAfterHearing(true);
    onSavingChange?.(true);
    try {
      await sendInformClientTmMAfterOppositionHearingWhatsApp(leadId);
      toast.success("Inform Client about TM-M after Opposition Hearing resent");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Inform Client about TM-M after Opposition Hearing"
      );
    } finally {
      setResendingInformTmMAfterHearing(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(li) Order of Opposition Hearing to Client ─────────

  const persistOrderOfOppHearing =
    async (): Promise<OrderOfOppositionHearingStatus | null> => {
      if (!leadId || !orderOfOppHearing) return null;
      const apps = (orderOfOppHearing.applications ?? []).map((a) => ({
        tmNo: a.tmNo,
        outcomeOfHearing: orderOfOppHearingDraft[a.tmNo] ?? null,
      }));
      try {
        const updated = await saveOrderOfOppositionHearingDetails(leadId, apps);
        setOrderOfOppHearing(updated);
        return updated;
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to save Order of Opposition Hearing"
        );
        return null;
      }
    };

  const loadOrderOfOppHearingMessagesPreview = async (
    status: OrderOfOppositionHearingStatus | null,
  ) => {
    if (!leadId || !status) return;
    setOrderOfOppHearingMessagePreviewLoading(true);
    setOrderOfOppHearingMessagePreviewError(null);
    setOrderOfOppHearingMessagesPreview([]);
    try {
      const distinctOutcomes = Array.from(
        new Set(
          (status.applications ?? [])
            .map((a) => a.outcomeOfHearing)
            .filter(
              (o): o is OrderOfOppositionHearingOutcomeValue => o != null,
            ),
        ),
      );
      if (!distinctOutcomes.length) {
        setOrderOfOppHearingMessagesPreview([]);
        return;
      }
      const results = await Promise.all(
        distinctOutcomes.map((scenario) =>
          getOrderOfOppositionHearingMessagePreview(leadId, scenario),
        ),
      );
      setOrderOfOppHearingMessagesPreview(
        results.map((r) => r.message).filter(Boolean),
      );
    } catch (err) {
      setOrderOfOppHearingMessagePreviewError(
        err instanceof Error
          ? err.message
          : "Failed to load Order of Opposition Hearing message preview",
      );
    } finally {
      setOrderOfOppHearingMessagePreviewLoading(false);
    }
  };

  const handleOrderOfOppHearingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !orderOfOppHearing) return;
    if (hasSentOrderOfOppHearing) {
      // Last sub-step in Opposed flow; nothing to advance to
      toast.success("Order of Opposition Hearing already sent");
      return;
    }
    const apps = orderOfOppHearing.applications ?? [];
    const missingOutcome = apps.some(
      (a) => !orderOfOppHearingDraft[a.tmNo]
    );
    if (missingOutcome) {
      toast.error("Select Outcome of Opposition Hearing for every application");
      return;
    }
    onSavingChange?.(true);
    try {
      const updated = await persistOrderOfOppHearing();
      if (!updated) return;
      setOrderOfOppHearingSendConfirmOpen(true);
      void loadOrderOfOppHearingMessagesPreview(updated);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Order of Opposition Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const performOrderOfOppHearingSend = async () => {
    if (!leadId || orderOfOppHearingSending) return;
    setOrderOfOppHearingSending(true);
    onSavingChange?.(true);
    try {
      await sendAllOrderOfOppositionHearingMessages(leadId);
      setHasSentOrderOfOppHearing(true);
      setOrderOfOppHearingSendConfirmOpen(false);
      toast.success("Order of Opposition Hearing sent");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Order of Opposition Hearing"
      );
    } finally {
      setOrderOfOppHearingSending(false);
      onSavingChange?.(false);
    }
  };

  const openOrderOfOppHearingResendConfirm = () => {
    if (resendingOrderOfOppHearing) return;
    setOrderOfOppHearingResendConfirmOpen(true);
    void loadOrderOfOppHearingMessagesPreview(orderOfOppHearing);
  };

  const handleResendOrderOfOppHearing = async () => {
    if (!leadId || resendingOrderOfOppHearing) return;
    setResendingOrderOfOppHearing(true);
    onSavingChange?.(true);
    try {
      await sendAllOrderOfOppositionHearingMessages(leadId);
      setOrderOfOppHearingResendConfirmOpen(false);
      toast.success("Order of Opposition Hearing resent");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Order of Opposition Hearing"
      );
    } finally {
      setResendingOrderOfOppHearing(false);
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(xliv) Attend Opposition Hearing ─────────

  const handleAttendOppHearingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !attendOppHearing) return;
    const app = attendOppHearing.applications?.[attendOppHearingSelectedIdx];
    if (!app) {
      toast.error("No application selected");
      return;
    }
    const draft = attendOppHearingDraft[app.ackApplicationId];
    if (!draft) {
      toast.error("Draft not loaded");
      return;
    }
    if (!draft.hearingStatus) {
      toast.error("Select Hearing Status");
      return;
    }
    if (
      draft.hearingStatus === "ATTENDED" &&
      !draft.hearingOfficerRemark
    ) {
      toast.error("Select Remark of Hearing Officer");
      return;
    }
    const needsDirection =
      draft.hearingOfficerRemark === "FILE_TM_M" ||
      draft.hearingOfficerRemark === "DIRECTION_OF_OFFICER";
    if (
      draft.hearingOfficerRemark === "DIRECTION_OF_OFFICER" &&
      !draft.directionOfOfficer.trim()
    ) {
      toast.error("Enter Direction of Officer");
      return;
    }
    onSavingChange?.(true);
    try {
      await saveAttendOppositionHearing(leadId, {
        ackApplicationId: app.ackApplicationId,
        hearingStatus: draft.hearingStatus,
        hearingOfficerRemark:
          draft.hearingStatus === "ATTENDED"
            ? draft.hearingOfficerRemark
            : null,
        officerDirections:
          needsDirection && draft.directionOfOfficer.trim()
            ? [{ index: 1, direction: draft.directionOfOfficer.trim() }]
            : undefined,
      });
      toast.success("Attend Opposition Hearing saved");
      const nextIdx = attendOppHearingSelectedIdx + 1;
      if (
        attendOppHearing.applications &&
        nextIdx < attendOppHearing.applications.length
      ) {
        setAttendOppHearingSelectedIdx(nextIdx);
      } else {
        // Route to the next substep based on the hearing outcome of all apps:
        // - Any app without a final outcome (Not Attended / Didn't Happen / TM-M / Directions)
        //   → Subsequent Opposition Hearings (4A(l))
        // - All apps with final outcome (Accepted / Refused / Will Pass on Merits)
        //   → Order of Opposition Hearing to Client (4A(li))
        // - FILE_TM_M present anywhere → Inform Client first (TM-M sub-flow follows)
        const apps = attendOppHearing.applications ?? [];
        const anyFileTmM = apps.some(
          (a) => a.hearingOfficerRemark === "FILE_TM_M"
        );
        const anyNonFinal = apps.some(
          (a) =>
            a.hearingStatus === "NOT_ATTENDED" ||
            a.hearingStatus === "DIDNT_HAPPEN" ||
            a.hearingOfficerRemark === "DIRECTION_OF_OFFICER"
        );
        if (anyFileTmM) {
          setOpposedSubStep("inform-after-opp-hearing");
        } else if (anyNonFinal) {
          setOpposedSubStep("subsequent-opp-hearings");
        } else {
          setOpposedSubStep("order-of-opp-hearing");
        }
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Attend Opposition Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────── 4A(l) Subsequent Opposition Hearings ─────────

  const handleSubsequentOppHearingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !subsequentOppHearing) return;

    const activeApps = (subsequentOppHearing.applications ?? []).filter(
      (a) => a.isActive
    );
    if (activeApps.length === 0) {
      toast.error("No applications eligible for Subsequent Opposition Hearing");
      return;
    }

    // Validate per-app drafts
    const detailItems: SaveSubsequentOppositionHearingDetailsItem[] = [];
    const outcomeItems: SaveSubsequentOppositionHearingOutcomeItem[] = [];
    const postponementUploads: {
      tmNo: string;
      file: File;
    }[] = [];
    const noticeUploads: { tmNo: string; file: File }[] = [];

    for (const app of activeApps) {
      const draft = subsequentOppHearingDraft[app.tmNo];
      if (!draft) continue;

      // Date of next hearing is required to advance.
      if (!draft.dateOfHearing) {
        toast.error(
          `Enter Date of Hearing for ${formatTmNoForDisplay(app.tmNo)}`
        );
        return;
      }

      // If hearing status is set, enforce same validation as Attend Opposition Hearing.
      if (draft.hearingStatus === "ATTENDED" && !draft.hearingOfficerRemark) {
        toast.error(
          `Select Remark of Hearing Officer for ${formatTmNoForDisplay(app.tmNo)}`
        );
        return;
      }
      if (
        draft.hearingOfficerRemark === "DIRECTION_OF_OFFICER" &&
        !draft.directionOfOfficer.trim()
      ) {
        toast.error(
          `Enter Direction of Officer for ${formatTmNoForDisplay(app.tmNo)}`
        );
        return;
      }

      if (draft.pendingNoticeFile) {
        const reason = validateUploadFile(draft.pendingNoticeFile);
        if (reason) {
          toast.error(reason);
          return;
        }
        noticeUploads.push({ tmNo: app.tmNo, file: draft.pendingNoticeFile });
      }
      if (
        draft.hearingStatus === "DIDNT_HAPPEN" &&
        draft.pendingPostponementFile
      ) {
        const reason = validateUploadFile(draft.pendingPostponementFile);
        if (reason) {
          toast.error(reason);
          return;
        }
        postponementUploads.push({
          tmNo: app.tmNo,
          file: draft.pendingPostponementFile,
        });
      }
    }

    onSavingChange?.(true);
    try {
      // 1) Upload notice PDFs first (if any) to capture S3 keys.
      const noticeKeyByTmNo: Record<
        string,
        { key: string; name: string } | undefined
      > = {};
      for (const u of noticeUploads) {
        const key = await uploadAcknowledgementPdf(
          u.file,
          SUBSEQUENT_OPPOSITION_HEARING_NOTICE_FOLDER
        );
        noticeKeyByTmNo[u.tmNo] = { key, name: u.file.name };
      }

      // 2) Build details payload (date + notice file).
      for (const app of activeApps) {
        const draft = subsequentOppHearingDraft[app.tmNo];
        if (!draft) continue;
        const upload = noticeKeyByTmNo[app.tmNo];
        detailItems.push({
          tmNo: app.tmNo,
          dateOfHearing: draft.dateOfHearing || null,
          ...(upload
            ? {
                hearingNoticeFileKey: upload.key,
                hearingNoticeFilename: upload.name,
              }
            : {}),
        });

        // Build outcome payload only when hearing status is set.
        if (draft.hearingStatus) {
          const needsDirection =
            draft.hearingOfficerRemark === "FILE_TM_M" ||
            draft.hearingOfficerRemark === "DIRECTION_OF_OFFICER";
          outcomeItems.push({
            tmNo: app.tmNo,
            hearingStatus: draft.hearingStatus,
            hearingOfficerRemark:
              draft.hearingStatus === "ATTENDED"
                ? draft.hearingOfficerRemark
                : null,
            officerDirections:
              needsDirection && draft.directionOfOfficer.trim()
                ? [{ index: 1, direction: draft.directionOfOfficer.trim() }]
                : undefined,
          });
        }
      }

      // 3) Save details (date + notice).
      await saveSubsequentOppositionHearingDetails(
        leadId,
        subsequentOppHearing.currentHearingNumber,
        detailItems
      );

      // 4) Save outcome (if entered).
      if (outcomeItems.length) {
        await saveSubsequentOppositionHearingOutcome(
          leadId,
          subsequentOppHearing.currentHearingNumber,
          outcomeItems
        );
      }

      // 5) Upload postponement PDFs and save (if any).
      if (postponementUploads.length) {
        const postponementItems: SaveSubsequentOppositionHearingPostponementItem[] =
          [];
        for (const u of postponementUploads) {
          const key = await uploadAcknowledgementPdf(
            u.file,
            SUBSEQUENT_OPPOSITION_HEARING_POSTPONEMENT_FOLDER
          );
          postponementItems.push({
            tmNo: u.tmNo,
            postponementNoticeFileKey: key,
            postponementNoticeFilename: u.file.name,
          });
        }
        await saveSubsequentOppositionHearingPostponement(
          leadId,
          subsequentOppHearing.currentHearingNumber,
          postponementItems
        );
      }

      // 6) Refresh status.
      const updated = await getSubsequentOppositionHearingStatus(leadId);
      setSubsequentOppHearing(updated);
      const nextDraft: Record<string, SubsequentOppHearingDraft> = {};
      for (const app of updated.applications ?? []) {
        const parts = app.dateOfHearing
          ? parseDateParts(app.dateOfHearing)
          : null;
        nextDraft[app.tmNo] = {
          dateOfHearing: parts ? partsToIsoYmd(parts) : "",
          pendingNoticeFile: null,
          hearingStatus: app.hearingStatus,
          hearingOfficerRemark: app.hearingOfficerRemark,
          directionOfOfficer: app.officerDirections?.[0]?.direction ?? "",
          pendingPostponementFile: null,
        };
      }
      setSubsequentOppHearingDraft(nextDraft);

      toast.success("Subsequent Opposition Hearing saved");

      // Capture the post-payment route BEFORE the backend's auto-advance hides
      // the just-entered outcome behind the next hearing's empty data:
      // - All apps with a final outcome → after Payment, go to Order of Opposition Hearing
      // - Any app with a non-final / unset outcome → after Payment, loop back to Subsequent Hearings
      const justSavedApps = activeApps.map((app) => ({
        ...app,
        draft: subsequentOppHearingDraft[app.tmNo],
      }));
      const allFinalAfterSave =
        justSavedApps.length > 0 &&
        justSavedApps.every((entry) => {
          const status = entry.draft?.hearingStatus;
          const remark = entry.draft?.hearingOfficerRemark;
          return (
            status === "ATTENDED" &&
            (remark === "ACCEPTED" ||
              remark === "REFUSED" ||
              remark === "WILL_PASS_ON_MERITS")
          );
        });
      setAfterSubsequentOppHearingPaymentRoute(
        allFinalAfterSave ? "order-of-opp-hearing" : "subsequent-opp-hearings"
      );

      // Every subsequent hearing is a billable service (Rs.5000 × apps) regardless
      // of outcome, because the lawyer attended on the client's behalf.
      // Always route to Invoice → Payment first; the final-vs-non-final routing
      // happens after Payment is saved using the captured route above.
      setOpposedSubStep("invoice-subsequent-opp-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Subsequent Opposition Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleSendSubsequentOppHearingNotice = async () => {
    if (!leadId || !subsequentOppHearing) return;

    const eligibleApps = (subsequentOppHearing.applications ?? []).filter(
      (a) => a.isActive
    );

    // Auto-save any draft hearing date + notice PDF before sending so the user
    // doesn't have to click Save & Continue first. Send fails on the backend
    // when no date is persisted for this hearing number.
    const pendingDetails: SaveSubsequentOppositionHearingDetailsItem[] = [];
    const noticeUploads: { tmNo: string; file: File }[] = [];
    for (const app of eligibleApps) {
      const draft = subsequentOppHearingDraft[app.tmNo];
      if (!draft) continue;
      const dateChanged =
        (draft.dateOfHearing || "") !==
        (app.dateOfHearing
          ? (() => {
              const p = parseDateParts(app.dateOfHearing);
              return p ? partsToIsoYmd(p) : "";
            })()
          : "");
      if (draft.pendingNoticeFile) {
        const reason = validateUploadFile(draft.pendingNoticeFile);
        if (reason) {
          toast.error(reason);
          return;
        }
        noticeUploads.push({ tmNo: app.tmNo, file: draft.pendingNoticeFile });
      }
      if (dateChanged || draft.pendingNoticeFile) {
        pendingDetails.push({
          tmNo: app.tmNo,
          dateOfHearing: draft.dateOfHearing || null,
        });
      }
    }

    setSendingSubsequentOppHearingNotice(true);
    try {
      if (pendingDetails.length || noticeUploads.length) {
        // Upload notice PDFs first to capture S3 keys.
        const keyByTmNo = new Map<string, { key: string; name: string }>();
        for (const u of noticeUploads) {
          const key = await uploadAcknowledgementPdf(
            u.file,
            SUBSEQUENT_OPPOSITION_HEARING_NOTICE_FOLDER
          );
          keyByTmNo.set(u.tmNo, { key, name: u.file.name });
        }
        const detailsWithUploads: SaveSubsequentOppositionHearingDetailsItem[] =
          pendingDetails.map((it) => {
            const upload = keyByTmNo.get(it.tmNo);
            return upload
              ? {
                  ...it,
                  hearingNoticeFileKey: upload.key,
                  hearingNoticeFilename: upload.name,
                }
              : it;
          });
        // Include any uploads that didn't have a date change.
        for (const [tmNo, upload] of keyByTmNo) {
          if (!detailsWithUploads.some((d) => d.tmNo === tmNo)) {
            detailsWithUploads.push({
              tmNo,
              hearingNoticeFileKey: upload.key,
              hearingNoticeFilename: upload.name,
            });
          }
        }
        await saveSubsequentOppositionHearingDetails(
          leadId,
          subsequentOppHearing.currentHearingNumber,
          detailsWithUploads
        );
      }

      await sendSubsequentOppositionHearingNoticeToClient(leadId);
      toast.success("Subsequent Opposition Hearing notice sent to client");

      // Refresh status so the saved date + notice key are reflected in the form.
      const updated = await getSubsequentOppositionHearingStatus(leadId);
      setSubsequentOppHearing(updated);
      setSubsequentOppHearingDraft((prev) => {
        const next = { ...prev };
        for (const app of updated.applications ?? []) {
          const parts = app.dateOfHearing
            ? parseDateParts(app.dateOfHearing)
            : null;
          next[app.tmNo] = {
            ...(next[app.tmNo] ?? {
              dateOfHearing: "",
              pendingNoticeFile: null,
              hearingStatus: null,
              hearingOfficerRemark: null,
              directionOfOfficer: "",
              pendingPostponementFile: null,
            }),
            dateOfHearing: parts ? partsToIsoYmd(parts) : "",
            pendingNoticeFile: null,
          };
        }
        return next;
      });
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Subsequent Opposition Hearing notice"
      );
    } finally {
      setSendingSubsequentOppHearingNotice(false);
    }
  };

  // ───────── 4A(xxxiii) Subsequent Opposition Hearing Invoice ─────────

  const persistSubsequentOppHearingInvoice =
    async (): Promise<SubsequentOppositionHearingInvoiceStatus> => {
      if (!leadId || !subsequentOppHearingInvoice) {
        throw new Error("Invoice data not loaded yet. Please refresh.");
      }
      const hearingNumber = subsequentOppHearingInvoice.hearingNumber;
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const todayYmd = `${yyyy}-${mm}-${dd}`;
      const invoiceDate = subsequentOppHearingInvoiceDateDraft || todayYmd;

      // Per-TM amount = entered amount divided by no. of apps so total matches
      // (backend stores TM-wise; total displayed on screen is the sum).
      const noOfApps = subsequentOppHearingInvoice.noOfApps || 0;
      const trimmedAmount = subsequentOppHearingInvoiceAmountDraft.trim();
      const totalAmount =
        trimmedAmount === "" ? NaN : Number(trimmedAmount);
      const perTmAmount =
        Number.isFinite(totalAmount) && totalAmount >= 0 && noOfApps > 0
          ? Math.round((totalAmount / noOfApps) * 100) / 100
          : 5000;

      const apps =
        (subsequentOppHearingInvoice.applications ?? []).map((a) => ({
          tmNo: a.tmNo,
          invoiceAmount: perTmAmount,
          invoiceDate,
          // Apply the user's "Include Invoice No." choice to every TM row.
          includeInvoiceNo: subsequentOppHearingInvoiceIncludeNoDraft,
        })) as SaveSubsequentOppositionHearingInvoiceItem[];
      if (!apps.length) {
        throw new Error(
          "No applications eligible for invoice. Save Subsequent Hearing details first."
        );
      }

      const updated = await saveSubsequentOppositionHearingInvoice(
        leadId,
        hearingNumber,
        apps
      );
      setSubsequentOppHearingInvoice(updated);
      setSubsequentOppHearingInvoiceAmountDraft(
        updated.invoiceAmount != null
          ? String(Math.round(Number(updated.invoiceAmount)))
          : ""
      );
      if (updated.invoiceDate) {
        const parts = parseDateParts(updated.invoiceDate);
        setSubsequentOppHearingInvoiceDateDraft(
          parts ? partsToIsoYmd(parts) : ""
        );
      }
      setSubsequentOppHearingInvoiceIncludeNoDraft(
        Boolean(updated.includeInvoiceNo)
      );
      return updated;
    };

  const handleSubsequentOppHearingInvoiceSubmit = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();
    if (!leadId || !subsequentOppHearingInvoice) return;
    onSavingChange?.(true);
    try {
      await persistSubsequentOppHearingInvoice();
      toast.success("Subsequent Opposition Hearing Invoice saved");
      setOpposedSubStep("payment-subsequent-opp-hearing");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Subsequent Opposition Hearing Invoice"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  /**
   * Ensure a PDF exists for the current invoice — saves the draft first and
   * regenerates the PDF so it always reflects the latest amount/date.
   * Returns the latest invoice status (with PDF url) on success.
   */
  const ensureSubsequentOppHearingInvoicePdf =
    async (): Promise<SubsequentOppositionHearingInvoiceStatus> => {
      if (!leadId || !subsequentOppHearingInvoice) {
        throw new Error("Invoice data not loaded yet. Please refresh.");
      }
      await persistSubsequentOppHearingInvoice();
      await generateSubsequentOppositionHearingInvoicePdf(
        leadId,
        subsequentOppHearingInvoice.hearingNumber
      );
      const refreshed = await getSubsequentOppositionHearingInvoiceStatus(
        leadId,
        subsequentOppHearingInvoice.hearingNumber
      );
      setSubsequentOppHearingInvoice(refreshed);
      return refreshed;
    };

  const handleGenerateSubsequentOppHearingInvoicePdf = async () => {
    if (
      !leadId ||
      !subsequentOppHearingInvoice ||
      subsequentOppHearingInvoiceGenerating
    ) {
      return;
    }
    setSubsequentOppHearingInvoiceGenerating(true);
    try {
      await ensureSubsequentOppHearingInvoicePdf();
      toast.success("Invoice PDF generated");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to generate Subsequent Opposition Hearing Invoice PDF"
      );
    } finally {
      setSubsequentOppHearingInvoiceGenerating(false);
    }
  };

  const handleDownloadSubsequentOppHearingInvoicePdf = async () => {
    if (
      !leadId ||
      !subsequentOppHearingInvoice ||
      subsequentOppHearingInvoiceDownloading ||
      subsequentOppHearingInvoiceGenerating
    ) {
      return;
    }
    setSubsequentOppHearingInvoiceDownloading(true);
    try {
      // Regenerate to ensure the PDF reflects the latest draft, then download.
      const refreshed = await ensureSubsequentOppHearingInvoicePdf();
      if (!refreshed.invoicePdfUrl) {
        throw new Error("PDF URL not available");
      }
      const result = await downloadFileFromUrlWithFallback(
        refreshed.invoicePdfUrl,
        `Subsequent_Opposition_Hearing_${refreshed.hearingNumber}_Invoice.pdf`
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Download failed"
      );
    } finally {
      setSubsequentOppHearingInvoiceDownloading(false);
    }
  };

  const handleSendSubsequentOppHearingInvoice = async () => {
    if (
      !leadId ||
      !subsequentOppHearingInvoice ||
      subsequentOppHearingInvoiceSending
    ) {
      return;
    }
    setSubsequentOppHearingInvoiceSending(true);
    try {
      // Save current draft, then ensure PDF exists, then send.
      await persistSubsequentOppHearingInvoice();
      if (!subsequentOppHearingInvoice.invoicePdfKey) {
        await generateSubsequentOppositionHearingInvoicePdf(
          leadId,
          subsequentOppHearingInvoice.hearingNumber
        );
      }
      await sendSubsequentOppositionHearingInvoiceToClient(
        leadId,
        subsequentOppHearingInvoice.hearingNumber,
        { sendWhatsApp: true, sendEmail: false }
      );
      toast.success("Invoice sent to client");
      const refreshed = await getSubsequentOppositionHearingInvoiceStatus(
        leadId,
        subsequentOppHearingInvoice.hearingNumber
      );
      setSubsequentOppHearingInvoice(refreshed);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Subsequent Opposition Hearing Invoice"
      );
    } finally {
      setSubsequentOppHearingInvoiceSending(false);
    }
  };

  // ───────── 4A(xl) Subsequent Opposition Hearing Payment ─────────

  const handleSubsequentOppHearingPaymentSubmit = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();
    if (!leadId || !subsequentOppHearingInvoice) return;

    const thisPayment = parseAmount(subsequentOppHearingCollectedDraft);
    if (!Number.isFinite(thisPayment) || thisPayment < 0) {
      toast.error("Enter a valid Collected Amount");
      return;
    }

    // Split the collected amount equally across TM apps so per-TM running
    // collected totals stay consistent (backend stores per-TM).
    const apps = subsequentOppHearingInvoice.applications ?? [];
    if (!apps.length) {
      toast.error("No applications eligible for payment");
      return;
    }
    const perTmIncrement = Math.round((thisPayment / apps.length) * 100) / 100;
    const items = apps.map((a) => ({
      tmNo: a.tmNo,
      collectedAmount:
        Number(a.collectedAmount ?? 0) + perTmIncrement,
    }));

    onSavingChange?.(true);
    try {
      const updated = await saveSubsequentOppositionHearingCollectedAmount(
        leadId,
        subsequentOppHearingInvoice.hearingNumber,
        items
      );
      setSubsequentOppHearingInvoice(updated);
      setSubsequentOppHearingCollectedDraft("");
      toast.success("Payment saved");

      // Refresh subsequent hearing state (backend may have auto-advanced to the
      // next hearing number, ready for the user's next entry).
      const subStatus = await getSubsequentOppositionHearingStatus(leadId);
      setSubsequentOppHearing(subStatus);

      // Use the route captured when the user saved the Subsequent Hearing
      // outcome — we recorded then whether the outcome was final, before the
      // backend's auto-advance hid that information.
      setOpposedSubStep(afterSubsequentOppHearingPaymentRoute);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Subsequent Opposition Hearing payment"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // ───────────────────── Render ─────────────────────

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  const variantOptions: { id: Variant; label: string }[] = [
    { id: "standard", label: "Standard Advertisement" },
    { id: "opposed", label: "Opposed" },
  ];

  return (
    <div
      className="flex flex-col gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Variant pills */}
      <div className="flex flex-row flex-wrap gap-3">
        {variantOptions.map((opt) => {
          const isActive = variant === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setVariant(opt.id)}
              className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[160px] text-xs font-normal transition-colors ${
                isActive
                  ? "bg-[#222222] text-white"
                  : "bg-[#F7F7F7] text-[#222222]"
              }`}
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <RadioCircle active={isActive} />
              <span className="text-left line-clamp-1">{opt.label}</span>
            </button>
          );
        })}
      </div>

      {variant === "opposed" ? (
        opposedLoading ? (
          <div className="border border-[#EEEEEE] rounded-xl px-4 py-6">
            <p className="text-sm text-[#6D6D6D]">Loading…</p>
          </div>
        ) : opposedTms.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center gap-1 border border-dashed border-[#EEEEEE] rounded-xl py-12 px-6 text-center"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            <p className="text-sm font-medium text-[#222222]">
              No applications with this status yet.
            </p>
            <p className="text-xs text-[#6D6D6D]">
              Applications will appear here once their status is set to
              “Advertised in Journal — Opposed” or “Opposition Hearing”.
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-row flex-wrap gap-3">
              {visibleOpposedTms.map((entry) => {
                const idx = opposedTms.findIndex(
                  (t) => t.tmNo === entry.tmNo
                );
                const isActive = opposedSelectedTmIdx === idx;
                const deadline = evidenceDeadlineByTm[entry.tmNo] ?? {
                  state: "none" as const,
                  date: null,
                  daysUntil: null,
                };
                return (
                  <button
                    key={entry.tmNo}
                    type="button"
                    onClick={() => setOpposedSelectedTmIdx(idx)}
                    className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors ${
                      isActive
                        ? "bg-[#222222] text-white"
                        : "bg-[#F7F7F7] text-[#222222]"
                    }`}
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "0.75rem",
                    }}
                  >
                    <RadioCircle active={isActive} />
                    {formatTmNoForDisplay(entry.tmNo)}
                    <EvidenceDeadlineDot
                      state={deadline.state}
                      date={deadline.date}
                    />
                  </button>
                );
              })}
            </div>

            {/*
              4A(xi): the Last Date for Evidence by Opponent alert must always be visible in the
              application details, not only while the Notice of Counter substep is open. It sits at
              application level so switching substeps keeps it on screen for the selected TM.
            */}
            <EvidenceDeadlineBanner
              state={selectedEvidenceDeadline.state}
              date={selectedEvidenceDeadline.date}
            />

            <div className="border border-[#EEEEEE] rounded-xl overflow-hidden flex flex-col md:flex-row min-h-0">
              <div className="w-full md:w-[260px] md:flex-shrink-0 md:border-r md:border-b-0 border-b border-[#EEEEEE] p-2 flex md:flex-col gap-1 overflow-x-auto md:overflow-y-auto md:max-h-[640px]">
                {visibleOpposedSubSteps.map((step) => {
                  const isActive = opposedSubStep === step.id;
                  return (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => setOpposedSubStep(step.id)}
                      className={`w-full flex items-center justify-between px-2.5 py-2.5 gap-2.5 rounded-[6px] text-xs whitespace-nowrap md:whitespace-normal transition-colors ${
                        isActive
                          ? "bg-[#222222] text-white font-medium"
                          : "bg-[#F7F7F7] text-[#222222] font-normal hover:bg-[#EEEEEE]"
                      }`}
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      <span className="text-left line-clamp-1" title={step.label}>{step.label}</span>
                      <ChevronRight active={isActive} />
                    </button>
                  );
                })}
              </div>

              <div className="flex-1 min-w-0 px-4 py-5 md:px-5 overflow-y-auto">
                {opposedSubStep === "status-details-counter" ? (
                  <form
                    id={OPPOSITION_COUNTER_STATUS_FORM_ID}
                    onSubmit={(ev) => void handleCounterStatusSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <CounterStatusFields
                      app={opposedSelectedTm}
                      form={opposedSelectedForm}
                      derivedLastDate={opposedDerivedLastDate}
                      noOfApplicationsOpposed={
                        opposedStatus?.noOfApplicationsOpposed ?? 0
                      }
                      onPatch={(patch) =>
                        opposedSelectedTm &&
                        updateOpposedPerTm(opposedSelectedTm.tmNo, patch)
                      }
                      onPickImage={(file) =>
                        opposedSelectedTm &&
                        handleOpposedFileChosen(
                          opposedSelectedTm.tmNo,
                          "image",
                          file
                        )
                      }
                      onClearImage={() =>
                        opposedSelectedTm &&
                        updateOpposedPerTm(opposedSelectedTm.tmNo, {
                          pendingImageFile: null,
                        })
                      }
                    />

                    {opposedSelectedTm && (
                      <CounterNoticeUploadZone
                        app={opposedSelectedTm}
                        pendingFile={opposedSelectedForm.pendingNoticeFile}
                        isDragging={
                          opposedDragOverTmNo === opposedSelectedTm.tmNo
                        }
                        onDragOver={() =>
                          setOpposedDragOverTmNo(opposedSelectedTm.tmNo)
                        }
                        onDragLeave={() => setOpposedDragOverTmNo(null)}
                        onDrop={(file) => {
                          setOpposedDragOverTmNo(null);
                          handleOpposedFileChosen(
                            opposedSelectedTm.tmNo,
                            "notice",
                            file
                          );
                        }}
                        onPick={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleOpposedFileChosen(
                              opposedSelectedTm.tmNo,
                              "notice",
                              file
                            );
                          }
                          e.target.value = "";
                        }}
                        onPreview={() => {
                          if (opposedSelectedForm.pendingNoticeFile) {
                            setPreview({
                              url: URL.createObjectURL(
                                opposedSelectedForm.pendingNoticeFile
                              ),
                              name: opposedSelectedForm.pendingNoticeFile.name,
                              kind: detectPreviewKind(
                                opposedSelectedForm.pendingNoticeFile.name,
                                opposedSelectedForm.pendingNoticeFile.type
                              ),
                              revokeOnClose: true,
                            });
                          } else if (
                            opposedSelectedTm.oppositionNoticeFileUrl &&
                            opposedSelectedTm.oppositionNoticeFilename
                          ) {
                            setPreview({
                              url: opposedSelectedTm.oppositionNoticeFileUrl,
                              name: opposedSelectedTm.oppositionNoticeFilename,
                              kind: detectPreviewKind(
                                opposedSelectedTm.oppositionNoticeFilename
                              ),
                            });
                          }
                        }}
                        onRemove={() => {
                          updateOpposedPerTm(opposedSelectedTm.tmNo, {
                            pendingNoticeFile: null,
                          });
                          setOpposedStatus((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  applications: prev.applications.map((a) =>
                                    a.tmNo === opposedSelectedTm.tmNo
                                      ? {
                                          ...a,
                                          oppositionNoticeFileKey: null,
                                          oppositionNoticeFilename: null,
                                          oppositionNoticeFileUrl: null,
                                        }
                                      : a
                                  ),
                                }
                              : prev
                          );
                        }}
                      />
                    )}
                  </form>
                ) : opposedSubStep === "notice-to-client-counter" ? (
                  <form
                    id={OPPOSITION_COUNTER_NOTICE_FORM_ID}
                    onSubmit={(ev) => void handleCounterNoticeSubmit(ev)}
                    className="flex flex-col gap-3 pt-1"
                  >
                    {opposedTmsWithNoticePdf.length === 0 ? (
                      <p className="text-sm text-[#6D6D6D]">
                        No Opposition Notice PDFs uploaded yet. Add them in
                        Status details for Counter first.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {opposedTmsWithNoticePdf.map((app) => (
                          <CounterNoticePdfRow
                            key={app.tmNo}
                            app={app}
                            onPreview={() => {
                              if (
                                app.oppositionNoticeFileUrl &&
                                app.oppositionNoticeFilename
                              ) {
                                setPreview({
                                  url: app.oppositionNoticeFileUrl,
                                  name: app.oppositionNoticeFilename,
                                  kind: detectPreviewKind(
                                    app.oppositionNoticeFilename
                                  ),
                                });
                              }
                            }}
                          />
                        ))}
                      </div>
                    )}

                    {hasSentCounterNotice && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={
                            resendingCounterNotice || !canSendCounterNotice
                          }
                          onClick={() => {
                            setCounterNoticeResendConfirmOpen(true);
                            void loadCounterNoticePreview();
                          }}
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingCounterNotice
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "inform-client-about-counter" ? (
                  <form
                    id={OPPOSITION_COUNTER_INFORM_FORM_ID}
                    onSubmit={(ev) => void handleCounterInformSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <CounterInformMessagePreview preview={counterInformPreview} />
                    {hasSentCounterInform && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={resendingCounterInform}
                          onClick={() =>
                            openResendConfirm(
                              "Resend Inform Client about Counter?",
                              "The client will receive this WhatsApp message again.",
                              () => void handleResendCounterInform(),
                              counterInformPreview?.message,
                            )
                          }
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingCounterInform
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "call-client-counter" ? (
                  <form
                    id={OPPOSITION_COUNTER_CALL_FORM_ID}
                    onSubmit={(ev) => void handleCounterCallSubmit(ev)}
                    className="flex flex-col w-full pt-1"
                  >
                    <CounterCallClientPanel
                      recorded={hasRecordedCounterCall}
                      recording={recordingCounterCall}
                      onRecord={() => {
                        // Trigger the form submit so the dashboard footer's
                        // "Save & Continue" path can also drive this.
                        void handleCounterCallSubmit(
                          new Event("submit") as unknown as React.FormEvent
                        );
                      }}
                    />
                  </form>
                ) : opposedSubStep === "confirmation-from-client-counter" ? (
                  <form
                    id={OPPOSITION_COUNTER_CONFIRMATION_FORM_ID}
                    onSubmit={(ev) => void handleCounterConfirmationSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <CounterConfirmationFields
                      status={counterConfirmation}
                      draft={counterConfirmationDraft}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      noOpposedDraft={counterNoOpposedDraft}
                      onNoOpposedChange={setCounterNoOpposedDraft}
                      noForCounterDraft={counterNoForCounterDraft}
                      onNoForCounterChange={setCounterNoForCounterDraft}
                      onPick={(tmNo, val) =>
                        setCounterConfirmationDraft((p) => ({
                          ...p,
                          [tmNo]: val,
                        }))
                      }
                    />
                    {opposedSelectedTm &&
                      counterConfirmationDraft[opposedSelectedTm.tmNo] ===
                        false && (
                        <NonFilingNextStepChoice
                          variant="counter"
                          onGoToAbandonment={() => {
                            if (onAdvanceToRefusalAbandonment) {
                              onAdvanceToRefusalAbandonment();
                            } else {
                              toast.info(
                                "Next: 5B(i) Status details for Abandonment — open it from the Final Order section."
                              );
                            }
                          }}
                        />
                      )}
                  </form>
                ) : opposedSubStep === "invoice-to-client-counter" ? (
                  <form
                    id={OPPOSITION_COUNTER_INVOICE_FORM_ID}
                    onSubmit={(ev) => void handleCounterInvoiceSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <CounterInvoiceFields
                      leadId={leadId ?? null}
                      status={counterInvoice}
                      preview={counterInvoicePreview}
                      rows={counterInvoiceRows}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      generating={counterInvoiceGenerating}
                      onPatchRow={(tmNo, patch) =>
                        setCounterInvoiceRows((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              invoiceAmount: "0",
                              discount: "0",
                              invoiceDate: "",
                              includeInvoiceNo: true,
                            }),
                            ...patch,
                          },
                        }))
                      }
                      onGeneratePdf={() => void handleGenerateCounterInvoicePdf()}
                    />
                    <div className="flex flex-wrap items-center gap-3 w-full pt-1">
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={clientPhone}
                        readOnly
                        placeholder="e.g. +919876543210"
                        className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
                      />
                      {hasSentCounterInvoice && (
                        <button
                          type="button"
                          disabled={resendingCounterInvoice}
                          onClick={() => {
                            setCounterInvoiceResendConfirmOpen(true);
                          }}
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingCounterInvoice
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      )}
                    </div>
                  </form>
                ) : opposedSubStep === "payment-from-client-counter" ? (
                  <form
                    id={OPPOSITION_COUNTER_PAYMENT_FORM_ID}
                    onSubmit={(ev) => void handleCounterPaymentSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <CounterPaymentFields
                      status={counterInvoice}
                      rows={counterPaymentRows}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onChange={(tmNo, val) =>
                        setCounterPaymentRows((p) => ({ ...p, [tmNo]: val }))
                      }
                    />
                  </form>
                ) : opposedSubStep === "prepare-counter" ? (
                  <form
                    id={OPPOSITION_COUNTER_PREPARE_FORM_ID}
                    onSubmit={(ev) => void handlePrepareCounterSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <PrepareCounterFields
                      status={prepareCounter}
                      perTm={prepareCounterPerTm}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      generating={prepareCounterGenerating}
                      onPatch={(tmNo, patch) =>
                        setPrepareCounterPerTm((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              noOfParagraphsInOpponentIntro: "",
                              paragraphWiseReplies: [{ heading: "", text: "" }],
                              opponentAttorneyName: "",
                              opponentAttorneyAddress: "",
                              opponentAttorneyEmail: "",
                              dateOfEvidenceAffidavit: "",
                              dateOfFilingCounterStatement: "",
                            }),
                            ...patch,
                          },
                        }))
                      }
                      onGeneratePdf={(app) => void handleGeneratePrepareCounterPdf(app)}
                    />
                  </form>
                ) : opposedSubStep === "file-counter" ? (
                  <form
                    id={OPPOSITION_COUNTER_FILE_COUNTER_FORM_ID}
                    onSubmit={(ev) => void handleFileCounterSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <FileCounterFields
                      status={fileCounter}
                      perTm={fileCounterPerTm}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onPatch={(tmNo, patch) =>
                        setFileCounterPerTm((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? { filingDate: "", pendingFile: null }),
                            ...patch,
                          },
                        }))
                      }
                      onPreview={(row) => {
                        const draft = fileCounterPerTm[row.tmNo];
                        if (draft?.pendingFile) {
                          setPreview({
                            url: URL.createObjectURL(draft.pendingFile),
                            name: draft.pendingFile.name,
                            kind: detectPreviewKind(
                              draft.pendingFile.name,
                              draft.pendingFile.type
                            ),
                            revokeOnClose: true,
                          });
                        }
                      }}
                    />
                  </form>
                ) : opposedSubStep === "inform-client-filing-counter" ? (
                  <form
                    id={OPPOSITION_COUNTER_INFORM_FILING_FORM_ID}
                    onSubmit={(ev) => void handleInformFilingSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <InformFilingMessagePreview preview={informFilingPreview} />
                    {hasSentInformFiling && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={resendingInformFiling}
                          onClick={() =>
                            openResendConfirm(
                              "Resend Inform Client about Filing of Counter?",
                              "The client will receive this WhatsApp message again.",
                              () => void handleResendInformFiling(),
                              informFilingPreview?.message,
                            )
                          }
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingInformFiling
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "notice-of-counter-registry" ? (
                  <form
                    id={OPPOSITION_COUNTER_NOTICE_OF_COUNTER_FORM_ID}
                    onSubmit={(ev) => void handleNoticeOfCounterSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <NoticeOfCounterFields
                      rows={noticeOfCounterRows}
                      draft={noticeOfCounterDraft}
                      lastDateDraft={noticeOfCounterLastDateDraft}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onChange={(tmNo, val) => {
                        setNoticeOfCounterDraft((p) => ({ ...p, [tmNo]: val }));
                        const autoLast = val ? computeNoticeCounterLastDate(val) : null;
                        setNoticeOfCounterLastDateDraft((p) => ({
                          ...p,
                          [tmNo]: autoLast ? toLocalYmd(autoLast) : "",
                        }));
                      }}
                      onLastDateChange={(tmNo, val) =>
                        setNoticeOfCounterLastDateDraft((p) => ({
                          ...p,
                          [tmNo]: val,
                        }))
                      }
                    />
                  </form>
                ) : opposedSubStep === "inform-client-notice-counter" ? (
                  <form
                    id={OPPOSITION_COUNTER_INFORM_NOTICE_FORM_ID}
                    onSubmit={(ev) => void handleInformNoticeSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <InformNoticeOfCounterMessagePreview
                      preview={informNoticeOfCounterPreview}
                    />
                    {hasSentInformNotice && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={resendingInformNotice}
                          onClick={() =>
                            openResendConfirm(
                              "Resend Inform Client about Notice of Counter?",
                              "The client will receive this WhatsApp message again.",
                              () => void handleResendInformNotice(),
                              informNoticeOfCounterPreview?.message,
                            )
                          }
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingInformNotice
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "status-evidence-by-opp" ? (
                  <form
                    id={OPPOSITION_COUNTER_EVIDENCE_BY_OPPONENT_FORM_ID}
                    onSubmit={(ev) => void handleEvidenceByOpponentSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <EvidenceByOpponentFields
                      status={evidenceByOpponent}
                      draft={evidenceByOpponentDraft}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onPatch={(tmNo, patch) =>
                        setEvidenceByOpponentDraft((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              opponentEvidenceFiled: null,
                              dateOfEvidenceByOpponent: "",
                              dateOfReceiptOfEvidenceByOpponent: "",
                              lastDateOfFilingEvidence: "",
                              pendingFile: null,
                            }),
                            ...patch,
                          },
                        }))
                      }
                      onPreview={(app) => {
                        const pending =
                          evidenceByOpponentDraft[app.tmNo]?.pendingFile;
                        if (pending) {
                          setPreview({
                            url: URL.createObjectURL(pending),
                            name: pending.name,
                            kind: detectPreviewKind(
                              pending.name,
                              pending.type
                            ),
                            revokeOnClose: true,
                          });
                          return;
                        }
                        if (
                          app.evidenceByOpponentFileUrl &&
                          app.evidenceByOpponentFilename
                        ) {
                          setPreview({
                            url: app.evidenceByOpponentFileUrl,
                            name: app.evidenceByOpponentFilename,
                            kind: detectPreviewKind(
                              app.evidenceByOpponentFilename
                            ),
                          });
                        }
                      }}
                      onRemove={(app) => {
                        // Clear pending; clear saved key/filename so save will not include them
                        setEvidenceByOpponentDraft((prev) => ({
                          ...prev,
                          [app.tmNo]: {
                            ...(prev[app.tmNo] ?? {
                              opponentEvidenceFiled: null,
                              dateOfEvidenceByOpponent: "",
                              dateOfReceiptOfEvidenceByOpponent: "",
                              lastDateOfFilingEvidence: "",
                              pendingFile: null,
                            }),
                            pendingFile: null,
                          },
                        }));
                        setEvidenceByOpponent((prev) =>
                          prev
                            ? {
                                ...prev,
                                applications: prev.applications.map((a) =>
                                  a.tmNo === app.tmNo
                                    ? {
                                        ...a,
                                        evidenceByOpponentFileKey: null,
                                        evidenceByOpponentFilename: null,
                                        evidenceByOpponentFileUrl: null,
                                      }
                                    : a
                                ),
                              }
                            : prev
                        );
                      }}
                    />
                  </form>
                ) : opposedSubStep === "notice-evidence-by-opp" ? (
                  <form
                    id={OPPOSITION_COUNTER_NOTICE_EVIDENCE_BY_OPP_FORM_ID}
                    onSubmit={(ev) => void handleNoticeEvidenceByOppSubmit(ev)}
                    className="flex flex-col gap-3 pt-1"
                  >
                    {evidenceByOppTmsWithPdf.length === 0 ? (
                      <p className="text-sm text-[#6D6D6D]">
                        {evidenceByOppAllAnsweredNo
                          ? "Nothing to forward — Status details for Evidence by Opponent is set to NO, so the Opponent filed no Evidence for these applications."
                          : "No Evidence by Opponent PDFs uploaded yet. Answer YES and upload them in Status details for Evidence by Opponent first."}
                      </p>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {evidenceByOppTmsWithPdf.map((app) => (
                          <EvidenceByOppPdfRow
                            key={app.tmNo}
                            app={app}
                            onPreview={() => {
                              if (
                                app.evidenceByOpponentFileUrl &&
                                app.evidenceByOpponentFilename
                              ) {
                                setPreview({
                                  url: app.evidenceByOpponentFileUrl,
                                  name: app.evidenceByOpponentFilename,
                                  kind: detectPreviewKind(
                                    app.evidenceByOpponentFilename
                                  ),
                                });
                              }
                            }}
                          />
                        ))}
                      </div>
                    )}

                    {hasSentNoticeEvidenceByOpp && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={
                            resendingNoticeEvidenceByOpp ||
                            !canSendNoticeEvidenceByOpp
                          }
                          onClick={() => {
                            setNoticeEvidenceByOppResendConfirmOpen(true);
                            void loadNoticeEvidenceByOppPreview();
                          }}
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingNoticeEvidenceByOpp
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "inform-evidence-by-opp" ? (
                  <form
                    id={OPPOSITION_COUNTER_INFORM_EVIDENCE_BY_OPP_FORM_ID}
                    onSubmit={(ev) => void handleInformEvidenceByOppSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <InformEvidenceByOpponentMessagePreview
                      preview={informEvidenceByOppPreview}
                    />
                    {hasSentInformEvidenceByOpp && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={resendingInformEvidenceByOpp}
                          onClick={() =>
                            openResendConfirm(
                              "Resend Inform Client about Evidence by Opponent?",
                              "The client will receive this WhatsApp message again.",
                              () => void handleResendInformEvidenceByOpp(),
                              informEvidenceByOppPreview?.message,
                            )
                          }
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingInformEvidenceByOpp
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "call-client-evidence" ? (
                  <form
                    id={OPPOSITION_COUNTER_CALL_CLIENT_EVIDENCE_FORM_ID}
                    onSubmit={(ev) => void handleCallClientEvidenceSubmit(ev)}
                    className="flex flex-col w-full pt-1"
                  >
                    <CallClientForEvidencePanel
                      recorded={hasRecordedCallClientEvidence}
                      recording={recordingCallClientEvidence}
                      onRecord={() => {
                        void handleCallClientEvidenceSubmit(
                          new Event("submit") as unknown as React.FormEvent
                        );
                      }}
                    />
                  </form>
                ) : opposedSubStep === "confirmation-evidence" ? (
                  <form
                    id={OPPOSITION_COUNTER_EVIDENCE_CONFIRMATION_FORM_ID}
                    onSubmit={(ev) =>
                      void handleEvidenceConfirmationSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <EvidenceConfirmationFields
                      status={evidenceConfirmation}
                      draft={evidenceConfirmationDraft}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      noOfApps={evidenceNoOfAppsOverride}
                      onPick={(tmNo, val) =>
                        setEvidenceConfirmationDraft((p) => ({
                          ...p,
                          [tmNo]: val,
                        }))
                      }
                      onNoOfAppsChange={setEvidenceNoOfAppsOverride}
                    />
                    {opposedSelectedTm &&
                      evidenceConfirmationDraft[opposedSelectedTm.tmNo] ===
                        false && (
                        <NonFilingNextStepChoice
                          variant="evidence"
                          onGoToAbandonment={() => {
                            if (onAdvanceToRefusalAbandonment) {
                              onAdvanceToRefusalAbandonment();
                            } else {
                              toast.info(
                                "Next: 5B(i) Status details for Abandonment — open it from the Final Order section."
                              );
                            }
                          }}
                        />
                      )}
                  </form>
                ) : opposedSubStep === "invoice-evidence" ? (
                  <form
                    id={OPPOSITION_COUNTER_EVIDENCE_INVOICE_FORM_ID}
                    onSubmit={(ev) => void handleEvidenceInvoiceSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <EvidenceInvoiceFields
                      leadId={leadId}
                      status={evidenceInvoice}
                      preview={evidenceInvoicePreviewData}
                      rows={evidenceInvoiceRows}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      generating={evidenceInvoiceGenerating}
                      downloading={evidenceInvoiceDownloading}
                      onPatchRow={(tmNo, patch) =>
                        setEvidenceInvoiceRows((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              invoiceAmount: "0",
                              discount: "0",
                              invoiceDate: "",
                              includeInvoiceNo: false,
                            }),
                            ...patch,
                          },
                        }))
                      }
                      onGeneratePdf={() =>
                        void handleGenerateEvidenceInvoicePdf()
                      }
                      onDownloadPdf={() =>
                        void handleDownloadEvidenceInvoicePdf()
                      }
                      onPreparePdf={ensureEvidenceInvoicePdf}
                    />
                    <div className="flex flex-wrap items-center gap-3 w-full pt-1">
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={clientPhone}
                        readOnly
                        placeholder="e.g. +919876543210"
                        className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
                      />
                      {hasSentEvidenceInvoice && (
                        <button
                          type="button"
                          disabled={resendingEvidenceInvoice}
                          onClick={() => {
                            setEvidenceInvoiceResendConfirmOpen(true);
                          }}
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingEvidenceInvoice
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      )}
                    </div>
                  </form>
                ) : opposedSubStep === "payment-evidence" ? (
                  <form
                    id={OPPOSITION_COUNTER_EVIDENCE_PAYMENT_FORM_ID}
                    onSubmit={(ev) => void handleEvidencePaymentSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <EvidencePaymentFields
                      status={evidenceInvoice}
                      rows={evidencePaymentRows}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      recording={recordingEvidencePayment}
                      onChange={(tmNo, val) =>
                        setEvidencePaymentRows((p) => ({ ...p, [tmNo]: val }))
                      }
                      onRecordPayment={() => {
                        void handleEvidencePaymentSubmit(
                          new Event("submit") as unknown as React.FormEvent
                        );
                      }}
                      onSendPaymentMessage={() =>
                        void handleSendEvidencePaymentMessage()
                      }
                      onPreviewInvoice={() => {
                        if (evidenceInvoice?.evidenceInvoicePdfUrl) {
                          setPreview({
                            url: evidenceInvoice.evidenceInvoicePdfUrl,
                            name: "Evidence Invoice.pdf",
                            kind: detectPreviewKind("Evidence Invoice.pdf"),
                          });
                        }
                      }}
                    />
                  </form>
                ) : opposedSubStep === "stamp-vendor-evidence" ? (
                  <form
                    id={OPPOSITION_COUNTER_STAMP_VENDOR_EVIDENCE_FORM_ID}
                    onSubmit={(ev) =>
                      void handleStampPaperEvidenceSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <StampPaperForEvidenceFields
                      status={stampPaperEvidence}
                      draft={stampPaperEvidenceDraft}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      stampVendors={stampVendorsForEvidence}
                      onPatch={(tmNo, patch) =>
                        setStampPaperEvidenceDraft((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              stampPapersForEvidence: "",
                              stampVendorNameForEvidence: "",
                              stampVendorPhoneForEvidence: "",
                            }),
                            ...patch,
                          },
                        }))
                      }
                    />
                    {hasSentStampPaperEvidence && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={resendingStampPaperEvidence}
                          onClick={() => void handleResendStampPaperEvidence()}
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingStampPaperEvidence
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                    <ConfirmMessageModal
                      open={showStampEvidenceConfirmModal}
                      onClose={() => setShowStampEvidenceConfirmModal(false)}
                      previewContent={stampEvidencePreviewMessage || "—"}
                      onConfirm={() => void handleConfirmStampPaperEvidenceSend()}
                      disableConfirm={!stampEvidencePreviewMessage?.trim()}
                      title="Confirm Message to Stamp Vendor"
                    />
                  </form>
                ) : opposedSubStep === "stamp-from-vendor-evidence" ? (
                  <form
                    id={
                      OPPOSITION_COUNTER_STAMP_FROM_VENDOR_EVIDENCE_FORM_ID
                    }
                    onSubmit={(ev) =>
                      void handleStampPaperFromVendorSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <StampPaperFromVendorForEvidenceFields
                      status={stampPaperEvidence}
                      draft={stampPaperFromVendorDraft}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onPatch={(tmNo, patch) =>
                        setStampPaperFromVendorDraft((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              amountPayableToStampVendorForEvidence: "",
                              paidToVendorForEvidence: null,
                            }),
                            ...patch,
                          },
                        }))
                      }
                    />
                  </form>
                ) : opposedSubStep === "documents-from-client-evidence" ? (
                  <form
                    id={
                      OPPOSITION_COUNTER_DOCUMENTS_FROM_CLIENT_EVIDENCE_FORM_ID
                    }
                    onSubmit={(ev) =>
                      void handleDocumentsFromClientEvidenceSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <DocumentsFromClientForEvidenceFields
                      status={documentsFromClientEvidence}
                      draft={documentsFromClientEvidenceDraft}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onChange={(tmNo, val) =>
                        setDocumentsFromClientEvidenceDraft((prev) => ({
                          ...prev,
                          [tmNo]: val,
                        }))
                      }
                    />
                  </form>
                ) : opposedSubStep === "preparation-evidence-affidavit" ? (
                  <form
                    id={
                      OPPOSITION_COUNTER_PREPARATION_EVIDENCE_AFFIDAVIT_FORM_ID
                    }
                    onSubmit={(ev) =>
                      void handleEvidenceAffidavitSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <EvidenceAffidavitFields
                      status={evidenceAffidavit}
                      perTm={evidenceAffidavitPerTm}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      generating={evidenceAffidavitGenerating}
                      downloading={evidenceAffidavitDownloading}
                      onPatch={(tmNo, patch) =>
                        setEvidenceAffidavitPerTm((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              noOfParagraphsInOpponentIntro: "",
                              paragraphWiseReplies: [
                                { heading: "", text: "" },
                              ],
                              nameOfOpponentsAttorney: "",
                              addressOfOpponentsAttorney: "",
                              emailOfOpponentsAttorney: "",
                              dateOfEvidenceAffidavit: "",
                            }),
                            ...patch,
                          },
                        }))
                      }
                      onGeneratePdf={(app) =>
                        void handleGenerateEvidenceAffidavitPdf(app)
                      }
                      onDownloadPdf={(app) =>
                        void handleDownloadEvidenceAffidavitPdf(app)
                      }
                      onPreviewPdf={(app) => {
                        if (app.evidenceAffidavitPdfUrl) {
                          setPreview({
                            url: app.evidenceAffidavitPdfUrl,
                            name: `Evidence Affidavit - ${app.tmNo}`,
                            kind: detectPreviewKind(`${app.tmNo}.pdf`),
                          });
                        }
                      }}
                    />
                  </form>
                ) : opposedSubStep === "signature-evidence-affidavit" ? (
                  <form
                    id={OPPOSITION_COUNTER_SIGNATURE_AFFIDAVIT_FORM_ID}
                    onSubmit={(ev) =>
                      void handleSignatureAffidavitSubmit(ev)
                    }
                    className="flex flex-col w-full pt-1"
                  >
                    <ProcessToNextStepPanel
                      recorded={hasRecordedSignatureAffidavit}
                      recording={recordingSignatureAffidavit}
                      onProceed={() => {
                        void handleSignatureAffidavitSubmit(
                          new Event("submit") as unknown as React.FormEvent
                        );
                      }}
                      doneLabel="Signature Recorded"
                    />
                  </form>
                ) : opposedSubStep === "notarisation-evidence" ? (
                  <form
                    id={OPPOSITION_COUNTER_NOTARISATION_EVIDENCE_FORM_ID}
                    onSubmit={(ev) =>
                      void handleNotarisationEvidenceSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <NotarisationForEvidenceFields
                      status={notarisationEvidence}
                      draft={notarisationEvidenceDraft}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onPatch={(tmNo, patch) => {
                        setNotarisationEvidenceDraft((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              amountPayableToNotaryForEvidence: "",
                              paidToNotaryForEvidence: null,
                            }),
                            ...patch,
                          },
                        }));
                        scheduleNotarisationAutoSave(tmNo);
                      }}
                    />
                  </form>
                ) : opposedSubStep === "scanning-evidence" ? (
                  <form
                    id={OPPOSITION_COUNTER_SCANNING_EVIDENCE_FORM_ID}
                    onSubmit={(ev) => void handleScanningEvidenceSubmit(ev)}
                    className="flex flex-col w-full pt-1"
                  >
                    <ProcessToNextStepPanel
                      recorded={hasRecordedScanningEvidence}
                      recording={recordingScanningEvidence}
                      onProceed={() => {
                        void handleScanningEvidenceSubmit(
                          new Event("submit") as unknown as React.FormEvent
                        );
                      }}
                      doneLabel="Scanning Recorded"
                    />
                  </form>
                ) : opposedSubStep === "file-evidence-affidavit" ? (
                  <form
                    id={OPPOSITION_COUNTER_FILE_EVIDENCE_AFFIDAVIT_FORM_ID}
                    onSubmit={(ev) =>
                      void handleFileEvidenceAffidavitSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <FileEvidenceAffidavitFields
                      status={fileEvidenceAffidavit}
                      perTm={fileEvidenceAffidavitPerTm}
                      lastDateDraft={fileEvidenceAffidavitLastDateDraft}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onPatch={(tmNo, patch) => {
                        setFileEvidenceAffidavitPerTm((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              filingDate: "",
                              pendingFile: null,
                            }),
                            ...patch,
                          },
                        }));
                        // Clear manual override when the filing date changes so
                        // the new auto-calculation takes effect.
                        if (Object.prototype.hasOwnProperty.call(patch, "filingDate")) {
                          setFileEvidenceAffidavitLastDateDraft((prev) => ({
                            ...prev,
                            [tmNo]: "",
                          }));
                        }
                      }}
                      onLastDateChange={(tmNo, val) =>
                        setFileEvidenceAffidavitLastDateDraft((prev) => ({
                          ...prev,
                          [tmNo]: val,
                        }))
                      }
                      onPreviewExisting={(row) => {
                        if (
                          row.filedEvidenceAffidavitPdfUrl &&
                          row.filedEvidenceAffidavitPdfKey
                        ) {
                          setPreview({
                            url: row.filedEvidenceAffidavitPdfUrl,
                            name: `Filed Evidence Affidavit - ${row.tmNo}`,
                            kind: detectPreviewKind(
                              `${row.tmNo}.pdf`
                            ),
                          });
                        }
                      }}
                      onPreviewPending={(row, file) => {
                        setPreview({
                          url: URL.createObjectURL(file),
                          name: file.name,
                          kind: detectPreviewKind(file.name, file.type),
                          revokeOnClose: true,
                        });
                      }}
                    />
                  </form>
                ) : opposedSubStep === "inform-filing-evidence" ? (
                  <form
                    id={OPPOSITION_COUNTER_INFORM_FILING_EVIDENCE_FORM_ID}
                    onSubmit={(ev) =>
                      void handleInformFilingEvidenceSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <InformFilingEvidenceMessagePreview
                      preview={informFilingEvidencePreview}
                    />
                    {hasSentInformFilingEvidence && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={resendingInformFilingEvidence}
                          onClick={() =>
                            openResendConfirm(
                              "Resend Inform Client about Filing of Evidence?",
                              "The client will receive this WhatsApp message again.",
                              () => void handleResendInformFilingEvidence(),
                              informFilingEvidencePreview?.message,
                            )
                          }
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingInformFilingEvidence
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "download-rejoinder-affidavit" ? (
                  <form
                    id={OPPOSITION_COUNTER_DOWNLOAD_REJOINDER_FORM_ID}
                    onSubmit={(ev) => void handleDownloadRejoinderSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <DownloadRejoinderAffidavitFields
                      status={downloadRejoinder}
                      perTm={downloadRejoinderPerTm}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onPatch={(tmNo, patch) =>
                        setDownloadRejoinderPerTm((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              rejoinderDate: "",
                              pendingFile: null,
                            }),
                            ...patch,
                          },
                        }))
                      }
                      onPreviewExisting={(row) => {
                        if (
                          row.rejoinderAffidavitPdfUrl &&
                          row.rejoinderAffidavitPdfKey
                        ) {
                          setPreview({
                            url: row.rejoinderAffidavitPdfUrl,
                            name: `Rejoinder Affidavit - ${row.tmNo}`,
                            kind: detectPreviewKind(`${row.tmNo}.pdf`),
                          });
                        }
                      }}
                      onPreviewPending={(_row, file) => {
                        setPreview({
                          url: URL.createObjectURL(file),
                          name: file.name,
                          kind: detectPreviewKind(file.name, file.type),
                          revokeOnClose: true,
                        });
                      }}
                    />
                  </form>
                ) : opposedSubStep === "status-opposition-hearing" ? (
                  <form
                    id={
                      OPPOSITION_COUNTER_OPPOSITION_HEARING_STATUS_FORM_ID
                    }
                    onSubmit={(ev) =>
                      void handleOppositionHearingStatusSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <OppositionHearingStatusFields
                      status={oppositionHearing}
                      perTm={oppositionHearingPerTm}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onPatch={(tmNo, patch) =>
                        setOppositionHearingPerTm((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              hearingDate: "",
                              pendingFile: null,
                            }),
                            ...patch,
                          },
                        }))
                      }
                      onPreviewExisting={(row) => {
                        if (
                          row.oppositionHearingNoticeFileUrl &&
                          row.oppositionHearingNoticeFilename
                        ) {
                          setPreview({
                            url: row.oppositionHearingNoticeFileUrl,
                            name: row.oppositionHearingNoticeFilename,
                            kind: detectPreviewKind(
                              row.oppositionHearingNoticeFilename
                            ),
                          });
                        }
                      }}
                      onPreviewPending={(_row, file) => {
                        setPreview({
                          url: URL.createObjectURL(file),
                          name: file.name,
                          kind: detectPreviewKind(file.name, file.type),
                          revokeOnClose: true,
                        });
                      }}
                    />
                  </form>
                ) : opposedSubStep === "notice-opposition-hearing" ? (
                  <form
                    id={
                      OPPOSITION_COUNTER_OPPOSITION_HEARING_NOTICE_FORM_ID
                    }
                    onSubmit={(ev) =>
                      void handleOppositionHearingNoticeSubmit(ev)
                    }
                    className="flex flex-col gap-3 pt-1"
                  >
                    {oppositionHearingTmsWithPdf.length === 0 ? (
                      <p className="text-sm text-[#6D6D6D]">
                        No Opposition Hearing Notice PDFs uploaded yet. Add
                        them in Status details for Opposition Hearing first.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {oppositionHearingTmsWithPdf.map((app) => (
                          <OppositionHearingNoticePdfRow
                            key={app.tmNo}
                            app={app}
                            onPreview={() => {
                              if (
                                app.oppositionHearingNoticeFileUrl &&
                                app.oppositionHearingNoticeFilename
                              ) {
                                setPreview({
                                  url: app.oppositionHearingNoticeFileUrl,
                                  name: app.oppositionHearingNoticeFilename,
                                  kind: detectPreviewKind(
                                    app.oppositionHearingNoticeFilename
                                  ),
                                });
                              }
                            }}
                          />
                        ))}
                      </div>
                    )}
                    {hasSentOppositionHearingNotice && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={
                            resendingOppositionHearingNotice ||
                            !canSendOppositionHearingNotice
                          }
                          onClick={() =>
                            setOppositionHearingNoticeResendConfirmOpen(true)
                          }
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingOppositionHearingNotice
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "inform-opposition-hearing" ? (
                  <form
                    id={
                      OPPOSITION_COUNTER_OPPOSITION_HEARING_INFORM_FORM_ID
                    }
                    onSubmit={(ev) =>
                      void handleOppositionHearingInformSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <InformOppositionHearingMessagePreview
                      preview={informOppositionHearingPreview}
                    />
                    {hasSentOppositionHearingInform && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={resendingOppositionHearingInform}
                          onClick={() =>
                            openResendConfirm(
                              "Resend Inform Client about Opposition Hearing?",
                              "The client will receive this WhatsApp message again.",
                              () => void handleResendOppositionHearingInform(),
                              informOppositionHearingPreview?.message,
                            )
                          }
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingOppositionHearingInform
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "invoice-opposition-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_OPPOSITION_HEARING_INVOICE_FORM_ID}
                    onSubmit={(ev) =>
                      void handleOppositionHearingInvoiceSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <OppositionHearingInvoiceFields
                      leadId={leadId}
                      status={oppositionHearingInvoice}
                      preview={oppositionHearingInvoicePreviewData}
                      rows={oppositionHearingInvoiceRows}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      generating={oppositionHearingInvoiceGenerating}
                      onPatchRow={(tmNo, patch) =>
                        setOppositionHearingInvoiceRows((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              invoiceAmount: "0",
                              invoiceDate: "",
                            }),
                            ...patch,
                          },
                        }))
                      }
                      onGeneratePdf={() =>
                        void handleGenerateOppositionHearingInvoicePdf()
                      }
                    />
                    {hasSentOppositionHearingInvoice && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={resendingOppositionHearingInvoice}
                          onClick={() => {
                            setOppositionHearingInvoiceResendConfirmOpen(true);
                          }}
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingOppositionHearingInvoice
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "call-opposition-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_OPPOSITION_HEARING_CALL_FORM_ID}
                    onSubmit={(ev) =>
                      void handleOppositionHearingCallSubmit(ev)
                    }
                    className="flex flex-col w-full pt-1"
                  >
                    <CallClientPanel
                      title="Call the client to explain the objection and the need for a reply. No data entry required for this step."
                      recorded={hasRecordedOppositionHearingCall}
                      recording={recordingOppositionHearingCall}
                      onRecord={() => {
                        void handleOppositionHearingCallSubmit(
                          new Event("submit") as unknown as React.FormEvent
                        );
                      }}
                    />
                  </form>
                ) : opposedSubStep === "confirmation-opposition-hearing" ? (
                  <form
                    id={
                      OPPOSITION_COUNTER_OPPOSITION_HEARING_CONFIRMATION_FORM_ID
                    }
                    onSubmit={(ev) =>
                      void handleOppositionHearingConfirmationSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <OppositionHearingConfirmationFields
                      status={oppositionHearingConfirmation}
                      draft={oppositionHearingConfirmationDraft}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onPick={(tmNo, val) =>
                        setOppositionHearingConfirmationDraft((p) => ({
                          ...p,
                          [tmNo]: val,
                        }))
                      }
                    />
                  </form>
                ) : opposedSubStep === "invoice-tmm-before-opp-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_TM_M_BEFORE_HEARING_INVOICE_FORM_ID}
                    onSubmit={(ev) =>
                      void handleTmMBeforeHearingInvoiceSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <TmMBeforeHearingInvoiceFields
                      leadId={leadId}
                      status={tmMBeforeHearingInvoice}
                      preview={tmMBeforeHearingInvoicePreview}
                      invoiceDate={tmMBeforeHearingInvoiceDateDraft}
                      onInvoiceDateChange={setTmMBeforeHearingInvoiceDateDraft}
                      invoiceAmount={tmMBeforeHearingInvoiceAmountDraft}
                      onInvoiceAmountChange={
                        setTmMBeforeHearingInvoiceAmountDraft
                      }
                      generating={tmMBeforeHearingInvoiceGenerating}
                      downloading={tmMBeforeHearingInvoiceDownloading}
                      onGeneratePdf={() =>
                        void handleGenerateTmMBeforeHearingInvoicePdf()
                      }
                      onDownloadPdf={() =>
                        void handleDownloadTmMBeforeHearingInvoicePdf()
                      }
                    />
                  </form>
                ) : opposedSubStep === "payment-tmm-before-opp-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_TM_M_BEFORE_HEARING_PAYMENT_FORM_ID}
                    onSubmit={(ev) =>
                      void handleTmMBeforeHearingPaymentSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <TmMBeforeHearingPaymentFields
                      status={tmMBeforeHearingInvoice}
                      collectedAmount={tmMBeforeHearingCollectedAmountDraft}
                      onChange={(v) =>
                        setTmMBeforeHearingCollectedAmountDraft(v)
                      }
                    />
                  </form>
                ) : opposedSubStep === "file-tmm-before-opp-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_TM_M_BEFORE_HEARING_FILED_FORM_ID}
                    onSubmit={(ev) =>
                      void handleTmMBeforeHearingFiledSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <TmMBeforeHearingFiledFields
                      status={tmMBeforeHearingFiled}
                      perTm={tmMBeforeHearingFiledPerTm}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onPatch={(tmNo, patch) =>
                        setTmMBeforeHearingFiledPerTm((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              filingDate: "",
                              pendingFile: null,
                            }),
                            ...patch,
                          },
                        }))
                      }
                      onPreviewExisting={(row) => {
                        if (
                          row.tmMBeforeOppositionHearingFileUrl &&
                          row.tmMBeforeOppositionHearingFilename
                        ) {
                          setPreview({
                            url: row.tmMBeforeOppositionHearingFileUrl,
                            name: row.tmMBeforeOppositionHearingFilename,
                            kind: detectPreviewKind(
                              row.tmMBeforeOppositionHearingFilename
                            ),
                          });
                        }
                      }}
                      onPreviewPending={(_row, file) => {
                        setPreview({
                          url: URL.createObjectURL(file),
                          name: file.name,
                          kind: detectPreviewKind(file.name, file.type),
                          revokeOnClose: true,
                        });
                      }}
                    />
                  </form>
                ) : opposedSubStep === "inform-tmm-before-opp-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_TM_M_BEFORE_HEARING_INFORM_FORM_ID}
                    onSubmit={(ev) =>
                      void handleInformTmMBeforeHearingSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <InformTmMBeforeHearingMessagePreview
                      preview={informTmMBeforeHearingPreview}
                    />
                    {hasSentInformTmMBeforeHearing && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={resendingInformTmMBeforeHearing}
                          onClick={() =>
                            openResendConfirm(
                              "Resend Inform Client about TM-M before Hearing?",
                              "The client will receive this WhatsApp message again.",
                              () => void handleResendInformTmMBeforeHearing(),
                              informTmMBeforeHearingPreview?.message,
                            )
                          }
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingInformTmMBeforeHearing
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "payment-opposition-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_OPPOSITION_HEARING_PAYMENT_FORM_ID}
                    onSubmit={(ev) =>
                      void handleOppositionHearingPaymentSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <OppositionHearingPaymentFields
                      status={oppositionHearingPayment}
                      collectedAmount={oppositionHearingPaymentDraft}
                      onChange={(v) => setOppositionHearingPaymentDraft(v)}
                    />
                  </form>
                ) : opposedSubStep === "documents-opp-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_DOCUMENTS_OPP_HEARING_FORM_ID}
                    onSubmit={(ev) =>
                      void handleDocumentsOppHearingSubmit(ev)
                    }
                    className="flex flex-col w-full pt-1"
                  >
                    <ProcessToNextStepPanel
                      recorded={hasRecordedDocumentsOppHearing}
                      recording={recordingDocumentsOppHearing}
                      onProceed={() => {
                        void handleDocumentsOppHearingSubmit(
                          new Event("submit") as unknown as React.FormEvent
                        );
                      }}
                      doneLabel="Documents Recorded"
                    />
                  </form>
                ) : opposedSubStep === "filing-opp-hearing-document" ? (
                  <form
                    id={OPPOSITION_COUNTER_FILING_OPP_HEARING_DOC_FORM_ID}
                    onSubmit={(ev) =>
                      void handleFilingOppHearingDocSubmit(ev)
                    }
                    className="flex flex-col w-full pt-1"
                  >
                    <ProcessToNextStepPanel
                      recorded={hasRecordedFilingOppHearingDoc}
                      recording={recordingFilingOppHearingDoc}
                      onProceed={() => {
                        void handleFilingOppHearingDocSubmit(
                          new Event("submit") as unknown as React.FormEvent
                        );
                      }}
                      doneLabel="Filing Recorded"
                    />
                  </form>
                ) : opposedSubStep === "note-on-opp-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_NOTE_ON_OPP_HEARING_FORM_ID}
                    onSubmit={(ev) => void handleNoteOnOppHearingSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <NoteOnOppositionHearingFields
                      status={noteOnOppHearing}
                      selectedIdx={noteOnOppHearingSelectedIdx}
                      onSelectIdx={setNoteOnOppHearingSelectedIdx}
                      draft={noteOnOppHearingDraft}
                      onPatch={(ackId, patch) =>
                        setNoteOnOppHearingDraft((prev) => ({
                          ...prev,
                          [ackId]: {
                            ...(prev[ackId] ?? {
                              noteOtherOpposition1: "",
                              noteOtherOpposition2: "",
                              similarMarkNotes: [],
                            }),
                            ...patch,
                          },
                        }))
                      }
                      onPreviewImage={(url, name) =>
                        setPreview({
                          url,
                          name,
                          kind: detectPreviewKind(name),
                        })
                      }
                    />
                  </form>
                ) : opposedSubStep === "attend-opp-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_ATTEND_OPP_HEARING_FORM_ID}
                    onSubmit={(ev) => void handleAttendOppHearingSubmit(ev)}
                    className="flex flex-col gap-5 pt-1"
                  >
                    <AttendOppositionHearingFields
                      status={attendOppHearing}
                      selectedIdx={attendOppHearingSelectedIdx}
                      onSelectIdx={setAttendOppHearingSelectedIdx}
                      draft={attendOppHearingDraft}
                      onPatch={(ackId, patch) =>
                        setAttendOppHearingDraft((prev) => ({
                          ...prev,
                          [ackId]: {
                            ...(prev[ackId] ?? {
                              hearingStatus: null,
                              hearingOfficerRemark: null,
                              directionOfOfficer: "",
                            }),
                            ...patch,
                          },
                        }))
                      }
                    />
                  </form>
                ) : opposedSubStep === "inform-after-opp-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_INFORM_AFTER_OPP_HEARING_FORM_ID}
                    onSubmit={(ev) =>
                      void handleInformAfterOppHearingSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <InformAfterOppositionHearingFields
                      status={informAfterOppHearing}
                    />
                    {hasSentInformAfterOppHearing && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={resendingInformAfterOppHearing}
                          onClick={() =>
                            openResendConfirm(
                              "Resend message to client?",
                              "The client will receive this WhatsApp message again.",
                              () => void handleResendInformAfterOppHearing(),
                            )
                          }
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingInformAfterOppHearing
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "invoice-tmm-after-opp-hearing" ? (
                  <form
                    id={
                      OPPOSITION_COUNTER_INVOICE_TMM_AFTER_OPP_HEARING_FORM_ID
                    }
                    onSubmit={(ev) =>
                      void handleTmMAfterHearingInvoiceSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <TmMAfterHearingInvoiceFields
                      leadId={leadId}
                      status={tmMAfterHearingInvoice}
                      preview={tmMAfterHearingInvoicePreview}
                      draft={tmMAfterHearingInvoiceDraft}
                      generating={tmMAfterHearingInvoiceGenerating}
                      downloading={tmMAfterHearingInvoiceDownloading}
                      onPatch={(patch) =>
                        setTmMAfterHearingInvoiceDraft((prev) => ({
                          ...prev,
                          ...patch,
                        }))
                      }
                      onGeneratePdf={() =>
                        void handleGenerateTmMAfterHearingInvoicePdf()
                      }
                      onDownloadPdf={() =>
                        void handleDownloadTmMAfterHearingInvoicePdf()
                      }
                      onSaveDraft={async () => {
                        await persistTmMAfterHearingInvoice();
                      }}
                    />
                  </form>
                ) : opposedSubStep === "payment-tmm-after-opp-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_PAYMENT_TMM_AFTER_OPP_HEARING_FORM_ID}
                    onSubmit={(ev) =>
                      void handleFileTmMAfterHearingSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <TmMAfterHearingPaymentFields
                      status={tmMAfterHearingInvoice}
                      collectedAmount={tmMAfterHearingCollectedDraft}
                      onChange={(v) => setTmMAfterHearingCollectedDraft(v)}
                    />
                  </form>
                ) : opposedSubStep === "file-tmm-after-opp-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_FILE_TMM_AFTER_OPP_HEARING_FORM_ID}
                    onSubmit={(ev) =>
                      void handleTmMAfterHearingFiledSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <TmMAfterHearingFiledFields
                      status={tmMAfterHearingFiled}
                      perTm={tmMAfterHearingFiledPerTm}
                      selectedTmNo={opposedSelectedTm?.tmNo ?? null}
                      onPatch={(tmNo, patch) =>
                        setTmMAfterHearingFiledPerTm((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              filingDate: "",
                              pendingFile: null,
                            }),
                            ...patch,
                          },
                        }))
                      }
                      onPreviewExisting={(row) => {
                        if (
                          row.tmMAfterOppositionHearingFileUrl &&
                          row.tmMAfterOppositionHearingFilename
                        ) {
                          setPreview({
                            url: row.tmMAfterOppositionHearingFileUrl,
                            name: row.tmMAfterOppositionHearingFilename,
                            kind: detectPreviewKind(
                              row.tmMAfterOppositionHearingFilename
                            ),
                          });
                        }
                      }}
                      onPreviewPending={(_row, file) => {
                        setPreview({
                          url: URL.createObjectURL(file),
                          name: file.name,
                          kind: detectPreviewKind(file.name, file.type),
                          revokeOnClose: true,
                        });
                      }}
                    />
                  </form>
                ) : opposedSubStep === "inform-tmm-after-opp-hearing" ? (
                  <form
                    id={
                      OPPOSITION_COUNTER_INFORM_TMM_AFTER_OPP_HEARING_FORM_ID
                    }
                    onSubmit={(ev) =>
                      void handleInformTmMAfterHearingSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <InformTmMAfterHearingFields
                      preview={informTmMAfterHearingPreview}
                      filed={tmMAfterHearingFiled}
                    />
                    {hasSentInformTmMAfterHearing && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={resendingInformTmMAfterHearing}
                          onClick={() =>
                            openResendConfirm(
                              "Resend Inform Client about TM-M after Hearing?",
                              "The client will receive this WhatsApp message again.",
                              () => void handleResendInformTmMAfterHearing(),
                              informTmMAfterHearingPreview?.message,
                            )
                          }
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingInformTmMAfterHearing
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : opposedSubStep === "subsequent-opp-hearings" ? (
                  <form
                    id={OPPOSITION_COUNTER_SUBSEQUENT_OPP_HEARINGS_FORM_ID}
                    onSubmit={(ev) =>
                      void handleSubsequentOppHearingSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <SubsequentOppHearingFields
                      status={subsequentOppHearing}
                      draft={subsequentOppHearingDraft}
                      onPatch={(tmNo, patch) =>
                        setSubsequentOppHearingDraft((prev) => ({
                          ...prev,
                          [tmNo]: {
                            ...(prev[tmNo] ?? {
                              dateOfHearing: "",
                              pendingNoticeFile: null,
                              hearingStatus: null,
                              hearingOfficerRemark: null,
                              directionOfOfficer: "",
                              pendingPostponementFile: null,
                            }),
                            ...patch,
                          },
                        }))
                      }
                      onSendNotice={() =>
                        openResendConfirm(
                          "Send Subsequent Opposition Hearing notice to client?",
                          "The client will receive this WhatsApp message with the upcoming hearing date.",
                          () => void handleSendSubsequentOppHearingNotice(),
                          subsequentOppHearingNoticePreview?.message
                        )
                      }
                      sendingNotice={sendingSubsequentOppHearingNotice}
                      noticePreview={subsequentOppHearingNoticePreview}
                      noticePreviewError={subsequentOppHearingNoticePreviewError}
                    />
                  </form>
                ) : opposedSubStep === "invoice-subsequent-opp-hearing" ? (
                  <form
                    id={
                      OPPOSITION_COUNTER_SUBSEQUENT_OPP_HEARING_INVOICE_FORM_ID
                    }
                    onSubmit={(ev) =>
                      void handleSubsequentOppHearingInvoiceSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <SubsequentOppHearingInvoiceFields
                      status={subsequentOppHearingInvoice}
                      invoiceAmount={subsequentOppHearingInvoiceAmountDraft}
                      onInvoiceAmountChange={
                        setSubsequentOppHearingInvoiceAmountDraft
                      }
                      invoiceDate={subsequentOppHearingInvoiceDateDraft}
                      onInvoiceDateChange={
                        setSubsequentOppHearingInvoiceDateDraft
                      }
                      includeInvoiceNo={
                        subsequentOppHearingInvoiceIncludeNoDraft
                      }
                      onIncludeInvoiceNoChange={
                        setSubsequentOppHearingInvoiceIncludeNoDraft
                      }
                      generating={subsequentOppHearingInvoiceGenerating}
                      downloading={subsequentOppHearingInvoiceDownloading}
                      sending={subsequentOppHearingInvoiceSending}
                      onGeneratePdf={() =>
                        void handleGenerateSubsequentOppHearingInvoicePdf()
                      }
                      onDownloadPdf={() =>
                        void handleDownloadSubsequentOppHearingInvoicePdf()
                      }
                      onSendInvoice={() =>
                        void handleSendSubsequentOppHearingInvoice()
                      }
                    />
                  </form>
                ) : opposedSubStep === "payment-subsequent-opp-hearing" ? (
                  <form
                    id={
                      OPPOSITION_COUNTER_SUBSEQUENT_OPP_HEARING_PAYMENT_FORM_ID
                    }
                    onSubmit={(ev) =>
                      void handleSubsequentOppHearingPaymentSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <SubsequentOppHearingPaymentFields
                      status={subsequentOppHearingInvoice}
                      collectedAmount={subsequentOppHearingCollectedDraft}
                      onChange={setSubsequentOppHearingCollectedDraft}
                    />
                  </form>
                ) : opposedSubStep === "order-of-opp-hearing" ? (
                  <form
                    id={OPPOSITION_COUNTER_ORDER_OF_OPP_HEARING_FORM_ID}
                    onSubmit={(ev) =>
                      void handleOrderOfOppHearingSubmit(ev)
                    }
                    className="flex flex-col gap-5 pt-1"
                  >
                    <OrderOfOppHearingFields
                      status={orderOfOppHearing}
                      draft={orderOfOppHearingDraft}
                      onChange={(tmNo, outcome) =>
                        setOrderOfOppHearingDraft((prev) => ({
                          ...prev,
                          [tmNo]: outcome,
                        }))
                      }
                    />
                    {hasSentOrderOfOppHearing && (
                      <div className="flex flex-row items-center gap-3 w-full pt-1">
                        <button
                          type="button"
                          disabled={resendingOrderOfOppHearing}
                          onClick={openOrderOfOppHearingResendConfirm}
                          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {resendingOrderOfOppHearing
                            ? "Resending…"
                            : "Resend Message"}
                        </button>
                      </div>
                    )}
                  </form>
                ) : (
                  <p className="text-sm text-[#6D6D6D]">
                    {OPPOSED_SUB_STEPS.find((s) => s.id === opposedSubStep)
                      ?.label}{" "}
                    — Coming soon
                  </p>
                )}
              </div>
            </div>
          </>
        )
      ) : tms.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center gap-1 border border-dashed border-[#EEEEEE] rounded-xl py-12 px-6 text-center"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-sm font-medium text-[#222222]">
            No applications with this status yet.
          </p>
          <p className="text-xs text-[#6D6D6D]">
            Applications will appear here once their status is set to
            “Advertised in Journal”.
          </p>
        </div>
      ) : (
        <>
          {/* TM number selector — only TMs in this status are returned by the API */}
          <div className="flex flex-row flex-wrap gap-3">
            {tms.map((entry, idx) => {
              const isActive = selectedTmIdx === idx;
              return (
                <button
                  key={entry.tmNo}
                  type="button"
                  onClick={() => setSelectedTmIdx(idx)}
                  className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors ${
                    isActive
                      ? "bg-[#222222] text-white"
                      : "bg-[#F7F7F7] text-[#222222]"
                  }`}
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.75rem",
                  }}
                >
                  <RadioCircle active={isActive} />
                  {formatTmNoForDisplay(entry.tmNo)}
                </button>
              );
            })}
          </div>

          {/* Sub-step panel */}
          <div className="border border-[#EEEEEE] rounded-xl overflow-hidden flex flex-col md:flex-row min-h-0">
            {/* Left rail */}
            <div className="w-full md:w-[230px] md:flex-shrink-0 md:border-r md:border-b-0 border-b border-[#EEEEEE] p-2 flex md:flex-col gap-1 overflow-x-auto md:overflow-y-auto">
              {SUB_STEPS.map((step) => {
                const isActive = subStep === step.id;
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => setSubStep(step.id)}
                    className={`w-full flex items-center justify-between px-2.5 py-2.5 gap-2.5 rounded-[6px] text-xs whitespace-nowrap md:whitespace-normal transition-colors ${
                      isActive
                        ? "bg-[#222222] text-white font-medium"
                        : "bg-[#F7F7F7] text-[#222222] font-normal hover:bg-[#EEEEEE]"
                    }`}
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    <span className="text-left line-clamp-1" title={step.label}>{step.label}</span>
                    <ChevronRight active={isActive} />
                  </button>
                );
              })}
            </div>

            {/* Right content */}
            <div className="flex-1 min-w-0 px-4 py-5 md:px-5 overflow-y-auto">
              {subStep === "status-details" ? (
                <form
                  id={ADVERTISED_IN_JOURNAL_STATUS_FORM_ID}
                  onSubmit={(ev) => void handleStatusDetailsSubmit(ev)}
                  className="flex flex-col gap-5 pt-1"
                >
                  <StatusDetailsFields
                    selectedTm={selectedTm}
                    journalDate={selectedTmForm.journalDate}
                    lastDateForOppositionYmd={lastDateForOppositionYmd}
                    acknowledgementNo={status?.acknowledgementNo ?? null}
                    onJournalDateChange={(d) =>
                      selectedTm && handleJournalDateChange(selectedTm.tmNo, d)
                    }
                  />

                  {selectedTm && (
                    <FileUploadZone
                      app={selectedTm}
                      pendingFile={selectedTmForm.pendingFile}
                      isDragging={dragOverTmNo === selectedTm.tmNo}
                      onDragOver={() => setDragOverTmNo(selectedTm.tmNo)}
                      onDragLeave={() => setDragOverTmNo(null)}
                      onDrop={(file) => {
                        setDragOverTmNo(null);
                        handleFileChosen(selectedTm.tmNo, file);
                      }}
                      onPick={(e) => handleFileInput(selectedTm.tmNo, e)}
                      onPreview={() => openSelectedTmFilePreview(selectedTm)}
                      onRemove={() => removeFileForSelectedTm(selectedTm.tmNo)}
                    />
                  )}

                  {autoSaving && (
                    <p className="text-[11px] text-[#6D6D6D]">Saving…</p>
                  )}
                </form>
              ) : subStep === "notice-to-client" ? (
                <form
                  id={ADVERTISED_IN_JOURNAL_NOTICE_FORM_ID}
                  onSubmit={(ev) => void handleNoticeSubmit(ev)}
                  className="flex flex-col gap-3 pt-1"
                >
                  {advertisedTmsWithPdf.length === 0 ? (
                    <p className="text-sm text-[#6D6D6D]">
                      No Order from Registry PDFs uploaded yet. Add them in
                      Status Details first.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {advertisedTmsWithPdf.map((app) => (
                        <NoticePdfRow
                          key={app.tmNo}
                          app={app}
                          onPreview={() => openSelectedTmFilePreview(app)}
                        />
                      ))}
                    </div>
                  )}

                  {hasSentNotice && (
                    <div className="flex flex-row items-center gap-3 w-full pt-1">
                      <button
                        type="button"
                        disabled={resendingNotice || !canSendNotice}
                        onClick={() => {
                          setNoticeResendConfirmOpen(true);
                          void loadNoticePreview();
                        }}
                        className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {resendingNotice ? "Resending…" : "Resend Message"}
                      </button>
                    </div>
                  )}
                </form>
              ) : (
                <form
                  id={ADVERTISED_IN_JOURNAL_INFORM_FORM_ID}
                  onSubmit={(ev) => void handleInformSubmit(ev)}
                  className="flex flex-col gap-5 pt-1"
                >
                  <InformScenarioPicker
                    value={informScenario}
                    onChange={setInformScenario}
                    disabled={hasSentInform}
                  />

                  <InformMessagePreview
                    leadId={leadId}
                    scenario={informScenario}
                  />

                  {hasSentInform && (
                    <div className="flex flex-row items-center gap-3 w-full pt-1">
                      <button
                        type="button"
                        disabled={resendingInform || !informScenario}
                        onClick={() =>
                          openResendConfirm(
                            "Resend message to client?",
                            "The client will receive this WhatsApp message again.",
                            () => void handleResendInform(),
                          )
                        }
                        className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {resendingInform ? "Resending…" : "Resend Message"}
                      </button>
                    </div>
                  )}
                </form>
              )}
            </div>
          </div>
        </>
      )}

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url ?? null}
        kind={preview?.kind}
      />

      <ConfirmMessageModal
        open={counterInvoiceSendConfirmOpen}
        onClose={() => {
          if (!counterInvoiceSending) setCounterInvoiceSendConfirmOpen(false);
        }}
        title="Confirm invoice to client"
        disableConfirm={counterInvoiceSending}
        previewSlot={
          <CounterInvoiceConfirmPreview
            leadId={leadId ?? null}
            status={counterInvoice}
            preview={counterInvoicePreview}
            selectedTmNo={opposedSelectedTm?.tmNo ?? null}
          />
        }
        onConfirm={() => {
          void performCounterInvoiceSend();
        }}
      />

      <ConfirmMessageModal
        open={counterInvoiceResendConfirmOpen}
        onClose={() => {
          if (!resendingCounterInvoice)
            setCounterInvoiceResendConfirmOpen(false);
        }}
        title="Resend invoice to client"
        disableConfirm={resendingCounterInvoice}
        previewSlot={
          <CounterInvoiceConfirmPreview
            leadId={leadId ?? null}
            status={counterInvoice}
            preview={counterInvoicePreview}
            selectedTmNo={opposedSelectedTm?.tmNo ?? null}
          />
        }
        onConfirm={() => {
          void handleResendCounterInvoice();
        }}
      />

      <ConfirmMessageModal
        open={evidenceInvoiceResendConfirmOpen}
        onClose={() => {
          if (!resendingEvidenceInvoice)
            setEvidenceInvoiceResendConfirmOpen(false);
        }}
        title="Resend invoice to client"
        disableConfirm={resendingEvidenceInvoice}
        previewSlot={
          <EvidenceInvoiceConfirmPreview
            leadId={leadId ?? null}
            status={evidenceInvoice}
            preview={evidenceInvoicePreviewData}
            selectedTmNo={opposedSelectedTm?.tmNo ?? null}
          />
        }
        onConfirm={() => {
          void handleResendEvidenceInvoice();
        }}
      />

      <ConfirmMessageModal
        open={oppositionHearingInvoiceResendConfirmOpen}
        onClose={() => {
          if (!resendingOppositionHearingInvoice)
            setOppositionHearingInvoiceResendConfirmOpen(false);
        }}
        title="Resend invoice to client"
        disableConfirm={resendingOppositionHearingInvoice}
        previewSlot={
          <OppositionHearingInvoiceConfirmPreview
            leadId={leadId ?? null}
            status={oppositionHearingInvoice}
            preview={oppositionHearingInvoicePreviewData}
            selectedTmNo={opposedSelectedTm?.tmNo ?? null}
          />
        }
        onConfirm={() => {
          void handleResendOppositionHearingInvoice();
        }}
      />

      <ConfirmMessageModal
        open={noticeSendConfirmOpen}
        onClose={() => {
          if (!noticeSending) setNoticeSendConfirmOpen(false);
        }}
        title="Forward Order from Registry to client?"
        disableConfirm={noticeSending}
        previewSlot={
          <NoticeConfirmPreview
            apps={advertisedTmsWithPdf}
            preview={noticePreview}
            loading={noticePreviewLoading}
            error={noticePreviewError}
          />
        }
        onConfirm={() => {
          void performNoticeSend();
        }}
      />

      <ConfirmMessageModal
        open={noticeResendConfirmOpen}
        onClose={() => {
          if (!resendingNotice) setNoticeResendConfirmOpen(false);
        }}
        title="Resend Order from Registry to client?"
        disableConfirm={resendingNotice}
        previewSlot={
          <NoticeConfirmPreview
            apps={advertisedTmsWithPdf}
            preview={noticePreview}
            loading={noticePreviewLoading}
            error={noticePreviewError}
          />
        }
        onConfirm={() => {
          void handleResendNotice();
        }}
      />

      <ConfirmMessageModal
        open={counterNoticeSendConfirmOpen}
        onClose={() => {
          if (!counterNoticeSending) setCounterNoticeSendConfirmOpen(false);
        }}
        title="Forward Opposition Notice to client?"
        disableConfirm={counterNoticeSending}
        previewSlot={
          <CounterNoticeConfirmPreview
            apps={opposedTmsWithNoticePdf}
            preview={counterNoticePreview}
            loading={counterNoticePreviewLoading}
            error={counterNoticePreviewError}
          />
        }
        onConfirm={() => {
          void performCounterNoticeSend();
        }}
      />

      <ConfirmMessageModal
        open={counterNoticeResendConfirmOpen}
        onClose={() => {
          if (!resendingCounterNotice)
            setCounterNoticeResendConfirmOpen(false);
        }}
        title="Resend Opposition Notice to client?"
        disableConfirm={resendingCounterNotice}
        previewSlot={
          <CounterNoticeConfirmPreview
            apps={opposedTmsWithNoticePdf}
            preview={counterNoticePreview}
            loading={counterNoticePreviewLoading}
            error={counterNoticePreviewError}
          />
        }
        onConfirm={() => {
          void handleResendCounterNotice();
        }}
      />

      <ConfirmMessageModal
        open={noticeEvidenceByOppSendConfirmOpen}
        onClose={() => {
          if (!noticeEvidenceByOppSending)
            setNoticeEvidenceByOppSendConfirmOpen(false);
        }}
        title="Forward Evidence by Opponent to client?"
        disableConfirm={noticeEvidenceByOppSending}
        previewSlot={
          <NoticeEvidenceByOpponentConfirmPreview
            apps={evidenceByOppTmsWithPdf}
            preview={noticeEvidenceByOppPreview}
            loading={noticeEvidenceByOppPreviewLoading}
            error={noticeEvidenceByOppPreviewError}
          />
        }
        onConfirm={() => {
          void performNoticeEvidenceByOppSend();
        }}
      />

      <ConfirmMessageModal
        open={noticeEvidenceByOppResendConfirmOpen}
        onClose={() => {
          if (!resendingNoticeEvidenceByOpp)
            setNoticeEvidenceByOppResendConfirmOpen(false);
        }}
        title="Resend Evidence by Opponent to client?"
        disableConfirm={resendingNoticeEvidenceByOpp}
        previewSlot={
          <NoticeEvidenceByOpponentConfirmPreview
            apps={evidenceByOppTmsWithPdf}
            preview={noticeEvidenceByOppPreview}
            loading={noticeEvidenceByOppPreviewLoading}
            error={noticeEvidenceByOppPreviewError}
          />
        }
        onConfirm={() => {
          void handleResendNoticeEvidenceByOpp();
        }}
      />

      <ConfirmMessageModal
        open={oppositionHearingNoticeSendConfirmOpen}
        onClose={() => {
          if (!oppositionHearingNoticeSending)
            setOppositionHearingNoticeSendConfirmOpen(false);
        }}
        title="Forward Opposition Hearing Notice to client?"
        disableConfirm={oppositionHearingNoticeSending}
        previewSlot={
          <OppositionHearingNoticeConfirmPreview
            apps={oppositionHearingTmsWithPdf}
            preview={oppositionHearingNoticePreview}
          />
        }
        onConfirm={() => {
          void performOppositionHearingNoticeSend();
        }}
      />

      <ConfirmMessageModal
        open={oppositionHearingNoticeResendConfirmOpen}
        onClose={() => {
          if (!resendingOppositionHearingNotice)
            setOppositionHearingNoticeResendConfirmOpen(false);
        }}
        title="Resend Opposition Hearing Notice to client?"
        disableConfirm={resendingOppositionHearingNotice}
        previewSlot={
          <OppositionHearingNoticeConfirmPreview
            apps={oppositionHearingTmsWithPdf}
            preview={oppositionHearingNoticePreview}
          />
        }
        onConfirm={() => {
          void handleResendOppositionHearingNotice();
        }}
      />

      <ConfirmDialog
        open={informAfterOppHearingSendConfirmOpen}
        title="Send Inform Client after Opposition Hearing?"
        description="The client will receive this WhatsApp message."
        confirmLabel={informAfterOppHearingSending ? "Sending…" : "Send"}
        cancelLabel="Cancel"
        busy={informAfterOppHearingSending}
        onClose={() => setInformAfterOppHearingSendConfirmOpen(false)}
        onConfirm={() => {
          void performInformAfterOppHearingSend();
        }}
      />

      <ConfirmMessageModal
        open={orderOfOppHearingSendConfirmOpen}
        onClose={() => {
          if (!orderOfOppHearingSending)
            setOrderOfOppHearingSendConfirmOpen(false);
        }}
        title="Send Order of Opposition Hearing to client?"
        disableConfirm={
          orderOfOppHearingSending ||
          orderOfOppHearingMessagePreviewLoading ||
          !orderOfOppHearingMessagesPreview.length
        }
        previewSlot={
          <OrderOfOppHearingMessagesConfirmPreview
            messages={orderOfOppHearingMessagesPreview}
            loading={orderOfOppHearingMessagePreviewLoading}
            error={orderOfOppHearingMessagePreviewError}
          />
        }
        onConfirm={() => {
          void performOrderOfOppHearingSend();
        }}
      />

      <ConfirmMessageModal
        open={orderOfOppHearingResendConfirmOpen}
        onClose={() => {
          if (!resendingOrderOfOppHearing)
            setOrderOfOppHearingResendConfirmOpen(false);
        }}
        title="Resend Order of Opposition Hearing to client?"
        disableConfirm={
          resendingOrderOfOppHearing ||
          orderOfOppHearingMessagePreviewLoading ||
          !orderOfOppHearingMessagesPreview.length
        }
        previewSlot={
          <OrderOfOppHearingMessagesConfirmPreview
            messages={orderOfOppHearingMessagesPreview}
            loading={orderOfOppHearingMessagePreviewLoading}
            error={orderOfOppHearingMessagePreviewError}
          />
        }
        onConfirm={() => {
          void handleResendOrderOfOppHearing();
        }}
      />
      {resendConfirm?.previewContent != null ? (
        <ConfirmMessageModal
          open={resendConfirm !== null}
          title={resendConfirm.title}
          previewContent={resendConfirm.previewContent || "—"}
          disableConfirm={!resendConfirm.previewContent.trim()}
          onClose={() => setResendConfirm(null)}
          onConfirm={() => {
            const action = resendConfirm.onConfirm;
            setResendConfirm(null);
            action();
          }}
        />
      ) : (
        <ConfirmDialog
          open={resendConfirm !== null}
          title={resendConfirm?.title ?? ""}
          description={resendConfirm?.description}
          confirmLabel="Resend"
          cancelLabel="Cancel"
          onClose={() => setResendConfirm(null)}
          onConfirm={() => {
            const action = resendConfirm?.onConfirm;
            setResendConfirm(null);
            action?.();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusDetailsFields({
  selectedTm,
  journalDate,
  lastDateForOppositionYmd,
  acknowledgementNo,
  onJournalDateChange,
}: {
  selectedTm: AdvertisedInJournalApplication | null;
  journalDate: string;
  lastDateForOppositionYmd: string | null;
  acknowledgementNo: string | null;
  onJournalDateChange: (d: Date | null) => void;
}) {
  const journalDateParts = parseDateParts(journalDate);
  const journalDateValue = journalDateParts ? partsToDate(journalDateParts) : null;
  const oppositionParts = parseDateParts(lastDateForOppositionYmd);
  const oppositionValue = oppositionParts ? partsToDate(oppositionParts) : null;

  return (
    <>
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label
            className="text-xs font-normal text-[#222222] leading-5"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Journal Date
            <RequiredMark />
          </label>
          <DateTimePicker
            value={journalDateValue}
            onChange={(d) => onJournalDateChange(d ?? null)}
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label
            className="text-xs font-normal text-[#222222] leading-5"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Last date for Opposition
          </label>
          <div
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#6D6D6D] border border-[#EEEEEE] rounded-[6px] bg-[#FCFCFC] flex items-center"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
            title="Auto-calculated as Journal Date + 60 days"
          >
            {oppositionValue
              ? oppositionValue.toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "Auto-calculated from Journal Date"}
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label
            className="text-xs font-normal text-[#222222] leading-5"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Acknowledgement No.
          </label>
          <div
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#6D6D6D] border border-[#EEEEEE] rounded-[6px] bg-white flex items-center"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          >
            {acknowledgementNo || "—"}
          </div>
        </div>
        {/* Spacer to match Figma's two-column row when only one field is filled */}
        <div className="hidden md:block flex-1" />
      </div>

      {selectedTm && (
        <p className="text-[11px] text-[#6D6D6D] -mt-1">
          Mark: {selectedTm.mark}
        </p>
      )}
    </>
  );
}

function FileUploadZone({
  app,
  pendingFile,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  onPick,
  onPreview,
  onRemove,
}: {
  app: AdvertisedInJournalApplication;
  pendingFile: File | null;
  isDragging: boolean;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: (file: File) => void;
  onPick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPreview: () => void;
  onRemove: () => void;
}) {
  const inputId = `advertised-in-journal-file-input-${app.tmNo}`;
  const hasFile = !!(pendingFile || app.orderFromRegistryFileKey);
  const fileName =
    pendingFile?.name ?? app.orderFromRegistryFilename ?? "File selected";

  return (
    <div className="flex flex-col gap-3 border border-[#EEEEEE] rounded-[10px] p-4 md:p-5">
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-semibold text-[#101111]">
          File Upload
          <RequiredMark />
        </p>
        <p className="text-xs text-[#7D7D7D]">
          Choose a file and upload securely to proceed.
        </p>
      </div>

      <div
        className={`flex flex-col md:flex-row items-stretch md:items-center gap-4 rounded-2xl border border-dashed px-4 py-4 cursor-pointer transition-colors ${
          isDragging
            ? "border-[#222222] bg-[#F5F5F5]"
            : hasFile
            ? "border-[#22C55E] bg-[#F0FDF4]"
            : "border-[#D8DAEB] bg-white"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          onDragOver();
        }}
        onDragLeave={() => onDragLeave()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];
          if (file) onDrop(file);
        }}
        onClick={() => document.getElementById(inputId)?.click()}
      >
        <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
          <img
            src={uploadDropzoneGraphic}
            alt=""
            width={60}
            height={60}
            className="flex-shrink-0"
          />

          <div className="flex flex-col gap-1 min-w-0 flex-1">
            {hasFile ? (
              <p className="text-sm font-medium text-[#16A34A] truncate">
                {fileName}
              </p>
            ) : (
              <>
                <p className="text-base font-medium text-[#101111]">
                  Drag and drop your files
                </p>
                <p className="text-sm text-[#7D7D7D]">
                  {SUPPORTED_FORMATS_LABEL}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-row items-center gap-2 flex-shrink-0 self-end md:self-center">
          {!hasFile && (
            <button
              type="button"
              className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-sm font-medium text-[#2E3037] hover:bg-[#F5F5F5] transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                document.getElementById(inputId)?.click();
              }}
            >
              Select File
            </button>
          )}
          {hasFile && (
            <>
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview();
                }}
              >
                Preview
              </button>
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                Remove
              </button>
            </>
          )}
        </div>
      </div>

      <input
        id={inputId}
        type="file"
        accept={ALLOWED_FILE_ACCEPT}
        className="hidden"
        onChange={onPick}
      />
    </div>
  );
}

function NoticePdfRow({
  app,
  onPreview,
}: {
  app: AdvertisedInJournalApplication;
  onPreview: () => void;
}) {
  const filename = app.orderFromRegistryFilename ?? "Order from Registry";
  const label = app.mark || filename;
  return (
    <div
      className="flex flex-row items-center gap-3 px-4 py-1 rounded-[6px] bg-[#FCFCFC] border border-[#EEEEEE] h-11"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <img src={pdfIcon} alt="" width={22} height={22} className="flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-xs font-medium text-[#222222] truncate block">
          {label}
        </span>
      </div>
      <button
        type="button"
        onClick={onPreview}
        className="px-4 h-9 rounded-[6px] bg-[#222222] text-white text-xs font-medium hover:bg-[#333333]"
      >
        Preview
      </button>
    </div>
  );
}

function NoticeConfirmPreview({
  apps,
  preview,
  loading,
  error,
}: {
  apps: AdvertisedInJournalApplication[];
  preview: AdvertisedInJournalNoticePreview | null;
  loading: boolean;
  error: string | null;
}) {
  const recipientPhone = preview?.recipientPhone ?? null;
  const recipientEmail = preview?.recipientEmail ?? null;
  const channels: string[] = [];
  if (recipientPhone) channels.push("WhatsApp");
  if (recipientEmail) channels.push("Email");
  const channelText =
    channels.length === 0
      ? "WhatsApp"
      : channels.length === 1
        ? channels[0]
        : channels.join(" and ");

  return (
    <div
      className="flex flex-col gap-4 px-3.5 py-3.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-2.5">
        <span
          className="text-base font-normal text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          Order from Registry PDFs
        </span>
        <div className="flex flex-col gap-2">
          {apps.map((app) => (
            <div
              key={app.tmNo}
              className="flex flex-row items-center gap-3 px-4 py-1 rounded-[6px] bg-[#FCFCFC] border border-[#EEEEEE] h-11"
            >
              <img
                src={pdfIcon}
                alt=""
                width={22}
                height={22}
                className="flex-shrink-0"
              />
              <span className="text-xs font-medium text-[#222222] truncate flex-1 min-w-0">
                {app.mark || app.orderFromRegistryFilename || "Order from Registry"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span
          className="text-xs font-medium text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          Recipient
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="tel"
            inputMode="numeric"
            value={recipientPhone ?? ""}
            readOnly
            placeholder="WhatsApp number not set"
            className="w-full min-w-0 sm:w-[220px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
          />
          {recipientEmail ? (
            <input
              type="email"
              value={recipientEmail}
              readOnly
              className="w-full min-w-0 sm:w-[260px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7]"
            />
          ) : (
            <input
              type="email"
              value=""
              readOnly
              placeholder="Email not set on lead"
              className="w-full min-w-0 sm:w-[260px] h-9 px-[10px] py-2 text-[12px] text-[#B45309] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#B45309]"
            />
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span
          className="text-xs font-medium text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          WhatsApp Message Preview
        </span>
        <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[160px] bg-white">
          {loading ? (
            <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
              Loading preview…
            </p>
          ) : error ? (
            <p className="text-xs text-[#B45309]" style={{ lineHeight: "20px" }}>
              {error}
            </p>
          ) : preview?.message ? (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              {renderWhatsAppFormatting(preview.message)}
            </p>
          ) : (
            <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
              The Order from Registry message body will appear here once the
              preview loads.
            </p>
          )}
        </div>
      </div>

      {recipientEmail && (
        <div className="flex flex-col gap-2">
          <span
            className="text-xs font-medium text-[#222222]"
            style={{ lineHeight: "20px" }}
          >
            Email Preview
          </span>
          <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[160px] bg-white">
            {loading ? (
              <p
                className="text-xs text-[#6D6D6D]"
                style={{ lineHeight: "20px" }}
              >
                Loading preview…
              </p>
            ) : error ? (
              <p
                className="text-xs text-[#B45309]"
                style={{ lineHeight: "20px" }}
              >
                {error}
              </p>
            ) : preview?.emailPreview ? (
              <div className="flex flex-col gap-2">
                <p
                  className="text-xs font-medium text-[#222222]"
                  style={{ lineHeight: "20px" }}
                >
                  Subject: {preview.emailPreview.subject}
                </p>
                <p
                  className="text-xs text-[#222222] whitespace-pre-line"
                  style={{ lineHeight: "20px" }}
                >
                  {preview.emailPreview.body}
                </p>
              </div>
            ) : (
              <p
                className="text-xs text-[#6D6D6D]"
                style={{ lineHeight: "20px" }}
              >
                The email body will appear here once the preview loads.
              </p>
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "18px" }}>
        The merged Order from Registry PDF will be forwarded to the client via{" "}
        {channelText}.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inform Client about Advertised in Journal — sub-components
// ---------------------------------------------------------------------------

function InformScenarioPicker({
  value,
  onChange,
  disabled,
}: {
  value: AdvertisedInJournalAdvertisedFrom | null;
  onChange: (next: AdvertisedInJournalAdvertisedFrom) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {INFORM_SCENARIOS.map((opt) => {
        const isActive = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.id)}
            className={`flex items-center gap-2.5 w-full text-left px-3 py-2 h-10 rounded-[6px] border transition-colors ${
              isActive
                ? "border-[#222222] bg-white"
                : "border-[#EEEEEE] bg-white hover:bg-[#F7F7F7]"
            } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            <CheckboxBox checked={isActive} />
            <span className="text-xs font-normal text-[#222222] leading-5">
              {opt.label}
              {opt.id !== "ACCEPTANCE" && <RequiredMark />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function CheckboxBox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex items-center justify-center w-5 h-5 rounded-[6px] border ${
        checked ? "bg-[#222222] border-[#222222]" : "bg-white border-[#D6D8DB]"
      }`}
    >
      {checked && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.5 6L5 8.5L9.5 4"
            stroke="white"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );
}

function InformMessagePreview({
  leadId,
  scenario,
}: {
  leadId: string;
  scenario: AdvertisedInJournalAdvertisedFrom | null;
}) {
  const [messages, setMessages] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pull the real scenario-specific text the send path would dispatch, rather than describing it.
  // Only `advertisedFrom` is sent, mirroring handleInformSubmit — the statement of reason is
  // derived server-side from the objection records.
  useEffect(() => {
    if (!leadId || !scenario) {
      setMessages(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    previewInformClientAdvertisedInJournal(leadId, { advertisedFrom: scenario })
      .then((res) => {
        if (!cancelled) setMessages(res.messages ?? []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setMessages(null);
        setError(
          err instanceof Error ? err.message : "Failed to load message preview"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, scenario]);

  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-base font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Message Preview
      </span>
      <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[120px] bg-white">
        {!scenario ? (
          <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
            Select an acceptance scenario above to preview what will be sent.
          </p>
        ) : loading ? (
          <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
            Loading message…
          </p>
        ) : error ? (
          <p className="text-xs text-[#D14343]" style={{ lineHeight: "20px" }}>
            {error}
          </p>
        ) : messages && messages.length > 0 ? (
          <div className="flex flex-col gap-3.5">
            {messages.map((msg, i) => (
              <div key={i} className="flex flex-col gap-1.5">
                {messages.length > 1 && (
                  <span className="text-[11px] leading-4 text-[#6D6D6D]">
                    Message {i + 1} of {messages.length}
                  </span>
                )}
                <p
                  className="text-xs text-[#222222] whitespace-pre-line"
                  style={{ lineHeight: "20px" }}
                >
                  {msg}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
            No message could be built for this scenario. Acceptance after
            Objection and Acceptance after Hearing need the Statement of Reason
            for Objection to be recorded first.
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Opposed → Status details for Counter — sub-components
// ---------------------------------------------------------------------------

function CounterStatusFields({
  app,
  form,
  derivedLastDate,
  noOfApplicationsOpposed,
  onPatch,
  onPickImage,
  onClearImage,
}: {
  app: OppositionCounterApplication | null;
  form: OpposedPerTmFormState;
  derivedLastDate: string;
  noOfApplicationsOpposed: number;
  onPatch: (patch: Partial<OpposedPerTmFormState>) => void;
  onPickImage: (file: File) => void;
  onClearImage: () => void;
}) {
  const labelCls = "text-xs font-normal text-[#222222] leading-5";
  const labelStyle = { fontFamily: "Inter, sans-serif" } as const;
  const dateInputs = {
    dateOfOpposition: parseDateParts(form.dateOfOpposition),
    dateOfReceiptOfOpposition: parseDateParts(form.dateOfReceiptOfOpposition),
    userDateOfOpponentsMark: parseDateParts(form.userDateOfOpponentsMark),
    lastDateOfFilingCounter: parseDateParts(
      form.lastDateOfFilingCounter || derivedLastDate
    ),
  };

  const imagePreviewUrl = (() => {
    if (form.pendingImageFile) {
      return URL.createObjectURL(form.pendingImageFile);
    }
    return app?.imageOfOpponentsMarkUrl ?? null;
  })();

  const imageInputId = `opposed-image-input-${app?.tmNo ?? "x"}`;

  return (
    <>
      {/* Row 1: Opposition No. * | Date of Opposition * */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className={labelCls} style={labelStyle}>
            Opposition No.
            <RequiredMark />
          </label>
          <input
            type="text"
            value={form.oppositionNo}
            onChange={(e) => onPatch({ oppositionNo: e.target.value })}
            placeholder="Opposition No."
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className={labelCls} style={labelStyle}>
            Date of Opposition
            <RequiredMark />
          </label>
          <DateTimePicker
            value={
              dateInputs.dateOfOpposition
                ? partsToDate(dateInputs.dateOfOpposition)
                : null
            }
            onChange={(d) =>
              onPatch({
                dateOfOpposition: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
      </div>

      {/* Row 2: Date of Receipt * | Last date of filing Counter */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className={labelCls} style={labelStyle}>
            Date of Receipt of Opposition
            <RequiredMark />
          </label>
          <DateTimePicker
            value={
              dateInputs.dateOfReceiptOfOpposition
                ? partsToDate(dateInputs.dateOfReceiptOfOpposition)
                : null
            }
            onChange={(d) =>
              onPatch({
                dateOfReceiptOfOpposition: d ? toLocalYmd(d) : "",
                lastDateOfFilingCounter: "",
              })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <label className={labelCls} style={labelStyle}>
              Last date of filing Counter
            </label>
            <span className="relative inline-flex items-center group">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="cursor-help flex-shrink-0">
                <circle cx="8" cy="8" r="6.5" stroke="#666666" strokeWidth="1.25" />
                <path d="M8 7.5 V11" stroke="#666666" strokeWidth="1.25" strokeLinecap="round" />
                <circle cx="8" cy="5.33" r="0.6" fill="#666666" />
              </svg>
              <span role="tooltip" className="hidden group-hover:flex absolute z-20 left-0 top-[20px] rounded-[6px] text-white pointer-events-none" style={{ width: "300px", maxWidth: "calc(100vw - 40px)", padding: "6px 10px", background: "#565656", fontSize: "11px", lineHeight: "18px", fontWeight: 400 }}>
                Last date of filing Counter is exactly 2 months from Date of Receipt of Opposition.
              </span>
            </span>
          </div>
          <DateTimePicker
            value={
              dateInputs.lastDateOfFilingCounter
                ? partsToDate(dateInputs.lastDateOfFilingCounter)
                : null
            }
            onChange={(d) =>
              onPatch({
                lastDateOfFilingCounter: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
          />
        </div>
      </div>

      {/* Row 3: Address | Place */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className={labelCls} style={labelStyle}>
            Address of Opponent
            <RequiredMark />
          </label>
          <input
            type="text"
            value={form.addressOfOpponent}
            onChange={(e) => onPatch({ addressOfOpponent: e.target.value })}
            placeholder="Address of Opponent"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className={labelCls} style={labelStyle}>
            Place of Opponent
            <RequiredMark />
          </label>
          <input
            type="text"
            value={form.placeOfOpponent}
            onChange={(e) => onPatch({ placeOfOpponent: e.target.value })}
            placeholder="Place of Opponent"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
      </div>

      {/* Row 4: Opponent's Mark | Class of Opponent's Mark */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className={labelCls} style={labelStyle}>
            Opponent&apos;s Mark
            <RequiredMark />
          </label>
          <input
            type="text"
            value={form.opponentsMark}
            onChange={(e) => onPatch({ opponentsMark: e.target.value })}
            placeholder="Opponent's Mark"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className={labelCls} style={labelStyle}>
            Class of Opponent&apos;s Mark
            <RequiredMark />
          </label>
          <input
            type="text"
            value={form.classOfOpponentsMark}
            onChange={(e) =>
              onPatch({ classOfOpponentsMark: e.target.value })
            }
            placeholder="Class"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
      </div>

      {/* Row 5: Goods/Services | User Date */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className={labelCls} style={labelStyle}>
            Goods/Services of Opponent&apos;s Mark
            <RequiredMark />
          </label>
          <input
            type="text"
            value={form.goodsServicesOfOpponentsMark}
            onChange={(e) =>
              onPatch({ goodsServicesOfOpponentsMark: e.target.value })
            }
            placeholder="Goods/Services"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className={labelCls} style={labelStyle}>
            User Date of Opponent&apos;s Mark
            <RequiredMark />
          </label>
          <DateTimePicker
            value={
              dateInputs.userDateOfOpponentsMark
                ? partsToDate(dateInputs.userDateOfOpponentsMark)
                : null
            }
            onChange={(d) =>
              onPatch({
                userDateOfOpponentsMark: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
      </div>

      {/* Row 6: Name of Opponent | No. of applications Opposed */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className={labelCls} style={labelStyle}>
            Name of Opponent
            <RequiredMark />
          </label>
          <input
            type="text"
            value={form.nameOfOpponent}
            onChange={(e) => onPatch({ nameOfOpponent: e.target.value })}
            placeholder="Name of Opponent"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className={labelCls} style={labelStyle}>
            No. of applications Opposed
          </label>
          <div
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#6D6D6D] border border-[#EEEEEE] rounded-[6px] bg-[#FCFCFC] flex items-center"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
            title="Auto-calculated from acknowledgement"
          >
            {noOfApplicationsOpposed > 0 ? noOfApplicationsOpposed : "—"}
          </div>
        </div>
      </div>

      {/* Row 7: Image of Mark (full-width) */}
      <div className="flex flex-col gap-1">
        <label className={labelCls} style={labelStyle}>
          Image of Mark
        </label>
        <div
          className="relative w-full h-[155px] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] overflow-hidden cursor-pointer"
          onClick={() => document.getElementById(imageInputId)?.click()}
        >
          {imagePreviewUrl ? (
            <img
              src={imagePreviewUrl}
              alt="Opponent's mark"
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <span
                className="text-xs text-[#6D6D6D]"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                {app?.imageOfOpponentsMarkFileKey
                  ? "Image uploaded — pick a new file to replace"
                  : "Click to upload Opponent's mark image"}
              </span>
            </div>
          )}
          {form.pendingImageFile && (
            <button
              type="button"
              className="absolute top-2 right-2 px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
              onClick={(e) => {
                e.stopPropagation();
                onClearImage();
              }}
            >
              Remove
            </button>
          )}
        </div>
        <input
          id={imageInputId}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onPickImage(file);
            e.target.value = "";
          }}
        />
      </div>

      {app && (
        <p className="text-[11px] text-[#6D6D6D] -mt-1">Mark: {app.mark}</p>
      )}
    </>
  );
}

function CounterNoticeUploadZone({
  app,
  pendingFile,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  onPick,
  onPreview,
  onRemove,
}: {
  app: OppositionCounterApplication;
  pendingFile: File | null;
  isDragging: boolean;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: (file: File) => void;
  onPick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPreview: () => void;
  onRemove: () => void;
}) {
  const inputId = `opposed-notice-input-${app.tmNo}`;
  const hasFile = !!(pendingFile || app.oppositionNoticeFileKey);
  const fileName =
    pendingFile?.name ?? app.oppositionNoticeFilename ?? "File selected";

  return (
    <div className="flex flex-col gap-3 border border-[#EEEEEE] rounded-[10px] p-4 md:p-5">
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-semibold text-[#101111]">
          File Upload
          <RequiredMark />
        </p>
        <p className="text-xs text-[#7D7D7D]">
          Choose a file and upload securely to proceed.
        </p>
      </div>

      <div
        className={`flex flex-col md:flex-row items-stretch md:items-center gap-4 rounded-2xl border border-dashed px-4 py-4 cursor-pointer transition-colors ${
          isDragging
            ? "border-[#222222] bg-[#F5F5F5]"
            : hasFile
            ? "border-[#22C55E] bg-[#F0FDF4]"
            : "border-[#D8DAEB] bg-white"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          onDragOver();
        }}
        onDragLeave={() => onDragLeave()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];
          if (file) onDrop(file);
        }}
        onClick={() => document.getElementById(inputId)?.click()}
      >
        <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
          <img
            src={uploadDropzoneGraphic}
            alt=""
            width={60}
            height={60}
            className="flex-shrink-0"
          />

          <div className="flex flex-col gap-1 min-w-0 flex-1">
            {hasFile ? (
              <p className="text-sm font-medium text-[#16A34A] truncate">
                {fileName}
              </p>
            ) : (
              <>
                <p className="text-base font-medium text-[#101111]">
                  Drag and drop your files
                </p>
                <p className="text-sm text-[#7D7D7D]">
                  {SUPPORTED_FORMATS_LABEL}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-row items-center gap-2 flex-shrink-0 self-end md:self-center">
          {!hasFile && (
            <button
              type="button"
              className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-sm font-medium text-[#2E3037] hover:bg-[#F5F5F5] transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                document.getElementById(inputId)?.click();
              }}
            >
              Select File
            </button>
          )}
          {hasFile && (
            <>
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview();
                }}
              >
                Preview
              </button>
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                Remove
              </button>
            </>
          )}
        </div>
      </div>

      <input
        id={inputId}
        type="file"
        accept={ALLOWED_FILE_ACCEPT}
        className="hidden"
        onChange={onPick}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Opposed → Notice / Inform / Call Client for Counter — sub-components
// ---------------------------------------------------------------------------

function CounterNoticePdfRow({
  app,
  onPreview,
}: {
  app: OppositionCounterApplication;
  onPreview: () => void;
}) {
  const filename = app.oppositionNoticeFilename ?? "Opposition Notice";
  const label = app.mark || filename;
  return (
    <div
      className="flex flex-row items-center gap-3 px-4 py-1 rounded-[6px] bg-[#FCFCFC] border border-[#EEEEEE] h-11"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <img src={pdfIcon} alt="" width={22} height={22} className="flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-xs font-medium text-[#222222] truncate block">
          {label}
        </span>
      </div>
      <button
        type="button"
        onClick={onPreview}
        className="px-4 h-9 rounded-[6px] bg-[#222222] text-white text-xs font-medium hover:bg-[#333333]"
      >
        Preview
      </button>
    </div>
  );
}

function CounterNoticeConfirmPreview({
  apps,
  preview,
  loading,
  error,
}: {
  apps: OppositionCounterApplication[];
  preview: CounterNoticePreview | null;
  loading: boolean;
  error: string | null;
}) {
  const recipientPhone = preview?.recipientPhone ?? null;
  const recipientEmail = preview?.recipientEmail ?? null;
  const channels: string[] = [];
  if (recipientPhone) channels.push("WhatsApp");
  if (recipientEmail) channels.push("Email");
  const channelText =
    channels.length === 0
      ? "WhatsApp"
      : channels.length === 1
        ? channels[0]
        : channels.join(" and ");

  return (
    <div
      className="flex flex-col gap-4 px-3.5 py-3.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-2.5">
        <span
          className="text-base font-normal text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          Opposition Notice PDFs
        </span>
        <div className="flex flex-col gap-2">
          {apps.map((app) => (
            <div
              key={app.tmNo}
              className="flex flex-row items-center gap-3 px-4 py-1 rounded-[6px] bg-[#FCFCFC] border border-[#EEEEEE] h-11"
            >
              <img
                src={pdfIcon}
                alt=""
                width={22}
                height={22}
                className="flex-shrink-0"
              />
              <span className="text-xs font-medium text-[#222222] truncate flex-1 min-w-0">
                {app.mark || app.oppositionNoticeFilename || "Opposition Notice"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span
          className="text-xs font-medium text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          Recipient
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="tel"
            inputMode="numeric"
            value={recipientPhone ?? ""}
            readOnly
            placeholder="WhatsApp number not set"
            className="w-full min-w-0 sm:w-[220px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
          />
          {recipientEmail ? (
            <input
              type="email"
              value={recipientEmail}
              readOnly
              className="w-full min-w-0 sm:w-[260px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7]"
            />
          ) : (
            <input
              type="email"
              value=""
              readOnly
              placeholder="Email not set on lead"
              className="w-full min-w-0 sm:w-[260px] h-9 px-[10px] py-2 text-[12px] text-[#B45309] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#B45309]"
            />
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span
          className="text-xs font-medium text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          WhatsApp Message Preview
        </span>
        <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[160px] bg-white">
          {loading ? (
            <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
              Loading preview…
            </p>
          ) : error ? (
            <p className="text-xs text-[#B45309]" style={{ lineHeight: "20px" }}>
              {error}
            </p>
          ) : preview?.message ? (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              {renderWhatsAppFormatting(preview.message)}
            </p>
          ) : (
            <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
              The Opposition Notice message body will appear here once the
              preview loads.
            </p>
          )}
        </div>
      </div>

      {recipientEmail && (
        <div className="flex flex-col gap-2">
          <span
            className="text-xs font-medium text-[#222222]"
            style={{ lineHeight: "20px" }}
          >
            Email Preview
          </span>
          <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[160px] bg-white">
            {loading ? (
              <p
                className="text-xs text-[#6D6D6D]"
                style={{ lineHeight: "20px" }}
              >
                Loading preview…
              </p>
            ) : error ? (
              <p
                className="text-xs text-[#B45309]"
                style={{ lineHeight: "20px" }}
              >
                {error}
              </p>
            ) : preview?.emailPreview ? (
              <div className="flex flex-col gap-2">
                <p
                  className="text-xs font-medium text-[#222222]"
                  style={{ lineHeight: "20px" }}
                >
                  Subject: {preview.emailPreview.subject}
                </p>
                <p
                  className="text-xs text-[#222222] whitespace-pre-line"
                  style={{ lineHeight: "20px" }}
                >
                  {preview.emailPreview.body}
                </p>
              </div>
            ) : (
              <p
                className="text-xs text-[#6D6D6D]"
                style={{ lineHeight: "20px" }}
              >
                The email body will appear here once the preview loads.
              </p>
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "18px" }}>
        The merged Opposition Notice PDF will be forwarded to the client via{" "}
        {channelText}.
      </p>
    </div>
  );
}

function NoticeEvidenceByOpponentConfirmPreview({
  apps,
  preview,
  loading,
  error,
}: {
  apps: EvidenceByOpponentStatus["applications"];
  preview: EvidenceByOpponentNoticePreview | null;
  loading: boolean;
  error: string | null;
}) {
  const recipientPhone = preview?.recipientPhone ?? null;
  const recipientEmail = preview?.recipientEmail ?? null;
  const channelText = recipientEmail ? "WhatsApp and Email" : "WhatsApp";

  return (
    <div
      className="flex flex-col gap-4 px-3.5 py-3.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-2.5">
        <span
          className="text-base font-normal text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          Evidence by Opponent PDFs
        </span>
        <div className="flex flex-col gap-2">
          {apps.map((app) => (
            <div
              key={app.tmNo}
              className="flex flex-row items-center gap-3 px-4 py-1 rounded-[6px] bg-[#FCFCFC] border border-[#EEEEEE] h-11"
            >
              <img
                src={pdfIcon}
                alt=""
                width={22}
                height={22}
                className="flex-shrink-0"
              />
              <span className="text-xs font-medium text-[#222222] truncate flex-1 min-w-0">
                {app.mark ||
                  app.evidenceByOpponentFilename ||
                  "Evidence by Opponent"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span
          className="text-xs font-medium text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          Recipient
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="tel"
            inputMode="numeric"
            value={recipientPhone ?? ""}
            readOnly
            placeholder="WhatsApp number not set"
            className="w-full min-w-0 sm:w-[220px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
          />
          <input
            type="email"
            value={recipientEmail ?? ""}
            readOnly
            placeholder="Email not set"
            className="w-full min-w-0 sm:w-[260px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span
          className="text-xs font-medium text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          WhatsApp Message Preview
        </span>
        <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[160px] bg-white">
          {loading ? (
            <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
              Loading preview…
            </p>
          ) : error ? (
            <p className="text-xs text-[#B45309]" style={{ lineHeight: "20px" }}>
              {error}
            </p>
          ) : preview?.message ? (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              {renderWhatsAppFormatting(preview.message)}
            </p>
          ) : (
            <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
              The Evidence by Opponent message body will appear here once the
              preview loads.
            </p>
          )}
        </div>
      </div>

      {recipientEmail && (
        <div className="flex flex-col gap-2">
          <span
            className="text-xs font-medium text-[#222222]"
            style={{ lineHeight: "20px" }}
          >
            Email Preview
          </span>
          <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[160px] bg-white">
            {loading ? (
              <p
                className="text-xs text-[#6D6D6D]"
                style={{ lineHeight: "20px" }}
              >
                Loading preview…
              </p>
            ) : error ? (
              <p
                className="text-xs text-[#B45309]"
                style={{ lineHeight: "20px" }}
              >
                {error}
              </p>
            ) : preview?.emailPreview ? (
              <div className="flex flex-col gap-2">
                <p
                  className="text-xs font-medium text-[#222222]"
                  style={{ lineHeight: "20px" }}
                >
                  Subject: {preview.emailPreview.subject}
                </p>
                <p
                  className="text-xs text-[#222222] whitespace-pre-line"
                  style={{ lineHeight: "20px" }}
                >
                  {preview.emailPreview.body}
                </p>
              </div>
            ) : (
              <p
                className="text-xs text-[#6D6D6D]"
                style={{ lineHeight: "20px" }}
              >
                The email body will appear here once the preview loads.
              </p>
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "18px" }}>
        The merged Evidence by Opponent PDF will be forwarded to the client via{" "}
        {channelText}.
      </p>
    </div>
  );
}

function CounterInformMessagePreview({
  preview,
}: {
  preview: { message: string } | null;
}) {
  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-base font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Message Preview
      </span>
      <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[200px] bg-white">
        {preview?.message ? (
          <p
            className="text-xs text-[#222222] whitespace-pre-line"
            style={{ lineHeight: "20px" }}
          >
            {renderWhatsAppFormatting(preview.message)}
          </p>
        ) : (
          <p
            className="text-xs text-[#222222] whitespace-pre-line"
            style={{ lineHeight: "20px" }}
          >
            A &quot;Trademark Update&quot; message will be sent to the client
            informing them that the application/s were advertised in the
            Trademarks Journal but have been opposed by a third party. The
            message includes the mark, type, class, TM No., date of filing,
            status badges, the opponent details (name, place, mark) and the
            deadline (4n) for filing the Counter Statement.
            {"\n\n"}
            <span className="text-[#6D6D6D]">
              The exact message body is generated server-side from the lead
              and opposition data; click Send &amp; Continue to send it via
              WhatsApp.
            </span>
          </p>
        )}
      </div>
    </div>
  );
}

function CounterCallClientPanel({
  recorded,
  recording,
  onRecord,
}: {
  recorded: boolean;
  recording: boolean;
  onRecord: () => void;
}) {
  return (
    <div style={{ fontFamily: "Inter, sans-serif" }}>
      <div
        className="flex flex-row items-center gap-4 p-4 rounded-xl"
        style={{
          background: "#FFF8EE",
          border: "1.5px dashed #F5A623",
          borderRadius: "12px",
        }}
      >
        <img
          src={legalAdviceHeadset}
          alt=""
          className="w-[60px] h-[60px] flex-shrink-0"
        />

        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <p
            className="text-sm font-normal text-[#222222]"
            style={{ lineHeight: "20px" }}
          >
            Call the client to explain the Opposition and the need to file a
            Counter Statement.
          </p>

          {recorded ? (
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <circle cx="8" cy="8" r="7.5" fill="#222222" />
                <path
                  d="M5.33325 8.00008L7.33325 10.0001L10.6666 6.00008"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className="text-xs font-semibold text-[#222222]"
                style={{ lineHeight: "20px" }}
              >
                Call Completed
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={onRecord}
              disabled={recording}
              className="self-start h-8 px-4 text-xs font-medium text-[#222222] bg-white border border-[#D1D5DB] rounded-md hover:bg-[#F9FAFB] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              style={{ lineHeight: "20px" }}
            >
              {recording ? "Recording…" : "Call Completed"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Confirmation from Client for Counter — sub-components
// ---------------------------------------------------------------------------

/**
 * Review items 74 / 75 — the missing route after 4A(v) / 4A(xvii) = NO.
 *
 * Spec pg 119 (4A(v)) and the 4A(xvii) section each say the NO answer normally goes
 * straight to 5B(i) Status details for Abandonment, and add one line: *"Sometimes
 * from here it will proceed to 'Hearing with regard to Non Filing of
 * Counter/Evidence'."* Item 75's complaint is literally that this option is not
 * available — and before this, 4A(v) NO jumped straight to 5B(i) (item 73) while
 * 4A(xvii) NO went nowhere at all.
 *
 * ⚠️ The spec never defines that hearing's own substeps, fields or messages — the
 * sentence above is its only appearance in the whole document. What *is* fully
 * specified is where the two paths end up: 5B(i), with reason "Non Filing of
 * Counter/Evidence" (situations 7 / 10) when abandoned outright, or
 * "Non-appearance in hearing" / "Disallowance after hearing" (situations 8 / 9 /
 * 11 / 12) once a hearing has happened. All of those reasons already exist in
 * 5B(i). So this surfaces the choice and names the reasons rather than inventing an
 * unspecified hearing workflow.
 */
function NonFilingNextStepChoice({
  variant,
  onGoToAbandonment,
}: {
  variant: "counter" | "evidence";
  onGoToAbandonment: () => void;
}) {
  const what = variant === "counter" ? "Counter" : "Evidence";
  return (
    <div
      className="flex flex-col gap-3 self-stretch rounded-[8px] border border-dashed border-[#E18308] bg-[#FFF8EE] px-3.5 py-3"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span className="text-[13px] font-medium leading-5 text-[#92400E]">
        Client is not going ahead — choose what happened next
      </span>
      <span className="text-[12px] leading-5 text-[#B45309]">
        Non Filing of {what} normally goes straight to abandonment. If the Registry
        fixed a hearing with regard to Non Filing of {what}, record its outcome in
        5B(i) as “Non-appearance in hearing” or “Disallowance after hearing (after
        Non Filing of {what})”.
      </span>
      <div className="flex flex-row flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onGoToAbandonment}
          className="h-8 px-3 text-xs font-medium text-[#222222] bg-white border border-[#222222] rounded-[8px] hover:bg-gray-50"
        >
          Abandoned — go to 5B(i)
        </button>
        <button
          type="button"
          onClick={() => {
            toast.info(
              `Record the hearing outcome in 5B(i) — reason “Non-appearance in hearing” or “Disallowance after hearing” (after Non Filing of ${what}).`
            );
            onGoToAbandonment();
          }}
          className="h-8 px-3 text-xs font-medium text-[#222222] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50"
        >
          A hearing was fixed for Non Filing of {what}
        </button>
      </div>
    </div>
  );
}

function CounterConfirmationFields({
  status,
  draft,
  selectedTmNo,
  noOpposedDraft,
  onNoOpposedChange,
  noForCounterDraft,
  onNoForCounterChange,
  onPick,
}: {
  status: CounterConfirmationStatus | null;
  draft: Record<string, boolean | null>;
  selectedTmNo: string | null;
  noOpposedDraft: string;
  onNoOpposedChange: (val: string) => void;
  noForCounterDraft: string;
  onNoForCounterChange: (val: string) => void;
  onPick: (tmNo: string, value: boolean) => void;
}) {
  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">Loading confirmation status…</p>
    );
  }
  const value = draft[selectedTmNo];
  const isNoForSelected = value === false;
  return (
    <div className="flex flex-col gap-5" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="flex flex-row gap-3">
        {[
          { id: true, label: "YES" },
          { id: false, label: "NO" },
        ].map((opt) => {
          const isActive = value === opt.id;
          return (
            <button
              key={String(opt.id)}
              type="button"
              onClick={() => onPick(selectedTmNo, opt.id)}
              className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-[6px] h-9 min-w-[230px] text-xs transition-colors ${
                isActive
                  ? "bg-[#222222] text-white border border-[#222222]"
                  : "bg-[#FCFCFC] text-[#6D6D6D] border border-[#EEEEEE]"
              }`}
            >
              <CheckboxBox checked={isActive} />
              <span className="text-left">{opt.label}</span>
            </button>
          );
        })}
      </div>

      {status.suggestedNextStepWhenNo && (
        <div
          className="rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2 text-xs text-[#6D6D6D] leading-5"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          Next step: {status.suggestedNextStepWhenNo}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="Acknowledgement No."
          value={status.acknowledgementNo ?? "—"}
        />
        <ReadOnlyField
          label="No. of applications Opposed"
          value={
            status.noOfApplicationsOpposed != null
              ? String(status.noOfApplicationsOpposed)
              : "—"
          }
        />
      </div>

      {!isNoForSelected && (
        <div className="flex flex-col md:flex-row gap-4 md:gap-5">
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <label
              className="text-xs font-normal text-[#222222] leading-5"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              No. of applications for Counter
            </label>
            <input
              type="number"
              min={1}
              value={noForCounterDraft}
              onChange={(e) => onNoForCounterChange(e.target.value)}
              placeholder={String(
                status.noOfApplicationsForCounter ??
                  status.noOfApplicationsOpposed ??
                  "—"
              )}
              className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:border-[#222222] transition-colors"
              style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 flex-1 min-w-0">
      <label
        className="text-xs font-normal text-[#222222] leading-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {label}
      </label>
      <div
        className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#6D6D6D] border border-[#EEEEEE] rounded-[6px] bg-[#FCFCFC] flex items-center"
        style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function FetchedDetailsPanel({
  rows,
}: {
  rows: Array<{ label: string; value: string; multiline?: boolean }>;
}) {
  return (
    <div
      className="bg-[#F7F7F7] rounded-[8px] p-4 md:p-5 flex flex-col gap-3 w-full"
      style={{
        fontFamily: "Inter, sans-serif",
        fontFeatureSettings: "'ss01' on, 'cv01' on",
      }}
    >
      {rows.map(({ label, value, multiline }) => (
        <div
          key={label}
          className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4"
        >
          <span className="text-xs font-normal leading-5 text-[#6D6D6D] md:w-[220px] md:shrink-0">
            {label}
          </span>
          <span
            className={`text-xs font-normal leading-5 break-words ${
              multiline ? "whitespace-pre-wrap" : ""
            } ${value ? "text-[#222222]" : "text-[#6D6D6D]"}`}
          >
            {value || "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invoice to Client for Counter — sub-components
// ---------------------------------------------------------------------------

function formatCounterRupees(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return "Rs. 0/-";
  return `Rs. ${Math.round(v).toLocaleString("en-IN")}/-`;
}

function CounterInvoiceAmountTooltip({
  defaultAmount,
  govtFee,
  professionalCharges,
  noOfApps,
}: {
  defaultAmount: number | null;
  govtFee: number | null;
  professionalCharges: number | null;
  noOfApps: number;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is the invoice amount calculated?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 text-[#6D6D6D] hover:text-[#222] focus:outline-none focus:ring-1 focus:ring-[#222] rounded-full"
      >
        <HiOutlineInformationCircle className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[300px] rounded-[8px] border border-[#EEEEEE] bg-white p-3 shadow-lg"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-[11px] font-semibold leading-4 text-[#222]">
            How the invoice amount is calculated
          </p>
          <p className="mt-1 text-[11px] leading-4 text-[#6D6D6D]">
            Govt fee + Professional charges for filing the Counter Statement,
            for this application. You can override the value if needed; the
            final amount on the summary bar applies the discount.
          </p>
          <div className="mt-2 flex flex-col gap-1 text-[11px] leading-4 text-[#222]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Govt fee</span>
              <span className="font-medium">{formatCounterRupees(govtFee)}</span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Professional charges</span>
              <span className="font-medium">
                {formatCounterRupees(professionalCharges)}
              </span>
            </div>
            {/* Context only — the figures above are per application, so this is not a multiplier. */}
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">
                Applications on this invoice
              </span>
              <span className="font-medium">{noOfApps}</span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-[#EEEEEE] pt-1.5">
              <span className="font-semibold text-[#222]">
                Default invoice amount
              </span>
              <span className="font-semibold text-[#222]">
                {formatCounterRupees(defaultAmount)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CounterInvoiceFields({
  leadId,
  status,
  preview,
  rows,
  selectedTmNo,
  generating,
  onPatchRow,
  onGeneratePdf,
}: {
  leadId: string | null;
  status: CounterInvoiceStatus | null;
  preview: CounterInvoicePreview | null;
  rows: Record<
    string,
    {
      invoiceAmount: string;
      discount: string;
      invoiceDate: string;
      includeInvoiceNo: boolean;
    }
  >;
  selectedTmNo: string | null;
  generating: boolean;
  onPatchRow: (
    tmNo: string,
    patch: Partial<{
      invoiceAmount: string;
      discount: string;
      invoiceDate: string;
      includeInvoiceNo: boolean;
    }>
  ) => void;
  onGeneratePdf: () => void;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<
    "details" | "pdf" | "message" | "email"
  >("details");
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);
  const [messagePreview, setMessagePreview] =
    useState<CounterInvoiceWhatsAppPreview | null>(null);
  const [messagePreviewLoading, setMessagePreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!previewOpen || !leadId || !selectedTmNo) return;
    let cancelled = false;
    setMessagePreviewLoading(true);
    getCounterInvoiceWhatsAppPreview(leadId, [selectedTmNo])
      .then((data) => {
        if (!cancelled) setMessagePreview(data);
      })
      .catch(() => {
        if (!cancelled) setMessagePreview(null);
      })
      .finally(() => {
        if (!cancelled) setMessagePreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [previewOpen, leadId, selectedTmNo, status?.counterInvoicePdfKey]);

  if (!status || !selectedTmNo) {
    return <p className="text-sm text-[#6D6D6D]">Loading invoice…</p>;
  }
  const tmRow = (status.tmInvoiceRows ?? []).find((r) => r.tmNo === selectedTmNo);
  const draft = rows[selectedTmNo];
  if (!tmRow || !draft) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Selected TM is not part of the Counter invoice (confirm it as YES first).
      </p>
    );
  }
  const previewApp = preview?.applications.find((a) => a.tmNo === selectedTmNo);
  const dateParts = parseDateParts(draft.invoiceDate);

  const FORM_STYLES = { fontFamily: "Inter, sans-serif" } as const;
  const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
  const READONLY_CLASS =
    "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border";
  const INPUT_CLASS =
    "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";

  const parsedInvoice = Number(draft.invoiceAmount);
  const parsedDiscount = Number(draft.discount);
  const invoiceAmt = Number.isFinite(parsedInvoice) ? Math.max(0, parsedInvoice) : 0;
  const discountAmt = Number.isFinite(parsedDiscount) ? Math.max(0, parsedDiscount) : 0;
  const finalInvoiceAmount = Math.max(invoiceAmt - discountAmt, 0);
  const formattedFinal = `Rs. ${Math.round(finalInvoiceAmount).toLocaleString("en-IN")}/-`;

  const pdfUrl = status.counterInvoicePdfUrl ?? null;

  const formatRupees = (n: number | null | undefined): string => {
    const v = Number(n ?? 0);
    if (!Number.isFinite(v)) return "Rs. 0/-";
    return `Rs. ${Math.round(v).toLocaleString("en-IN")}/-`;
  };
  const formatDateDDMMYYYY = (input: string | null | undefined): string => {
    if (!input) return "—";
    const iso = input.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (iso) {
      return `${iso[3].padStart(2, "0")}-${iso[2].padStart(2, "0")}-${iso[1]}`;
    }
    const dmy = input.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
    if (dmy) {
      return `${dmy[1].padStart(2, "0")}-${dmy[2].padStart(2, "0")}-${dmy[3]}`;
    }
    return input;
  };
  const addresseeLines = (preview?.addressee ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const detailApplications = preview?.applications ?? [];
  const displayInvoiceDate =
    preview?.invoiceDate ?? (draft.invoiceDate || null);
  const displayInvoiceNo =
    tmRow.individualInvoiceNo ?? preview?.invoiceNo ?? status.invoiceNo ?? null;

  const closePreviewModal = () => {
    setPreviewOpen(false);
    setPreviewNumPages(null);
    setPreviewTab("details");
  };

  const handleDownload = async () => {
    if (!pdfUrl || downloading) return;
    setDownloading(true);
    try {
      const result = await downloadFileFromUrlWithFallback(
        pdfUrl,
        "Invoice_for_Counter_Statement.pdf"
      );
      if (result === "local") {
        toast.success("Download started");
      } else {
        toast.success(
          "Opened the document in a new tab — use the browser's download option to save it."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex w-full max-w-[1085px] flex-col gap-5" style={FORM_STYLES}>
      {/* Row 1: Date | Invoice Amount (editable) | Discount */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Date of Invoice for Counter Statement</label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) =>
              onPatchRow(selectedTmNo, {
                invoiceDate: d ? toLocalYmd(d) : "",
              })
            }
            showTime={false}
            placeholder="Select Date"
            maxDate={new Date()}
            buttonClassName="!h-9 !min-h-0 !text-[12px] rounded-[6px]"
          />
        </div>
        {/*
          Spec pg 121 lists Govt Fee and Professional Charges as sub-fields of 4q. They existed only
          inside the tooltip before, which the client read as "two sub fields required… not
          available". They are shown for the application on screen (× 1), so they visibly add up to
          the 4q field below it — the acknowledgement-wide totals live in the preview window.
        */}
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Govt Fee for filing Counter</label>
          <div className={READONLY_CLASS}>
            {formatRupees(status.govtFeePerApp)}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>
            Professional Charges for filing Counter
          </label>
          <div className={READONLY_CLASS}>
            {formatRupees(status.professionalChargesPerApp)}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <label className={LABEL_CLASS}>
              Invoice Amount for Counter Statement
              <RequiredMark />
            </label>
            <CounterInvoiceAmountTooltip
              defaultAmount={tmRow.defaultInvoiceAmount ?? null}
              govtFee={status.govtFeePerApp}
              professionalCharges={status.professionalChargesPerApp}
              noOfApps={status.noOfApps ?? 0}
            />
          </div>
          <input
            type="text"
            inputMode="decimal"
            value={draft.invoiceAmount}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                onPatchRow(selectedTmNo, { invoiceAmount: v });
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            placeholder="0"
            className={INPUT_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Discount (Rs.)</label>
          <input
            type="text"
            inputMode="decimal"
            value={draft.discount}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                onPatchRow(selectedTmNo, { discount: v });
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            placeholder="0"
            className={INPUT_CLASS}
          />
        </div>
      </div>

      {/* Row 2: Invoice No. (readonly) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Invoice No. for filing Counter</label>
          <input
            type="text"
            value={tmRow.individualInvoiceNo ?? status.invoiceNo ?? ""}
            placeholder="Auto-generated on save"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      {/* Include Invoice No checkbox */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="counter-include-invoice-no"
          checked={draft.includeInvoiceNo}
          onCheckedChange={(checked) =>
            onPatchRow(selectedTmNo, { includeInvoiceNo: Boolean(checked) })
          }
        />
        <label
          htmlFor="counter-include-invoice-no"
          className="text-xs md:text-sm text-[#222222] cursor-pointer"
          style={FORM_STYLES}
        >
          Include Invoice No. in PDF
        </label>
      </div>

      {/* Invoice Amount summary bar */}
      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 rounded-[8px] px-4 sm:px-5 py-4 sm:py-0 min-h-[72px] sm:h-[72px]"
        style={{ background: "#222222" }}
      >
        <div className="flex flex-row items-center gap-2 min-w-0">
          <img
            src={invoiceAmountCheck}
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span
            className="text-[12px] md:text-sm font-normal leading-5 text-white/90 truncate"
            style={FORM_STYLES}
          >
            INVOICE AMOUNT FOR COUNTER
          </span>
        </div>
        <span
          className="text-lg md:text-[22px] font-bold leading-8 md:leading-10 text-white shrink-0"
          style={FORM_STYLES}
        >
          {formattedFinal}
        </span>
      </div>

      {/* Generate / Download / Preview */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
          <img
            src={invoiceGenerate}
            alt=""
            className="w-[22px] h-[22px] flex-shrink-0"
          />
          <button
            type="button"
            onClick={onGeneratePdf}
            disabled={generating}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] md:text-xs font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
            style={FORM_STYLES}
          >
            {generating
              ? "Generating…"
              : status.counterInvoicePdfKey
                ? "Regenerate PDF"
                : "Generate PDF"}
          </button>
        </div>
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={!pdfUrl || downloading || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={FORM_STYLES}
        >
          {downloading ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={() => {
            setPreviewTab("details");
            setPreviewNumPages(null);
            setPreviewOpen(true);
          }}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 touch-manipulation"
          style={FORM_STYLES}
        >
          Preview
        </button>
      </div>

      <FilePreviewModal
        open={previewOpen}
        onClose={closePreviewModal}
        name="Counter Invoice Preview"
      >
        <div className="flex w-full max-w-3xl flex-col gap-3" style={FORM_STYLES}>
          <div
            className="flex shrink-0 gap-1 border-b border-[#e3e3e3]"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={previewTab === "details"}
              onClick={() => setPreviewTab("details")}
              className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                previewTab === "details"
                  ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                  : "text-[#6d6d6d] hover:text-[#222]"
              }`}
            >
              Details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={previewTab === "pdf"}
              onClick={() => setPreviewTab("pdf")}
              disabled={!pdfUrl}
              className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
                previewTab === "pdf"
                  ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                  : "text-[#6d6d6d] hover:text-[#222]"
              }`}
            >
              Generated PDF
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={previewTab === "message"}
              onClick={() => setPreviewTab("message")}
              className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                previewTab === "message"
                  ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                  : "text-[#6d6d6d] hover:text-[#222]"
              }`}
            >
              WhatsApp Message
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={previewTab === "email"}
              onClick={() => setPreviewTab("email")}
              className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                previewTab === "email"
                  ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                  : "text-[#6d6d6d] hover:text-[#222]"
              }`}
            >
              Email
            </button>
          </div>

          {previewTab === "details" ? (
            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Data for Invoice for Counter Statement
              </p>
              <div
                className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
                style={FORM_STYLES}
              >
                {/* Addressee (multi-line) */}
                <div className="flex items-start font-medium text-[#222]">
                  Addressee
                </div>
                <div className="flex items-start text-[#222]">:</div>
                <div className="text-[#222] break-words min-w-0">
                  {addresseeLines.length > 0 ? (
                    <div className="flex flex-col">
                      {addresseeLines.map((line, i) => (
                        <span key={i}>{line}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[#6D6D6D]">—</span>
                  )}
                </div>

                {/* Per-application block: TM No, Date of Filing, Mark (Type),
                    Class, Opponent: Name (Place) */}
                {detailApplications.length === 0 ? (
                  <>
                    <div className="col-span-3 mt-1.5 border-t border-dashed border-[#EEEEEE]" />
                    <div className="col-span-3 text-[#6D6D6D]">
                      No applications.
                    </div>
                  </>
                ) : (
                  detailApplications.map((app, idx) => (
                    <Fragment key={`${app.tmNo}-${idx}`}>
                      <div className="col-span-3 mt-1.5 border-t border-dashed border-[#EEEEEE]" />
                      <div className="font-medium text-[#222]">
                        Application Number (TM No.)
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {formatTmNoForDisplay(app.tmNo) || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </div>

                      <div className="font-medium text-[#222]">
                        Date of Filing
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {formatDateDDMMYYYY(app.applicationDate)}
                      </div>

                      <div className="font-medium text-[#222]">
                        Mark (Type)
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {app.mark?.trim() || "—"}
                        {app.typeOfMark?.trim()
                          ? ` (${app.typeOfMark.trim()})`
                          : ""}
                      </div>

                      <div className="font-medium text-[#222]">Class</div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {app.classNumber != null ? (
                          String(app.classNumber)
                        ) : (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </div>

                      <div className="font-medium text-[#222]">Opponent</div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {app.nameOfOpponent?.trim() || "—"}
                        {app.placeOfOpponent?.trim()
                          ? ` (${app.placeOfOpponent.trim()})`
                          : ""}
                      </div>
                    </Fragment>
                  ))
                )}

                {/* Summary */}
                <div className="col-span-3 mt-1.5 border-t border-dashed border-[#EEEEEE]" />
                <div className="font-medium text-[#222]">
                  Last date for filing Counter Statement
                </div>
                <div className="text-[#222]">:</div>
                <div className="text-[#222] break-words min-w-0">
                  {formatDateDDMMYYYY(preview?.lastDateOfFilingCounter)}
                </div>

                <div className="font-medium text-[#222]">
                  Govt Fee for filing Counter
                </div>
                <div className="text-[#222]">:</div>
                <div className="text-[#222] break-words min-w-0">
                  {formatRupees(preview?.govtFee)}
                </div>

                <div className="font-medium text-[#222]">
                  Professional Charges for filing Counter
                </div>
                <div className="text-[#222]">:</div>
                <div className="text-[#222] break-words min-w-0">
                  {formatRupees(preview?.professionalCharges)}
                </div>

                <div className="font-medium text-[#222]">
                  Invoice Amount for Counter Statement
                </div>
                <div className="text-[#222]">:</div>
                <div className="text-[#222] break-words min-w-0">
                  {/*
                    Spec pg 121: 4q is the sum of Govt Fee and Professional Charges. This window
                    covers the whole acknowledgement (it lists every application above), and the two
                    rows just above are acknowledgement-wide — so 4q must be too. It used to render
                    `finalInvoiceAmount`, which is the *selected application's* draft amount, so a
                    2-application invoice read "Rs. 5,400 + Rs. 14,000 = Rs. 9,700".
                  */}
                  {formatRupees(preview?.invoiceAmount ?? finalInvoiceAmount)}
                </div>

                <div className="font-medium text-[#222]">
                  Date of Invoice for Counter Statement
                </div>
                <div className="text-[#222]">:</div>
                <div className="text-[#222] break-words min-w-0">
                  {formatDateDDMMYYYY(displayInvoiceDate)}
                </div>

                <div className="font-medium text-[#222]">
                  Invoice No. for Counter Statement
                </div>
                <div className="text-[#222]">:</div>
                <div className="text-[#222] break-words min-w-0">
                  {displayInvoiceNo?.trim() ? (
                    displayInvoiceNo
                  ) : (
                    <span className="text-[#6D6D6D]">
                      Auto-generated on save
                    </span>
                  )}
                </div>
              </div>
            </section>
          ) : previewTab === "pdf" ? (
            pdfUrl ? (
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages }) => setPreviewNumPages(numPages)}
                loading={
                  <p className="text-sm text-[#6d6d6d]">Loading PDF…</p>
                }
                error={
                  <p className="text-sm text-red-600">Failed to load PDF.</p>
                }
                className="flex flex-col items-center gap-4"
              >
                {previewNumPages != null &&
                  Array.from({ length: previewNumPages }, (_, i) => (
                    <Page
                      key={i + 1}
                      pageNumber={i + 1}
                      width={Math.min(
                        800,
                        typeof window !== "undefined"
                          ? window.innerWidth - 48
                          : 800
                      )}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  ))}
              </Document>
            ) : (
              <p className="text-sm text-[#6D6D6D]">No PDF available.</p>
            )
          ) : previewTab === "message" ? (
            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be sent to client WhatsApp after the invoice PDF
              </p>
              <WhatsAppMessageBlock
                message={messagePreview?.paymentMessage ?? ""}
                loading={messagePreviewLoading}
              />
            </section>
          ) : (
            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be emailed to the client with the invoice PDF
              </p>
              <EmailPreviewBlock
                preview={messagePreview?.emailPreview ?? null}
                loading={messagePreviewLoading}
                attachmentFallback={
                  pdfUrl
                    ? {
                        filename: "Invoice_for_Counter_Statement.pdf",
                        url: pdfUrl,
                      }
                    : null
                }
              />
            </section>
          )}
        </div>
      </FilePreviewModal>
    </div>
  );
}

function CounterInvoiceConfirmPreview({
  leadId,
  status,
  preview,
  selectedTmNo,
}: {
  leadId: string | null;
  status: CounterInvoiceStatus | null;
  preview: CounterInvoicePreview | null;
  selectedTmNo: string | null;
}) {
  const [tab, setTab] = useState<"details" | "pdf" | "message">("details");
  const [numPages, setNumPages] = useState<number | null>(null);
  const [messagePreview, setMessagePreview] =
    useState<CounterInvoiceWhatsAppPreview | null>(null);
  const [messagePreviewLoading, setMessagePreviewLoading] = useState(false);

  useEffect(() => {
    if (!leadId || !selectedTmNo) return;
    let cancelled = false;
    setMessagePreviewLoading(true);
    getCounterInvoiceWhatsAppPreview(leadId, [selectedTmNo])
      .then((data) => {
        if (!cancelled) setMessagePreview(data);
      })
      .catch(() => {
        if (!cancelled) setMessagePreview(null);
      })
      .finally(() => {
        if (!cancelled) setMessagePreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selectedTmNo, status?.counterInvoicePdfKey]);

  const FORM_STYLES = { fontFamily: "Inter, sans-serif" } as const;

  if (!status || !selectedTmNo) {
    return (
      <div className="p-4 text-sm text-[#6D6D6D]" style={FORM_STYLES}>
        Loading invoice…
      </div>
    );
  }
  const previewApp = preview?.applications.find((a) => a.tmNo === selectedTmNo);
  const tmRow = (status.tmInvoiceRows ?? []).find((r) => r.tmNo === selectedTmNo);
  const pdfUrl = status.counterInvoicePdfUrl ?? null;

  const formatRupees = (n: number | null | undefined) =>
    n != null && Number.isFinite(Number(n))
      ? `Rs.${Math.round(Number(n)).toLocaleString("en-IN")}/-`
      : "—";

  const contextRows: Array<{ label: string; value: string }> = [
    { label: "Addressee", value: preview?.addressee ?? "—" },
    { label: "Form of Address", value: preview?.formOfAddress ?? "—" },
    { label: "Mark", value: previewApp?.mark ?? "—" },
    { label: "Acknowledgement No.", value: preview?.acknowledgementNo ?? "—" },
    { label: "Date of Filing", value: preview?.dateOfFiling ?? "—" },
    { label: "Name of Opponent", value: previewApp?.nameOfOpponent ?? "—" },
    { label: "Place of Opponent", value: previewApp?.placeOfOpponent ?? "—" },
    {
      label: "No. of applications for Counter",
      value: String(status.noOfApps ?? 0),
    },
    { label: "Govt Fee", value: formatRupees(status.govtFee ?? preview?.govtFee) },
    {
      label: "Professional Charges",
      value: formatRupees(
        status.professionalCharges ?? preview?.professionalCharges
      ),
    },
    { label: "Invoice Amount", value: formatRupees(tmRow?.invoiceAmount) },
    { label: "Discount", value: formatRupees(tmRow?.discount) },
  ];

  const pageWidth = Math.min(
    800,
    typeof window !== "undefined" ? window.innerWidth - 96 : 800
  );

  return (
    <div className="flex flex-col" style={FORM_STYLES}>
      <div
        className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === "details"}
          onClick={() => setTab("details")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            tab === "details"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Invoice details
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "pdf"}
          onClick={() => setTab("pdf")}
          disabled={!pdfUrl}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
            tab === "pdf"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Generated PDF
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "message"}
          onClick={() => setTab("message")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            tab === "message"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Message Preview
        </button>
      </div>

      <div className="max-h-[60vh] min-h-0 overflow-auto bg-[#fafafa] p-3 sm:p-4">
        {tab === "details" ? (
          <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
            <p className="mb-3 text-[13px] font-semibold leading-5 text-[#222]">
              Data : Invoice for Counter Statement
            </p>
            <div
              className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
              style={FORM_STYLES}
            >
              {contextRows.map((row) => (
                <Fragment key={row.label}>
                  <div className="font-medium text-[#222]">{row.label}</div>
                  <div className="text-[#222]">:</div>
                  <div className="text-[#222] break-words min-w-0">
                    {row.value?.trim() ? (
                      row.value
                    ) : (
                      <span className="text-[#6D6D6D]">—</span>
                    )}
                  </div>
                </Fragment>
              ))}
            </div>
          </section>
        ) : tab === "pdf" ? (
          pdfUrl ? (
            <div className="flex justify-center">
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                loading={<p className="text-sm text-[#6d6d6d]">Loading PDF…</p>}
                error={
                  <p className="text-sm text-red-600">Failed to load PDF.</p>
                }
                className="flex flex-col items-center gap-4"
              >
                {numPages != null &&
                  Array.from({ length: numPages }, (_, i) => (
                    <Page
                      key={i + 1}
                      pageNumber={i + 1}
                      width={pageWidth}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  ))}
              </Document>
            </div>
          ) : (
            <p className="text-sm text-[#6D6D6D]">
              No PDF has been generated yet. Generate the invoice PDF before
              sending.
            </p>
          )
        ) : (
          <div className="flex flex-col gap-4">
            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be sent to client WhatsApp after the invoice PDF
              </p>
              <WhatsAppMessageBlock
                message={messagePreview?.paymentMessage ?? ""}
                loading={messagePreviewLoading}
              />
            </section>
            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be emailed to the client with the invoice PDF
              </p>
              <EmailPreviewBlock
                preview={messagePreview?.emailPreview ?? null}
                loading={messagePreviewLoading}
                attachmentFallback={
                  pdfUrl
                    ? {
                        filename: "Invoice_for_Counter_Statement.pdf",
                        url: pdfUrl,
                      }
                    : null
                }
              />
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function EvidenceInvoiceConfirmPreview({
  leadId,
  status,
  preview,
  selectedTmNo,
}: {
  leadId: string | null;
  status: EvidenceInvoiceStatus | null;
  preview: EvidenceInvoicePreview | null;
  selectedTmNo: string | null;
}) {
  const [tab, setTab] = useState<"details" | "pdf" | "message">("details");
  const [numPages, setNumPages] = useState<number | null>(null);
  const [messagePreview, setMessagePreview] = useState<{
    pdfMessage: string;
    paymentMessage: string;
    emailPreview: {
      to: string | null;
      subject: string;
      body: string;
      filename: string | null;
      downloadUrl: string | null;
    };
  } | null>(null);
  const [messagePreviewLoading, setMessagePreviewLoading] = useState(false);

  useEffect(() => {
    if (!leadId || !selectedTmNo) return;
    let cancelled = false;
    setMessagePreviewLoading(true);
    getEvidenceInvoiceWhatsAppPreview(leadId, [selectedTmNo])
      .then((data) => {
        if (!cancelled) setMessagePreview(data);
      })
      .catch(() => {
        if (!cancelled) setMessagePreview(null);
      })
      .finally(() => {
        if (!cancelled) setMessagePreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selectedTmNo, status?.evidenceInvoicePdfKey]);

  const FORM_STYLES = { fontFamily: "Inter, sans-serif" } as const;

  if (!status || !selectedTmNo) {
    return (
      <div className="p-4 text-sm text-[#6D6D6D]" style={FORM_STYLES}>
        Loading invoice…
      </div>
    );
  }
  const previewApp = preview?.applications.find((a) => a.tmNo === selectedTmNo);
  const tmRow = (status.tmInvoiceRows ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const pdfUrl = status.evidenceInvoicePdfUrl ?? null;

  const formatRupees = (n: number | null | undefined) =>
    n != null && Number.isFinite(Number(n))
      ? `Rs.${Math.round(Number(n)).toLocaleString("en-IN")}/-`
      : "—";

  const contextRows: Array<{ label: string; value: string }> = [
    { label: "Addressee", value: preview?.addressee ?? "—" },
    { label: "Form of Address", value: preview?.formOfAddress ?? "—" },
    { label: "Mark", value: previewApp?.mark ?? "—" },
    { label: "Acknowledgement No.", value: preview?.acknowledgementNo ?? "—" },
    { label: "Date of Filing", value: preview?.dateOfFiling ?? "—" },
    { label: "Name of Opponent", value: previewApp?.nameOfOpponent ?? "—" },
    { label: "Place of Opponent", value: previewApp?.placeOfOpponent ?? "—" },
    {
      label: "No. of applications for Evidence",
      value: String(
        preview?.noOfApplicationsForEvidence ?? status.noOfApps ?? 0
      ),
    },
    {
      label: "Professional Charges",
      value: formatRupees(
        status.professionalCharges ?? preview?.professionalCharges
      ),
    },
    { label: "Invoice Amount", value: formatRupees(tmRow?.invoiceAmount) },
    { label: "Discount", value: formatRupees(tmRow?.discount) },
  ];

  const pageWidth = Math.min(
    800,
    typeof window !== "undefined" ? window.innerWidth - 96 : 800
  );

  return (
    <div className="flex flex-col" style={FORM_STYLES}>
      <div
        className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === "details"}
          onClick={() => setTab("details")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            tab === "details"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Invoice details
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "pdf"}
          onClick={() => setTab("pdf")}
          disabled={!pdfUrl}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
            tab === "pdf"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Generated PDF
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "message"}
          onClick={() => setTab("message")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            tab === "message"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Message Preview
        </button>
      </div>

      <div className="max-h-[60vh] min-h-0 overflow-auto bg-[#fafafa] p-3 sm:p-4">
        {tab === "details" ? (
          <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
            <p className="mb-3 text-[13px] font-semibold leading-5 text-[#222]">
              Data : Invoice for Evidence
            </p>
            <div
              className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
              style={FORM_STYLES}
            >
              {contextRows.map((row) => (
                <Fragment key={row.label}>
                  <div className="font-medium text-[#222]">{row.label}</div>
                  <div className="text-[#222]">:</div>
                  <div className="text-[#222] break-words min-w-0">
                    {row.value?.trim() ? (
                      row.value
                    ) : (
                      <span className="text-[#6D6D6D]">—</span>
                    )}
                  </div>
                </Fragment>
              ))}
            </div>
          </section>
        ) : tab === "pdf" ? (
          pdfUrl ? (
            <div className="flex justify-center">
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                loading={<p className="text-sm text-[#6d6d6d]">Loading PDF…</p>}
                error={
                  <p className="text-sm text-red-600">Failed to load PDF.</p>
                }
                className="flex flex-col items-center gap-4"
              >
                {numPages != null &&
                  Array.from({ length: numPages }, (_, i) => (
                    <Page
                      key={i + 1}
                      pageNumber={i + 1}
                      width={pageWidth}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  ))}
              </Document>
            </div>
          ) : (
            <p className="text-sm text-[#6D6D6D]">
              No PDF has been generated yet. Generate the invoice PDF before
              sending.
            </p>
          )
        ) : (
          <div className="flex flex-col gap-4">
            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be sent to client WhatsApp after the invoice PDF
              </p>
              <WhatsAppMessageBlock
                message={messagePreview?.paymentMessage ?? ""}
                loading={messagePreviewLoading}
              />
            </section>
            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be emailed to the client with the invoice PDF
              </p>
              <EmailPreviewBlock
                preview={messagePreview?.emailPreview ?? null}
                loading={messagePreviewLoading}
                attachmentFallback={
                  pdfUrl
                    ? {
                        filename: "Evidence Invoice.pdf",
                        url: pdfUrl,
                      }
                    : null
                }
              />
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function OppositionHearingInvoiceConfirmPreview({
  leadId,
  status,
  preview,
  selectedTmNo,
}: {
  leadId: string | null;
  status: OppositionHearingInvoiceStatus | null;
  preview: OppositionHearingInvoicePreview | null;
  selectedTmNo: string | null;
}) {
  const [tab, setTab] = useState<"details" | "pdf" | "message">("details");
  const [numPages, setNumPages] = useState<number | null>(null);
  const [messagePreview, setMessagePreview] =
    useState<OppositionHearingInvoiceWhatsAppPreview | null>(null);
  const [messagePreviewLoading, setMessagePreviewLoading] = useState(false);

  useEffect(() => {
    if (!leadId || !selectedTmNo) return;
    let cancelled = false;
    setMessagePreviewLoading(true);
    getOppositionHearingInvoiceWhatsAppPreview(leadId, [selectedTmNo])
      .then((data) => {
        if (!cancelled) setMessagePreview(data);
      })
      .catch(() => {
        if (!cancelled) setMessagePreview(null);
      })
      .finally(() => {
        if (!cancelled) setMessagePreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selectedTmNo, status?.oppositionHearingInvoicePdfKey]);

  const FORM_STYLES = { fontFamily: "Inter, sans-serif" } as const;

  if (!status || !selectedTmNo) {
    return (
      <div className="p-4 text-sm text-[#6D6D6D]" style={FORM_STYLES}>
        Loading invoice…
      </div>
    );
  }
  const previewApp = preview?.applications.find((a) => a.tmNo === selectedTmNo);
  const tmRow = (status.tmInvoiceRows ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const pdfUrl = status.oppositionHearingInvoicePdfUrl ?? null;

  const formatRupees = (n: number | null | undefined) =>
    n != null && Number.isFinite(Number(n))
      ? `Rs.${Math.round(Number(n)).toLocaleString("en-IN")}/-`
      : "—";

  const contextRows: Array<{ label: string; value: string }> = [
    { label: "Addressee", value: preview?.addressee ?? "—" },
    { label: "Form of Address", value: preview?.formOfAddress ?? "—" },
    { label: "Mark", value: previewApp?.mark ?? preview?.mark ?? "—" },
    {
      label: "Acknowledgement No.",
      value: preview?.acknowledgementNo ?? "—",
    },
    { label: "Date of Filing", value: preview?.dateOfFiling ?? "—" },
    {
      label: "Date of Opposition Hearing",
      value: previewApp?.hearingDate ?? "—",
    },
    {
      label: "No. of applications for Opposition Hearing",
      value: String(
        preview?.noOfApplicationsForOppositionHearing ?? status.noOfApps ?? 0
      ),
    },
    {
      label: "Professional Charges",
      value: formatRupees(
        status.professionalCharges ?? preview?.professionalCharges
      ),
    },
    { label: "Invoice Amount", value: formatRupees(tmRow?.invoiceAmount) },
    { label: "Discount", value: formatRupees(tmRow?.discount) },
  ];

  const pageWidth = Math.min(
    800,
    typeof window !== "undefined" ? window.innerWidth - 96 : 800
  );

  return (
    <div className="flex flex-col" style={FORM_STYLES}>
      <div
        className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === "details"}
          onClick={() => setTab("details")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            tab === "details"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Invoice details
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "pdf"}
          onClick={() => setTab("pdf")}
          disabled={!pdfUrl}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
            tab === "pdf"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Generated PDF
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "message"}
          onClick={() => setTab("message")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            tab === "message"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Message Preview
        </button>
      </div>

      <div className="max-h-[60vh] min-h-0 overflow-auto bg-[#fafafa] p-3 sm:p-4">
        {tab === "details" ? (
          <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
            <p className="mb-3 text-[13px] font-semibold leading-5 text-[#222]">
              Data : Invoice for Opposition Hearing
            </p>
            <div
              className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
              style={FORM_STYLES}
            >
              {contextRows.map((row) => (
                <Fragment key={row.label}>
                  <div className="font-medium text-[#222]">{row.label}</div>
                  <div className="text-[#222]">:</div>
                  <div className="text-[#222] break-words min-w-0">
                    {row.value?.trim() ? (
                      row.value
                    ) : (
                      <span className="text-[#6D6D6D]">—</span>
                    )}
                  </div>
                </Fragment>
              ))}
            </div>
          </section>
        ) : tab === "pdf" ? (
          pdfUrl ? (
            <div className="flex justify-center">
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                loading={<p className="text-sm text-[#6d6d6d]">Loading PDF…</p>}
                error={
                  <p className="text-sm text-red-600">Failed to load PDF.</p>
                }
                className="flex flex-col items-center gap-4"
              >
                {numPages != null &&
                  Array.from({ length: numPages }, (_, i) => (
                    <Page
                      key={i + 1}
                      pageNumber={i + 1}
                      width={pageWidth}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  ))}
              </Document>
            </div>
          ) : (
            <p className="text-sm text-[#6D6D6D]">
              No PDF has been generated yet. Generate the invoice PDF before
              sending.
            </p>
          )
        ) : (
          <div className="flex flex-col gap-4">
            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be sent to client WhatsApp with the invoice PDF
              </p>
              <WhatsAppMessageBlock
                message={messagePreview?.pdfMessage ?? ""}
                loading={messagePreviewLoading}
              />
            </section>
            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be emailed to the client with the invoice PDF
              </p>
              <EmailPreviewBlock
                preview={messagePreview?.emailPreview ?? null}
                loading={messagePreviewLoading}
                attachmentFallback={
                  pdfUrl
                    ? {
                        filename: "Opposition Hearing Invoice.pdf",
                        url: pdfUrl,
                      }
                    : null
                }
              />
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payment from Client for Counter — sub-components
// ---------------------------------------------------------------------------

function CurrencyAmountField({
  label,
  value,
  onChange,
  readOnly = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}) {
  return (
    <div
      className="flex flex-col items-start gap-1"
      style={{ flex: "1 1 0", minWidth: 0 }}
    >
      <label
        className="flex items-center text-[12px] font-normal leading-5 text-[#222222]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {label}
      </label>
      <div
        className="flex flex-row justify-between items-center w-full h-9 px-0 gap-2.5"
        style={{
          background: readOnly ? "#F7F7F7" : "#FFFFFF",
          border: "1px solid #EEEEEE",
          borderRadius: "6px",
          boxSizing: "border-box",
        }}
      >
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => {
            if (readOnly) return;
            const cleaned = e.target.value.replace(/[^\d.]/g, "");
            const parts = cleaned.split(".");
            const next =
              parts.length > 1
                ? `${parts[0]}.${parts.slice(1).join("")}`
                : cleaned;
            onChange(next);
          }}
          onKeyDown={(e) => {
            if (readOnly) return;
            if (
              e.key.length === 1 &&
              !e.ctrlKey &&
              !e.metaKey &&
              !/[\d.]/.test(e.key)
            ) {
              e.preventDefault();
            }
          }}
          readOnly={readOnly}
          placeholder="00.00"
          className="flex-1 min-w-0 h-full pl-2.5 pr-0 py-0 bg-transparent text-[12px] font-normal leading-5 text-[#222222] placeholder:text-[#6D6D6D] border-0 outline-none focus:ring-0 disabled:bg-transparent disabled:cursor-default"
          style={{ fontFamily: "Inter, sans-serif" }}
        />
        <div
          className="flex flex-row justify-center items-center shrink-0 px-2.5 py-2 gap-2.5 h-9 min-w-[76px]"
          style={{ background: "#F7F7F7", borderRadius: "0 6px 6px 0" }}
        >
          <span className="text-[12px] font-normal leading-5 text-[#6D6D6D]">
            Rs(₹)
          </span>
          <img
            src={chevronDownIcon}
            alt=""
            className="w-4 h-4 shrink-0"
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
}

function formatAmount(num: number | null | undefined): string {
  if (num == null || !Number.isFinite(num) || num === 0) return "";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseAmount(s: string): number {
  const cleaned = String(s || "").replace(/[^\d.]/g, "");
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

function CounterPaymentFields({
  status,
  rows,
  selectedTmNo,
  onChange,
}: {
  status: CounterInvoiceStatus | null;
  rows: Record<string, string>;
  selectedTmNo: string | null;
  onChange: (tmNo: string, val: string) => void;
}) {
  if (!status || !selectedTmNo) {
    return <p className="text-sm text-[#6D6D6D]">Loading payment status…</p>;
  }
  const tmRow = (status.tmInvoiceRows ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const invoiceAmount = tmRow ? tmRow.invoiceAmount - (tmRow.discount ?? 0) : 0;
  const savedCollected = Number(tmRow?.collectedAmount ?? 0) || 0;
  const thisPayment = parseAmount(rows[selectedTmNo] ?? "");
  const totalCollected = savedCollected + thisPayment;
  const outstanding = Math.max(0, invoiceAmount - totalCollected);
  const outstandingFormatted = outstanding.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return (
    <div
      className="flex flex-col items-start gap-[22px] w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-start gap-5 w-full">
        <CurrencyAmountField
          label="Invoice Amount for Counter"
          value={formatAmount(invoiceAmount)}
          onChange={() => {}}
          readOnly
        />
        <div
          className="flex flex-col gap-1"
          style={{ flex: "1 1 0", minWidth: 0 }}
        >
          <CurrencyAmountField
            label="Collected Amount for Counter"
            value={rows[selectedTmNo] ?? ""}
            onChange={(v) => onChange(selectedTmNo, v)}
          />
          {savedCollected > 0 && (
            <span className="text-[11px] leading-4 text-[#6D6D6D]">
              Already collected: Rs.{" "}
              {savedCollected.toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
              /-
            </span>
          )}
        </div>
      </div>

      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 w-full px-4 sm:px-5 py-4 sm:py-0 min-h-[60px] sm:h-[60px]"
        style={{ background: "#222222", borderRadius: "8px" }}
      >
        <span className="text-[12px] sm:text-[14px] font-normal leading-5 text-white/90">
          OUTSTANDING AMOUNT FOR COUNTER
        </span>
        <span className="text-[18px] sm:text-[22px] font-bold leading-8 sm:leading-10 text-white">
          Rs. {outstandingFormatted}/-
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Prepare Counter — sub-components
// ---------------------------------------------------------------------------

function PrepareCounterFields({
  status,
  perTm,
  selectedTmNo,
  generating,
  onPatch,
  onGeneratePdf,
}: {
  status: PrepareCounterStatus | null;
  perTm: Record<string, PrepareCounterDraft>;
  selectedTmNo: string | null;
  generating: boolean;
  onPatch: (tmNo: string, patch: Partial<PrepareCounterDraft>) => void;
  onGeneratePdf: (app: PrepareCounterApplicationItem) => void;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<"details" | "pdf">("details");
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);

  if (!status || !selectedTmNo) {
    return <p className="text-sm text-[#6D6D6D]">Loading Prepare Counter…</p>;
  }
  const app = (status.counterStatements ?? []).find(
    (a) => a.tmNo === selectedTmNo
  );
  const draft = perTm[selectedTmNo];
  if (!app || !draft) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Prepare Counter is not available for this TM yet.
      </p>
    );
  }

  const dateParts = parseDateParts(draft.dateOfEvidenceAffidavit);
  const filingCounterDateParts = parseDateParts(draft.dateOfFilingCounterStatement);
  const pdfUrl = app.counterStatementPdfUrl ?? null;

  const handleDownloadPdf = async () => {
    if (!pdfUrl || downloading) return;
    setDownloading(true);
    try {
      const filename = `Counter_Statement_${app.tmNo || "counter"}.pdf`;
      const result = await downloadFileFromUrlWithFallback(pdfUrl, filename);
      if (result === "local") {
        toast.success("Download started");
      } else {
        toast.success(
          "Opened the document in a new tab — use the browser's download option to save it."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const closePreviewModal = () => {
    setPreviewOpen(false);
    setPreviewNumPages(null);
    setPreviewTab("details");
  };

  const selectedClassLabel =
    app.classNumber != null ? `Class ${app.classNumber}` : "";

  // Spec pg 125 states the signatory, class+goods and opposition no.+date as single lines. The
  // backend already composes all three for this payload (`buildSignatoryDetailsString`,
  // `classWithGoodsServices`, `oppositionNoWithDate`) — the same strings the generated PDF uses, and
  // the same ones the sibling Evidence Affidavit preview already reads. Use them rather than
  // re-deriving here, so the window can't drift from the document.
  const signatoryDetailsLine = app.signatoryDetails?.trim() || "";
  const oppositionNoWithDate = app.oppositionNoWithDate?.trim() || "";
  // NB: the payload's `classWithGoodsServices` is already prefixed ("Class 9 : Computers, …"), so it
  // is deliberately *not* used as the value under a "Class 9" label — that would render the class
  // twice. Spec pg 125 wants the class as the label and the bare goods list as the value.
  const goodsServicesForClass = app.goodsServices?.trim() || "";

  const previewRows: Array<{ label: string; value: string }> = [
    { label: "Mark", value: app.mark ?? "" },
    { label: "Applicant", value: app.applicant ?? "" },
    { label: "Applicant Address", value: app.applicantAddress ?? "" },
    { label: "Applicant Type", value: app.applicantType ?? "" },
    // Spec pg 125 states the signatory as one line — "Anoop Gangadharan, aged 44 years,
    // S/o Gangadharan, Managing Trustee" — rather than five separate rows (review item 38).
    { label: "Signatory details", value: signatoryDetailsLine },
    { label: "User Date", value: app.userDate ?? "" },
    // Spec pg 125 uses the class as the label and the goods/services as its value
    // ("Class 45 : Charitable activities, …"), the same shape already adopted for the UA preview.
    {
      label: selectedClassLabel || "Goods / Services",
      value: goodsServicesForClass,
    },
    // "Description of Applicant" intentionally omitted — spec pg 125 ("Data for Counter") lists
    // Applicant Type only, and both rows side by side read as duplicates (review item 13).
    { label: "GS Type A", value: app.gsTypeA ?? "" },
    { label: "GS Type B", value: app.gsTypeB ?? "" },
    { label: "GS Type C", value: app.gsTypeC ?? "" },
    // Spec pg 125: "Opposition No. : 1926851 dated 22/09/2025" — number and date on one line.
    { label: "Opposition No.", value: oppositionNoWithDate },
    {
      label: "Date of Receipt of Opposition",
      value: app.dateOfReceiptOfOpposition ?? "",
    },
    { label: "Name of Opponent", value: app.nameOfOpponent ?? "" },
    { label: "Address of Opponent", value: app.addressOfOpponent ?? "" },
    { label: "Opponent's Mark", value: app.opponentsMark ?? "" },
    {
      label: "Class of Opponent's Mark",
      value: app.classOfOpponentsMark ?? "",
    },
    {
      label: "Goods/Services of Opponent's Mark",
      value: app.goodsServicesOfOpponentsMark ?? "",
    },
    {
      label: "User Date of Opponent's Mark",
      value: app.userDateOfOpponentsMark ?? "",
    },
  ];

  const trimmedReplies = (draft.paragraphWiseReplies ?? []).filter(
    (p) => (p.heading ?? "").trim() || (p.text ?? "").trim()
  );

  const updateReply = (
    idx: number,
    patch: Partial<{ heading: string; text: string }>
  ) => {
    onPatch(selectedTmNo, {
      paragraphWiseReplies: draft.paragraphWiseReplies.map((p, i) =>
        i === idx ? { ...p, ...patch } : p
      ),
    });
  };
  const addReply = () =>
    onPatch(selectedTmNo, {
      paragraphWiseReplies: [
        ...draft.paragraphWiseReplies,
        { heading: "", text: "" },
      ],
    });
  const removeReply = (idx: number) =>
    onPatch(selectedTmNo, {
      paragraphWiseReplies: draft.paragraphWiseReplies.filter((_, i) => i !== idx),
    });

  return (
    <div className="flex flex-col gap-5" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Editable fields per the Prepare Counter DTO */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            No. of Paragraphs in Opponent&apos;s Intro
            <RequiredMark />
          </label>
          <input
            type="number"
            min={0}
            value={draft.noOfParagraphsInOpponentIntro}
            onChange={(e) =>
              onPatch(selectedTmNo, {
                noOfParagraphsInOpponentIntro: e.target.value,
              })
            }
            placeholder="0"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Evidence Affidavit
            <RequiredMark />
          </label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) =>
              onPatch(selectedTmNo, {
                dateOfEvidenceAffidavit: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
          />
        </div>
      </div>

      {/* Paragraph-wise replies */}
      <div className="flex flex-col gap-3">
        <label className="text-xs font-normal text-[#222222] leading-5">
          Paragraph-wise Replies for Counter
          <RequiredMark />
        </label>
        {draft.paragraphWiseReplies.map((reply, idx) => (
          <div
            key={idx}
            className="flex flex-col md:flex-row gap-3 md:gap-5 p-3 border border-[#EEEEEE] rounded-[6px]"
          >
            <div className="flex flex-col gap-1 flex-1 min-w-0">
              <label className="text-xs font-normal text-[#222222] leading-5">
                Heading
                <RequiredMark />
              </label>
              <input
                type="text"
                value={reply.heading}
                onChange={(e) => updateReply(idx, { heading: e.target.value })}
                placeholder="Heading (e.g., Paragraphs 1 to 5)"
                className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
                style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
              />
            </div>
            <div className="flex flex-col gap-1 flex-[2] min-w-0">
              <label className="text-xs font-normal text-[#222222] leading-5">
                Reply Text
                <RequiredMark />
              </label>
              <textarea
                value={reply.text}
                onChange={(e) => updateReply(idx, { text: e.target.value })}
                placeholder="Reply text"
                rows={2}
                className="w-full px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222] resize-y"
                style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
              />
            </div>
            {draft.paragraphWiseReplies.length > 1 && (
              <button
                type="button"
                onClick={() => removeReply(idx)}
                className="self-start md:self-end px-3 h-9 rounded-[6px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
              >
                Remove
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addReply}
          className="self-start px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
        >
          Add Paragraph Reply
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Name of Opponent&apos;s Attorney
            <RequiredMark />
          </label>
          <input
            type="text"
            value={draft.opponentAttorneyName}
            onChange={(e) =>
              onPatch(selectedTmNo, { opponentAttorneyName: e.target.value })
            }
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Address of Opponent&apos;s Attorney
            <RequiredMark />
          </label>
          <input
            type="text"
            value={draft.opponentAttorneyAddress}
            onChange={(e) =>
              onPatch(selectedTmNo, { opponentAttorneyAddress: e.target.value })
            }
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Email of Opponent&apos;s Attorney
            <RequiredMark />
          </label>
          <input
            type="email"
            value={draft.opponentAttorneyEmail}
            onChange={(e) =>
              onPatch(selectedTmNo, { opponentAttorneyEmail: e.target.value })
            }
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
        <div className="hidden md:block flex-1" />
      </div>

      {/* 4u) Date of Filing Counter Statement — spec pg 125 places this Date on the
          Prepare Counter window, directly above Generate PDF. The template's
          "Dated this {dateOfFilingCounterStatementFormatted}" line reads from it, so
          without it here the generated PDF shows "Dated this" followed by a blank.
          4A(ix) File Counter writes the same column, so the two stay in sync. */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Filing Counter Statement
          </label>
          <DateTimePicker
            value={
              filingCounterDateParts ? partsToDate(filingCounterDateParts) : null
            }
            onChange={(d) =>
              onPatch(selectedTmNo, {
                dateOfFilingCounterStatement: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
          />
        </div>
        <div className="hidden md:block flex-1" />
      </div>

      {/* Generate / Download / Preview Counter Statement PDF */}
      <div className="flex flex-row flex-wrap items-center gap-3 pt-1">
        <button
          type="button"
          onClick={() => onGeneratePdf(app)}
          disabled={generating}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {generating
            ? "Generating…"
            : app.counterStatementPdfKey
              ? "Regenerate Counter Statement PDF"
              : "Generate Counter Statement PDF"}
        </button>
        <button
          type="button"
          onClick={() => void handleDownloadPdf()}
          disabled={!pdfUrl || downloading || generating}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-white bg-[#222222] rounded-[10px] hover:bg-[#333333] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {downloading ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={() => {
            setPreviewTab("details");
            setPreviewNumPages(null);
            setPreviewOpen(true);
          }}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5]"
        >
          Preview
        </button>
      </div>

      <FilePreviewModal
        open={previewOpen}
        onClose={closePreviewModal}
        name={`Counter Statement Preview${app.tmNo ? ` — ${app.tmNo}` : ""}`}
      >
        <div
          className="flex w-full max-w-3xl flex-col gap-3"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <div
            className="flex shrink-0 gap-1 border-b border-[#e3e3e3]"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={previewTab === "details"}
              onClick={() => setPreviewTab("details")}
              className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                previewTab === "details"
                  ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                  : "text-[#6d6d6d] hover:text-[#222]"
              }`}
            >
              Details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={previewTab === "pdf"}
              onClick={() => setPreviewTab("pdf")}
              disabled={!pdfUrl}
              className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
                previewTab === "pdf"
                  ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                  : "text-[#6d6d6d] hover:text-[#222]"
              }`}
            >
              Generated PDF
            </button>
          </div>

          {previewTab === "details" ? (
            <section
              className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Application Details
              </p>
              <div
                className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                {previewRows.map((row) => (
                  <Fragment key={row.label}>
                    <div className="font-medium text-[#222]">{row.label}</div>
                    <div className="text-[#222]">:</div>
                    <div className="text-[#222] break-words whitespace-pre-wrap min-w-0">
                      {row.value?.trim() ? (
                        row.value
                      ) : (
                        <span className="text-[#6D6D6D]">—</span>
                      )}
                    </div>
                  </Fragment>
                ))}
                <div className="font-medium text-[#222]">Image of Mark</div>
                <div className="text-[#222]">:</div>
                <div className="min-w-0">
                  {app.imageOfMarkUrl ? (
                    <div className="border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] overflow-hidden h-[140px] max-w-[260px]">
                      <img
                        src={app.imageOfMarkUrl}
                        alt=""
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <span className="text-[#6D6D6D]">—</span>
                  )}
                </div>
                <div className="font-medium text-[#222]">
                  Image of Opponent&apos;s Mark
                </div>
                <div className="text-[#222]">:</div>
                <div className="min-w-0">
                  {app.imageOfOpponentsMarkUrl ? (
                    <div className="border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] overflow-hidden h-[140px] max-w-[260px]">
                      <img
                        src={app.imageOfOpponentsMarkUrl}
                        alt=""
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <span className="text-[#6D6D6D]">—</span>
                  )}
                </div>
              </div>

              <p className="mt-4 mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                List of Paragraph-wise Replies
              </p>
              <div className="rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5">
                {trimmedReplies.length === 0 ? (
                  <span className="text-[#6D6D6D]">—</span>
                ) : (
                  <ul className="flex flex-col gap-1.5">
                    {trimmedReplies.map((reply, i) => (
                      <li
                        key={i}
                        className="flex flex-col sm:flex-row gap-1 sm:gap-2 text-[#222]"
                      >
                        <span className="font-medium shrink-0">
                          &gt; {reply.heading?.trim() || "—"} :
                        </span>
                        <span className="whitespace-pre-wrap break-words min-w-0">
                          {reply.text?.trim() || "—"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <p className="mt-4 mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Date
              </p>
              <div className="rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5 text-[#222]">
                {dateParts
                  ? formatOrdinalLongDate(partsToDate(dateParts))
                  : <span className="text-[#6D6D6D]">—</span>}
              </div>
            </section>
          ) : pdfUrl ? (
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages }) => setPreviewNumPages(numPages)}
              loading={<p className="text-sm text-[#6d6d6d]">Loading PDF…</p>}
              error={
                <p className="text-sm text-red-600">Failed to load PDF.</p>
              }
              className="flex flex-col items-center gap-4"
            >
              {previewNumPages != null &&
                Array.from({ length: previewNumPages }, (_, i) => (
                  <Page
                    key={i + 1}
                    pageNumber={i + 1}
                    width={Math.min(
                      800,
                      typeof window !== "undefined"
                        ? window.innerWidth - 48
                        : 800
                    )}
                    renderTextLayer
                    renderAnnotationLayer
                  />
                ))}
            </Document>
          ) : (
            <p className="text-sm text-[#6D6D6D]">
              No PDF generated yet. Click &quot;Generate Counter Statement
              PDF&quot; to create one.
            </p>
          )}
        </div>
      </FilePreviewModal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// File Counter — sub-components
// ---------------------------------------------------------------------------

function FileCounterFields({
  status,
  perTm,
  selectedTmNo,
  onPatch,
  onPreview,
}: {
  status: FileCounterStatus | null;
  perTm: Record<string, { filingDate: string; pendingFile: File | null }>;
  selectedTmNo: string | null;
  onPatch: (
    tmNo: string,
    patch: Partial<{ filingDate: string; pendingFile: File | null }>
  ) => void;
  onPreview: (row: FileCounterApplicationRow) => void;
}) {
  if (!status || !selectedTmNo) {
    return <p className="text-sm text-[#6D6D6D]">Loading File Counter…</p>;
  }
  const app = (status.applications ?? []).find((a) => a.tmNo === selectedTmNo);
  const draft = perTm[selectedTmNo];
  if (!app || !draft) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        File Counter is not available for this TM yet.
      </p>
    );
  }
  const dateParts = parseDateParts(draft.filingDate);
  const inputId = `file-counter-input-${selectedTmNo}`;
  const hasFile = !!(draft.pendingFile || app.pdfUploaded);
  const fileName = draft.pendingFile?.name ?? "Counter Statement";
  const canPreview = !!draft.pendingFile;
  return (
    <div className="flex flex-col gap-5" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Filing Counter Statement
            <RequiredMark />
          </label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) =>
              onPatch(selectedTmNo, {
                filingDate: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
        <div className="hidden md:block flex-1" />
      </div>

      <div className="flex flex-col gap-3 border border-[#EEEEEE] rounded-[10px] p-4 md:p-5">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold text-[#101111]">
            Counter Statement PDF
            <RequiredMark />
          </p>
          <p className="text-xs text-[#7D7D7D]">
            Upload the filed Counter Statement PDF for this TM.
          </p>
        </div>

        <div
          className={`flex flex-col md:flex-row items-stretch md:items-center gap-4 rounded-2xl border border-dashed px-4 py-4 cursor-pointer transition-colors ${
            hasFile
              ? "border-[#22C55E] bg-[#F0FDF4]"
              : "border-[#D8DAEB] bg-white"
          }`}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
            <img
              src={uploadDropzoneGraphic}
              alt=""
              width={60}
              height={60}
              className="flex-shrink-0"
            />
            <div className="flex flex-col gap-1 min-w-0 flex-1">
              {hasFile ? (
                <p className="text-sm font-medium text-[#16A34A] truncate">
                  {fileName}
                </p>
              ) : (
                <>
                  <p className="text-base font-medium text-[#101111]">
                    Drag and drop your file
                  </p>
                  <p className="text-sm text-[#7D7D7D]">{SUPPORTED_FORMATS_LABEL}</p>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-row items-center gap-2 flex-shrink-0 self-end md:self-center">
            {!hasFile && (
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-sm font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById(inputId)?.click();
                }}
              >
                Select File
              </button>
            )}
            {hasFile && (
              <>
                {canPreview && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreview(app);
                    }}
                    className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                  >
                    Preview
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPatch(selectedTmNo, { pendingFile: null });
                  }}
                  className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
                >
                  Remove
                </button>
              </>
            )}
          </div>
        </div>

        <input
          id={inputId}
          type="file"
          accept={ALLOWED_FILE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              const reason = validateUploadFile(file);
              if (reason) {
                toast.error(reason);
                return;
              }
              onPatch(selectedTmNo, { pendingFile: file });
            }
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inform Client about Filing of Counter — message preview
// ---------------------------------------------------------------------------

function InformFilingMessagePreview({
  preview,
}: {
  preview: { message: string } | null;
}) {
  return (
    <div className="flex flex-col gap-2.5" style={{ fontFamily: "Inter, sans-serif" }}>
      <span className="text-base font-normal text-[#222222]" style={{ lineHeight: "20px" }}>
        Message Preview
      </span>
      <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[120px] bg-white">
        {preview?.message ? (
          <p className="text-xs text-[#222222] whitespace-pre-line" style={{ lineHeight: "20px" }}>
            {preview.message}
          </p>
        ) : (
          <p className="text-xs text-[#222222] whitespace-pre-line" style={{ lineHeight: "20px" }}>
            A &quot;Trademark Update&quot; message will be sent to the client
            informing them that the Counter Statement has been filed for the TM
            No(s) under this acknowledgement.
            {"\n\n"}
            <span className="text-[#6D6D6D]">
              The exact message body is generated server-side; click Send &amp; Continue to dispatch via WhatsApp.
            </span>
          </p>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Notice of Counter by Registry — sub-components
// ---------------------------------------------------------------------------

function computeNoticeCounterLastDate(noticeYmd: string): Date | null {
  const parts = parseDateParts(noticeYmd);
  if (!parts) return null;
  const d = partsToDate(parts);
  const day = d.getDate();
  const targetDate = new Date(d.getFullYear(), d.getMonth() + 2, 1);
  const lastDayOfMonth = new Date(
    targetDate.getFullYear(),
    targetDate.getMonth() + 1,
    0,
  ).getDate();
  targetDate.setDate(Math.min(day, lastDayOfMonth));
  return targetDate;
}

/**
 * Derives the alert state for the "Last Date for Evidence by Opponent"
 * (substep 4A(xi)). Per spec, the alert is always shown while a date is set:
 *   - upcoming: a date is set and has not yet passed (always shown)
 *   - overdue:  the date has passed (server `expired` flag, or daysUntil < 0)
 *   - none:     no date set yet (nothing to alert on)
 */
type EvidenceDeadlineState = "none" | "upcoming" | "overdue";
function deriveEvidenceDeadlineState(
  lastDateYmd: string | null | undefined,
  expired: boolean | undefined,
): { state: EvidenceDeadlineState; date: Date | null; daysUntil: number | null } {
  const parts = parseDateParts(lastDateYmd ?? null);
  if (!parts) return { state: "none", date: null, daysUntil: null };
  const target = partsToDate(parts);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysUntil = Math.round((target.getTime() - today.getTime()) / msPerDay);
  if (expired === true || daysUntil < 0) {
    return { state: "overdue", date: target, daysUntil };
  }
  return { state: "upcoming", date: target, daysUntil };
}

function formatLongDate(d: Date): string {
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * 4A(xi) alert copy. 4w) is always exactly 2 months after 4v), so the deadline date — not a
 * countdown — is the fact staff act on. The old wording led on a day count relative to today
 * ("due in 48 days"), which the client flagged as confusing because it has no visible anchor.
 */
function evidenceDeadlineMessage(state: EvidenceDeadlineState, date: Date): string {
  return state === "overdue"
    ? `Last Date for Evidence by Opponent has passed (${formatLongDate(date)}).`
    : `Last Date for Evidence by Opponent: ${formatLongDate(date)} — 2 months from the Date of Notice of Counter Statement.`;
}

function EvidenceDeadlineDot({
  state,
  date,
}: {
  state: EvidenceDeadlineState;
  date: Date | null;
}) {
  if (state === "none" || !date) return null;
  const color = state === "overdue" ? "#DC2626" : "#E18308";
  const title = evidenceDeadlineMessage(state, date);
  return (
    <span
      aria-label={title}
      title={title}
      className="inline-block w-[7px] h-[7px] rounded-full flex-shrink-0"
      style={{ backgroundColor: color }}
    />
  );
}

function EvidenceDeadlineBanner({
  state,
  date,
}: {
  state: EvidenceDeadlineState;
  date: Date | null;
}) {
  if (state === "none" || !date) return null;
  const isOverdue = state === "overdue";
  const message = evidenceDeadlineMessage(state, date);
  const borderColor = isOverdue ? "#DC2626" : "#E18308";
  const bgColor = isOverdue ? "#FEE2E2" : "#FFECD3";
  return (
    <div
      className="flex flex-row items-center gap-2 p-3 rounded-[10px] border border-dashed"
      style={{ borderColor, backgroundColor: bgColor }}
    >
      <span
        className="inline-block w-[8px] h-[8px] rounded-full flex-shrink-0"
        style={{ backgroundColor: borderColor }}
      />
      <p className="text-xs font-medium text-[#222222]" style={{ lineHeight: "18px" }}>
        {message}
      </p>
    </div>
  );
}

function NoticeLastDateTooltip() {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="relative inline-flex items-center ml-1"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is this date calculated?"
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-3.5 h-3.5 text-[#6D6D6D] hover:text-[#222] focus:outline-none"
      >
        <HiOutlineInformationCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[230px] rounded-[8px] border border-[#EEEEEE] bg-white p-2.5 shadow-lg"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-[11px] leading-4 text-[#6D6D6D]">
            Auto-calculated as Date of Notice of Counter Statement + 2 months.
          </p>
        </div>
      )}
    </span>
  );
}

function EvidenceLastDateTooltip() {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="relative inline-flex items-center ml-1"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is this date calculated?"
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-3.5 h-3.5 text-[#6D6D6D] hover:text-[#222] focus:outline-none"
      >
        <HiOutlineInformationCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[260px] rounded-[8px] border border-[#EEEEEE] bg-white p-2.5 shadow-lg"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-[11px] leading-4 text-[#6D6D6D]">
            Auto-calculated as Date of Receipt of Evidence by Opponent (4y) + 2 months.
          </p>
        </div>
      )}
    </span>
  );
}

function RejoinderLastDateTooltip() {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="relative inline-flex items-center ml-1"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is this date calculated?"
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-3.5 h-3.5 text-[#6D6D6D] hover:text-[#222] focus:outline-none"
      >
        <HiOutlineInformationCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[270px] rounded-[8px] border border-[#EEEEEE] bg-white p-2.5 shadow-lg"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-[11px] leading-4 text-[#6D6D6D]">
            Auto-calculated as Date of Filing Evidence Affidavit (4zt) + 60 days. You can edit this if needed.
          </p>
        </div>
      )}
    </span>
  );
}

function computeRejoinderLastDate(filingYmd: string): Date | null {
  const parts = parseDateParts(filingYmd);
  if (!parts) return null;
  const d = partsToDate(parts);
  d.setDate(d.getDate() + 60);
  return d;
}

function NoticeOfCounterFields({
  rows,
  draft,
  lastDateDraft,
  selectedTmNo,
  onChange,
  onLastDateChange,
}: {
  rows: NoticeOfCounterRow[];
  draft: Record<string, string>;
  lastDateDraft: Record<string, string>;
  selectedTmNo: string | null;
  onChange: (tmNo: string, val: string) => void;
  onLastDateChange: (tmNo: string, val: string) => void;
}) {
  if (!selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">Select a TM application first.</p>
    );
  }
  const noticeYmd = draft[selectedTmNo] ?? "";
  const noticeParts = parseDateParts(noticeYmd);
  // Auto-calculate from notice date; user override stored in lastDateDraft
  const autoCalcLastDate = noticeYmd
    ? computeNoticeCounterLastDate(noticeYmd)
    : null;
  const lastDateYmd = lastDateDraft[selectedTmNo] ?? "";
  const lastDateParts = parseDateParts(lastDateYmd);
  const lastDateValue = lastDateParts
    ? partsToDate(lastDateParts)
    : autoCalcLastDate;
  return (
    <div className="flex flex-col gap-5" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label
            className="text-xs font-normal text-[#222222] leading-5"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Date of Notice of Counter Statement
            <RequiredMark />
          </label>
          <DateTimePicker
            value={noticeParts ? partsToDate(noticeParts) : null}
            onChange={(d) =>
              onChange(selectedTmNo, d ? toLocalYmd(d) : "")
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label
            className="text-xs font-normal text-[#222222] leading-5 flex items-center"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Last Date for Evidence by Opponent
            <NoticeLastDateTooltip />
          </label>
          <DateTimePicker
            value={lastDateValue}
            onChange={(d) =>
              onLastDateChange(selectedTmNo, d ? toLocalYmd(d) : "")
            }
            placeholder={autoCalcLastDate ? autoCalcLastDate.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "Auto-calculated from Notice Date"}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inform Client about Notice of Counter — message preview
// ---------------------------------------------------------------------------

function InformNoticeOfCounterMessagePreview({
  preview,
}: {
  preview: InformClientNoticeOfCounterPreview | null;
}) {
  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-base font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Message Preview
      </span>
      <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[120px] bg-white">
        {preview?.message ? (
          <p
            className="text-xs text-[#222222] whitespace-pre-line"
            style={{ lineHeight: "20px" }}
          >
            {renderWhatsAppFormatting(preview.message)}
          </p>
        ) : (
          <p
            className="text-xs text-[#222222] whitespace-pre-line"
            style={{ lineHeight: "20px" }}
          >
            A &quot;Trademark Update&quot; message will be sent to the client
            informing them that the Trademarks Registry has served a copy of
            the filed Counter Statement to the opponent. The opponent now has
            to file their Evidence Affidavit before the Last Date for Evidence
            by Opponent.
            {"\n\n"}
            <span className="text-[#6D6D6D]">
              The exact message body is generated server-side from the lead
              and opposition data; click Send &amp; Continue to dispatch via
              WhatsApp.
            </span>
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status details for Evidence by Opponent — sub-components
// ---------------------------------------------------------------------------

function EvidenceByOpponentFields({
  status,
  draft,
  selectedTmNo,
  onPatch,
  onPreview,
  onRemove,
}: {
  status: EvidenceByOpponentStatus | null;
  draft: Record<
    string,
    {
      opponentEvidenceFiled: boolean | null;
      dateOfEvidenceByOpponent: string;
      dateOfReceiptOfEvidenceByOpponent: string;
      lastDateOfFilingEvidence: string;
      pendingFile: File | null;
    }
  >;
  selectedTmNo: string | null;
  onPatch: (
    tmNo: string,
    patch: Partial<{
      opponentEvidenceFiled: boolean | null;
      dateOfEvidenceByOpponent: string;
      dateOfReceiptOfEvidenceByOpponent: string;
      lastDateOfFilingEvidence: string;
      pendingFile: File | null;
    }>
  ) => void;
  onPreview: (
    app: EvidenceByOpponentStatus["applications"][number]
  ) => void;
  onRemove: (
    app: EvidenceByOpponentStatus["applications"][number]
  ) => void;
}) {
  if (!status || !selectedTmNo) {
    return <p className="text-sm text-[#6D6D6D]">Loading Evidence by Opponent…</p>;
  }
  const app = (status.applications ?? []).find((a) => a.tmNo === selectedTmNo);
  const d = draft[selectedTmNo];
  if (!app || !d) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Evidence by Opponent is not available for this TM yet.
      </p>
    );
  }

  const evidenceParts = parseDateParts(d.dateOfEvidenceByOpponent);
  const receiptParts = parseDateParts(d.dateOfReceiptOfEvidenceByOpponent);
  // Auto-derive 4z: exactly 2 months from 4y (Date of Receipt of Evidence by Opponent)
  const derivedLast = (() => {
    if (d.lastDateOfFilingEvidence) return d.lastDateOfFilingEvidence;
    if (!d.dateOfReceiptOfEvidenceByOpponent) return "";
    const ymd = addMonthsToYmd(d.dateOfReceiptOfEvidenceByOpponent, 2);
    return ymd ?? "";
  })();
  const lastFilingParts = parseDateParts(derivedLast);
  const lastForEvidenceParts = parseDateParts(app.lastDateForEvidenceByOpponent);

  const evidenceFiled = d.opponentEvidenceFiled === true;

  return (
    <div className="flex flex-col gap-5" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="flex flex-col gap-2">
        <label
          className="text-xs font-normal text-[#222222] leading-5"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          Has the Opponent filed Evidence Affidavit?
          <RequiredMark />
        </label>
        <div className="flex flex-row gap-3">
          {[
            { id: true, label: "YES" },
            { id: false, label: "NO" },
          ].map((opt) => {
            const isActive = d.opponentEvidenceFiled === opt.id;
            return (
              <button
                key={String(opt.id)}
                type="button"
                onClick={() =>
                  onPatch(selectedTmNo, { opponentEvidenceFiled: opt.id })
                }
                className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-[6px] h-9 min-w-[140px] text-xs transition-colors ${
                  isActive
                    ? "bg-[#222222] text-white border border-[#222222]"
                    : "bg-[#FCFCFC] text-[#6D6D6D] border border-[#EEEEEE]"
                }`}
              >
                <CheckboxBox checked={isActive} />
                <span className="text-left">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Evidence by Opponent
            {evidenceFiled && <RequiredMark />}
          </label>
          <DateTimePicker
            value={evidenceParts ? partsToDate(evidenceParts) : null}
            onChange={(dt) =>
              onPatch(selectedTmNo, {
                dateOfEvidenceByOpponent: dt ? toLocalYmd(dt) : "",
              })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Receipt of Evidence by Opponent
            {evidenceFiled && <RequiredMark />}
          </label>
          <DateTimePicker
            value={receiptParts ? partsToDate(receiptParts) : null}
            onChange={(dt) =>
              onPatch(selectedTmNo, {
                dateOfReceiptOfEvidenceByOpponent: dt ? toLocalYmd(dt) : "",
                lastDateOfFilingEvidence: "",
              })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5 flex items-center">
            Last date of filing Evidence
            <EvidenceLastDateTooltip />
          </label>
          <DateTimePicker
            value={lastFilingParts ? partsToDate(lastFilingParts) : null}
            onChange={(dt) =>
              onPatch(selectedTmNo, {
                lastDateOfFilingEvidence: dt ? toLocalYmd(dt) : "",
              })
            }
            placeholder="Select Date"
          />
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Last Date for Evidence by Opponent
          </label>
          <div
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#6D6D6D] border border-[#EEEEEE] rounded-[6px] bg-[#FCFCFC] flex items-center"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
            title="Fetched from Notice of Counter (4w)"
          >
            {lastForEvidenceParts
              ? partsToDate(lastForEvidenceParts).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "Auto-calculated from Notice of Counter"}
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="Acknowledgement No."
          value={status.acknowledgementNo ?? "—"}
        />
        <div className="hidden md:block flex-1" />
      </div>

      <EvidenceByOpponentUploadZone
        app={app}
        pendingFile={d.pendingFile}
        onPick={(file) =>
          onPatch(selectedTmNo, { pendingFile: file })
        }
        onPreview={() => onPreview(app)}
        onRemove={() => onRemove(app)}
      />
    </div>
  );
}

function EvidenceByOpponentUploadZone({
  app,
  pendingFile,
  onPick,
  onPreview,
  onRemove,
}: {
  app: EvidenceByOpponentStatus["applications"][number];
  pendingFile: File | null;
  onPick: (file: File) => void;
  onPreview: () => void;
  onRemove: () => void;
}) {
  const inputId = `evidence-by-opp-input-${app.tmNo}`;
  const hasFile = !!(pendingFile || app.evidenceByOpponentFileKey);
  const fileName =
    pendingFile?.name ?? app.evidenceByOpponentFilename ?? "File selected";
  const [isDragging, setIsDragging] = useState(false);

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reason = validateUploadFile(file);
    if (reason) {
      toast.error(reason);
      return;
    }
    onPick(file);
    e.target.value = "";
  };

  return (
    <div className="flex flex-col gap-3 border border-[#EEEEEE] rounded-[10px] p-4 md:p-5">
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-semibold text-[#101111]">
          Evidence by Opponent PDF
        </p>
        <p className="text-xs text-[#7D7D7D]">
          Upload the Evidence Affidavit filed by the Opponent for this TM No.
        </p>
      </div>

      <div
        className={`flex flex-col md:flex-row items-stretch md:items-center gap-4 rounded-2xl border border-dashed px-4 py-4 cursor-pointer transition-colors ${
          isDragging
            ? "border-[#222222] bg-[#F5F5F5]"
            : hasFile
            ? "border-[#22C55E] bg-[#F0FDF4]"
            : "border-[#D8DAEB] bg-white"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const file = e.dataTransfer.files[0];
          if (!file) return;
          const reason = validateUploadFile(file);
          if (reason) {
            toast.error(reason);
            return;
          }
          onPick(file);
        }}
        onClick={() => document.getElementById(inputId)?.click()}
      >
        <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
          <img
            src={uploadDropzoneGraphic}
            alt=""
            width={60}
            height={60}
            className="flex-shrink-0"
          />

          <div className="flex flex-col gap-1 min-w-0 flex-1">
            {hasFile ? (
              <p className="text-sm font-medium text-[#16A34A] truncate">
                {fileName}
              </p>
            ) : (
              <>
                <p className="text-base font-medium text-[#101111]">
                  Drag and drop your file
                </p>
                <p className="text-sm text-[#7D7D7D]">
                  {SUPPORTED_FORMATS_LABEL}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-row items-center gap-2 flex-shrink-0 self-end md:self-center">
          {!hasFile && (
            <button
              type="button"
              className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-sm font-medium text-[#2E3037] hover:bg-[#F5F5F5] transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                document.getElementById(inputId)?.click();
              }}
            >
              Select File
            </button>
          )}
          {hasFile && (
            <>
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview();
                }}
              >
                Preview
              </button>
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                Remove
              </button>
            </>
          )}
        </div>
      </div>

      <input
        id={inputId}
        type="file"
        accept={ALLOWED_FILE_ACCEPT}
        className="hidden"
        onChange={handlePick}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notice to Client for Evidence by Opponent — sub-components
// ---------------------------------------------------------------------------

function EvidenceByOppPdfRow({
  app,
  onPreview,
}: {
  app: EvidenceByOpponentStatus["applications"][number];
  onPreview: () => void;
}) {
  const filename = app.evidenceByOpponentFilename ?? "Evidence by Opponent";
  const label = app.mark || filename;
  return (
    <div
      className="flex flex-row items-center gap-3 px-4 py-1 rounded-[6px] bg-[#FCFCFC] border border-[#EEEEEE] h-11"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <img
        src={pdfIcon}
        alt=""
        width={22}
        height={22}
        className="flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <span className="text-xs font-medium text-[#222222] truncate block">
          {label}
        </span>
      </div>
      <button
        type="button"
        onClick={onPreview}
        className="px-4 h-9 rounded-[6px] bg-[#222222] text-white text-xs font-medium hover:bg-[#333333]"
      >
        Preview
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inform Client about Evidence by Opponent — message preview
// ---------------------------------------------------------------------------

function InformEvidenceByOpponentMessagePreview({
  preview,
}: {
  preview: InformClientEvidenceByOpponentPreview | null;
}) {
  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-base font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Message Preview
      </span>
      <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[120px] bg-white">
        {preview?.message ? (
          <p
            className="text-xs text-[#222222] whitespace-pre-line"
            style={{ lineHeight: "20px" }}
          >
            {renderWhatsAppFormatting(preview.message)}
          </p>
        ) : (
          <p
            className="text-xs text-[#222222] whitespace-pre-line"
            style={{ lineHeight: "20px" }}
          >
            A &quot;Trademark Update&quot; message will be sent to the client
            informing them whether the Opponent has filed Evidence Affidavit
            against the Counter Statement, and the next required action.
            {"\n\n"}
            <span className="text-[#6D6D6D]">
              The exact message body is generated server-side from the
              YES/NO selection saved in Status details for Evidence by
              Opponent. Click Send &amp; Continue to dispatch via WhatsApp.
            </span>
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Call Client for Evidence — panel
// ---------------------------------------------------------------------------

function CallClientForEvidencePanel(props: {
  recorded: boolean;
  recording: boolean;
  onRecord: () => void;
}) {
  return (
    <CallClientPanel
      {...props}
      title="Call the client to explain the objection and the need for a reply. No data entry required for this step."
    />
  );
}

function CallClientPanel({
  title,
  recorded,
  recording,
  onRecord,
}: {
  title: string;
  recorded: boolean;
  recording: boolean;
  onRecord: () => void;
}) {
  return (
    <div style={{ fontFamily: "Inter, sans-serif" }}>
      <div
        className="flex flex-row items-center gap-4 p-4 rounded-xl"
        style={{
          background: "#FFF8EE",
          border: "1.5px dashed #F5A623",
          borderRadius: "12px",
        }}
      >
        <img
          src={legalAdviceHeadset}
          alt=""
          className="w-[60px] h-[60px] flex-shrink-0"
        />

        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <p
            className="text-sm font-normal text-[#222222]"
            style={{ lineHeight: "20px" }}
          >
            {title}
          </p>

          {recorded ? (
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <circle cx="8" cy="8" r="7.5" fill="#222222" />
                <path
                  d="M5.33325 8.00008L7.33325 10.0001L10.6666 6.00008"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className="text-xs font-semibold text-[#222222]"
                style={{ lineHeight: "20px" }}
              >
                Call Completed
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={onRecord}
              disabled={recording}
              className="self-start h-8 px-4 text-xs font-medium text-[#222222] bg-white border border-[#D1D5DB] rounded-md hover:bg-[#F9FAFB] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              style={{ lineHeight: "20px" }}
            >
              {recording ? "Recording…" : "Call Completed"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirmation from Client for Evidence — sub-components
// ---------------------------------------------------------------------------

function EvidenceConfirmationFields({
  status,
  draft,
  selectedTmNo,
  noOfApps,
  onPick,
  onNoOfAppsChange,
}: {
  status: EvidenceConfirmationStatus | null;
  draft: Record<string, boolean | null>;
  selectedTmNo: string | null;
  noOfApps: string;
  onPick: (tmNo: string, value: boolean) => void;
  onNoOfAppsChange: (value: string) => void;
}) {
  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Confirmation from Client for Evidence…
      </p>
    );
  }
  const value = draft[selectedTmNo];
  const isNoForSelected = value === false;
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-row gap-3">
        {[
          { id: true, label: "YES" },
          { id: false, label: "NO" },
        ].map((opt) => {
          const isActive = value === opt.id;
          return (
            <button
              key={String(opt.id)}
              type="button"
              onClick={() => onPick(selectedTmNo, opt.id)}
              className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-[6px] h-9 min-w-[230px] text-xs transition-colors ${
                isActive
                  ? "bg-[#222222] text-white border border-[#222222]"
                  : "bg-[#FCFCFC] text-[#6D6D6D] border border-[#EEEEEE]"
              }`}
            >
              <CheckboxBox checked={isActive} />
              <span className="text-left">{opt.label}</span>
            </button>
          );
        })}
      </div>

      {isNoForSelected && status.suggestedNextStepWhenNo && (
        <div
          className="rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2 text-xs text-[#6D6D6D] leading-5"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          Next step: {status.suggestedNextStepWhenNo}
        </div>
      )}

      {!isNoForSelected && (
        <div className="flex flex-col md:flex-row gap-4 md:gap-5">
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <label
              htmlFor="evidence-confirmation-no-of-applications"
              className="text-xs font-normal text-[#222222] leading-5"
            >
              No. of applications for Evidence
            </label>
            <input
              id="evidence-confirmation-no-of-applications"
              type="number"
              min={0}
              step={1}
              placeholder={String(
                status.noOfApplicationsForCounter ??
                  status.noOfApplicationsOpposed ??
                  "—"
              )}
              value={noOfApps}
              onChange={(e) => onNoOfAppsChange(e.target.value)}
              className="w-full h-9 px-2.5 py-2 text-xs font-normal border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]"
            />
          </div>
          <div className="hidden md:block flex-1" />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invoice to Client for Evidence — sub-components
// ---------------------------------------------------------------------------

function EvidenceInvoiceAmountTooltip({
  defaultAmount,
}: {
  defaultAmount: number;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is the invoice amount calculated?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 text-[#6D6D6D] hover:text-[#222] focus:outline-none focus:ring-1 focus:ring-[#222] rounded-full"
      >
        <HiOutlineInformationCircle className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[280px] rounded-[8px] border border-[#EEEEEE] bg-white p-3 shadow-lg"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-[11px] font-semibold leading-4 text-[#222]">
            How the invoice amount is calculated
          </p>
          <p className="mt-1 text-[11px] leading-4 text-[#6D6D6D]">
            Professional charges for filing the Evidence Affidavit. You can
            override the value if needed; the final amount on the summary bar
            applies the discount.
          </p>
          <div className="mt-2 flex flex-col gap-1 text-[11px] leading-4 text-[#222]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Professional charges</span>
              <span className="font-medium">
                ₹{defaultAmount.toLocaleString("en-IN")}/-
              </span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-[#EEEEEE] pt-1.5">
              <span className="font-semibold text-[#222]">Auto-calculated</span>
              <span className="font-semibold text-[#222]">
                ₹{defaultAmount.toLocaleString("en-IN")}/-
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EvidenceInvoiceFields({
  leadId,
  status,
  preview,
  rows,
  selectedTmNo,
  generating,
  downloading,
  onPatchRow,
  onGeneratePdf,
  onDownloadPdf,
  onPreparePdf,
}: {
  leadId: string | null | undefined;
  status: EvidenceInvoiceStatus | null;
  preview: EvidenceInvoicePreview | null;
  rows: Record<
    string,
    {
      invoiceAmount: string;
      discount: string;
      invoiceDate: string;
      includeInvoiceNo: boolean;
    }
  >;
  selectedTmNo: string | null;
  generating: boolean;
  downloading: boolean;
  onPatchRow: (
    tmNo: string,
    patch: Partial<{
      invoiceAmount: string;
      discount: string;
      invoiceDate: string;
      includeInvoiceNo: boolean;
    }>
  ) => void;
  onGeneratePdf: () => void;
  onDownloadPdf: () => void;
  onPreparePdf: () => Promise<{ url: string; filename: string } | null>;
}) {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<
    "details" | "pdf" | "message" | "email"
  >("details");
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);
  const [preparingPreview, setPreparingPreview] = useState(false);
  const [whatsappPreview, setWhatsappPreview] = useState<{
    pdfMessage: string;
    paymentMessage: string;
    emailPreview: {
      to: string | null;
      subject: string;
      body: string;
      filename: string | null;
      downloadUrl: string | null;
    };
  } | null>(null);
  const [whatsappPreviewLoading, setWhatsappPreviewLoading] = useState(false);

  if (!status || !selectedTmNo) {
    return <p className="text-sm text-[#6D6D6D]">Loading invoice…</p>;
  }
  const tmRow = (status.tmInvoiceRows ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const draft = rows[selectedTmNo];
  if (!tmRow || !draft) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Selected TM is not part of the Evidence invoice (confirm it as YES
        first in 4A(xvii)).
      </p>
    );
  }
  const previewApp = preview?.applications.find(
    (a) => a.tmNo === selectedTmNo
  );
  const dateParts = parseDateParts(draft.invoiceDate);

  // Live summary mirrors backend: invoice amount minus discount, never < 0.
  const draftInvoiceAmount = Number(draft.invoiceAmount) || 0;
  const draftDiscount = Number(draft.discount) || 0;
  const liveInvoiceTotal = Math.max(0, draftInvoiceAmount - draftDiscount);

  const labelClass = "text-[12px] font-normal leading-5 text-[#222222]";
  const inputClass =
    "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";

  return (
    <div
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Editable: Date of Invoice | Invoice Amount | Discount */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Date of Invoice for Evidence</label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) =>
              onPatchRow(selectedTmNo, {
                invoiceDate: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass}>
            <span className="flex items-center gap-1">
              Invoice Amount for Evidence
              <EvidenceInvoiceAmountTooltip
                defaultAmount={tmRow.defaultInvoiceAmount}
              />
              <RequiredMark />
            </span>
          </label>
          <input
            type="number"
            min={0}
            value={draft.invoiceAmount}
            onChange={(e) =>
              onPatchRow(selectedTmNo, { invoiceAmount: e.target.value })
            }
            className={inputClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Discount (Rs.)</label>
          <input
            type="number"
            min={0}
            value={draft.discount}
            onChange={(e) =>
              onPatchRow(selectedTmNo, { discount: e.target.value })
            }
            className={inputClass}
          />
        </div>
      </div>

      {/* Invoice No. (read-only, full context lives in Preview → Details) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <ReadOnlyField
          label="Invoice No. for filing Evidence"
          value={tmRow.individualInvoiceNo ?? status.invoiceNo ?? "—"}
        />
      </div>

      {selectedTmNo && (
        <IncludeInvoiceNoCheckbox
          id="evidence-invoice-include-invoice-no"
          checked={Boolean(draft.includeInvoiceNo)}
          onChange={(checked) =>
            onPatchRow(selectedTmNo, { includeInvoiceNo: checked })
          }
        />
      )}

      {/* Invoice Amount summary bar */}
      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 rounded-[8px] px-4 sm:px-5 py-4 sm:py-0 min-h-[72px] sm:h-[72px]"
        style={{ background: "#222222" }}
      >
        <div className="flex flex-row items-center gap-2 min-w-0">
          <img
            src={invoiceAmountCheck}
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span className="text-[12px] md:text-sm font-normal leading-5 text-white/90 truncate">
            INVOICE AMOUNT FOR EVIDENCE
          </span>
        </div>
        <span className="text-lg md:text-[22px] font-bold leading-8 md:leading-10 text-white shrink-0">
          Rs. {liveInvoiceTotal.toLocaleString("en-IN")}/-
        </span>
      </div>

      {/* Generate PDF + Download + Preview */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
          <img
            src={invoiceGenerate}
            alt=""
            className="w-[22px] h-[22px] flex-shrink-0"
          />
          <button
            type="button"
            onClick={onGeneratePdf}
            disabled={generating}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] md:text-xs font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          >
            {generating
              ? "Generating…"
              : status.evidenceInvoicePdfKey
                ? "Regenerate PDF"
                : "Generate PDF"}
          </button>
        </div>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={downloading || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
        >
          {downloading ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={async () => {
            if (preparingPreview || generating) return;
            setPreparingPreview(true);
            try {
              await onPreparePdf();
            } finally {
              setPreparingPreview(false);
            }
            setPreviewTab("details");
            setPreviewNumPages(null);
            setPreviewModalOpen(true);
            // Fetch WhatsApp preview non-blocking
            if (!leadId) return;
            setWhatsappPreviewLoading(true);
            getEvidenceInvoiceWhatsAppPreview(leadId)
              .then((res) => setWhatsappPreview(res))
              .catch(() => {})
              .finally(() => setWhatsappPreviewLoading(false));
          }}
          disabled={preparingPreview || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
        >
          {preparingPreview ? "Preparing…" : "Preview"}
        </button>
      </div>

      {previewModalOpen && (
        <EvidenceInvoicePreviewModal
          previewTab={previewTab}
          onTabChange={setPreviewTab}
          previewNumPages={previewNumPages}
          onPreviewNumPagesChange={setPreviewNumPages}
          status={status}
          preview={preview}
          previewApp={previewApp}
          draft={draft}
          liveInvoiceTotal={liveInvoiceTotal}
          whatsappPreview={whatsappPreview}
          whatsappPreviewLoading={whatsappPreviewLoading}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewNumPages(null);
            setPreviewTab("details");
          }}
        />
      )}
    </div>
  );
}

function EvidenceInvoicePreviewModal({
  previewTab,
  onTabChange,
  previewNumPages,
  onPreviewNumPagesChange,
  status,
  preview,
  previewApp,
  draft,
  liveInvoiceTotal,
  whatsappPreview,
  whatsappPreviewLoading,
  onClose,
}: {
  previewTab: "details" | "pdf" | "message" | "email";
  onTabChange: (tab: "details" | "pdf" | "message" | "email") => void;
  previewNumPages: number | null;
  onPreviewNumPagesChange: (n: number | null) => void;
  status: EvidenceInvoiceStatus;
  preview: EvidenceInvoicePreview | null;
  // Payload row type — the hand-written subset omitted tmNo / applicationDate (items 39, 44).
  previewApp: EvidenceInvoicePreviewApplication | undefined;
  draft: { invoiceAmount: string; discount: string; invoiceDate: string };
  liveInvoiceTotal: number;
  whatsappPreview: {
    pdfMessage: string;
    paymentMessage: string;
    emailPreview: {
      to: string | null;
      subject: string;
      body: string;
      filename: string | null;
      downloadUrl: string | null;
    };
  } | null;
  whatsappPreviewLoading: boolean;
  onClose: () => void;
}) {
  const pdfUrl = status.evidenceInvoicePdfUrl ?? null;
  const pdfFilename = "Evidence Invoice.pdf";

  const tabs: Array<{
    id: "details" | "pdf" | "message" | "email";
    label: string;
    disabled?: boolean;
  }> = [
    { id: "details", label: "Details" },
    { id: "pdf", label: "Generated PDF", disabled: !pdfUrl },
    { id: "message", label: "WhatsApp Message" },
    { id: "email", label: "Email" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
          <span
            className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {pdfFilename}
          </span>
          <div className="flex items-center gap-3 shrink-0">
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#2E3037] underline underline-offset-2"
              >
                Open in new tab
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
              aria-label="Close preview"
            >
              <HiOutlineX className="size-5" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div
          className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
          role="tablist"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={previewTab === tab.id}
              disabled={tab.disabled}
              onClick={() => onTabChange(tab.id)}
              className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
                previewTab === tab.id
                  ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                  : "text-[#6d6d6d] hover:text-[#222]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4 flex justify-center">
          {previewTab === "details" ? (
            <div
              className="flex w-full max-w-3xl flex-col gap-3"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                  Invoice details
                </p>
                <EvidenceInvoiceDetailsPreview
                  status={status}
                  preview={preview}
                  previewApp={previewApp}
                  draft={draft}
                  liveInvoiceTotal={liveInvoiceTotal}
                />
              </section>
            </div>
          ) : previewTab === "pdf" ? (
            pdfUrl ? (
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages: n }) =>
                  onPreviewNumPagesChange(n)
                }
                loading={
                  <p className="text-sm text-[#6d6d6d]">Loading PDF…</p>
                }
                error={
                  <p className="text-sm text-red-600">
                    Failed to load PDF.
                  </p>
                }
                className="flex flex-col items-center gap-4"
              >
                {previewNumPages != null &&
                  Array.from({ length: previewNumPages }, (_, i) => (
                    <Page
                      key={i + 1}
                      pageNumber={i + 1}
                      width={Math.min(
                        800,
                        typeof window !== "undefined"
                          ? window.innerWidth - 48
                          : 800
                      )}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  ))}
              </Document>
            ) : (
              <p className="text-sm text-[#6D6D6D]">
                Generate the invoice PDF to preview it here.
              </p>
            )
          ) : previewTab === "message" ? (
            <div
              className="w-full max-w-2xl flex flex-col gap-4"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <div className="rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                  Payment message (sent via Send Payment Message)
                </p>
                <WhatsAppMessageBlock
                  message={whatsappPreview?.paymentMessage ?? ""}
                  loading={whatsappPreviewLoading}
                  emptyText="Message preview not available."
                />
              </div>
            </div>
          ) : (
            <div
              className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be emailed to the client with the invoice PDF
              </p>
              <EmailPreviewBlock
                preview={whatsappPreview?.emailPreview ?? null}
                loading={whatsappPreviewLoading}
                attachmentFallback={
                  pdfUrl ? { filename: pdfFilename, url: pdfUrl } : null
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EvidenceInvoiceDetailsPreview({
  status,
  preview,
  previewApp,
  draft,
  liveInvoiceTotal,
}: {
  status: EvidenceInvoiceStatus;
  preview: EvidenceInvoicePreview | null;
  // Take the payload row type directly — the hand-written subset omitted tmNo and applicationDate,
  // which spec pg 132 requires in this window (review items 39 and 44).
  previewApp: EvidenceInvoicePreviewApplication | undefined;
  draft: { invoiceAmount: string; discount: string; invoiceDate: string };
  liveInvoiceTotal: number;
}) {
  const formatRupees = (n: number) =>
    `Rs. ${Math.max(0, Math.round(n)).toLocaleString("en-IN")}/-`;
  const formatDateDisplay = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return y && m && d ? `${d}-${m}-${y}` : iso;
  };

  const draftInvoiceAmount = Number(draft.invoiceAmount) || 0;
  const draftDiscount = Number(draft.discount) || 0;
  const addresseeLines = (preview?.addressee || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  // Spec pg 132 renders the opponent as "Name (Place)". Falls back to whichever part exists so a
  // missing place doesn't leave dangling brackets.
  const opponentName = previewApp?.nameOfOpponent?.trim() ?? "";
  const opponentPlace = previewApp?.placeOfOpponent?.trim() ?? "";
  const opponentNameWithPlace = opponentName
    ? opponentPlace
      ? `${opponentName} (${opponentPlace})`
      : opponentName
    : opponentPlace;

  const Row = ({
    label,
    children,
    alignTop = false,
  }: {
    label: string;
    children: React.ReactNode;
    alignTop?: boolean;
  }) => {
    const align = alignTop ? "items-start" : "items-baseline";
    return (
      <>
        <div className={`flex ${align} font-medium text-[#222]`}>{label}</div>
        <div className={`flex ${align} text-[#222]`}>:</div>
        <div className="text-[#222] break-words min-w-0">{children}</div>
      </>
    );
  };

  return (
    <div
      className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <Row label="Addressee" alignTop>
        {addresseeLines.length > 0 ? (
          <div className="flex flex-col">
            {addresseeLines.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
          </div>
        ) : (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </Row>
      <Row label="Form of Address">
        {preview?.formOfAddress?.trim() || (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </Row>
      <Row label="Acknowledgement No.">
        {preview?.acknowledgementNo?.trim() || (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </Row>
      {/*
        Spec pg 132 ("Data for Invoice for Evidence") opens each application's block with Application
        Number (TM No.) then Date of Filing, then Mark (Type), Class and Opponent as "Name (Place)".
        TM No. was absent entirely — that is review item 44 ("In preview invoice – application number
        is missing"), filed under 4A(xxv) but describing this window.
      */}
      <Row label="Application Number (TM No.)">
        {previewApp?.tmNo ? (
          formatTmNoForDisplay(previewApp.tmNo)
        ) : (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </Row>
      <Row label="Date of Filing">
        {previewApp?.applicationDate || preview?.dateOfFiling || (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </Row>
      <Row label="Mark">
        {previewApp?.mark?.trim() || <span className="text-[#6D6D6D]">—</span>}
      </Row>
      <Row label="Selected Class">
        {previewApp?.classNumber != null ? (
          String(previewApp.classNumber)
        ) : (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </Row>
      <Row label="Selected Type of Mark">
        {previewApp?.typeOfMark?.trim() || (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </Row>
      {/* Spec pg 132 states the opponent as one line: "Opponent : Name (Place)". */}
      <Row label="Opponent">
        {opponentNameWithPlace || <span className="text-[#6D6D6D]">—</span>}
      </Row>
      <Row label="No. of applications for Evidence">
        {String(status.noOfApps ?? 0)}
      </Row>
      {/* Both missing before (review item 39): spec pg 132 lists them between the application
          block and the invoice amount. */}
      <Row label="Last date for filing Evidence">
        {preview?.lastDateOfFilingEvidence || (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </Row>
      <Row label="Professional Charges for filing Evidence">
        {formatRupees(
          preview?.professionalCharges ?? status.professionalCharges ?? 0
        )}
      </Row>
      <Row label="Invoice Amount for Evidence">
        {formatRupees(draftInvoiceAmount)}
      </Row>
      {draftDiscount > 0 && (
        <Row label="− Discount">{formatRupees(draftDiscount)}</Row>
      )}
      <Row label="Net Invoice Amount">{formatRupees(liveInvoiceTotal)}</Row>
      <Row label="Invoice No. for filing Evidence">
        {status.invoiceNo?.trim() || (
          <span className="text-[#6D6D6D]">Auto-generated on save</span>
        )}
      </Row>
      <Row label="Date of Invoice for Evidence">
        {formatDateDisplay(draft.invoiceDate)}
      </Row>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payment from Client for Evidence — sub-components
// ---------------------------------------------------------------------------

function EvidencePaymentFields({
  status,
  rows,
  selectedTmNo,
  recording,
  onChange,
  onRecordPayment,
  onSendPaymentMessage,
  onPreviewInvoice,
}: {
  status: EvidenceInvoiceStatus | null;
  rows: Record<string, string>;
  selectedTmNo: string | null;
  recording: boolean;
  onChange: (tmNo: string, val: string) => void;
  onRecordPayment: () => void;
  onSendPaymentMessage: () => void;
  onPreviewInvoice: () => void;
}) {
  if (!status || !selectedTmNo) {
    return <p className="text-sm text-[#6D6D6D]">Loading payment status…</p>;
  }
  const tmRow = (status.tmInvoiceRows ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const total = tmRow ? tmRow.invoiceAmount - (tmRow.discount ?? 0) : 0;
  const savedCollected = Number(tmRow?.collectedAmount ?? 0) || 0;
  const collected = Number(rows[selectedTmNo] ?? savedCollected) || 0;
  const outstanding = Math.max(total - collected, 0);
  const outstandingFormatted = outstanding.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return (
    <div
      className="flex flex-col items-start gap-[22px] w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col sm:flex-row items-start gap-5 w-full">
        <CurrencyAmountField
          label="Invoice Amount for Evidence"
          value={formatAmount(total)}
          onChange={() => {}}
          readOnly
        />
        <div
          className="flex flex-col gap-1"
          style={{ flex: "1 1 0", minWidth: 0 }}
        >
          <CurrencyAmountField
            label="Collected Amount for Evidence"
            value={rows[selectedTmNo] ?? ""}
            onChange={(v) => onChange(selectedTmNo, v)}
          />
          {savedCollected > 0 && (
            <span className="text-[11px] leading-4 text-[#6D6D6D]">
              Already collected: Rs.{" "}
              {savedCollected.toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
              /-
            </span>
          )}
        </div>
      </div>

      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 w-full px-4 sm:px-5 py-4 sm:py-0 min-h-[60px] sm:h-[60px]"
        style={{ background: "#222222", borderRadius: "8px" }}
      >
        <span className="text-[12px] sm:text-[14px] font-normal leading-5 text-white/90">
          OUTSTANDING AMOUNT FOR EVIDENCE
        </span>
        <span className="text-[18px] sm:text-[22px] font-bold leading-8 sm:leading-10 text-white">
          Rs. {outstandingFormatted}/-
        </span>
      </div>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Stamp Paper details to Vendor for Evidence — sub-components
// ---------------------------------------------------------------------------

const STAMP_EVIDENCE_APPLICANT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "SOLE_PROPRIETORSHIP", label: "Sole Proprietorship" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP_FIRM", label: "Partnership Firm" },
  { value: "SOCIETY", label: "Society" },
  { value: "PRIVATE_LIMITED_COMPANY", label: "Private Limited Company" },
  { value: "LIMITED_LIABILITY_PARTNERSHIP", label: "Limited Liability Partnership" },
  { value: "OTHERS", label: "Others" },
];

function formatStampEvidenceApplicantType(value: string | null | undefined): string {
  if (!value) return "";
  const found = STAMP_EVIDENCE_APPLICANT_TYPE_OPTIONS.find((o) => o.value === value);
  return found?.label ?? value;
}

function StampPaperForEvidenceFields({
  status,
  draft,
  selectedTmNo,
  stampVendors,
  onPatch,
}: {
  status: StampPaperForEvidenceStatus | null;
  draft: Record<
    string,
    {
      stampPapersForEvidence: string;
      stampVendorNameForEvidence: string;
      stampVendorPhoneForEvidence: string;
    }
  >;
  selectedTmNo: string | null;
  stampVendors: StampVendorOption[];
  onPatch: (
    tmNo: string,
    patch: Partial<{
      stampPapersForEvidence: string;
      stampVendorNameForEvidence: string;
      stampVendorPhoneForEvidence: string;
    }>
  ) => void;
}) {
  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Stamp Paper details to Vendor for Evidence…
      </p>
    );
  }
  const d = draft[selectedTmNo];
  if (!d) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Selected TM is not part of this opposition (no row to fill).
      </p>
    );
  }
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-1">
        <label className="text-xs font-normal text-[#222222] leading-5">
          Number of Stamp Papers needed for Evidence
          <RequiredMark />
        </label>
        <input
          type="number"
          min={1}
          value={d.stampPapersForEvidence}
          onChange={(e) =>
            onPatch(selectedTmNo, { stampPapersForEvidence: e.target.value })
          }
          placeholder="Text Here"
          className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
          style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
        />
      </div>

      {stampVendors.length > 0 && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Quick select vendor (optional)
          </label>
          <Select
            value={
              d.stampVendorNameForEvidence &&
              stampVendors.some((v) => v.name === d.stampVendorNameForEvidence)
                ? d.stampVendorNameForEvidence
                : "__none__"
            }
            onValueChange={(value) => {
              if (value === "__none__") return;
              const vendor = stampVendors.find((v) => v.name === value);
              onPatch(selectedTmNo, {
                stampVendorNameForEvidence: value,
                stampVendorPhoneForEvidence: vendor?.phone ?? "",
              });
            }}
          >
            <SelectTrigger className="h-9 w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select to autofill" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Not from list —</SelectItem>
              {stampVendors
                .filter((v) => typeof v.name === "string" && v.name.trim().length > 0)
                .map((v) => (
                  <SelectItem key={v.name} value={v.name}>
                    {v.name}
                    {v.isDefault ? " (default)" : ""}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Name of Stamp Vendor for Evidence
            <RequiredMark />
          </label>
          <input
            type="text"
            value={d.stampVendorNameForEvidence}
            onChange={(e) =>
              onPatch(selectedTmNo, {
                stampVendorNameForEvidence: e.target.value,
              })
            }
            placeholder="Text Here"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Stamp Vendor Phone Number
            <RequiredMark />
          </label>
          <input
            type="tel"
            inputMode="tel"
            value={d.stampVendorPhoneForEvidence}
            onChange={(e) =>
              onPatch(selectedTmNo, {
                stampVendorPhoneForEvidence: e.target.value,
              })
            }
            placeholder="Phone Number"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
      </div>

      <FetchedDetailsPanel
        rows={[
          { label: "Applicant Type", value: formatStampEvidenceApplicantType(status.applicantType) },
          { label: "Applicant", value: status.applicant ?? "" },
          { label: "Applicant Address", value: status.applicantAddress ?? "" },
          { label: "Signatory's Name", value: status.signatoryName ?? "" },
          { label: "Signatory's Designation", value: status.signatoryDesignation ?? "" },
          { label: "User Date", value: status.userDate ?? "" },
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stamp Paper from Vendor for Evidence — sub-components
// ---------------------------------------------------------------------------

function StampVendorEvidenceAmountTooltip({
  stampPapersForEvidence,
  defaultAmount,
  currentValue,
}: {
  stampPapersForEvidence: number | null;
  defaultAmount: number | null;
  currentValue: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const count = stampPapersForEvidence ?? 0;
  const defAmt = defaultAmount ?? 0;
  const rate = count > 0 && defAmt > 0 ? Math.round(defAmt / count) : null;
  const isOverridden =
    currentValue.trim() !== "" && Number(currentValue) !== defAmt;

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is the amount calculated?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 text-[#6D6D6D] hover:text-[#222] focus:outline-none focus:ring-1 focus:ring-[#222] rounded-full"
      >
        <HiOutlineInformationCircle className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[280px] rounded-[8px] border border-[#EEEEEE] bg-white p-3 shadow-lg"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-[11px] font-semibold leading-4 text-[#222]">
            Calculation Breakdown
          </p>
          <div className="mt-2 flex flex-col gap-1 text-[11px] leading-4 text-[#222]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">
                Stamp Papers
                {rate != null ? ` (${count} × ₹${rate})` : ` (${count})`}
              </span>
              <span className="font-medium">
                ₹{defAmt.toLocaleString("en-IN")}/-
              </span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-[#EEEEEE] pt-1.5">
              <span className="font-semibold text-[#222]">Auto-calculated</span>
              <span className="font-semibold text-[#222]">
                ₹{defAmt.toLocaleString("en-IN")}/-
              </span>
            </div>
            {isOverridden && (
              <p className="mt-1.5 text-[10px] text-[#6D6D6D]">
                Override in use. Clear the field to revert to the
                auto-calculated amount.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StampPaperFromVendorForEvidenceFields({
  status,
  draft,
  selectedTmNo,
  onPatch,
}: {
  status: StampPaperForEvidenceStatus | null;
  draft: Record<
    string,
    {
      amountPayableToStampVendorForEvidence: string;
      paidToVendorForEvidence: boolean | null;
    }
  >;
  selectedTmNo: string | null;
  onPatch: (
    tmNo: string,
    patch: Partial<{
      amountPayableToStampVendorForEvidence: string;
      paidToVendorForEvidence: boolean | null;
    }>
  ) => void;
}) {
  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Stamp Paper from Vendor for Evidence…
      </p>
    );
  }
  const tmRow = (status.applications ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const d = draft[selectedTmNo];
  if (!tmRow || !d) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Save Stamp Paper details for this TM first.
      </p>
    );
  }
  const selectValue =
    d.paidToVendorForEvidence === true
      ? "yes"
      : d.paidToVendorForEvidence === false
      ? "no"
      : "__none__";
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-normal text-[#222222] leading-5">
              Amount Payable to Stamp Vendor for Evidence
            </label>
            <StampVendorEvidenceAmountTooltip
              stampPapersForEvidence={tmRow.stampPapersForEvidence}
              defaultAmount={tmRow.defaultAmountPayableToStampVendorForEvidence}
              currentValue={d.amountPayableToStampVendorForEvidence}
            />
          </div>
          <input
            type="number"
            min={0}
            value={d.amountPayableToStampVendorForEvidence}
            onChange={(e) =>
              onPatch(selectedTmNo, {
                amountPayableToStampVendorForEvidence: e.target.value,
              })
            }
            placeholder="Text Here"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Paid to Vendor
            <RequiredMark />
          </label>
          <Select
            value={selectValue}
            onValueChange={(v) =>
              onPatch(selectedTmNo, {
                paidToVendorForEvidence:
                  v === "yes" ? true : v === "no" ? false : null,
              })
            }
          >
            <SelectTrigger className="h-9 min-h-[36px] w-full border border-[#EEEEEE] rounded-[6px] px-2.5 py-2 text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Select an option</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
          {/* 4zi) spec pg 133: unpaid must read "Pending Payment". */}
          <PaymentStatusLine paid={d.paidToVendorForEvidence} />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="Number of Stamp Papers needed for Evidence"
          value={
            tmRow.stampPapersForEvidence != null
              ? String(tmRow.stampPapersForEvidence)
              : "—"
          }
        />
        <div className="hidden md:block flex-1" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Documents from Client for Evidence — sub-components
// ---------------------------------------------------------------------------

function DocumentsFromClientForEvidenceFields({
  status,
  draft,
  selectedTmNo,
  onChange,
}: {
  status: DocumentsFromClientForEvidenceStatus | null;
  draft: Record<string, string>;
  selectedTmNo: string | null;
  onChange: (tmNo: string, val: string) => void;
}) {
  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Documents from Client for Evidence…
      </p>
    );
  }
  const tmRow = (status.applications ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  if (!tmRow) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Selected TM is not on the Evidence invoice (confirm YES in 4A(xvii)
        and save the invoice first).
      </p>
    );
  }
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            No. of Exhibits for Evidence
            <RequiredMark />
          </label>
          <input
            type="number"
            min={0}
            value={draft[selectedTmNo] ?? ""}
            onChange={(e) => onChange(selectedTmNo, e.target.value)}
            placeholder="Text Here"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
        <div className="hidden md:block flex-1" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preparation of Evidence Affidavit — sub-components
// ---------------------------------------------------------------------------

type EvidenceAffidavitDraftType = {
  noOfParagraphsInOpponentIntro: string;
  paragraphWiseReplies: Array<{ heading: string; text: string }>;
  nameOfOpponentsAttorney: string;
  addressOfOpponentsAttorney: string;
  emailOfOpponentsAttorney: string;
  dateOfEvidenceAffidavit: string;
};

function EvidenceAffidavitFields({
  status,
  perTm,
  selectedTmNo,
  generating,
  downloading,
  onPatch,
  onGeneratePdf,
  onDownloadPdf,
  onPreviewPdf,
}: {
  status: EvidenceAffidavitStatus | null;
  perTm: Record<string, EvidenceAffidavitDraftType>;
  selectedTmNo: string | null;
  generating: boolean;
  downloading: boolean;
  onPatch: (
    tmNo: string,
    patch: Partial<EvidenceAffidavitDraftType>
  ) => void;
  onGeneratePdf: (app: EvidenceAffidavitItem) => void;
  onDownloadPdf: (app: EvidenceAffidavitItem) => void;
  onPreviewPdf: (app: EvidenceAffidavitItem) => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Review item 40 — the "view all the data" window required by spec pg 137.
  const [reviewOpen, setReviewOpen] = useState(false);

  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Preparation of Evidence Affidavit…
      </p>
    );
  }
  const app = (status.evidenceAffidavits ?? []).find(
    (a) => a.tmNo === selectedTmNo
  );
  const draft = perTm[selectedTmNo];
  if (!app || !draft) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Preparation of Evidence Affidavit is not available for this TM yet.
      </p>
    );
  }
  const dateParts = parseDateParts(draft.dateOfEvidenceAffidavit);
  // Only replies with content reach the review window, matching the Prepare Counter window.
  const reviewReplies = (draft.paragraphWiseReplies ?? []).filter(
    (p) => (p.heading ?? "").trim() || (p.text ?? "").trim()
  );

  const updateReply = (
    idx: number,
    patch: Partial<{ heading: string; text: string }>
  ) => {
    onPatch(selectedTmNo, {
      paragraphWiseReplies: draft.paragraphWiseReplies.map((p, i) =>
        i === idx ? { ...p, ...patch } : p
      ),
    });
  };
  const addReply = () =>
    onPatch(selectedTmNo, {
      paragraphWiseReplies: [
        ...draft.paragraphWiseReplies,
        { heading: "", text: "" },
      ],
    });
  const removeReply = (idx: number) =>
    onPatch(selectedTmNo, {
      paragraphWiseReplies: draft.paragraphWiseReplies.filter(
        (_, i) => i !== idx
      ),
    });

  const inputClass =
    "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";
  const textareaClass =
    "min-h-[80px] w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] resize-y";
  const labelClass = "text-[12px] font-normal leading-5 text-[#222222]";

  const detailRows = [
    { label: "Mark", value: app.mark ?? "—" },
    { label: "Selected Class", value: app.classWithGoodsServices ?? "—" },
    { label: "Applicant", value: app.applicant ?? "—" },
    { label: "Applicant Address", value: app.applicantAddress ?? "—" },
    { label: "Applicant Type", value: app.applicantType ?? "—" },
    { label: "User Date", value: app.userDate ?? "—" },
    { label: "Signatory Details", value: app.signatoryDetails ?? "—" },
    { label: "GS Type A", value: app.gsTypeA ?? "—" },
    { label: "GS Type B", value: app.gsTypeB ?? "—" },
    { label: "GS Type C", value: app.gsTypeC ?? "—" },
    // See note above — spec pg 125 lists Applicant Type only (review item 13).
    { label: "Opposition No.", value: app.oppositionNoWithDate ?? "—" },
    { label: "Name of Opponent", value: app.nameOfOpponent ?? "—" },
    { label: "Address of Opponent", value: app.addressOfOpponent ?? "—" },
    { label: "Opponent's Mark", value: app.opponentsMark ?? "—" },
    { label: "Class of Opponent's Mark", value: app.classOfOpponentsMark ?? "—" },
    { label: "Goods/Services of Opponent's Mark", value: app.goodsServicesOfOpponentsMark ?? "—" },
    { label: "User Date of Opponent's Mark", value: app.userDateOfOpponentsMark ?? "—" },
    {
      label: "No. of Exhibits for Evidence",
      value: app.noOfExhibitsForEvidence != null ? String(app.noOfExhibitsForEvidence) : "—",
    },
  ];

  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Fetched details — collapsible accordion */}
      <div className="w-full rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] overflow-hidden">
        <button
          type="button"
          onClick={() => setDetailsOpen((o) => !o)}
          className="flex w-full items-center justify-between px-3 py-2.5 text-[12px] font-medium text-[#222] hover:bg-[#F7F7F7] transition-colors touch-manipulation min-h-[44px] md:min-h-0"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <span>Fetched Details</span>
          <HiChevronDown
            className="w-4 h-4 text-[#6D6D6D] transition-transform duration-200 shrink-0"
            style={{ transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </button>
        {detailsOpen && (
          <div className="border-t border-[#EEEEEE] grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-3 py-2.5 text-[12px] leading-5">
            {detailRows.map((row) => {
              const isEmpty = !row.value || row.value === "—";
              return (
                <Fragment key={row.label}>
                  <div className="flex items-baseline font-medium text-[#222]">{row.label}</div>
                  <div className="flex items-baseline text-[#222]">:</div>
                  <div className="text-[#222] break-words min-w-0">
                    {isEmpty ? <span className="text-[#6D6D6D]">—</span> : row.value}
                  </div>
                </Fragment>
              );
            })}
          </div>
        )}
      </div>

      {/* Editable fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={labelClass}>No. of Paragraphs in Opponent&apos;s intro</label>
          <input
            type="number"
            min={0}
            value={draft.noOfParagraphsInOpponentIntro}
            onChange={(e) =>
              onPatch(selectedTmNo, {
                noOfParagraphsInOpponentIntro: e.target.value,
              })
            }
            placeholder="0"
            className={inputClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Date of Evidence Affidavit</label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) =>
              onPatch(selectedTmNo, {
                dateOfEvidenceAffidavit: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
            buttonClassName="!h-9 !min-h-0 !text-[12px] rounded-[6px]"
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <label className={labelClass}>Headings &amp; Text of Paragraph-wise Replies</label>
          <button
            type="button"
            onClick={addReply}
            className="h-8 px-3 text-[11px] font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50"
          >
            + Add Reply
          </button>
        </div>
        {draft.paragraphWiseReplies.map((reply, idx) => (
          <div
            key={idx}
            className="flex flex-col gap-2 rounded-[6px] border border-[#EEEEEE] p-3 bg-white"
          >
            <div className="flex items-start justify-between gap-2">
              <input
                type="text"
                value={reply.heading}
                onChange={(e) => updateReply(idx, { heading: e.target.value })}
                placeholder='e.g. "Paragraphs 1 to 5"'
                className={inputClass}
              />
              {draft.paragraphWiseReplies.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeReply(idx)}
                  className="h-9 px-3 text-[11px] font-medium text-[#D92D20] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] whitespace-nowrap"
                >
                  Remove
                </button>
              )}
            </div>
            <textarea
              value={reply.text}
              onChange={(e) => updateReply(idx, { text: e.target.value })}
              placeholder="Reply text…"
              rows={4}
              className={textareaClass}
            />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Name of Opponent&apos;s Attorney</label>
          <input
            type="text"
            value={draft.nameOfOpponentsAttorney}
            onChange={(e) =>
              onPatch(selectedTmNo, {
                nameOfOpponentsAttorney: e.target.value,
              })
            }
            placeholder="Text Here"
            className={inputClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Email of Opponent&apos;s Attorney</label>
          <input
            type="email"
            value={draft.emailOfOpponentsAttorney}
            onChange={(e) =>
              onPatch(selectedTmNo, {
                emailOfOpponentsAttorney: e.target.value,
              })
            }
            placeholder="attorney@example.com"
            className={inputClass}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label className={labelClass}>Address of Opponent&apos;s Attorney</label>
        <textarea
          value={draft.addressOfOpponentsAttorney}
          onChange={(e) =>
            onPatch(selectedTmNo, {
              addressOfOpponentsAttorney: e.target.value,
            })
          }
          placeholder="Text Here"
          rows={3}
          className={textareaClass}
        />
      </div>

      {app.imageOfOpponentsMarkUrl && (
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Image of Opponent&apos;s Mark</label>
          <div className="w-full min-h-[160px] rounded-[8px] border border-[#EEEEEE] bg-[#F7F7F7] flex items-center justify-center overflow-hidden">
            <img
              src={app.imageOfOpponentsMarkUrl}
              alt="Opponent's mark"
              className="max-h-[280px] object-contain"
            />
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
          <img src={invoiceGenerate} alt="" className="w-[22px] h-[22px] flex-shrink-0" />
          <button
            type="button"
            onClick={() => onGeneratePdf(app)}
            disabled={generating}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {generating
              ? "Generating..."
              : app.evidenceAffidavitPdfKey
                ? "Regenerate PDF"
                : "Generate Evidence Affidavit PDF"}
          </button>
        </div>
        {/*
          Review item 40: spec pg 137 wants "an option to view all the data like below in a window"
          before generating. The data existed only as the inline "Fetched Details" accordion, which
          isn't a review window and omits the entered replies and Date. This opens the same rows plus
          the paragraph-wise replies and Date, mirroring the sibling Prepare Counter window.
        */}
        <button
          type="button"
          onClick={() => setReviewOpen(true)}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0"
        >
          Preview
        </button>
        <button
          type="button"
          onClick={() => onPreviewPdf(app)}
          disabled={!app.evidenceAffidavitPdfKey || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Preview PDF
        </button>
        <button
          type="button"
          onClick={() => onDownloadPdf(app)}
          disabled={!app.evidenceAffidavitPdfKey || downloading || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {downloading ? "Downloading..." : "Download PDF"}
        </button>
      </div>

      <FilePreviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        name={`Evidence Affidavit Preview${app.tmNo ? ` — ${formatTmNoForDisplay(app.tmNo)}` : ""}`}
      >
        <div
          className="flex w-full max-w-3xl flex-col gap-3"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
              Data for Evidence Affidavit
            </p>
            <div className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5">
              {detailRows.map((row) => {
                const isEmpty = !row.value || row.value === "—";
                return (
                  <Fragment key={row.label}>
                    <div className="flex items-baseline font-medium text-[#222]">
                      {row.label}
                    </div>
                    <div className="flex items-baseline text-[#222]">:</div>
                    <div className="text-[#222] break-words min-w-0">
                      {isEmpty ? (
                        <span className="text-[#6D6D6D]">—</span>
                      ) : (
                        row.value
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </div>

            <p className="mt-4 mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
              List of Paragraph-wise Replies
            </p>
            <div className="rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5">
              {reviewReplies.length === 0 ? (
                <span className="text-[#6D6D6D]">—</span>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {reviewReplies.map((reply, i) => (
                    <li
                      key={i}
                      className="flex flex-col sm:flex-row gap-1 sm:gap-2 text-[#222]"
                    >
                      <span className="font-medium shrink-0">
                        &gt; {reply.heading?.trim() || "—"} :
                      </span>
                      <span className="whitespace-pre-wrap break-words min-w-0">
                        {reply.text?.trim() || "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <p className="mt-4 mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
              Date
            </p>
            <div className="rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5 text-[#222]">
              {dateParts ? (
                formatOrdinalLongDate(partsToDate(dateParts))
              ) : (
                <span className="text-[#6D6D6D]">—</span>
              )}
            </div>
          </section>

          {/* Spec pg 137 puts Generate PDF inside this window. Edits stay on the form behind it. */}
          <div className="flex flex-row items-center gap-2.5">
            <button
              type="button"
              onClick={() => onGeneratePdf(app)}
              disabled={generating}
              className="inline-flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] font-medium text-white hover:bg-[#333333] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {generating
                ? "Generating..."
                : app.evidenceAffidavitPdfKey
                  ? "Regenerate PDF"
                  : "Generate PDF"}
            </button>
          </div>
        </div>
      </FilePreviewModal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Signature from Client for Affidavit — sub-components
// ---------------------------------------------------------------------------

function ProcessToNextStepPanel({
  recorded,
  recording,
  onProceed,
  doneLabel,
}: {
  recorded: boolean;
  recording: boolean;
  onProceed: () => void;
  doneLabel: string;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-3 py-10"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <img
        src={processToNextStep}
        alt=""
        width={100}
        height={100}
        className="flex-shrink-0"
      />
      <span
        className="text-base font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Process to next step
      </span>
      {recorded ? (
        <div className="flex items-center gap-2">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="flex-shrink-0"
          >
            <circle cx="8" cy="8" r="7.5" fill="#222222" />
            <path
              d="M5.33325 8.00008L7.33325 10.0001L10.6666 6.00008"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-xs font-semibold text-[#222222] leading-5">
            {doneLabel}
          </span>
        </div>
      ) : (
        <button
          type="button"
          onClick={onProceed}
          disabled={recording}
          className="h-9 px-6 text-xs font-medium text-[#222222] bg-white border border-[#D1D5DB] rounded-[6px] hover:bg-[#F9FAFB] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          style={{ lineHeight: "20px" }}
        >
          {recording ? "Recording…" : "Proceed"}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notarisation of Documents for Evidence — sub-components
// ---------------------------------------------------------------------------

function NotarisationForEvidenceAmountTooltip({
  stampPapersForEvidence,
  defaultAmount,
  currentValue,
}: {
  stampPapersForEvidence: number | null;
  defaultAmount: number | null;
  currentValue: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const count = stampPapersForEvidence ?? 0;
  const defAmt = defaultAmount ?? 0;
  const rate = count > 0 && defAmt > 0 ? Math.round(defAmt / count) : null;
  const isOverridden =
    currentValue.trim() !== "" && Number(currentValue) !== defAmt;

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is the amount calculated?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 text-[#6D6D6D] hover:text-[#222] focus:outline-none focus:ring-1 focus:ring-[#222] rounded-full"
      >
        <HiOutlineInformationCircle className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[280px] rounded-[8px] border border-[#EEEEEE] bg-white p-3 shadow-lg"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-[11px] font-semibold leading-4 text-[#222]">
            Calculation Breakdown
          </p>
          <div className="mt-2 flex flex-col gap-1 text-[11px] leading-4 text-[#222]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">
                Stamp Papers
                {rate != null ? ` (${count} × ₹${rate})` : ` (${count})`}
              </span>
              <span className="font-medium">
                ₹{defAmt.toLocaleString("en-IN")}/-
              </span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-[#EEEEEE] pt-1.5">
              <span className="font-semibold text-[#222]">Auto-calculated</span>
              <span className="font-semibold text-[#222]">
                ₹{defAmt.toLocaleString("en-IN")}/-
              </span>
            </div>
            {isOverridden && (
              <p className="mt-1.5 text-[10px] text-[#6D6D6D]">
                Override in use. Clear the field to revert to the
                auto-calculated amount.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotarisationForEvidenceFields({
  status,
  draft,
  selectedTmNo,
  onPatch,
}: {
  status: NotarisationForEvidenceStatus | null;
  draft: Record<
    string,
    {
      amountPayableToNotaryForEvidence: string;
      paidToNotaryForEvidence: boolean | null;
    }
  >;
  selectedTmNo: string | null;
  onPatch: (
    tmNo: string,
    patch: Partial<{
      amountPayableToNotaryForEvidence: string;
      paidToNotaryForEvidence: boolean | null;
    }>
  ) => void;
}) {
  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Notarisation of Documents for Evidence…
      </p>
    );
  }
  const tmRow = (status.applications ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const d = draft[selectedTmNo];
  if (!tmRow || !d) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Save Stamp Paper details for this TM first.
      </p>
    );
  }
  const selectValue =
    d.paidToNotaryForEvidence === true
      ? "yes"
      : d.paidToNotaryForEvidence === false
      ? "no"
      : "__none__";
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-normal text-[#222222] leading-5">
              Amount Payable to Notary for Evidence
            </label>
            <NotarisationForEvidenceAmountTooltip
              stampPapersForEvidence={
                tmRow.stampPapersForEvidence ?? status.stampPapersForEvidence
              }
              defaultAmount={tmRow.defaultAmountPayableToNotaryForEvidence}
              currentValue={d.amountPayableToNotaryForEvidence}
            />
          </div>
          <input
            type="number"
            min={0}
            value={d.amountPayableToNotaryForEvidence}
            onChange={(e) =>
              onPatch(selectedTmNo, {
                amountPayableToNotaryForEvidence: e.target.value,
              })
            }
            placeholder="Text Here"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Paid to Notary for Evidence
            <RequiredMark />
          </label>
          <Select
            value={selectValue}
            onValueChange={(v) =>
              onPatch(selectedTmNo, {
                paidToNotaryForEvidence:
                  v === "yes" ? true : v === "no" ? false : null,
              })
            }
          >
            <SelectTrigger className="h-9 min-h-[36px] w-full border border-[#EEEEEE] rounded-[6px] px-2.5 py-2 text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select an Option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Select an Option</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
          {/* 4zs) spec pg 138: unpaid must read "Pending Payment". */}
          <PaymentStatusLine paid={d.paidToNotaryForEvidence} />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="Number of Stamp Papers needed for Evidence"
          value={
            // 4zf) for the application on screen — 4zr is derived from it (× Rs.250), so showing
            // the acknowledgement-wide total here would contradict the amount beside it.
            (tmRow.stampPapersForEvidence ?? status.stampPapersForEvidence) != null
              ? String(
                  tmRow.stampPapersForEvidence ?? status.stampPapersForEvidence
                )
              : "—"
          }
        />
        <div className="hidden md:block flex-1" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// File Evidence Affidavit — sub-components
// ---------------------------------------------------------------------------

function FileEvidenceAffidavitFields({
  status,
  perTm,
  lastDateDraft,
  selectedTmNo,
  onPatch,
  onLastDateChange,
  onPreviewExisting,
  onPreviewPending,
}: {
  status: FileEvidenceAffidavitStatus | null;
  perTm: Record<string, { filingDate: string; pendingFile: File | null }>;
  lastDateDraft: Record<string, string>;
  selectedTmNo: string | null;
  onPatch: (
    tmNo: string,
    patch: Partial<{ filingDate: string; pendingFile: File | null }>
  ) => void;
  onLastDateChange: (tmNo: string, val: string) => void;
  onPreviewExisting: (row: FileEvidenceAffidavitApplicationRow) => void;
  onPreviewPending: (
    row: FileEvidenceAffidavitApplicationRow,
    file: File
  ) => void;
}) {
  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading File Evidence Affidavit…
      </p>
    );
  }
  const tmRow = (status.applications ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const draft = perTm[selectedTmNo];
  if (!tmRow || !draft) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        File Evidence Affidavit is not available for this TM yet.
      </p>
    );
  }
  const dateParts = parseDateParts(draft.filingDate);
  // Auto-calculate from filing date; user override stored in lastDateDraft.
  const autoCalcLastDate = draft.filingDate
    ? computeRejoinderLastDate(draft.filingDate)
    : parseDateParts(tmRow.lastDateForRejoinderByOpponent)
      ? partsToDate(
          parseDateParts(tmRow.lastDateForRejoinderByOpponent) as {
            year: number;
            month: number;
            day: number;
          }
        )
      : null;
  const lastDateYmd = lastDateDraft[selectedTmNo] ?? "";
  const lastDateParts = parseDateParts(lastDateYmd);
  const lastDateValue = lastDateParts
    ? partsToDate(lastDateParts)
    : autoCalcLastDate;
  const inputId = `file-evidence-affidavit-input-${tmRow.tmNo}`;
  const hasFile = !!(
    draft.pendingFile || tmRow.filedEvidenceAffidavitPdfKey
  );
  const fileName =
    draft.pendingFile?.name ??
    (tmRow.filedEvidenceAffidavitPdfKey
      ? "Filed Evidence Affidavit"
      : "File selected");

  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Filing Evidence Affidavit
            <RequiredMark />
          </label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) =>
              onPatch(selectedTmNo, {
                filingDate: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5 flex items-center">
            Last Date for Rejoinder by Opponent
            <RejoinderLastDateTooltip />
          </label>
          <DateTimePicker
            value={lastDateValue}
            onChange={(d) =>
              onLastDateChange(selectedTmNo, d ? toLocalYmd(d) : "")
            }
            placeholder={
              autoCalcLastDate
                ? autoCalcLastDate.toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "Auto-calculated from Filing Date"
            }
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 border border-[#EEEEEE] rounded-[10px] p-4 md:p-5">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold text-[#101111]">
            Filed Evidence Affidavit PDF
            <RequiredMark />
          </p>
          <p className="text-xs text-[#7D7D7D]">
            Upload the filed Evidence Affidavit PDF downloaded from the
            Registry for this TM No.
          </p>
        </div>

        <div
          className={`flex flex-col md:flex-row items-stretch md:items-center gap-4 rounded-2xl border border-dashed px-4 py-4 cursor-pointer transition-colors ${
            hasFile
              ? "border-[#22C55E] bg-[#F0FDF4]"
              : "border-[#D8DAEB] bg-white"
          }`}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
            <img
              src={uploadDropzoneGraphic}
              alt=""
              width={60}
              height={60}
              className="flex-shrink-0"
            />
            <div className="flex flex-col gap-1 min-w-0 flex-1">
              {hasFile ? (
                <p className="text-sm font-medium text-[#16A34A] truncate">
                  {fileName}
                </p>
              ) : (
                <>
                  <p className="text-base font-medium text-[#101111]">
                    Drag and drop your file
                  </p>
                  <p className="text-sm text-[#7D7D7D]">
                    {SUPPORTED_FORMATS_LABEL}
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-row items-center gap-2 flex-shrink-0 self-end md:self-center">
            {!hasFile && (
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-sm font-medium text-[#2E3037] hover:bg-[#F5F5F5] transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById(inputId)?.click();
                }}
              >
                Select File
              </button>
            )}
            {hasFile && (
              <>
                <button
                  type="button"
                  className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (draft.pendingFile) {
                      onPreviewPending(tmRow, draft.pendingFile);
                    } else {
                      onPreviewExisting(tmRow);
                    }
                  }}
                >
                  Preview
                </button>
                <button
                  type="button"
                  className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPatch(selectedTmNo, { pendingFile: null });
                  }}
                >
                  Remove
                </button>
              </>
            )}
          </div>
        </div>

        <input
          id={inputId}
          type="file"
          accept={ALLOWED_FILE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reason = validateUploadFile(file);
            if (reason) {
              toast.error(reason);
              return;
            }
            onPatch(selectedTmNo, { pendingFile: file });
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inform Client about Filing of Evidence — message preview
// ---------------------------------------------------------------------------

function InformFilingEvidenceMessagePreview({
  preview,
}: {
  preview: InformClientFilingOfEvidencePreview | null;
}) {
  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-base font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Message Preview
      </span>
      <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[120px] bg-white">
        {preview?.message ? (
          <p
            className="text-xs text-[#222222] whitespace-pre-line"
            style={{ lineHeight: "20px" }}
          >
            {renderWhatsAppFormatting(preview.message)}
          </p>
        ) : (
          <p
            className="text-xs text-[#222222] whitespace-pre-line"
            style={{ lineHeight: "20px" }}
          >
            A &quot;Trademark Update&quot; message will be sent to the client
            informing them that the Evidence Affidavit has been filed against
            the Opposition.
            {"\n\n"}
            <span className="text-[#6D6D6D]">
              The exact message body is generated server-side; click Send
              &amp; Continue to dispatch via WhatsApp.
            </span>
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Download Rejoinder Affidavit — sub-components
// ---------------------------------------------------------------------------

function DownloadRejoinderAffidavitFields({
  status,
  perTm,
  selectedTmNo,
  onPatch,
  onPreviewExisting,
  onPreviewPending,
}: {
  status: DownloadRejoinderAffidavitStatus | null;
  perTm: Record<string, { rejoinderDate: string; pendingFile: File | null }>;
  selectedTmNo: string | null;
  onPatch: (
    tmNo: string,
    patch: Partial<{ rejoinderDate: string; pendingFile: File | null }>
  ) => void;
  onPreviewExisting: (row: DownloadRejoinderAffidavitApplicationRow) => void;
  onPreviewPending: (
    row: DownloadRejoinderAffidavitApplicationRow,
    file: File
  ) => void;
}) {
  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Download Rejoinder Affidavit…
      </p>
    );
  }
  const tmRow = (status.applications ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const draft = perTm[selectedTmNo];
  if (!tmRow || !draft) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Download Rejoinder Affidavit is not available for this TM yet.
      </p>
    );
  }
  const dateParts = parseDateParts(draft.rejoinderDate);
  const inputId = `download-rejoinder-input-${tmRow.tmNo}`;
  const hasFile = !!(draft.pendingFile || tmRow.rejoinderAffidavitPdfKey);
  const fileName =
    draft.pendingFile?.name ??
    (tmRow.rejoinderAffidavitPdfKey
      ? "Rejoinder Affidavit"
      : "File selected");

  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Rejoinder by Opponent
            <RequiredMark />
          </label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) =>
              onPatch(selectedTmNo, {
                rejoinderDate: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
        <div className="hidden md:block flex-1" />
      </div>

      <div className="flex flex-col gap-3 border border-[#EEEEEE] rounded-[10px] p-4 md:p-5">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold text-[#101111]">
            File Upload
            <RequiredMark />
          </p>
          <p className="text-xs text-[#7D7D7D]">
            Choose a file and upload securely to proceed.
          </p>
        </div>

        <div
          className={`flex flex-col md:flex-row items-stretch md:items-center gap-4 rounded-2xl border border-dashed px-4 py-4 cursor-pointer transition-colors ${
            hasFile
              ? "border-[#22C55E] bg-[#F0FDF4]"
              : "border-[#D8DAEB] bg-white"
          }`}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
            <img
              src={uploadDropzoneGraphic}
              alt=""
              width={60}
              height={60}
              className="flex-shrink-0"
            />
            <div className="flex flex-col gap-1 min-w-0 flex-1">
              {hasFile ? (
                <p className="text-sm font-medium text-[#16A34A] truncate">
                  {fileName}
                </p>
              ) : (
                <>
                  <p className="text-base font-medium text-[#101111]">
                    Drag and drop your files
                  </p>
                  <p className="text-sm text-[#7D7D7D]">
                    {SUPPORTED_FORMATS_LABEL}
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-row items-center gap-2 flex-shrink-0 self-end md:self-center">
            {!hasFile && (
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-sm font-medium text-[#2E3037] hover:bg-[#F5F5F5] transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById(inputId)?.click();
                }}
              >
                Select File
              </button>
            )}
            {hasFile && (
              <>
                <button
                  type="button"
                  className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (draft.pendingFile) {
                      onPreviewPending(tmRow, draft.pendingFile);
                    } else {
                      onPreviewExisting(tmRow);
                    }
                  }}
                >
                  Preview
                </button>
                <button
                  type="button"
                  className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPatch(selectedTmNo, { pendingFile: null });
                  }}
                >
                  Remove
                </button>
              </>
            )}
          </div>
        </div>

        <input
          id={inputId}
          type="file"
          accept={ALLOWED_FILE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reason = validateUploadFile(file);
            if (reason) {
              toast.error(reason);
              return;
            }
            onPatch(selectedTmNo, { pendingFile: file });
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status details of Opposition Hearing — sub-components
// ---------------------------------------------------------------------------

function OppositionHearingStatusFields({
  status,
  perTm,
  selectedTmNo,
  onPatch,
  onPreviewExisting,
  onPreviewPending,
}: {
  status: OppositionHearingStatus | null;
  perTm: Record<string, { hearingDate: string; pendingFile: File | null }>;
  selectedTmNo: string | null;
  onPatch: (
    tmNo: string,
    patch: Partial<{ hearingDate: string; pendingFile: File | null }>
  ) => void;
  onPreviewExisting: (row: OppositionHearingStatus["applications"][number]) => void;
  onPreviewPending: (
    row: OppositionHearingStatus["applications"][number],
    file: File
  ) => void;
}) {
  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">Loading Opposition Hearing…</p>
    );
  }
  const tmRow = (status.applications ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const draft = perTm[selectedTmNo];
  if (!draft) {
    return (
      <p className="text-sm text-[#6D6D6D]">Loading Opposition Hearing…</p>
    );
  }
  const dateParts = parseDateParts(draft.hearingDate);
  const inputId = `opposition-hearing-input-${selectedTmNo}`;
  const hasFile = !!(
    draft.pendingFile || tmRow?.oppositionHearingNoticeFileKey
  );
  const fileName =
    draft.pendingFile?.name ??
    tmRow?.oppositionHearingNoticeFilename ??
    "File selected";

  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Opposition Hearing
            <RequiredMark />
          </label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) =>
              onPatch(selectedTmNo, {
                hearingDate: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
          />
        </div>
        <ReadOnlyField
          label="Acknowledgement No."
          value={status.acknowledgementNo ?? "—"}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="No. of applications for Opposition Hearing"
          value={String(status.noOfApplicationsForOppositionHearing ?? 0)}
        />
        <div className="hidden md:block flex-1" />
      </div>

      <div className="flex flex-col gap-3 border border-[#EEEEEE] rounded-[10px] p-4 md:p-5">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold text-[#101111]">
            Opposition Hearing Notice PDF
          </p>
          <p className="text-xs text-[#7D7D7D]">
            Upload the Hearing Notice PDF downloaded from the Registry for
            this TM.
          </p>
        </div>

        <div
          className={`flex flex-col md:flex-row items-stretch md:items-center gap-4 rounded-2xl border border-dashed px-4 py-4 cursor-pointer transition-colors ${
            hasFile
              ? "border-[#22C55E] bg-[#F0FDF4]"
              : "border-[#D8DAEB] bg-white"
          }`}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
            <img
              src={uploadDropzoneGraphic}
              alt=""
              width={60}
              height={60}
              className="flex-shrink-0"
            />
            <div className="flex flex-col gap-1 min-w-0 flex-1">
              {hasFile ? (
                <p className="text-sm font-medium text-[#16A34A] truncate">
                  {fileName}
                </p>
              ) : (
                <>
                  <p className="text-base font-medium text-[#101111]">
                    Drag and drop your file
                  </p>
                  <p className="text-sm text-[#7D7D7D]">
                    {SUPPORTED_FORMATS_LABEL}
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-row items-center gap-2 flex-shrink-0 self-end md:self-center">
            {!hasFile && (
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-sm font-medium text-[#2E3037] hover:bg-[#F5F5F5] transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById(inputId)?.click();
                }}
              >
                Select File
              </button>
            )}
            {hasFile && (
              <>
                <button
                  type="button"
                  className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (draft.pendingFile) {
                      onPreviewPending(tmRow!, draft.pendingFile);
                    } else if (tmRow) {
                      onPreviewExisting(tmRow);
                    }
                  }}
                >
                  Preview
                </button>
                <button
                  type="button"
                  className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPatch(selectedTmNo, { pendingFile: null });
                  }}
                >
                  Remove
                </button>
              </>
            )}
          </div>
        </div>

        <input
          id={inputId}
          type="file"
          accept={ALLOWED_FILE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reason = validateUploadFile(file);
            if (reason) {
              toast.error(reason);
              return;
            }
            onPatch(selectedTmNo, { pendingFile: file });
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notice to Client for Opposition Hearing — sub-components
// ---------------------------------------------------------------------------

function OppositionHearingNoticePdfRow({
  app,
  onPreview,
}: {
  app: OppositionHearingStatus["applications"][number];
  onPreview: () => void;
}) {
  const filename =
    app.oppositionHearingNoticeFilename ?? "Opposition Hearing Notice";
  const label = app.mark || filename;
  return (
    <div
      className="flex flex-row items-center gap-3 px-4 py-1 rounded-[6px] bg-[#FCFCFC] border border-[#EEEEEE] h-11"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <img
        src={pdfIcon}
        alt=""
        width={22}
        height={22}
        className="flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <span className="text-xs font-medium text-[#222222] truncate block">
          {label}
        </span>
      </div>
      <button
        type="button"
        onClick={onPreview}
        className="px-4 h-9 rounded-[6px] bg-[#222222] text-white text-xs font-medium hover:bg-[#333333]"
      >
        Preview
      </button>
    </div>
  );
}

function OppositionHearingNoticeConfirmPreview({
  apps,
  preview,
}: {
  apps: OppositionHearingStatus["applications"];
  preview: OppositionHearingNoticePreview | null;
}) {
  const recipientPhone = preview?.recipientPhone ?? null;
  const recipientEmail = preview?.recipientEmail ?? null;
  const channelText = recipientEmail ? "WhatsApp and Email" : "WhatsApp";

  return (
    <div
      className="flex flex-col gap-4 px-3.5 py-3.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-2.5">
        <span
          className="text-base font-normal text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          Opposition Hearing Notice PDFs
        </span>
        <div className="flex flex-col gap-2">
          {apps.map((app) => (
            <div
              key={app.tmNo}
              className="flex flex-row items-center gap-3 px-4 py-1 rounded-[6px] bg-[#FCFCFC] border border-[#EEEEEE] h-11"
            >
              <img
                src={pdfIcon}
                alt=""
                width={22}
                height={22}
                className="flex-shrink-0"
              />
              <span className="text-xs font-medium text-[#222222] truncate flex-1 min-w-0">
                {app.mark ||
                  app.oppositionHearingNoticeFilename ||
                  "Opposition Hearing Notice"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span
          className="text-xs font-medium text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          Recipient
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="tel"
            inputMode="numeric"
            value={recipientPhone ?? ""}
            readOnly
            placeholder="WhatsApp number not set"
            className="w-full min-w-0 sm:w-[220px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
          />
          <input
            type="email"
            value={recipientEmail ?? ""}
            readOnly
            placeholder="Email not set"
            className="w-full min-w-0 sm:w-[260px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span
          className="text-xs font-medium text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          WhatsApp Message Preview
        </span>
        <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[160px] bg-white">
          {preview?.message ? (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              {renderWhatsAppFormatting(preview.message)}
            </p>
          ) : (
            <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
              The Opposition Hearing Notice message body will appear here once
              the preview loads.
            </p>
          )}
        </div>
      </div>

      {recipientEmail && (
        <div className="flex flex-col gap-2">
          <span
            className="text-xs font-medium text-[#222222]"
            style={{ lineHeight: "20px" }}
          >
            Email Preview
          </span>
          <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[160px] bg-white">
            {preview?.emailPreview ? (
              <div className="flex flex-col gap-2">
                <p
                  className="text-xs font-medium text-[#222222]"
                  style={{ lineHeight: "20px" }}
                >
                  Subject: {preview.emailPreview.subject}
                </p>
                <p
                  className="text-xs text-[#222222] whitespace-pre-line"
                  style={{ lineHeight: "20px" }}
                >
                  {preview.emailPreview.body}
                </p>
              </div>
            ) : (
              <p
                className="text-xs text-[#6D6D6D]"
                style={{ lineHeight: "20px" }}
              >
                The email body will appear here once the preview loads.
              </p>
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "18px" }}>
        The merged Opposition Hearing Notice PDF will be forwarded to the
        client via {channelText}.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inform Client about Opposition Hearing — message preview
// ---------------------------------------------------------------------------

function InformOppositionHearingMessagePreview({
  preview,
}: {
  preview: InformClientOppositionHearingPreview | null;
}) {
  const tmNumbers = preview?.tmNumbers ?? [];
  const applicationCount = preview?.applicationCount ?? tmNumbers.length;
  const hasScope = applicationCount > 0;

  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-base font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Message Preview
      </span>
      <div className="border border-[#EEEEEE] rounded-[6px] bg-white">
        {hasScope && (
          <div className="flex flex-col gap-2 border-b border-[#EEEEEE] px-3.5 py-2.5">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Applications
              </span>
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#F1F1F1] text-[11px] font-medium text-[#222222]">
                {applicationCount}
              </span>
            </div>
            {tmNumbers.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {tmNumbers.map((tm) => (
                  <span
                    key={tm}
                    className="inline-flex items-center h-6 px-2 rounded-[4px] bg-[#F7F7F7] border border-[#EEEEEE] text-[11px] font-medium text-[#222222]"
                  >
                    TM No. {tm}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="px-3.5 py-3.5 min-h-[160px]">
          {preview?.message ? (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              {renderWhatsAppFormatting(preview.message)}
            </p>
          ) : (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              A &quot;Trademark Update&quot; message will be sent to the client
              informing them of the Date of Opposition Hearing for the listed
              applications.
              {"\n\n"}
              <span className="text-[#6D6D6D]">
                The exact message body is generated server-side once Date of
                Opposition Hearing is saved for at least one TM. Click Send
                &amp; Continue to dispatch via WhatsApp.
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invoice to Client for Opposition Hearing — sub-components
// ---------------------------------------------------------------------------

function OppositionHearingInvoicePreviewModal({
  previewTab,
  onTabChange,
  previewNumPages,
  onPreviewNumPagesChange,
  status,
  preview,
  selectedTmNo,
  rows,
  messagePreview,
  messagePreviewLoading,
  onClose,
}: {
  previewTab: "details" | "pdf" | "message" | "email";
  onTabChange: (tab: "details" | "pdf" | "message" | "email") => void;
  previewNumPages: number | null;
  onPreviewNumPagesChange: (n: number | null) => void;
  status: OppositionHearingInvoiceStatus;
  preview: OppositionHearingInvoicePreview | null;
  selectedTmNo: string | null;
  rows: Record<string, { invoiceAmount: string; invoiceDate: string }>;
  messagePreview: OppositionHearingInvoiceWhatsAppPreview | null;
  messagePreviewLoading: boolean;
  onClose: () => void;
}) {
  const pdfUrl = status.oppositionHearingInvoicePdfUrl ?? null;
  const pdfFilename = "Opposition Hearing Invoice.pdf";

  const formatRupees = (n: number | null | undefined) =>
    `Rs. ${Math.max(0, Math.round(Number(n ?? 0))).toLocaleString("en-IN")}/-`;
  const formatDateDisplay = (raw: string | null | undefined) => {
    if (!raw) return "—";
    const parts = raw.split("-");
    if (parts.length !== 3 || parts.some((p) => !p)) return raw;
    if (parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    return raw;
  };

  const draft = selectedTmNo ? rows[selectedTmNo] : null;
  const previewApp = preview?.applications.find((a) => a.tmNo === selectedTmNo);
  const addresseeLines = (preview?.addressee ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const tabs: Array<{
    id: "details" | "pdf" | "message" | "email";
    label: string;
    disabled?: boolean;
  }> = [
    { id: "details", label: "Details" },
    { id: "pdf", label: "Generated PDF", disabled: !pdfUrl },
    { id: "message", label: "WhatsApp Message" },
    { id: "email", label: "Email" },
  ];

  const Row = ({
    label,
    children,
    alignTop,
  }: {
    label: string;
    children: React.ReactNode;
    alignTop?: boolean;
  }) => (
    <>
      <div
        className={`flex ${alignTop ? "items-start" : "items-baseline"} font-medium text-[#222]`}
      >
        {label}
      </div>
      <div
        className={`flex ${alignTop ? "items-start" : "items-baseline"} text-[#222]`}
      >
        :
      </div>
      <div className="text-[#222] break-words min-w-0">{children}</div>
    </>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
          <span
            className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {pdfFilename}
          </span>
          <div className="flex items-center gap-3 shrink-0">
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#2E3037] underline underline-offset-2"
              >
                Open in new tab
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
              aria-label="Close preview"
            >
              <HiOutlineX className="size-5" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div
          className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
          role="tablist"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={previewTab === tab.id}
              disabled={tab.disabled}
              onClick={() => onTabChange(tab.id)}
              className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
                previewTab === tab.id
                  ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                  : "text-[#6d6d6d] hover:text-[#222]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4 flex justify-center">
          {previewTab === "details" ? (
            <div
              className="flex w-full max-w-3xl flex-col gap-3"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                  Invoice details
                </p>
                <div
                  className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <Row label="Addressee" alignTop>
                    {addresseeLines.length > 0 ? (
                      <div className="flex flex-col">
                        {addresseeLines.map((line, i) => (
                          <span key={i}>{line}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[#6D6D6D]">—</span>
                    )}
                  </Row>
                  <Row label="Form of Address">
                    {preview?.formOfAddress?.trim() || <span className="text-[#6D6D6D]">—</span>}
                  </Row>
                  <Row label="Mark">
                    {(previewApp?.mark ?? preview?.mark)?.trim() || <span className="text-[#6D6D6D]">—</span>}
                  </Row>
                  <Row label="Selected Class">
                    {(previewApp?.classNumber ?? preview?.classNumber) != null
                      ? String(previewApp?.classNumber ?? preview?.classNumber)
                      : <span className="text-[#6D6D6D]">—</span>}
                  </Row>
                  <Row label="No. of Applications for Opposition Hearing">
                    {String(status.noOfApps ?? 0)}
                  </Row>
                  <Row label="Invoice Amount for Opposition Hearing">
                    {formatRupees(draft?.invoiceAmount ? Number(draft.invoiceAmount) : status.invoiceAmount)}
                  </Row>
                  <Row label="Invoice No. for Opposition Hearing">
                    {status.invoiceNo?.trim() || <span className="text-[#6D6D6D]">Auto-generated on save</span>}
                  </Row>
                  <Row label="Date of Invoice for Opposition Hearing">
                    {formatDateDisplay(draft?.invoiceDate || status.invoiceDate)}
                  </Row>
                </div>
              </section>
              {preview && preview.applications.length > 0 && (
                <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                    Applications
                  </p>
                  <div className="overflow-x-auto rounded-[6px] border border-[#EEEEEE]">
                    <table className="w-full border-collapse text-[12px] leading-5">
                      <thead className="bg-[#F7F7F7]">
                        <tr>
                          <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">Mark</th>
                          <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">Type</th>
                          <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">Class</th>
                          <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] whitespace-nowrap">Date of Hearing</th>
                          <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] whitespace-nowrap">Invoice Amount</th>
                          <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] whitespace-nowrap">Invoice Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.applications.map((app, idx) => (
                          <tr
                            key={`${app.tmNo}-${idx}`}
                            className={idx < preview.applications.length - 1 ? "border-b border-[#EEEEEE]" : ""}
                          >
                            <td className="px-2.5 py-2 text-[#222222] align-top">{app.mark?.trim() || "—"}</td>
                            <td className="px-2.5 py-2 text-[#222222] align-top">{app.typeOfMark?.trim() || "—"}</td>
                            <td className="px-2.5 py-2 text-[#222222] align-top">{String(app.classNumber)}</td>
                            <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">{formatDateDisplay(app.hearingDate)}</td>
                            <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">{formatRupees(app.invoiceAmount)}</td>
                            <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">{formatDateDisplay(app.invoiceDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </div>
          ) : previewTab === "pdf" ? (
            pdfUrl ? (
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages: n }) => onPreviewNumPagesChange(n)}
                loading={<p className="text-sm text-[#6d6d6d]">Loading PDF…</p>}
                error={<p className="text-sm text-red-600">Failed to load PDF.</p>}
                className="flex flex-col items-center gap-4"
              >
                {previewNumPages != null &&
                  Array.from({ length: previewNumPages }, (_, i) => (
                    <Page
                      key={i + 1}
                      pageNumber={i + 1}
                      width={Math.min(800, typeof window !== "undefined" ? window.innerWidth - 48 : 800)}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  ))}
              </Document>
            ) : (
              <p className="text-sm text-[#6D6D6D]">Generate the invoice PDF to preview it here.</p>
            )
          ) : previewTab === "message" ? (
            <div
              className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be sent to client WhatsApp after the invoice PDF
              </p>
              <WhatsAppMessageBlock
                message={messagePreview?.paymentMessage ?? ""}
                loading={messagePreviewLoading}
              />
            </div>
          ) : (
            <div
              className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be emailed to the client with the invoice PDF
              </p>
              <EmailPreviewBlock
                preview={messagePreview?.emailPreview ?? null}
                loading={messagePreviewLoading}
                attachmentFallback={pdfUrl ? { filename: pdfFilename, url: pdfUrl } : null}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OppositionHearingInvoiceFields({
  leadId,
  status,
  preview,
  rows,
  selectedTmNo,
  generating,
  onPatchRow,
  onGeneratePdf,
}: {
  leadId: string | null;
  status: OppositionHearingInvoiceStatus | null;
  preview: OppositionHearingInvoicePreview | null;
  rows: Record<string, { invoiceAmount: string; invoiceDate: string }>;
  selectedTmNo: string | null;
  generating: boolean;
  onPatchRow: (
    tmNo: string,
    patch: Partial<{ invoiceAmount: string; invoiceDate: string }>
  ) => void;
  onGeneratePdf: () => void;
}) {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<"details" | "pdf" | "message" | "email">("details");
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [messagePreview, setMessagePreview] =
    useState<OppositionHearingInvoiceWhatsAppPreview | null>(null);
  const [messagePreviewLoading, setMessagePreviewLoading] = useState(false);

  useEffect(() => {
    if (!previewModalOpen || !leadId || !selectedTmNo) return;
    let cancelled = false;
    setMessagePreviewLoading(true);
    getOppositionHearingInvoiceWhatsAppPreview(leadId, [selectedTmNo])
      .then((data) => {
        if (!cancelled) setMessagePreview(data);
      })
      .catch(() => {
        if (!cancelled) setMessagePreview(null);
      })
      .finally(() => {
        if (!cancelled) setMessagePreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    previewModalOpen,
    leadId,
    selectedTmNo,
    status?.oppositionHearingInvoicePdfKey,
  ]);

  const handleDownload = async () => {
    const pdfUrl = status?.oppositionHearingInvoicePdfUrl;
    if (!pdfUrl || downloading) return;
    setDownloading(true);
    try {
      const result = await downloadFileFromUrlWithFallback(
        pdfUrl,
        "Opposition_Hearing_Invoice.pdf"
      );
      if (result === "local") {
        toast.success("Download started");
      } else {
        toast.success(
          "Opened the document in a new tab — use the browser's download option to save it."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Opposition Hearing Invoice…
      </p>
    );
  }
  const tmRow = (status.tmInvoiceRows ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const draft = rows[selectedTmNo];
  if (!tmRow || !draft) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Selected TM is not on the Opposition Hearing Invoice yet.
      </p>
    );
  }
  const dateParts = parseDateParts(draft.invoiceDate);
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="Acknowledgement No."
          value={preview?.acknowledgementNo ?? "—"}
        />
        <div className="hidden md:block flex-1" />
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Invoice Amount for Opposition Hearing
            <RequiredMark />
          </label>
          <input
            type="number"
            min={0}
            value={draft.invoiceAmount}
            onChange={(e) =>
              onPatchRow(selectedTmNo, { invoiceAmount: e.target.value })
            }
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
        <ReadOnlyField
          label="Invoice No. for Opposition Hearing"
          value={tmRow.individualInvoiceNo ?? status.invoiceNo ?? "—"}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Invoice for Opposition Hearing
          </label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) =>
              onPatchRow(selectedTmNo, {
                invoiceDate: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
        <div className="hidden md:block flex-1" />
      </div>

      <div className="flex flex-row items-center gap-3 pt-1 flex-wrap">
        <button
          type="button"
          onClick={onGeneratePdf}
          disabled={generating}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {generating ? "Generating…" : "Generate Invoice PDF"}
        </button>
        {/*
          Review item 41: the whole block below used to sit behind
          `status.oppositionHearingInvoicePdfKey`, so the Details / WhatsApp Message / Email tabs —
          none of which need a PDF — only appeared once one had been generated **and persisted**.
          That is why the client had to "go to the next step and come back" to see any preview
          (spec pg 143 expects it inline). Preview is now always available; only Download still
          requires the generated file.
        */}
        <button
          type="button"
          onClick={() => {
            setPreviewTab("details");
            setPreviewNumPages(null);
            setPreviewModalOpen(true);
          }}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5]"
        >
          Preview Invoice
        </button>
        {status.oppositionHearingInvoicePdfKey && (
          <button
            type="button"
            onClick={() => void handleDownload()}
            disabled={!status.oppositionHearingInvoicePdfUrl || downloading || generating}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {downloading ? "Downloading…" : "Download"}
          </button>
        )}
      </div>

      {previewModalOpen && (
        <OppositionHearingInvoicePreviewModal
          previewTab={previewTab}
          onTabChange={setPreviewTab}
          previewNumPages={previewNumPages}
          onPreviewNumPagesChange={setPreviewNumPages}
          status={status}
          preview={preview}
          selectedTmNo={selectedTmNo}
          rows={rows}
          messagePreview={messagePreview}
          messagePreviewLoading={messagePreviewLoading}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewNumPages(null);
            setPreviewTab("details");
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invoice for TM-M before Opposition Hearing — sub-components
// ---------------------------------------------------------------------------

const TM_M_BEFORE_HEARING_RATE_PER_APP = 1500;

function TmMBeforeHearingInvoiceAmountTooltip({
  noOfApps,
}: {
  noOfApps: number;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const computedDefault = TM_M_BEFORE_HEARING_RATE_PER_APP * noOfApps;
  const formatted = `Rs. ${computedDefault.toLocaleString("en-IN")}/-`;

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is the invoice amount calculated?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 text-[#6D6D6D] hover:text-[#222] focus:outline-none focus:ring-1 focus:ring-[#222] rounded-full"
      >
        <HiOutlineInformationCircle className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[320px] rounded-[8px] border border-[#EEEEEE] bg-white p-3 shadow-lg"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-[11px] font-semibold leading-4 text-[#222]">
            How the invoice amount is calculated
          </p>
          <p className="mt-1 text-[11px] leading-4 text-[#6D6D6D]">
            Rs. {TM_M_BEFORE_HEARING_RATE_PER_APP.toLocaleString("en-IN")}/- per
            Application Number (TM No.), multiplied by the No. of applications
            for TM-M before Opposition Hearing.
          </p>
          <div className="mt-2 flex flex-col gap-1 text-[11px] leading-4 text-[#222]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Hearing charge</span>
              <span className="font-medium">
                Rs.{" "}
                {TM_M_BEFORE_HEARING_RATE_PER_APP.toLocaleString("en-IN")}/- per
                app
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">
                No. of applications for TM-M
              </span>
              <span className="font-medium">{noOfApps}</span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Calculation</span>
              <span className="font-medium">
                {TM_M_BEFORE_HEARING_RATE_PER_APP.toLocaleString("en-IN")} ×{" "}
                {noOfApps}
              </span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-[#EEEEEE] pt-1.5">
              <span className="font-semibold text-[#222]">
                Default invoice amount
              </span>
              <span className="font-semibold text-[#222]">{formatted}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TmMBeforeHearingInvoiceFields({
  leadId,
  status,
  preview,
  invoiceDate,
  onInvoiceDateChange,
  invoiceAmount,
  onInvoiceAmountChange,
  generating,
  downloading,
  onGeneratePdf,
  onDownloadPdf,
}: {
  leadId: string | null;
  status: TmMBeforeOppositionHearingInvoiceStatus | null;
  preview: TmMBeforeOppositionHearingInvoicePreview | null;
  invoiceDate: string;
  onInvoiceDateChange: (v: string) => void;
  invoiceAmount: string;
  onInvoiceAmountChange: (v: string) => void;
  generating: boolean;
  downloading: boolean;
  onGeneratePdf: () => void;
  onDownloadPdf: () => void;
}) {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<
    "details" | "pdf" | "message" | "email"
  >("details");
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);
  const [messagePreview, setMessagePreview] =
    useState<TmMBeforeOppositionHearingInvoiceWhatsAppPreview | null>(null);
  const [messagePreviewLoading, setMessagePreviewLoading] = useState(false);

  useEffect(() => {
    if (!previewModalOpen || !leadId) return;
    let cancelled = false;
    setMessagePreviewLoading(true);
    getTmMBeforeOppositionHearingInvoiceWhatsAppPreview(leadId)
      .then((data) => {
        if (!cancelled) setMessagePreview(data);
      })
      .catch(() => {
        if (!cancelled) setMessagePreview(null);
      })
      .finally(() => {
        if (!cancelled) setMessagePreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    previewModalOpen,
    leadId,
    status?.tmMBeforeOppositionHearingInvoicePdfKey,
  ]);

  if (!status) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading TM-M before Opposition Hearing Invoice…
      </p>
    );
  }
  if (!status.isActive) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        No applications are eligible to be invoiced. Mark clientConfirmation =
        POSTPONE for at least one application in the previous step
        (Confirmation from Client for Opposition Hearing) first.
      </p>
    );
  }
  const dateParts = parseDateParts(invoiceDate);
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="Acknowledgement No."
          value={preview?.acknowledgementNo ?? "—"}
        />
        <ReadOnlyField
          label="No. of Applications for TM-M before Opposition Hearing"
          value={String(status.noOfAppsForTmMBeforeOppositionHearing ?? 0)}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            <span className="inline-flex items-center gap-1">
              Invoice Amount for TM-M before Opposition Hearing
              <TmMBeforeHearingInvoiceAmountTooltip
                noOfApps={status.noOfAppsForTmMBeforeOppositionHearing ?? 0}
              />
              <RequiredMark />
            </span>
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={invoiceAmount}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                onInvoiceAmountChange(v);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            placeholder="0"
            className="h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border"
          />
        </div>
        <ReadOnlyField
          label="Invoice No. for TM-M before Opposition Hearing"
          value={status.invoiceNo ?? "—"}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Invoice for TM-M before Opposition Hearing
          </label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) => onInvoiceDateChange(d ? toLocalYmd(d) : "")}
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
        <div className="hidden md:block flex-1" />
      </div>

      <div className="flex flex-row items-center gap-3 pt-1 flex-wrap">
        <button
          type="button"
          onClick={onGeneratePdf}
          disabled={generating}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {generating ? "Generating…" : "Generate Invoice PDF"}
        </button>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={downloading || generating}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {downloading ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={() => {
            setPreviewTab("details");
            setPreviewNumPages(null);
            setPreviewModalOpen(true);
          }}
          disabled={generating}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Preview Invoice
        </button>
      </div>

      {previewModalOpen && (
        <TmMBeforeHearingInvoicePreviewModal
          previewTab={previewTab}
          onTabChange={setPreviewTab}
          previewNumPages={previewNumPages}
          onPreviewNumPagesChange={setPreviewNumPages}
          status={status}
          preview={preview}
          invoiceDate={invoiceDate}
          invoiceAmount={invoiceAmount}
          messagePreview={messagePreview}
          messagePreviewLoading={messagePreviewLoading}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewNumPages(null);
            setPreviewTab("details");
          }}
        />
      )}
    </div>
  );
}

function TmMBeforeHearingInvoicePreviewModal({
  previewTab,
  onTabChange,
  previewNumPages,
  onPreviewNumPagesChange,
  status,
  preview,
  invoiceDate,
  invoiceAmount,
  messagePreview,
  messagePreviewLoading,
  onClose,
}: {
  previewTab: "details" | "pdf" | "message" | "email";
  onTabChange: (tab: "details" | "pdf" | "message" | "email") => void;
  previewNumPages: number | null;
  onPreviewNumPagesChange: (n: number | null) => void;
  status: TmMBeforeOppositionHearingInvoiceStatus;
  preview: TmMBeforeOppositionHearingInvoicePreview | null;
  invoiceDate: string;
  invoiceAmount: string;
  messagePreview: TmMBeforeOppositionHearingInvoiceWhatsAppPreview | null;
  messagePreviewLoading: boolean;
  onClose: () => void;
}) {
  const pdfUrl = status.tmMBeforeOppositionHearingInvoicePdfUrl ?? null;
  const pdfFilename = "Invoice for TM-M before Opposition Hearing.pdf";

  const formatRupees = (n: number | string | null | undefined) => {
    const v = typeof n === "string" ? Number(n) : Number(n ?? 0);
    return `Rs. ${Math.max(0, Math.round(Number.isFinite(v) ? v : 0)).toLocaleString("en-IN")}/-`;
  };
  const formatDateDisplay = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return y && m && d ? `${d}-${m}-${y}` : iso;
  };

  const tabs: Array<{
    id: "details" | "pdf" | "message" | "email";
    label: string;
    disabled?: boolean;
  }> = [
    { id: "details", label: "Details" },
    { id: "pdf", label: "Generated PDF", disabled: !pdfUrl },
    { id: "message", label: "WhatsApp Message" },
    { id: "email", label: "Email" },
  ];

  const Row = ({
    label,
    children,
    alignTop,
  }: {
    label: string;
    children: React.ReactNode;
    alignTop?: boolean;
  }) => (
    <>
      <div
        className={`flex ${alignTop ? "items-start" : "items-baseline"} font-medium text-[#222]`}
      >
        {label}
      </div>
      <div
        className={`flex ${alignTop ? "items-start" : "items-baseline"} text-[#222]`}
      >
        :
      </div>
      <div className="text-[#222] break-words min-w-0">{children}</div>
    </>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
          <span
            className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {pdfFilename}
          </span>
          <div className="flex items-center gap-3 shrink-0">
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#2E3037] underline underline-offset-2"
              >
                Open in new tab
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
              aria-label="Close preview"
            >
              <HiOutlineX className="size-5" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div
          className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
          role="tablist"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={previewTab === tab.id}
              disabled={tab.disabled}
              onClick={() => onTabChange(tab.id)}
              className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
                previewTab === tab.id
                  ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                  : "text-[#6d6d6d] hover:text-[#222]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4 flex justify-center">
          {previewTab === "details" ? (
            (() => {
              const addresseeLines = [
                preview?.addresseeLine1,
                preview?.addresseeLine2,
                preview?.addresseeLine3,
              ]
                .map((line) => line?.trim())
                .filter((line): line is string => Boolean(line));
              const fallbackAddresseeLines = (preview?.addressee ?? "")
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean);
              const resolvedAddresseeLines =
                addresseeLines.length > 0
                  ? addresseeLines
                  : fallbackAddresseeLines;
              return (
                <div
                  className="flex w-full max-w-3xl flex-col gap-3"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                      Data for Invoice for TM-M before Opposition Hearing
                    </p>
                    <div
                      className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      <Row label="Addressee" alignTop>
                        {resolvedAddresseeLines.length > 0 ? (
                          <div className="flex flex-col">
                            {resolvedAddresseeLines.map((line, i) => (
                              <span key={i}>{line}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </Row>
                      <Row label="Form of Address">
                        {preview?.formOfAddress?.trim() || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </Row>
                    </div>
                  </section>

                  {preview && preview.applications.length > 0 && (
                    <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                        Applications
                      </p>
                      <div className="flex flex-col gap-2">
                        {preview.applications.map((app, idx) => (
                          <div
                            key={`${app.tmNo}-${idx}`}
                            className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
                            style={{ fontFamily: "Inter, sans-serif" }}
                          >
                            <Row label="Application Number (TM No.)">
                              {formatTmNoForDisplay(app.tmNo)}
                            </Row>
                            <Row label="Date of Filing">
                              {formatDateDisplay(app.applicationDate)}
                            </Row>
                            <Row label="Mark (Type)">
                              {(() => {
                                const markText = app.mark?.trim();
                                const typeText = app.typeOfMark?.trim();
                                if (markText && typeText)
                                  return `${markText} (${typeText})`;
                                if (markText) return markText;
                                if (typeText) return typeText;
                                return <span className="text-[#6D6D6D]">—</span>;
                              })()}
                            </Row>
                            <Row label="Class">
                              {app.classNumber != null
                                ? String(app.classNumber)
                                : <span className="text-[#6D6D6D]">—</span>}
                            </Row>
                            <Row label="Hearing Date">
                              {formatDateDisplay(app.dateOfOppositionHearing)}
                            </Row>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                      Invoice
                    </p>
                    <div
                      className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      <Row label="Invoice Amount for TM-M before Opposition Hearing">
                        {formatRupees(
                          invoiceAmount !== ""
                            ? invoiceAmount
                            : status.invoiceAmount
                        )}
                      </Row>
                      <Row label="Date of Invoice for TM-M before Opposition Hearing">
                        {formatDateDisplay(invoiceDate || status.invoiceDate)}
                      </Row>
                      <Row label="Invoice No. for TM-M before Opposition Hearing">
                        {status.invoiceNo?.trim() || (
                          <span className="text-[#6D6D6D]">
                            Auto-generated on save
                          </span>
                        )}
                      </Row>
                    </div>
                  </section>
                </div>
              );
            })()
          ) : previewTab === "pdf" ? (
            pdfUrl ? (
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages: n }) =>
                  onPreviewNumPagesChange(n)
                }
                loading={
                  <p className="text-sm text-[#6d6d6d]">Loading PDF…</p>
                }
                error={
                  <p className="text-sm text-red-600">Failed to load PDF.</p>
                }
                className="flex flex-col items-center gap-4"
              >
                {previewNumPages != null &&
                  Array.from({ length: previewNumPages }, (_, i) => (
                    <Page
                      key={i + 1}
                      pageNumber={i + 1}
                      width={Math.min(
                        800,
                        typeof window !== "undefined"
                          ? window.innerWidth - 48
                          : 800
                      )}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  ))}
              </Document>
            ) : (
              <p className="text-sm text-[#6D6D6D]">
                Generate the invoice PDF to preview it here.
              </p>
            )
          ) : previewTab === "message" ? (
            <div
              className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be sent to client WhatsApp after the invoice PDF
              </p>
              <WhatsAppMessageBlock
                message={messagePreview?.paymentMessage ?? ""}
                loading={messagePreviewLoading}
              />
            </div>
          ) : (
            <div
              className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be emailed to the client with the invoice PDF
              </p>
              <EmailPreviewBlock
                preview={messagePreview?.emailPreview ?? null}
                loading={messagePreviewLoading}
                attachmentFallback={
                  pdfUrl ? { filename: pdfFilename, url: pdfUrl } : null
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirmation from Client for Opposition Hearing — sub-components
// ---------------------------------------------------------------------------

function OppositionHearingConfirmationFields({
  status,
  draft,
  selectedTmNo,
  onPick,
}: {
  status: OppositionHearingConfirmationStatus | null;
  draft: Record<string, OppositionHearingClientConfirmation | null>;
  selectedTmNo: string | null;
  onPick: (
    tmNo: string,
    value: OppositionHearingClientConfirmation
  ) => void;
}) {
  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Confirmation from Client for Opposition Hearing…
      </p>
    );
  }
  const value = draft[selectedTmNo];
  const options: { id: OppositionHearingClientConfirmation; label: string }[] =
    [
      { id: "YES", label: "YES" },
      { id: "NO", label: "NO" },
      { id: "POSTPONE", label: "POSTPONE" },
    ];
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-row gap-3 flex-wrap">
        {options.map((opt) => {
          const isActive = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onPick(selectedTmNo, opt.id)}
              className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-[6px] h-9 min-w-[160px] text-xs transition-colors ${
                isActive
                  ? "bg-[#222222] text-white border border-[#222222]"
                  : "bg-[#FCFCFC] text-[#6D6D6D] border border-[#EEEEEE]"
              }`}
            >
              <CheckboxBox checked={isActive} />
              <span className="text-left">{opt.label}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="No. of applications for Opposition Hearing"
          value={String(status.noOfApplicationsForOppositionHearing ?? 0)}
        />
        <div className="hidden md:block flex-1" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payment from Client for TM-M before Opposition Hearing — sub-components
// ---------------------------------------------------------------------------

function TmMBeforeHearingPaymentFields({
  status,
  collectedAmount,
  onChange,
}: {
  status: TmMBeforeOppositionHearingInvoiceStatus | null;
  collectedAmount: string;
  onChange: (v: string) => void;
}) {
  if (!status) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading TM-M before Opposition Hearing Payment…
      </p>
    );
  }
  if (!status.isActive) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        This sub-step is only active when the client chooses POSTPONE in
        Confirmation from Client for Opposition Hearing.
      </p>
    );
  }
  const invoiceAmount = Number(status.invoiceAmount ?? 0);
  const savedCollected = Number(status.collectedAmount ?? 0) || 0;
  const thisPayment = parseAmount(collectedAmount);
  const totalCollected = savedCollected + thisPayment;
  const outstanding = Math.max(0, invoiceAmount - totalCollected);
  const outstandingFormatted = outstanding.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="Invoice Amount for TM-M before Opposition Hearing"
          value={
            status.invoiceAmount != null
              ? `Rs. ${Number(status.invoiceAmount).toLocaleString()}/-`
              : "—"
          }
        />
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Collected Amount for TM-M before Opposition Hearing
            <RequiredMark />
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={collectedAmount}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/[^\d.]/g, "");
              const parts = cleaned.split(".");
              const next =
                parts.length > 1
                  ? `${parts[0]}.${parts.slice(1).join("")}`
                  : cleaned;
              onChange(next);
            }}
            onKeyDown={(e) => {
              if (
                e.key.length === 1 &&
                !e.ctrlKey &&
                !e.metaKey &&
                !/[\d.]/.test(e.key)
              ) {
                e.preventDefault();
              }
            }}
            placeholder="0"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
          {savedCollected > 0 && (
            <span className="text-[11px] leading-4 text-[#6D6D6D]">
              Already collected: Rs.{" "}
              {savedCollected.toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
              /-
            </span>
          )}
        </div>
      </div>

      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 w-full px-4 sm:px-5 py-4 sm:py-0 min-h-[60px] sm:h-[60px]"
        style={{ background: "#222222", borderRadius: "8px" }}
      >
        <span className="text-[12px] sm:text-[14px] font-normal leading-5 text-white/90">
          OUTSTANDING AMOUNT FOR TM-M BEFORE OPPOSITION HEARING
        </span>
        <span className="text-[18px] sm:text-[22px] font-bold leading-8 sm:leading-10 text-white">
          Rs. {outstandingFormatted}/-
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// File TM-M before Opposition Hearing — sub-components
// ---------------------------------------------------------------------------

function TmMBeforeHearingFiledFields({
  status,
  perTm,
  selectedTmNo,
  onPatch,
  onPreviewExisting,
  onPreviewPending,
}: {
  status: TmMBeforeOppositionHearingFiledStatus | null;
  perTm: Record<string, { filingDate: string; pendingFile: File | null }>;
  selectedTmNo: string | null;
  onPatch: (
    tmNo: string,
    patch: Partial<{ filingDate: string; pendingFile: File | null }>
  ) => void;
  onPreviewExisting: (
    row: TmMBeforeOppositionHearingFiledApplicationRow
  ) => void;
  onPreviewPending: (
    row: TmMBeforeOppositionHearingFiledApplicationRow,
    file: File
  ) => void;
}) {
  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading File TM-M before Opposition Hearing…
      </p>
    );
  }
  if (!status.isActive) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        This sub-step is only active when the client chooses POSTPONE in
        Confirmation from Client for Opposition Hearing.
      </p>
    );
  }
  const tmRow = (status.applications ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const draft = perTm[selectedTmNo];
  if (!tmRow || !draft) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Selected TM is not on this TM-M list.
      </p>
    );
  }
  const dateParts = parseDateParts(draft.filingDate);
  const inputId = `tmm-before-opp-hearing-filed-input-${tmRow.tmNo}`;
  const hasFile = !!(
    draft.pendingFile || tmRow.tmMBeforeOppositionHearingFileKey
  );
  const fileName =
    draft.pendingFile?.name ??
    tmRow.tmMBeforeOppositionHearingFilename ??
    "File selected";

  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Filing of TM-M before Opposition Hearing
            <RequiredMark />
          </label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) =>
              onPatch(selectedTmNo, {
                filingDate: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
        <div className="hidden md:block flex-1" />
      </div>

      <div className="flex flex-col gap-3 border border-[#EEEEEE] rounded-[10px] p-4 md:p-5">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold text-[#101111]">
            TM-M before Opposition Hearing PDF
          </p>
          <p className="text-xs text-[#7D7D7D]">
            Upload the filed TM-M PDF downloaded from the Registry for this TM
            No.
          </p>
        </div>

        <div
          className={`flex flex-col md:flex-row items-stretch md:items-center gap-4 rounded-2xl border border-dashed px-4 py-4 cursor-pointer transition-colors ${
            hasFile
              ? "border-[#22C55E] bg-[#F0FDF4]"
              : "border-[#D8DAEB] bg-white"
          }`}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
            <img
              src={uploadDropzoneGraphic}
              alt=""
              width={60}
              height={60}
              className="flex-shrink-0"
            />
            <div className="flex flex-col gap-1 min-w-0 flex-1">
              {hasFile ? (
                <p className="text-sm font-medium text-[#16A34A] truncate">
                  {fileName}
                </p>
              ) : (
                <>
                  <p className="text-base font-medium text-[#101111]">
                    Drag and drop your file
                  </p>
                  <p className="text-sm text-[#7D7D7D]">
                    {SUPPORTED_FORMATS_LABEL}
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-row items-center gap-2 flex-shrink-0 self-end md:self-center">
            {!hasFile && (
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-sm font-medium text-[#2E3037] hover:bg-[#F5F5F5] transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById(inputId)?.click();
                }}
              >
                Select File
              </button>
            )}
            {hasFile && (
              <>
                <button
                  type="button"
                  className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (draft.pendingFile) {
                      onPreviewPending(tmRow, draft.pendingFile);
                    } else {
                      onPreviewExisting(tmRow);
                    }
                  }}
                >
                  Preview
                </button>
                <button
                  type="button"
                  className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPatch(selectedTmNo, { pendingFile: null });
                  }}
                >
                  Remove
                </button>
              </>
            )}
          </div>
        </div>

        <input
          id={inputId}
          type="file"
          accept={ALLOWED_FILE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reason = validateUploadFile(file);
            if (reason) {
              toast.error(reason);
              return;
            }
            onPatch(selectedTmNo, { pendingFile: file });
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inform Client about Filing of TM-M before Opposition Hearing — message preview
// ---------------------------------------------------------------------------

function InformTmMBeforeHearingMessagePreview({
  preview,
}: {
  preview: InformClientTmMBeforeOppositionHearingPreview | null;
}) {
  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-base font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Message Preview
      </span>
      <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[200px] bg-white">
        {preview?.message ? (
          <p
            className="text-xs text-[#222222] whitespace-pre-line"
            style={{ lineHeight: "20px" }}
          >
            {preview.message}
          </p>
        ) : (
          <p
            className="text-xs text-[#222222] whitespace-pre-line"
            style={{ lineHeight: "20px" }}
          >
            A &quot;Trademark Update&quot; message will be sent to the client
            informing them that the TM-M form has been filed to postpone the
            Opposition Hearing.
            {"\n\n"}
            <span className="text-[#6D6D6D]">
              The exact message body is generated server-side; click Send
              &amp; Continue to dispatch via WhatsApp.
            </span>
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payment from Client for Opposition Hearing — sub-components
// ---------------------------------------------------------------------------

function OppositionHearingPaymentFields({
  status,
  collectedAmount,
  onChange,
}: {
  status: OppositionHearingPaymentStatus | null;
  collectedAmount: string;
  onChange: (v: string) => void;
}) {
  if (!status) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Payment from Client for Opposition Hearing…
      </p>
    );
  }

  const invoiceAmount = Number(status.invoiceAmount ?? 0);
  const savedCollected = Number(status.collectedAmount ?? 0) || 0;
  const thisPayment = parseAmount(collectedAmount);
  const totalCollected = savedCollected + thisPayment;
  const outstanding = Math.max(0, invoiceAmount - totalCollected);
  const outstandingFormatted = outstanding.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return (
    <div
      className="flex flex-col items-start gap-[22px] w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-start gap-5 w-full">
        <CurrencyAmountField
          label="Invoice Amount for Opposition Hearing"
          value={formatAmount(invoiceAmount)}
          onChange={() => {}}
          readOnly
        />
        <div
          className="flex flex-col gap-1"
          style={{ flex: "1 1 0", minWidth: 0 }}
        >
          <CurrencyAmountField
            label="Collected Amount for Opposition Hearing"
            value={collectedAmount}
            onChange={onChange}
          />
          {savedCollected > 0 && (
            <span className="text-[11px] leading-4 text-[#6D6D6D]">
              Already collected: Rs.{" "}
              {savedCollected.toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
              /-
            </span>
          )}
        </div>
      </div>

      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 w-full px-4 sm:px-5 py-4 sm:py-0 min-h-[60px] sm:h-[60px]"
        style={{ background: "#222222", borderRadius: "8px" }}
      >
        <span className="text-[12px] sm:text-[14px] font-normal leading-5 text-white/90">
          OUTSTANDING AMOUNT FOR OPPOSITION HEARING
        </span>
        <span className="text-[18px] sm:text-[22px] font-bold leading-8 sm:leading-10 text-white">
          Rs. {outstandingFormatted}/-
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Note on Opposition Hearing (4A(xliii)) — read-mostly fields + editable notes
// ---------------------------------------------------------------------------

type NoteOnOppHearingDraftEntry = {
  noteOtherOpposition1: string;
  noteOtherOpposition2: string;
  similarMarkNotes: Array<{
    mark: string;
    tmNo: string;
    imageKey?: string | null;
    userDate?: string | null;
    goodsServices?: string | null;
    address?: string | null;
    note1?: string | null;
    note2?: string | null;
  }>;
};

function NoteOnOppHearingPreviewLine({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  const display = value && String(value).trim() ? String(value) : "";
  return (
    <div className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4">
      <span className="text-[#6D6D6D] md:w-[200px] md:shrink-0">{label}</span>
      <span className={display ? "text-[#222222]" : "text-[#6D6D6D]"}>
        {display || "—"}
      </span>
    </div>
  );
}

function NoteOnOppositionHearingFields({
  status,
  selectedIdx,
  onSelectIdx,
  draft,
  onPatch,
  onPreviewImage,
}: {
  status: NoteOnOppositionHearingStatus | null;
  selectedIdx: number;
  onSelectIdx: (idx: number) => void;
  draft: Record<string, NoteOnOppHearingDraftEntry>;
  onPatch: (
    ackApplicationId: string,
    patch: Partial<NoteOnOppHearingDraftEntry>
  ) => void;
  onPreviewImage: (url: string, name: string) => void;
}) {
  const [dataPreviewOpen, setDataPreviewOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (!status) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Note on Opposition Hearing…
      </p>
    );
  }
  const apps = status.applications ?? [];
  if (apps.length === 0) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        No applications in Opposition Hearing status for this acknowledgement.
      </p>
    );
  }
  const app = apps[Math.min(selectedIdx, apps.length - 1)];
  const d = draft[app.ackApplicationId];

  const objectionList: string[] = [];
  const r = app.objectionReasons;
  if (r?.similarity) objectionList.push("Similarity");
  if (r?.nonDistinctive) objectionList.push("Non-distinctive");
  if (r?.supportingDocuments) objectionList.push("Supporting Documents");
  if (r?.others)
    objectionList.push(
      r.othersSpecify ? `Others: ${r.othersSpecify}` : "Others"
    );
  const reasonForObjection = objectionList.join(", ");

  const similarMarksText = (app.similarMarks ?? [])
    .map((m) =>
      [m.mark, m.tmNo].filter(Boolean).join(" / ")
    )
    .filter(Boolean)
    .join(", ");

  const detailRows = [
    { label: "Mark", value: app.mark || "—" },
    {
      label: "Selected Class",
      value: app.classNumber ? String(app.classNumber) : "—",
    },
    { label: "Selected Type of Mark", value: app.typeOfMark || "—" },
    { label: "Goods/Services", value: app.goodsServices || "—" },
    { label: "User Date", value: app.userDate || "—" },
    { label: "User Date for Hearing", value: app.userDateForHearing || "—" },
    { label: "Reason for Objection", value: reasonForObjection || "—" },
    { label: "Similar Marks", value: similarMarksText || "—" },
    { label: "Acknowledgement No.", value: status.acknowledgementNo || "—" },
    {
      label: "Opposition No.",
      value: app.oppositionInfo?.oppositionNo || "—",
    },
    {
      label: "Date of Opposition",
      value: app.oppositionInfo?.dateOfOpposition || "—",
    },
    {
      label: "Name of Opponent",
      value: app.oppositionInfo?.nameOfOpponent || "—",
    },
    {
      label: "Address of Opponent",
      value: app.oppositionInfo?.addressOfOpponent || "—",
    },
  ];

  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {apps.length > 1 && (
        <div className="flex flex-row flex-wrap gap-2">
          {apps.map((a, idx) => {
            const isActive = idx === selectedIdx;
            return (
              <button
                key={a.ackApplicationId}
                type="button"
                onClick={() => onSelectIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-[6px] h-9 text-xs transition-colors ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                {`Note ${idx + 1}`}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-[#222222] leading-5">
          Note on overcoming Opposition
        </label>

        {(d?.similarMarkNotes ?? []).length > 0 && (
          <div className="flex flex-col gap-3">
            {(d?.similarMarkNotes ?? []).map((sm, idx) => (
              <div
                key={`${sm.mark}-${sm.tmNo}-${idx}`}
                className="border border-[#EEEEEE] rounded-[6px] p-3 flex flex-col gap-2"
              >
                <div className="text-xs font-medium text-[#222222]">
                  Note on Similarity
                  {sm.mark || sm.tmNo
                    ? ` — ${[sm.mark, sm.tmNo].filter(Boolean).join(" / ")}`
                    : ""}
                </div>
                <div className="flex flex-col md:flex-row gap-3">
                  <textarea
                    rows={3}
                    value={sm.note1 ?? ""}
                    onChange={(e) => {
                      const next = [...(d?.similarMarkNotes ?? [])];
                      next[idx] = { ...next[idx], note1: e.target.value };
                      onPatch(app.ackApplicationId, {
                        similarMarkNotes: next,
                      });
                    }}
                    placeholder="Note 1"
                    className="w-full px-2.5 py-2 text-xs text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white resize-y focus:outline-none focus:ring-1 focus:ring-[#222]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  />
                  <textarea
                    rows={3}
                    value={sm.note2 ?? ""}
                    onChange={(e) => {
                      const next = [...(d?.similarMarkNotes ?? [])];
                      next[idx] = { ...next[idx], note2: e.target.value };
                      onPatch(app.ackApplicationId, {
                        similarMarkNotes: next,
                      });
                    }}
                    placeholder="Note 2"
                    className="w-full px-2.5 py-2 text-xs text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white resize-y focus:outline-none focus:ring-1 focus:ring-[#222]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border border-[#EEEEEE] rounded-[6px] p-3 flex flex-col gap-2">
          <div className="text-xs font-medium text-[#222222]">
            Note on Other Opposition Points
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <textarea
              rows={3}
              value={d?.noteOtherOpposition1 ?? ""}
              onChange={(e) =>
                onPatch(app.ackApplicationId, {
                  noteOtherOpposition1: e.target.value,
                })
              }
              placeholder="Note 1"
              className="w-full px-2.5 py-2 text-xs text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white resize-y focus:outline-none focus:ring-1 focus:ring-[#222]"
              style={{ fontFamily: "Inter, sans-serif" }}
            />
            <textarea
              rows={3}
              value={d?.noteOtherOpposition2 ?? ""}
              onChange={(e) =>
                onPatch(app.ackApplicationId, {
                  noteOtherOpposition2: e.target.value,
                })
              }
              placeholder="Note 2"
              className="w-full px-2.5 py-2 text-xs text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white resize-y focus:outline-none focus:ring-1 focus:ring-[#222]"
              style={{ fontFamily: "Inter, sans-serif" }}
            />
          </div>
        </div>
      </div>

      <div className="w-full rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] overflow-hidden">
        <button
          type="button"
          onClick={() => setDetailsOpen((o) => !o)}
          className="flex w-full items-center justify-between px-3 py-2.5 text-[12px] font-medium text-[#222] hover:bg-[#F7F7F7] transition-colors touch-manipulation min-h-[44px] md:min-h-0"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <span>Fetched Details</span>
          <HiChevronDown
            className="w-4 h-4 text-[#6D6D6D] transition-transform duration-200 shrink-0"
            style={{
              transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </button>
        {detailsOpen && (
          <div className="border-t border-[#EEEEEE] flex flex-col gap-3 px-3 py-2.5">
            <div className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[12px] leading-5">
              {detailRows.map((row) => {
                const isEmpty = !row.value || row.value === "—";
                return (
                  <Fragment key={row.label}>
                    <div className="flex items-baseline font-medium text-[#222]">
                      {row.label}
                    </div>
                    <div className="flex items-baseline text-[#222]">:</div>
                    <div className="text-[#222] break-words min-w-0">
                      {isEmpty ? (
                        <span className="text-[#6D6D6D]">—</span>
                      ) : (
                        row.value
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </div>
            {app.imageOfMarkUrl && (
              <div className="flex flex-col gap-1 pt-1">
                <span className="text-[12px] font-medium text-[#222] leading-5">
                  Image of Mark
                </span>
                <button
                  type="button"
                  onClick={() =>
                    onPreviewImage(
                      app.imageOfMarkUrl ?? "",
                      `Mark - ${app.mark || app.tmNo}`
                    )
                  }
                  className="block w-full max-w-[640px] rounded-[6px] overflow-hidden border border-[#EEEEEE] bg-white cursor-zoom-in"
                >
                  <img
                    src={app.imageOfMarkUrl}
                    alt="Image of Mark"
                    className="w-full h-auto block"
                    style={{ maxHeight: 200, objectFit: "contain" }}
                  />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-row items-center gap-3 pt-1">
        <button
          type="button"
          onClick={() => setDataPreviewOpen(true)}
          className="h-9 px-4 rounded-[6px] bg-white border border-[#D5D3E0] text-[#2E3037] text-xs font-medium hover:bg-gray-50"
        >
          Preview
        </button>
      </div>

      {dataPreviewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setDataPreviewOpen(false)}
        >
          <div
            className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-4 py-3">
              <span className="text-sm font-medium text-[#222222]">
                Note on Opposition Hearing — Preview
              </span>
              <button
                type="button"
                onClick={() => setDataPreviewOpen(false)}
                className="flex size-8 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222]"
                aria-label="Close preview"
              >
                <HiOutlineX className="size-5" strokeWidth={2} />
              </button>
            </div>

            <div
              className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5 flex flex-col gap-3"
              style={{ fontSize: "12px", lineHeight: "20px", color: "#222222" }}
            >
              <NoteOnOppHearingPreviewLine
                label="Applicant"
                value={status.applicant}
              />
              <NoteOnOppHearingPreviewLine
                label="Application Number (TM No.)"
                value={app.tmNo}
              />
              <NoteOnOppHearingPreviewLine
                label="Date of Filing"
                value={app.dateOfFiling}
              />
              <NoteOnOppHearingPreviewLine
                label="Mark (Type)"
                value={
                  app.mark
                    ? `${app.mark}${app.typeOfMark ? ` (${app.typeOfMark})` : ""}`
                    : ""
                }
              />
              <div className="flex flex-col md:flex-row md:items-start gap-1 md:gap-4">
                <span className="text-[#6D6D6D] md:w-[200px] md:shrink-0">
                  Image of Mark
                </span>
                {app.imageOfMarkUrl ? (
                  <img
                    src={app.imageOfMarkUrl}
                    alt="Mark"
                    className="rounded-[4px] border border-[#EEEEEE]"
                    style={{ maxHeight: 80, objectFit: "contain" }}
                  />
                ) : (
                  <span className="text-[#6D6D6D]">—</span>
                )}
              </div>
              <NoteOnOppHearingPreviewLine
                label="Class"
                value={app.classNumber ? String(app.classNumber) : ""}
              />
              <NoteOnOppHearingPreviewLine
                label="Goods / Services"
                value={app.goodsServices}
              />
              <NoteOnOppHearingPreviewLine
                label="Opponent"
                value={app.oppositionInfo?.nameOfOpponent}
              />
              <NoteOnOppHearingPreviewLine
                label="Opponent's Address"
                value={app.oppositionInfo?.addressOfOpponent}
              />
              <NoteOnOppHearingPreviewLine
                label="Opposition No."
                value={app.oppositionInfo?.oppositionNo}
              />
              <NoteOnOppHearingPreviewLine
                label="Opposition Date"
                value={app.oppositionInfo?.dateOfOpposition}
              />
              <NoteOnOppHearingPreviewLine
                label="Hearing Date"
                value={app.dateOfOppositionHearing}
              />

              {(d?.similarMarkNotes ?? []).map((sm, i) => (
                <div
                  key={`prev-${sm.mark}-${sm.tmNo}-${i}`}
                  className="flex flex-col gap-1 pt-3 border-t border-[#EEEEEE]"
                >
                  <span className="font-medium text-[#222222]">
                    {i + 1}. Opponent's Mark :{" "}
                    <span className="font-normal">
                      {sm.mark || "—"}
                      {sm.tmNo ? ` (${sm.tmNo})` : ""}
                    </span>
                  </span>
                  <span className="text-[#222222] pl-3">
                    User Date : {sm.userDate || "—"}
                  </span>
                  <span className="text-[#222222] pl-3">
                    Goods / Services : {sm.goodsServices || "—"}
                  </span>
                  <span className="text-[#222222] pl-3">
                    Address : {sm.address || "—"}
                  </span>
                  <span className="text-[#222222] pl-3">
                    Note 1 : {sm.note1 || "—"}
                  </span>
                  <span className="text-[#222222] pl-3">
                    Note 2 : {sm.note2 || "—"}
                  </span>
                </div>
              ))}

              <div className="flex flex-col gap-1 pt-3 border-t border-[#EEEEEE]">
                <span className="font-medium text-[#222222]">
                  Note on Other Opposition Points
                </span>
                <span className="text-[#222222] pl-3">
                  Note 1 : {d?.noteOtherOpposition1 || "—"}
                </span>
                <span className="text-[#222222] pl-3">
                  Note 2 : {d?.noteOtherOpposition2 || "—"}
                </span>
              </div>
            </div>

            <div className="flex shrink-0 items-center justify-end border-t border-[#e3e3e3] px-4 py-3 gap-2">
              <button
                type="button"
                onClick={() => setDataPreviewOpen(false)}
                className="h-9 px-4 rounded-[6px] bg-[#222222] hover:bg-[#333] text-white text-xs font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attend Opposition Hearing (4A(xliv)) — Hearing Status + Officer Remark
// ---------------------------------------------------------------------------

const HEARING_STATUS_OPTIONS: Array<{
  value: AttendOppositionHearingStatusValue;
  label: string;
}> = [
  { value: "ATTENDED", label: "Attended" },
  { value: "NOT_ATTENDED", label: "Not Attended" },
  { value: "DIDNT_HAPPEN", label: "Didn't Happen" },
];

const HEARING_OFFICER_REMARK_OPTIONS: Array<{
  value: AttendOppositionHearingOfficerRemarkValue;
  label: string;
}> = [
  { value: "ACCEPTED", label: "Accepted" },
  { value: "REFUSED", label: "Refused" },
  { value: "WILL_PASS_ON_MERITS", label: "Will Pass on Merits" },
  { value: "FILE_TM_M", label: "File TM-M" },
  { value: "DIRECTION_OF_OFFICER", label: "Direction of Officer" },
];

function AttendOppositionHearingFields({
  status,
  selectedIdx,
  onSelectIdx,
  draft,
  onPatch,
}: {
  status: AttendOppositionHearingStatusResponse | null;
  selectedIdx: number;
  onSelectIdx: (idx: number) => void;
  draft: Record<
    string,
    {
      hearingStatus: AttendOppositionHearingStatusValue | null;
      hearingOfficerRemark: AttendOppositionHearingOfficerRemarkValue | null;
      directionOfOfficer: string;
    }
  >;
  onPatch: (
    ackApplicationId: string,
    patch: Partial<{
      hearingStatus: AttendOppositionHearingStatusValue | null;
      hearingOfficerRemark: AttendOppositionHearingOfficerRemarkValue | null;
      directionOfOfficer: string;
    }>
  ) => void;
}) {
  if (!status) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Attend Opposition Hearing…
      </p>
    );
  }
  const apps = status.applications ?? [];
  if (apps.length === 0) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        No applications in Opposition Hearing status for this acknowledgement.
      </p>
    );
  }
  const app = apps[Math.min(selectedIdx, apps.length - 1)];
  const d = draft[app.ackApplicationId];
  const remarkActive = d?.hearingStatus === "ATTENDED";
  const directionActive =
    remarkActive &&
    (d?.hearingOfficerRemark === "FILE_TM_M" ||
      d?.hearingOfficerRemark === "DIRECTION_OF_OFFICER");

  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {apps.length > 1 && (
        <div className="flex flex-row flex-wrap gap-2">
          {apps.map((a, idx) => {
            const isActive = idx === selectedIdx;
            return (
              <button
                key={a.ackApplicationId}
                type="button"
                onClick={() => onSelectIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-[6px] h-9 text-xs transition-colors ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                {formatTmNoForDisplay(a.tmNo)}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Hearing Status
            <RequiredMark />
          </label>
          <Select
            value={d?.hearingStatus ?? "__none__"}
            onValueChange={(v) =>
              onPatch(app.ackApplicationId, {
                hearingStatus:
                  v === "__none__"
                    ? null
                    : (v as AttendOppositionHearingStatusValue),
                // Clear remark when hearing status leaves ATTENDED
                hearingOfficerRemark:
                  v === "ATTENDED" ? d?.hearingOfficerRemark ?? null : null,
                directionOfOfficer:
                  v === "ATTENDED" ? d?.directionOfOfficer ?? "" : "",
              })
            }
          >
            <SelectTrigger className="h-9 min-h-[36px] w-full border border-[#EEEEEE] rounded-[6px] px-2.5 py-2 text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {HEARING_STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Remark of Hearing Officer
            {remarkActive && <RequiredMark />}
          </label>
          <Select
            value={d?.hearingOfficerRemark ?? "__none__"}
            onValueChange={(v) =>
              onPatch(app.ackApplicationId, {
                hearingOfficerRemark:
                  v === "__none__"
                    ? null
                    : (v as AttendOppositionHearingOfficerRemarkValue),
                directionOfOfficer:
                  v === "FILE_TM_M" || v === "DIRECTION_OF_OFFICER"
                    ? d?.directionOfOfficer ?? ""
                    : "",
              })
            }
            disabled={!remarkActive}
          >
            <SelectTrigger className="h-9 min-h-[36px] w-full border border-[#EEEEEE] rounded-[6px] px-2.5 py-2 text-xs text-[#222] data-[placeholder]:text-[#6D6D6D] disabled:opacity-60">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {HEARING_OFFICER_REMARK_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {directionActive && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Direction of Officer
            {d?.hearingOfficerRemark === "DIRECTION_OF_OFFICER" && (
              <RequiredMark />
            )}
          </label>
          <textarea
            rows={3}
            value={d?.directionOfOfficer ?? ""}
            onChange={(e) =>
              onPatch(app.ackApplicationId, {
                directionOfOfficer: e.target.value,
              })
            }
            placeholder="Text Here"
            className="w-full px-2.5 py-2 text-xs text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white resize-y focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif" }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subsequent Opposition Hearings (4A(l))
// ---------------------------------------------------------------------------

const HEARING_NUMBER_ORDINAL: Record<number, string> = {
  2: "2nd",
  3: "3rd",
  4: "4th",
  5: "5th",
  6: "6th",
  7: "7th",
};

function hearingOrdinal(n: number): string {
  return HEARING_NUMBER_ORDINAL[n] ?? `${n}th`;
}

type SubsequentOppHearingDraftShape = {
  dateOfHearing: string;
  pendingNoticeFile: File | null;
  hearingStatus: AttendOppositionHearingStatusValue | null;
  hearingOfficerRemark: AttendOppositionHearingOfficerRemarkValue | null;
  directionOfOfficer: string;
  pendingPostponementFile: File | null;
};

function SubsequentOppHearingFields({
  status,
  draft,
  onPatch,
  onSendNotice,
  sendingNotice,
  noticePreview,
  noticePreviewError,
}: {
  status: SubsequentOppositionHearingStatus | null;
  draft: Record<string, SubsequentOppHearingDraftShape>;
  onPatch: (
    tmNo: string,
    patch: Partial<SubsequentOppHearingDraftShape>
  ) => void;
  onSendNotice: () => void;
  sendingNotice: boolean;
  noticePreview: SubsequentOppositionHearingNoticePreview | null;
  noticePreviewError: string | null;
}) {
  if (!status) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Subsequent Opposition Hearings…
      </p>
    );
  }
  const eligibleApps = (status.applications ?? []).filter((a) => a.isActive);
  if (eligibleApps.length === 0) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Subsequent Opposition Hearings appear here when a prior hearing was Not
        Attended, Didn't Happen, or the officer directed File TM-M / further
        directions. Currently no applications need a subsequent hearing.
      </p>
    );
  }

  const anyDateEntered = eligibleApps.some(
    (a) => (draft[a.tmNo]?.dateOfHearing ?? "") !== ""
  );

  return (
    <div
      className="flex flex-col gap-6"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-row items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-[#222222]">
          {hearingOrdinal(status.currentHearingNumber)} Opposition Hearing
        </span>
        {status.acknowledgementNo && (
          <span className="text-xs text-[#6D6D6D]">
            Acknowledgement No.: {status.acknowledgementNo}
          </span>
        )}
      </div>

      {eligibleApps.map((app) => {
        const d = draft[app.tmNo] ?? {
          dateOfHearing: "",
          pendingNoticeFile: null,
          hearingStatus: null,
          hearingOfficerRemark: null,
          directionOfOfficer: "",
          pendingPostponementFile: null,
        };
        const remarkActive = d.hearingStatus === "ATTENDED";
        const directionActive =
          remarkActive &&
          (d.hearingOfficerRemark === "FILE_TM_M" ||
            d.hearingOfficerRemark === "DIRECTION_OF_OFFICER");
        const showPostponement = d.hearingStatus === "DIDNT_HAPPEN";

        return (
          <div
            key={app.tmNo}
            className="flex flex-col gap-4 p-4 border border-[#EEEEEE] rounded-[6px]"
          >
            <div className="flex flex-row items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-[#222222]">
                {formatTmNoForDisplay(app.tmNo)}
              </span>
              {app.mark && (
                <span className="text-xs text-[#6D6D6D]">— {app.mark}</span>
              )}
            </div>

            <div className="flex flex-col md:flex-row gap-4 md:gap-5">
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                <label className="text-xs font-normal text-[#222222] leading-5">
                  Date of Hearing
                  <RequiredMark />
                </label>
                <input
                  type="date"
                  value={d.dateOfHearing}
                  onChange={(e) =>
                    onPatch(app.tmNo, { dateOfHearing: e.target.value })
                  }
                  className="h-9 min-h-[36px] w-full border border-[#EEEEEE] rounded-[6px] px-2.5 py-2 text-xs text-[#222]"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                <label className="text-xs font-normal text-[#222222] leading-5">
                  Hearing Notice PDF
                </label>
                <input
                  type="file"
                  accept={ALLOWED_FILE_ACCEPT}
                  onChange={(e) =>
                    onPatch(app.tmNo, {
                      pendingNoticeFile: e.target.files?.[0] ?? null,
                    })
                  }
                  className="text-xs text-[#222]"
                />
                {!d.pendingNoticeFile && app.hearingNoticeFilename && (
                  <span className="text-xs text-[#6D6D6D]">
                    Current: {app.hearingNoticeFilename}
                    {app.hearingNoticeFileUrl && (
                      <>
                        {" — "}
                        <a
                          href={app.hearingNoticeFileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          View
                        </a>
                      </>
                    )}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 md:gap-5">
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                <label className="text-xs font-normal text-[#222222] leading-5">
                  Hearing Status
                </label>
                <Select
                  value={d.hearingStatus ?? "__none__"}
                  onValueChange={(v) =>
                    onPatch(app.tmNo, {
                      hearingStatus:
                        v === "__none__"
                          ? null
                          : (v as AttendOppositionHearingStatusValue),
                      hearingOfficerRemark:
                        v === "ATTENDED" ? d.hearingOfficerRemark : null,
                      directionOfOfficer:
                        v === "ATTENDED" ? d.directionOfOfficer : "",
                      pendingPostponementFile:
                        v === "DIDNT_HAPPEN"
                          ? d.pendingPostponementFile
                          : null,
                    })
                  }
                >
                  <SelectTrigger className="h-9 min-h-[36px] w-full border border-[#EEEEEE] rounded-[6px] px-2.5 py-2 text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {HEARING_STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                <label className="text-xs font-normal text-[#222222] leading-5">
                  Remark of Hearing Officer
                  {remarkActive && <RequiredMark />}
                </label>
                <Select
                  value={d.hearingOfficerRemark ?? "__none__"}
                  onValueChange={(v) =>
                    onPatch(app.tmNo, {
                      hearingOfficerRemark:
                        v === "__none__"
                          ? null
                          : (v as AttendOppositionHearingOfficerRemarkValue),
                      directionOfOfficer:
                        v === "FILE_TM_M" || v === "DIRECTION_OF_OFFICER"
                          ? d.directionOfOfficer
                          : "",
                    })
                  }
                  disabled={!remarkActive}
                >
                  <SelectTrigger className="h-9 min-h-[36px] w-full border border-[#EEEEEE] rounded-[6px] px-2.5 py-2 text-xs text-[#222] data-[placeholder]:text-[#6D6D6D] disabled:opacity-60">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {HEARING_OFFICER_REMARK_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {directionActive && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-normal text-[#222222] leading-5">
                  Direction of Officer
                  {d.hearingOfficerRemark === "DIRECTION_OF_OFFICER" && (
                    <RequiredMark />
                  )}
                </label>
                <textarea
                  rows={3}
                  value={d.directionOfOfficer}
                  onChange={(e) =>
                    onPatch(app.tmNo, { directionOfOfficer: e.target.value })
                  }
                  placeholder="Text Here"
                  className="w-full px-2.5 py-2 text-xs text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white resize-y focus:outline-none focus:ring-1 focus:ring-[#222]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                />
              </div>
            )}

            {showPostponement && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-normal text-[#222222] leading-5">
                  Notice of Postponement PDF
                </label>
                <input
                  type="file"
                  accept={ALLOWED_FILE_ACCEPT}
                  onChange={(e) =>
                    onPatch(app.tmNo, {
                      pendingPostponementFile: e.target.files?.[0] ?? null,
                    })
                  }
                  className="text-xs text-[#222]"
                />
                {!d.pendingPostponementFile &&
                  app.postponementNoticeFilename && (
                    <span className="text-xs text-[#6D6D6D]">
                      Current: {app.postponementNoticeFilename}
                      {app.postponementNoticeFileUrl && (
                        <>
                          {" — "}
                          <a
                            href={app.postponementNoticeFileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="underline"
                          >
                            View
                          </a>
                        </>
                      )}
                    </span>
                  )}
              </div>
            )}
          </div>
        );
      })}

      {anyDateEntered && (
        <div className="flex flex-col gap-3 w-full">
          <div className="flex flex-col gap-2.5">
            <span
              className="text-base font-normal text-[#222222]"
              style={{ lineHeight: "20px" }}
            >
              Message Preview
            </span>
            <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[120px] bg-white">
              {noticePreview?.message ? (
                <p
                  className="text-xs text-[#222222] whitespace-pre-line"
                  style={{ lineHeight: "20px" }}
                >
                  {renderWhatsAppFormatting(noticePreview.message)}
                </p>
              ) : noticePreviewError ? (
                <p
                  className="text-xs text-[#B45309] whitespace-pre-line"
                  style={{ lineHeight: "20px" }}
                >
                  {noticePreviewError}
                </p>
              ) : (
                <p
                  className="text-xs text-[#6D6D6D] whitespace-pre-line"
                  style={{ lineHeight: "20px" }}
                >
                  Save the Date of Hearing first to load the message preview, or
                  click Inform Client to save and send in one step.
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-row items-center gap-3 w-full">
            <button
              type="button"
              disabled={sendingNotice}
              onClick={onSendNotice}
              className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sendingNotice
                ? "Sending…"
                : "Inform Client about Subsequent Opposition Hearing"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subsequent Opposition Hearing Invoice (4A(xxxiii) for subsequent)
// ---------------------------------------------------------------------------

function SubsequentOppHearingInvoiceFields({
  status,
  invoiceAmount,
  onInvoiceAmountChange,
  invoiceDate,
  onInvoiceDateChange,
  includeInvoiceNo,
  onIncludeInvoiceNoChange,
  generating,
  downloading,
  sending,
  onGeneratePdf,
  onDownloadPdf,
  onSendInvoice,
}: {
  status: SubsequentOppositionHearingInvoiceStatus | null;
  invoiceAmount: string;
  onInvoiceAmountChange: (v: string) => void;
  invoiceDate: string;
  onInvoiceDateChange: (v: string) => void;
  includeInvoiceNo: boolean;
  onIncludeInvoiceNoChange: (v: boolean) => void;
  generating: boolean;
  downloading: boolean;
  sending: boolean;
  onGeneratePdf: () => void;
  onDownloadPdf: () => void;
  onSendInvoice: () => void;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);

  if (!status) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Subsequent Opposition Hearing Invoice…
      </p>
    );
  }
  if (!status.isActive) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Invoice for Subsequent Opposition Hearing is not active. Save details
        for the Subsequent Hearing first (Date of Hearing for at least one
        application).
      </p>
    );
  }
  const dateParts = parseDateParts(invoiceDate);
  const hasPdf = !!status.invoicePdfKey;
  const busy = generating || downloading || sending;

  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-row items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-[#222222]">
          {hearingOrdinal(status.hearingNumber)} Opposition Hearing Invoice
        </span>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="No. of Applications for Opposition Hearing"
          value={String(status.noOfApps ?? 0)}
        />
        <ReadOnlyField
          label="Invoice No. for Subsequent Opposition Hearing"
          value={status.invoiceNo ?? "—"}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            <span className="inline-flex items-center gap-1">
              Invoice Amount for Subsequent Opposition Hearing
              <RequiredMark />
            </span>
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={invoiceAmount}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                onInvoiceAmountChange(v);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            placeholder="0"
            className="h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border"
          />
          <span className="text-[11px] leading-4 text-[#6D6D6D]">
            Default: Rs. 5000 × {status.noOfApps ?? 0} (No. of Apps) = Rs.{" "}
            {(5000 * (status.noOfApps ?? 0)).toLocaleString("en-IN")}/-
          </span>
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Invoice
          </label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) => onInvoiceDateChange(d ? toLocalYmd(d) : "")}
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Checkbox
            id="subsequent-opp-hearing-include-invoice-no"
            checked={includeInvoiceNo}
            onCheckedChange={(checked) =>
              onIncludeInvoiceNoChange(Boolean(checked))
            }
          />
          <label
            htmlFor="subsequent-opp-hearing-include-invoice-no"
            className="text-xs text-[#222222] cursor-pointer"
          >
            Include Invoice No. in PDF
          </label>
        </div>
        <span className="text-[11px] leading-4 text-[#6D6D6D] pl-6">
          Tick this to print the auto-generated invoice number on the PDF.
          Leave unchecked for a draft/no-number invoice.
        </span>
      </div>

      <div className="flex flex-row items-center gap-3 pt-1 flex-wrap">
        <button
          type="button"
          onClick={onGeneratePdf}
          disabled={busy}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {generating
            ? "Generating…"
            : hasPdf
            ? "Re-generate Invoice PDF"
            : "Generate Invoice PDF"}
        </button>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={busy}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {downloading ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={() => {
            setPreviewNumPages(null);
            setPreviewOpen(true);
          }}
          disabled={!hasPdf || busy}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
          title={
            hasPdf
              ? "Preview the generated invoice PDF"
              : "Generate the invoice PDF first to preview it"
          }
        >
          Preview Invoice
        </button>
        <button
          type="button"
          onClick={onSendInvoice}
          disabled={busy}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {sending ? "Sending…" : "Send Invoice to Client"}
        </button>
      </div>

      {previewOpen && status.invoicePdfUrl && (
        <SubsequentOppHearingInvoicePreviewModal
          pdfUrl={status.invoicePdfUrl}
          hearingNumber={status.hearingNumber}
          numPages={previewNumPages}
          onNumPagesChange={setPreviewNumPages}
          onClose={() => {
            setPreviewOpen(false);
            setPreviewNumPages(null);
          }}
        />
      )}
    </div>
  );
}

function SubsequentOppHearingInvoicePreviewModal({
  pdfUrl,
  hearingNumber,
  numPages,
  onNumPagesChange,
  onClose,
}: {
  pdfUrl: string;
  hearingNumber: number;
  numPages: number | null;
  onNumPagesChange: (n: number | null) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="bg-white rounded-[12px] shadow-xl flex flex-col w-full max-w-[860px] max-h-[90vh] overflow-hidden"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="flex flex-row items-center justify-between border-b border-[#EEEEEE] px-4 py-3">
          <span className="text-sm font-semibold text-[#222222]">
            {hearingOrdinal(hearingNumber)} Opposition Hearing Invoice — Preview
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="inline-flex items-center justify-center w-8 h-8 rounded-[6px] hover:bg-[#F5F5F5] text-[#6D6D6D]"
          >
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-[#F7F7F7]">
          <div className="flex flex-col items-center gap-3">
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages: n }) => onNumPagesChange(n)}
              loading={
                <p className="text-xs text-[#6D6D6D]">Loading PDF…</p>
              }
              error={
                <p className="text-xs text-[#B45309]">
                  Failed to load PDF preview. Use Download to view it.
                </p>
              }
            >
              {Array.from({ length: numPages ?? 0 }, (_, i) => (
                <div
                  key={`page_${i + 1}`}
                  className="bg-white shadow-sm mb-3"
                >
                  <Page
                    pageNumber={i + 1}
                    width={780}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </div>
              ))}
            </Document>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2 border-t border-[#EEEEEE] px-4 py-3">
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5]"
          >
            Open in new tab
          </a>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-white bg-[#222222] rounded-[10px] hover:bg-[#333]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subsequent Opposition Hearing Payment (4A(xl) for subsequent)
// ---------------------------------------------------------------------------

function SubsequentOppHearingPaymentFields({
  status,
  collectedAmount,
  onChange,
}: {
  status: SubsequentOppositionHearingInvoiceStatus | null;
  collectedAmount: string;
  onChange: (v: string) => void;
}) {
  if (!status) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Subsequent Opposition Hearing Payment…
      </p>
    );
  }
  if (!status.isActive) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        This sub-step is active only when a Subsequent Opposition Hearing
        invoice has been generated.
      </p>
    );
  }
  const invoiceAmount = Number(status.invoiceAmount ?? 0);
  const savedCollected = Number(status.collectedAmount ?? 0) || 0;
  const thisPayment = parseAmount(collectedAmount);
  const totalCollected = savedCollected + thisPayment;
  const outstanding = Math.max(0, invoiceAmount - totalCollected);
  const outstandingFormatted = outstanding.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-row items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-[#222222]">
          {hearingOrdinal(status.hearingNumber)} Opposition Hearing Payment
        </span>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="Invoice Amount for Subsequent Opposition Hearing"
          value={
            status.invoiceAmount != null
              ? `Rs. ${Number(status.invoiceAmount).toLocaleString()}/-`
              : "—"
          }
        />
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Collected Amount
            <RequiredMark />
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={collectedAmount}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/[^\d.]/g, "");
              const parts = cleaned.split(".");
              const next =
                parts.length > 1
                  ? `${parts[0]}.${parts.slice(1).join("")}`
                  : cleaned;
              onChange(next);
            }}
            onKeyDown={(e) => {
              if (
                e.key.length === 1 &&
                !e.ctrlKey &&
                !e.metaKey &&
                !/[\d.]/.test(e.key)
              ) {
                e.preventDefault();
              }
            }}
            placeholder="0"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
          {savedCollected > 0 && (
            <span className="text-[11px] leading-4 text-[#6D6D6D]">
              Already collected: Rs.{" "}
              {savedCollected.toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
              /-
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="Outstanding Amount"
          value={`Rs. ${outstandingFormatted}/-`}
        />
        <div className="hidden md:block flex-1" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inform Client after Opposition Hearing (4A(xlv)) — read-only display
// ---------------------------------------------------------------------------

const ATTEND_HEARING_STATUS_LABELS: Record<
  AttendOppositionHearingStatusValue,
  string
> = {
  ATTENDED: "Attended",
  NOT_ATTENDED: "Not Attended",
  DIDNT_HAPPEN: "Didn't Happen",
};

const ATTEND_HEARING_OFFICER_REMARK_LABELS: Record<
  AttendOppositionHearingOfficerRemarkValue,
  string
> = {
  ACCEPTED: "Accepted",
  REFUSED: "Refused",
  WILL_PASS_ON_MERITS: "Will Pass on Merits",
  FILE_TM_M: "File TM-M",
  DIRECTION_OF_OFFICER: "Direction of Officer",
};

function InformAfterOppositionHearingFields({
  status,
}: {
  status: InformAfterOppositionHearingStatus | null;
}) {
  if (!status) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Inform Client after Opposition Hearing…
      </p>
    );
  }
  const apps = status.applications ?? [];
  if (apps.length === 0) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        No applications have Opposition Hearing data yet.
      </p>
    );
  }
  const app = apps[0];
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="Acknowledgement No."
          value={status.acknowledgementNo ?? ""}
        />
        <ReadOnlyField label="Date of Filing" value={app.dateOfFiling ?? ""} />
      </div>
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField label="Mark" value={app.mark ?? ""} />
        <ReadOnlyField
          label="Hearing Status"
          value={
            app.hearingStatus
              ? ATTEND_HEARING_STATUS_LABELS[app.hearingStatus]
              : ""
          }
        />
      </div>
      <ReadOnlyField
        label="Remark of Hearing Officer"
        value={
          app.hearingOfficerRemark
            ? ATTEND_HEARING_OFFICER_REMARK_LABELS[app.hearingOfficerRemark]
            : ""
        }
      />
      {status.groups?.length > 0 && (
        <div className="flex flex-col gap-3">
          {status.groups.map((g) => (
            <div
              key={g.scenario}
              className="rounded-[8px] border border-[#EEEEEE] bg-[#FCFCFC] p-3"
            >
              <div className="text-xs font-medium text-[#222222] mb-1">
                {g.label}
              </div>
              <div className="text-xs text-[#6D6D6D] whitespace-pre-wrap">
                {g.message || "—"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invoice for TM-M after Opposition Hearing (4A(xlvi)) — invoice form
// ---------------------------------------------------------------------------

const TM_M_AFTER_HEARING_RATE_PER_APP = 1500;

function TmMAfterHearingInvoiceAmountTooltip({
  defaultAmount,
  noOfApps,
}: {
  defaultAmount: number | null;
  noOfApps: number;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const formatted =
    defaultAmount != null
      ? `Rs. ${Math.round(Number(defaultAmount)).toLocaleString("en-IN")}/-`
      : "—";

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is the invoice amount calculated?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 text-[#6D6D6D] hover:text-[#222] focus:outline-none focus:ring-1 focus:ring-[#222] rounded-full"
      >
        <HiOutlineInformationCircle className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[320px] rounded-[8px] border border-[#EEEEEE] bg-white p-3 shadow-lg"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-[11px] font-semibold leading-4 text-[#222]">
            How the invoice amount is calculated
          </p>
          <p className="mt-1 text-[11px] leading-4 text-[#6D6D6D]">
            Rs. {TM_M_AFTER_HEARING_RATE_PER_APP.toLocaleString("en-IN")}/- per
            Application Number (TM No.), multiplied by the No. of applications
            for Opposition Hearing.
          </p>
          <div className="mt-2 flex flex-col gap-1 text-[11px] leading-4 text-[#222]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Hearing charge</span>
              <span className="font-medium">
                Rs.{" "}
                {TM_M_AFTER_HEARING_RATE_PER_APP.toLocaleString("en-IN")}/- per
                app
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">
                No. of applications for Opposition Hearing
              </span>
              <span className="font-medium">{noOfApps}</span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Calculation</span>
              <span className="font-medium">
                {TM_M_AFTER_HEARING_RATE_PER_APP.toLocaleString("en-IN")} ×{" "}
                {noOfApps}
              </span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-[#EEEEEE] pt-1.5">
              <span className="font-semibold text-[#222]">
                Default invoice amount
              </span>
              <span className="font-semibold text-[#222]">{formatted}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TmMAfterHearingInvoiceFields({
  leadId,
  status,
  preview,
  draft,
  generating,
  downloading,
  onPatch,
  onGeneratePdf,
  onDownloadPdf,
  onSaveDraft,
}: {
  leadId: string | null;
  status: TmMAfterOppositionHearingInvoiceStatus | null;
  preview: TmMAfterOppositionHearingInvoicePreview | null;
  draft: { invoiceAmount: string; invoiceDate: string };
  generating: boolean;
  downloading: boolean;
  onPatch: (
    patch: Partial<{ invoiceAmount: string; invoiceDate: string }>
  ) => void;
  onGeneratePdf: () => void;
  onDownloadPdf: () => void;
  onSaveDraft: () => Promise<void>;
}) {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewOpening, setPreviewOpening] = useState(false);
  const [previewTab, setPreviewTab] = useState<
    "details" | "pdf" | "message" | "email"
  >("details");
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);
  const [messagePreview, setMessagePreview] =
    useState<TmMAfterOppositionHearingInvoiceWhatsAppPreview | null>(null);
  const [messagePreviewLoading, setMessagePreviewLoading] = useState(false);

  useEffect(() => {
    if (!previewModalOpen || !leadId) return;
    let cancelled = false;
    setMessagePreviewLoading(true);
    getTmMAfterOppositionHearingInvoiceWhatsAppPreview(leadId)
      .then((data) => {
        if (!cancelled) setMessagePreview(data);
      })
      .catch(() => {
        if (!cancelled) setMessagePreview(null);
      })
      .finally(() => {
        if (!cancelled) setMessagePreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    previewModalOpen,
    leadId,
    status?.tmMAfterOppositionHearingInvoicePdfKey,
  ]);

  if (!status) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading TM-M after Opposition Hearing Invoice…
      </p>
    );
  }
  if (!status.isActive) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        This sub-step is only active when 4A(xliv) Hearing officer remark is
        "File TM-M".
      </p>
    );
  }
  const dateParts = parseDateParts(draft.invoiceDate);
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            <span className="inline-flex items-center gap-1">
              Invoice Amount for TM-M after Opposition Hearing
              <TmMAfterHearingInvoiceAmountTooltip
                defaultAmount={
                  status.defaultInvoiceAmount ??
                  preview?.invoiceAmount ??
                  status.invoiceAmount ??
                  null
                }
                noOfApps={status.noOfAppsForTmMAfterOppositionHearing ?? 0}
              />
              <RequiredMark />
            </span>
          </label>
          <input
            type="number"
            min={0}
            value={draft.invoiceAmount}
            onChange={(e) => onPatch({ invoiceAmount: e.target.value })}
            placeholder="0"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
        </div>
        <ReadOnlyField
          label="Invoice No. for TM-M after Opposition Hearing"
          value={status.invoiceNo ?? ""}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Invoice for TM-M after Opposition Hearing
          </label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) =>
              onPatch({ invoiceDate: d ? toLocalYmd(d) : "" })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
        <ReadOnlyField
          label="No. of applications for Opposition Hearing"
          value={String(status.noOfAppsForTmMAfterOppositionHearing ?? 0)}
        />
      </div>

      <div className="flex flex-row flex-wrap items-center gap-3 pt-1">
        <button
          type="button"
          onClick={onGeneratePdf}
          disabled={generating}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {generating ? "Generating…" : "Generate Invoice PDF"}
        </button>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={downloading || generating}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {downloading ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={async () => {
            if (previewOpening || generating) return;
            setPreviewOpening(true);
            try {
              await onSaveDraft();
            } finally {
              setPreviewOpening(false);
              setPreviewTab("details");
              setPreviewNumPages(null);
              setPreviewModalOpen(true);
            }
          }}
          disabled={generating || previewOpening}
          className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {previewOpening ? "Saving…" : "Preview Invoice"}
        </button>
      </div>

      {previewModalOpen && (
        <TmMAfterHearingInvoicePreviewModal
          previewTab={previewTab}
          onTabChange={setPreviewTab}
          previewNumPages={previewNumPages}
          onPreviewNumPagesChange={setPreviewNumPages}
          status={status}
          preview={preview}
          invoiceDate={draft.invoiceDate}
          invoiceAmount={draft.invoiceAmount}
          messagePreview={messagePreview}
          messagePreviewLoading={messagePreviewLoading}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewNumPages(null);
            setPreviewTab("details");
          }}
        />
      )}
    </div>
  );
}

function TmMAfterHearingInvoicePreviewModal({
  previewTab,
  onTabChange,
  previewNumPages,
  onPreviewNumPagesChange,
  status,
  preview,
  invoiceDate,
  invoiceAmount,
  messagePreview,
  messagePreviewLoading,
  onClose,
}: {
  previewTab: "details" | "pdf" | "message" | "email";
  onTabChange: (tab: "details" | "pdf" | "message" | "email") => void;
  previewNumPages: number | null;
  onPreviewNumPagesChange: (n: number | null) => void;
  status: TmMAfterOppositionHearingInvoiceStatus;
  preview: TmMAfterOppositionHearingInvoicePreview | null;
  invoiceDate: string;
  invoiceAmount: string;
  messagePreview: TmMAfterOppositionHearingInvoiceWhatsAppPreview | null;
  messagePreviewLoading: boolean;
  onClose: () => void;
}) {
  const pdfUrl = status.tmMAfterOppositionHearingInvoicePdfUrl ?? null;
  const pdfFilename = "Invoice for TM-M after Opposition Hearing.pdf";

  const formatRupees = (n: number | string | null | undefined) => {
    const v = typeof n === "string" ? Number(n) : Number(n ?? 0);
    return `Rs. ${Math.max(0, Math.round(Number.isFinite(v) ? v : 0)).toLocaleString("en-IN")}/-`;
  };
  const formatDateDisplay = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return y && m && d ? `${d}-${m}-${y}` : iso;
  };

  const tabs: Array<{
    id: "details" | "pdf" | "message" | "email";
    label: string;
    disabled?: boolean;
  }> = [
    { id: "details", label: "Details" },
    { id: "pdf", label: "Generated PDF", disabled: !pdfUrl },
    { id: "message", label: "WhatsApp Message" },
    { id: "email", label: "Email" },
  ];

  const Row = ({
    label,
    children,
    alignTop,
  }: {
    label: string;
    children: React.ReactNode;
    alignTop?: boolean;
  }) => (
    <>
      <div
        className={`flex ${alignTop ? "items-start" : "items-baseline"} font-medium text-[#222]`}
      >
        {label}
      </div>
      <div
        className={`flex ${alignTop ? "items-start" : "items-baseline"} text-[#222]`}
      >
        :
      </div>
      <div className="text-[#222] break-words min-w-0">{children}</div>
    </>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
          <span
            className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {pdfFilename}
          </span>
          <div className="flex items-center gap-3 shrink-0">
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#2E3037] underline underline-offset-2"
              >
                Open in new tab
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
              aria-label="Close preview"
            >
              <HiOutlineX className="size-5" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div
          className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
          role="tablist"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={previewTab === tab.id}
              disabled={tab.disabled}
              onClick={() => onTabChange(tab.id)}
              className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
                previewTab === tab.id
                  ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                  : "text-[#6d6d6d] hover:text-[#222]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4 flex justify-center">
          {previewTab === "details" ? (
            (() => {
              const addresseeLines = [
                preview?.addresseeLine1,
                preview?.addresseeLine2,
                preview?.addresseeLine3,
              ]
                .map((line) => line?.trim())
                .filter((line): line is string => Boolean(line));
              const fallbackAddresseeLines = (preview?.addressee ?? "")
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean);
              const resolvedAddresseeLines =
                addresseeLines.length > 0
                  ? addresseeLines
                  : fallbackAddresseeLines;
              return (
                <div
                  className="flex w-full max-w-3xl flex-col gap-3"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                      Data for Invoice for TM-M after Opposition Hearing
                    </p>
                    <div
                      className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      <Row label="Addressee" alignTop>
                        {resolvedAddresseeLines.length > 0 ? (
                          <div className="flex flex-col">
                            {resolvedAddresseeLines.map((line, i) => (
                              <span key={i}>{line}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </Row>
                      <Row label="Form of Address">
                        {preview?.formOfAddress?.trim() || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </Row>
                    </div>
                  </section>

                  {preview && preview.applications.length > 0 && (
                    <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                        Applications
                      </p>
                      <div className="flex flex-col gap-2">
                        {preview.applications.map((app, idx) => (
                          <div
                            key={`${app.tmNo}-${idx}`}
                            className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
                            style={{ fontFamily: "Inter, sans-serif" }}
                          >
                            <Row label="Application Number (TM No.)">
                              {formatTmNoForDisplay(app.tmNo)}
                            </Row>
                            <Row label="Date of Filing">
                              {formatDateDisplay(app.applicationDate)}
                            </Row>
                            <Row label="Mark (Type)">
                              {(() => {
                                const markText = app.mark?.trim();
                                const typeText = app.typeOfMark?.trim();
                                if (markText && typeText)
                                  return `${markText} (${typeText})`;
                                if (markText) return markText;
                                if (typeText) return typeText;
                                return <span className="text-[#6D6D6D]">—</span>;
                              })()}
                            </Row>
                            <Row label="Class">
                              {app.classNumber != null
                                ? String(app.classNumber)
                                : <span className="text-[#6D6D6D]">—</span>}
                            </Row>
                            <Row label="Hearing Date">
                              {formatDateDisplay(app.dateOfOppositionHearing)}
                            </Row>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                      Invoice
                    </p>
                    <div
                      className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      <Row label="Invoice Amount for TM-M after Opposition Hearing">
                        {formatRupees(
                          invoiceAmount !== ""
                            ? invoiceAmount
                            : status.invoiceAmount
                        )}
                      </Row>
                      <Row label="Date of Invoice for TM-M after Opposition Hearing">
                        {formatDateDisplay(invoiceDate || status.invoiceDate)}
                      </Row>
                      <Row label="Invoice No. for TM-M after Opposition Hearing">
                        {status.invoiceNo?.trim() || (
                          <span className="text-[#6D6D6D]">
                            Auto-generated on save
                          </span>
                        )}
                      </Row>
                    </div>
                  </section>
                </div>
              );
            })()
          ) : previewTab === "pdf" ? (
            pdfUrl ? (
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages: n }) =>
                  onPreviewNumPagesChange(n)
                }
                loading={
                  <p className="text-sm text-[#6d6d6d]">Loading PDF…</p>
                }
                error={
                  <p className="text-sm text-red-600">Failed to load PDF.</p>
                }
                className="flex flex-col items-center gap-4"
              >
                {previewNumPages != null &&
                  Array.from({ length: previewNumPages }, (_, i) => (
                    <Page
                      key={i + 1}
                      pageNumber={i + 1}
                      width={Math.min(
                        800,
                        typeof window !== "undefined"
                          ? window.innerWidth - 48
                          : 800
                      )}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  ))}
              </Document>
            ) : (
              <p className="text-sm text-[#6D6D6D]">
                Generate the invoice PDF to preview it here.
              </p>
            )
          ) : previewTab === "message" ? (
            <div
              className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be sent to client WhatsApp after the invoice PDF
              </p>
              <WhatsAppMessageBlock
                message={messagePreview?.paymentMessage ?? ""}
                loading={messagePreviewLoading}
              />
            </div>
          ) : (
            <div
              className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Will be emailed to the client with the invoice PDF
              </p>
              <EmailPreviewBlock
                preview={messagePreview?.emailPreview ?? null}
                loading={messagePreviewLoading}
                attachmentFallback={
                  pdfUrl ? { filename: pdfFilename, url: pdfUrl } : null
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// File TM-M after Opposition Hearing — Invoice + Collected amount (4A(xlvii))
// ---------------------------------------------------------------------------

function TmMAfterHearingPaymentFields({
  status,
  collectedAmount,
  onChange,
}: {
  status: TmMAfterOppositionHearingInvoiceStatus | null;
  collectedAmount: string;
  onChange: (v: string) => void;
}) {
  if (!status) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading TM-M after Opposition Hearing Payment…
      </p>
    );
  }
  if (!status.isActive) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        This sub-step is only active when 4A(xliv) Hearing officer remark is
        "File TM-M".
      </p>
    );
  }
  const invoiceAmount = Number(status.invoiceAmount ?? 0);
  const savedCollected = Number(status.collectedAmount ?? 0) || 0;
  const thisPayment = parseAmount(collectedAmount);
  const totalCollected = savedCollected + thisPayment;
  const outstanding = Math.max(0, invoiceAmount - totalCollected);
  const outstandingFormatted = outstanding.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="Invoice Amount for TM-M after Opp Hearing"
          value={
            status.invoiceAmount != null
              ? `Rs. ${Number(status.invoiceAmount).toLocaleString()}/-`
              : ""
          }
        />
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Collected Amount for TM-M after Opp Hearing
            <RequiredMark />
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={collectedAmount}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/[^\d.]/g, "");
              const parts = cleaned.split(".");
              const next =
                parts.length > 1
                  ? `${parts[0]}.${parts.slice(1).join("")}`
                  : cleaned;
              onChange(next);
            }}
            onKeyDown={(e) => {
              if (
                e.key.length === 1 &&
                !e.ctrlKey &&
                !e.metaKey &&
                !/[\d.]/.test(e.key)
              ) {
                e.preventDefault();
              }
            }}
            placeholder="0"
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222]"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
          />
          {savedCollected > 0 && (
            <span className="text-[11px] leading-4 text-[#6D6D6D]">
              Already collected: Rs.{" "}
              {savedCollected.toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
              /-
            </span>
          )}
        </div>
      </div>

      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 w-full px-4 sm:px-5 py-4 sm:py-0 min-h-[60px] sm:h-[60px]"
        style={{ background: "#222222", borderRadius: "8px" }}
      >
        <span className="text-[12px] sm:text-[14px] font-normal leading-5 text-white/90">
          OUTSTANDING AMOUNT FOR TM-M AFTER OPPOSITION HEARING
        </span>
        <span className="text-[18px] sm:text-[22px] font-bold leading-8 sm:leading-10 text-white">
          Rs. {outstandingFormatted}/-
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// File TM-M after Opposition Hearing (4A(xlviii)) — sub-components
// ---------------------------------------------------------------------------

function TmMAfterHearingFiledFields({
  status,
  perTm,
  selectedTmNo,
  onPatch,
  onPreviewExisting,
  onPreviewPending,
}: {
  status: TmMAfterOppositionHearingFiledStatus | null;
  perTm: Record<string, { filingDate: string; pendingFile: File | null }>;
  selectedTmNo: string | null;
  onPatch: (
    tmNo: string,
    patch: Partial<{ filingDate: string; pendingFile: File | null }>
  ) => void;
  onPreviewExisting: (
    row: TmMAfterOppositionHearingFiledApplicationRow
  ) => void;
  onPreviewPending: (
    row: TmMAfterOppositionHearingFiledApplicationRow,
    file: File
  ) => void;
}) {
  if (!status || !selectedTmNo) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading File TM-M after Opposition Hearing…
      </p>
    );
  }
  if (!status.isActive) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        This sub-step is only active when 4A(xliv) Hearing officer remark is
        "File TM-M".
      </p>
    );
  }
  const tmRow = (status.applications ?? []).find(
    (r) => r.tmNo === selectedTmNo
  );
  const draft = perTm[selectedTmNo];
  if (!tmRow || !draft) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Selected TM is not on this TM-M list.
      </p>
    );
  }
  const dateParts = parseDateParts(draft.filingDate);
  const inputId = `tmm-after-opp-hearing-filed-input-${tmRow.tmNo}`;
  const hasFile = !!(
    draft.pendingFile || tmRow.tmMAfterOppositionHearingFileKey
  );
  const fileName =
    draft.pendingFile?.name ??
    tmRow.tmMAfterOppositionHearingFilename ??
    "File selected";

  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Date of Filing of TM-M after Opposition Hearing
            <RequiredMark />
          </label>
          <DateTimePicker
            value={dateParts ? partsToDate(dateParts) : null}
            onChange={(d) =>
              onPatch(selectedTmNo, {
                filingDate: d ? toLocalYmd(d) : "",
              })
            }
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
        <div className="hidden md:block flex-1" />
      </div>

      <div className="flex flex-col gap-3 border border-[#EEEEEE] rounded-[10px] p-4 md:p-5">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold text-[#101111]">
            TM-M after Opposition Hearing PDF
          </p>
          <p className="text-xs text-[#7D7D7D]">
            Upload the filed TM-M PDF downloaded from the Registry for this TM
            No.
          </p>
        </div>

        <div
          className={`flex flex-col md:flex-row items-stretch md:items-center gap-4 rounded-2xl border border-dashed px-4 py-4 cursor-pointer transition-colors ${
            hasFile
              ? "border-[#22C55E] bg-[#F0FDF4]"
              : "border-[#D8DAEB] bg-white"
          }`}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
            <img
              src={uploadDropzoneGraphic}
              alt=""
              width={60}
              height={60}
              className="flex-shrink-0"
            />
            <div className="flex flex-col gap-1 min-w-0 flex-1">
              {hasFile ? (
                <p className="text-sm font-medium text-[#16A34A] truncate">
                  {fileName}
                </p>
              ) : (
                <>
                  <p className="text-base font-medium text-[#101111]">
                    Drag and drop your file
                  </p>
                  <p className="text-sm text-[#7D7D7D]">
                    {SUPPORTED_FORMATS_LABEL}
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-row items-center gap-2 flex-shrink-0 self-end md:self-center">
            {!hasFile && (
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-sm font-medium text-[#2E3037] hover:bg-[#F5F5F5] transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById(inputId)?.click();
                }}
              >
                Select File
              </button>
            )}
            {hasFile && (
              <>
                <button
                  type="button"
                  className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (draft.pendingFile) {
                      onPreviewPending(tmRow, draft.pendingFile);
                    } else {
                      onPreviewExisting(tmRow);
                    }
                  }}
                >
                  Preview
                </button>
                <button
                  type="button"
                  className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPatch(selectedTmNo, { pendingFile: null });
                  }}
                >
                  Remove
                </button>
              </>
            )}
          </div>
        </div>

        <input
          id={inputId}
          type="file"
          accept={ALLOWED_FILE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reason = validateUploadFile(file);
            if (reason) {
              toast.error(reason);
              return;
            }
            onPatch(selectedTmNo, { pendingFile: file });
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inform Client after filing TM-M after Opposition Hearing (4A(xlix))
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Subsequent Hearings + Order of Opposition Hearing (4A(l) + 4A(li))
// ---------------------------------------------------------------------------

type StampPaperEvidenceDraftRow = {
  stampPapersForEvidence: string;
  stampVendorNameForEvidence: string;
  stampVendorPhoneForEvidence: string;
};

/**
 * 4A(xx) validation across every opposed application.
 *
 * `persistStampPaperEvidence` posts the whole `applications[]`, defaulting untouched rows to
 * `0`/`""`. Validating only the TM on screen therefore let half-filled siblings reach the API, which
 * rejected them with a raw class-validator array — "applications.1.stampPapersForEvidence must not be
 * less than 1,…" — naming an array index the user cannot map to an application. This names each
 * incomplete application by TM No. and lists exactly what it is missing, with the TM on screen first.
 *
 * Returns the message to show, or null when every row is complete.
 */
function validateStampPaperEvidenceDrafts(
  rows: Array<{ tmNo: string }>,
  draft: Record<string, StampPaperEvidenceDraftRow>,
  selectedTmNo: string | null
): string | null {
  const ordered = [...rows].sort((a, b) =>
    a.tmNo === selectedTmNo ? -1 : b.tmNo === selectedTmNo ? 1 : 0
  );

  const incomplete: string[] = [];
  for (const row of ordered) {
    const d = draft[row.tmNo];
    const missing: string[] = [];
    if (!d || !d.stampPapersForEvidence || Number(d.stampPapersForEvidence) <= 0) {
      missing.push("Number of Stamp Papers needed for Evidence");
    }
    if (!d?.stampVendorNameForEvidence.trim()) {
      missing.push("Name of Stamp Vendor for Evidence");
    }
    const phoneDigits = (d?.stampVendorPhoneForEvidence ?? "").replace(/\D/g, "");
    if (phoneDigits.length < 8 || phoneDigits.length > 15) {
      missing.push("a valid Stamp Vendor Phone Number");
    }
    if (missing.length) {
      incomplete.push(
        `${formatTmNoForDisplay(row.tmNo)} — ${missing.join(", ")}`
      );
    }
  }

  if (!incomplete.length) return null;
  return (
    "Stamp Paper details are saved for all applications together, so complete every application first. " +
    `Missing: ${incomplete.join("; ")}.`
  );
}

const ORDER_OF_OPP_HEARING_OUTCOME_OPTIONS: Array<{
  value: OrderOfOppositionHearingOutcomeValue;
  label: string;
}> = [
  { value: "ACCEPTED_AFTER_HEARING", label: "Accepted after Hearing" },
  { value: "REFUSED_AFTER_HEARING", label: "Refused after Hearing" },
  {
    value: "REFUSED_AFTER_NON_APPEARANCE",
    label: "Refused after non-appearance",
  },
  {
    value: "ACCEPTED_AFTER_NON_APPEARANCE",
    label: "Accepted after non-appearance",
  },
];

function OrderOfOppHearingMessagesConfirmPreview({
  messages,
  loading,
  error,
}: {
  messages: string[];
  loading: boolean;
  error: string | null;
}) {
  return (
    <div
      className="flex flex-col gap-3 px-3.5 py-3.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-xs font-medium text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        WhatsApp Message Preview
      </span>
      {loading ? (
        <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[120px] bg-white">
          <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
            Loading preview…
          </p>
        </div>
      ) : error ? (
        <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[120px] bg-white">
          <p className="text-xs text-[#B45309]" style={{ lineHeight: "20px" }}>
            {error}
          </p>
        </div>
      ) : messages.length ? (
        <div className="flex flex-col gap-2.5">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 bg-white"
            >
              <p
                className="text-xs text-[#222222] whitespace-pre-line"
                style={{ lineHeight: "20px" }}
              >
                {renderWhatsAppFormatting(msg)}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[120px] bg-white">
          <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
            The Order of Opposition Hearing message body will appear here once
            the preview loads.
          </p>
        </div>
      )}
    </div>
  );
}

function OrderOfOppHearingFields({
  status,
  draft,
  onChange,
}: {
  status: OrderOfOppositionHearingStatus | null;
  draft: Record<string, OrderOfOppositionHearingOutcomeValue | null>;
  onChange: (
    tmNo: string,
    outcome: OrderOfOppositionHearingOutcomeValue | null
  ) => void;
}) {
  if (!status) {
    return (
      <p className="text-sm text-[#6D6D6D]">Loading Order of Opposition Hearing…</p>
    );
  }
  const apps = status.applications ?? [];
  if (apps.length === 0) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        No applications in Opposition Hearing status for this acknowledgement.
      </p>
    );
  }
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {apps.map((app) => {
        // Spec pg 158 lists all four outcomes unconditionally. They were previously filtered down
        // to two by Hearing Status (4zzj), which also blanked a saved outcome that no longer
        // matched the current status. The Hearing Officer can pass any of the four.
        const value = draft[app.tmNo] ?? "__none__";
        return (
          <div key={app.tmNo} className="flex flex-col gap-1 max-w-[460px]">
            <label className="text-xs font-normal text-[#222222] leading-5">
              Outcome of Opposition Hearing
              {apps.length > 1 ? ` — ${formatTmNoForDisplay(app.tmNo)}` : ""}
              <RequiredMark />
            </label>
            <Select
              value={value}
              onValueChange={(v) =>
                onChange(
                  app.tmNo,
                  v === "__none__"
                    ? null
                    : (v as OrderOfOppositionHearingOutcomeValue)
                )
              }
            >
              <SelectTrigger className="h-9 min-h-[36px] w-full border border-[#EEEEEE] rounded-[6px] px-2.5 py-2 text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                {ORDER_OF_OPP_HEARING_OUTCOME_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      })}
    </div>
  );
}

function InformTmMAfterHearingFields({
  preview,
  filed,
}: {
  preview: InformClientTmMAfterOppositionHearingPreview | null;
  filed: TmMAfterOppositionHearingFiledStatus | null;
}) {
  if (!preview && !filed) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        Loading Inform Client after filing TM-M after Opposition Hearing…
      </p>
    );
  }
  const firstApp = filed?.applications?.[0] ?? null;
  return (
    <div
      className="flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <ReadOnlyField
          label="Acknowledgement No."
          value={filed?.acknowledgementNo ?? ""}
        />
        <ReadOnlyField
          label="Date of Filing"
          value={firstApp?.dateOfTmMAfterOppositionHearingFiled ?? filed?.dateOfFiling ?? ""}
        />
      </div>
      <ReadOnlyField label="Mark" value={firstApp?.mark ?? ""} />
      <div className="flex flex-col gap-2.5">
        <span
          className="text-base font-normal text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          Message Preview
        </span>
        <div className="border border-[#EEEEEE] rounded-[6px] px-3.5 py-3.5 min-h-[120px] bg-white">
          {preview?.message ? (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              {renderWhatsAppFormatting(preview.message)}
            </p>
          ) : (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              A &quot;Trademark Update&quot; message will be sent to the client
              informing them that the TM-M form has been filed as directed by
              the hearing officer during the Opposition Hearing.
              {"\n\n"}
              <span className="text-[#6D6D6D]">
                The exact message body is generated server-side from the
                applications for which TM-M has been filed; click Send &amp;
                Continue to dispatch via WhatsApp.
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

